// ═══════════════════════════════════════════════════════════════════════════════
// UI strings — zh-Hant / zh-Hans / en / id / fil
// ═══════════════════════════════════════════════════════════════════════════════

import { LANGUAGE, type Language } from '@fdh/shared';
import {
  USAGE_REGION_UI_MESSAGES_EN,
  USAGE_REGION_UI_MESSAGES_FIL,
  USAGE_REGION_UI_MESSAGES_ID,
  USAGE_REGION_UI_MESSAGES_ZH_HANS,
  USAGE_REGION_UI_MESSAGES_ZH_HANT,
} from './usageRegionUiMessages.js';
import { UI_MESSAGES_FIL } from './uiMessagesFil.js';
import { UI_MESSAGES_ID } from './uiMessagesId.js';

export type MessageParams = Record<string, string | number>;

export type UiMessageTable = Record<string, string>;

export const UI_MESSAGES: Record<Language, UiMessageTable> = {
  [LANGUAGE.ZH_HANT]: {
    ...USAGE_REGION_UI_MESSAGES_ZH_HANT,
    'common.loading': '載入中…',
    'common.datePlaceholder': '選擇日期',
    'common.openCalendarAria': '開啟日曆選擇日期',
    'common.cancel': '取消',
    'common.close': '關閉',
    'common.closeViewer': '關閉預覽',
    'common.imageViewer.zoomHint': '雙指捏合可放大／縮小；雙擊還原；亦可使用下方 + −',
    'common.imageViewer.zoomInAria': '放大相片',
    'common.imageViewer.zoomOutAria': '縮小相片',
    'common.imageViewer.resetZoomAria': '還原縮放',
    'common.imageViewer.scalePercent': '{{percent}}%',
    'common.confirm': '確定',
    'common.next': '下一步',
    'common.back': '上一步',
    'common.save': '儲存',
    'common.appTagline': '僱主與家務助理的家務管理平台',
    'common.languageSwitcherAria': '介面語言',
    'notFound.title': '找不到頁面',
    'notFound.body': '您要前往的頁面不存在或已被移除。',
    'language.zhHant': '繁體中文',
    'language.zhHans': '简体中文',
    'language.en': '英文',
    'language.id': '印尼文',
    'language.fil': '菲律賓文',
    'legal.contentEnglishNotice': '此頁內容以英文顯示。',
    'login.title': '登入',
    'login.subtitle': '使用電郵與密碼登入帳戶',
    'auth.errorGeneric': '發生錯誤，請重試',
    'auth.emailLabel': '電郵',
    'auth.passwordLabel': '密碼',
    'auth.rememberMe': '記住我',
    'auth.forgotPasswordLink': '忘記密碼？',
    'auth.signIn': '登入',
    'auth.signInWithGoogle': '使用 Google 登入',
    'auth.signingIn': '登入中…',
    'auth.linkRegister': '還沒有帳戶？註冊',
    'auth.linkLogin': '已有帳戶？登入',
    'auth.registerTitle': '建立帳戶',
    'auth.registerSubtitle': '選擇身份並填寫資料',
    'auth.roleLabel': '身份',
    'auth.roleRequired': '請先選擇身份。',
    'auth.passwordRulesHint': '8–16 個字元，需含大寫、小寫字母與數字',
    'auth.nicknameLabel': '稱呼（暱稱）',
    'auth.pairingCodeOptional': '配對碼（選填）',
    'auth.pairingCodePlaceholder': '6 位字元',
    'auth.pairingCodeHint':
      '若有僱主提供的配對碼可在此輸入；亦可註冊後再到「輸入配對碼」頁面完成配對。',
    'auth.invitationCodeLabel': '邀請碼',
    'auth.invitationCodeOptional': '邀請碼（選填）',
    'auth.invitationCodePlaceholder': '8 位字元',
    'auth.invitationCodeHint': '如有邀請碼請輸入',
    'auth.invitationCodeRequired': '請輸入邀請碼',
    'auth.invitationCodeValid': '邀請碼可用',
    'auth.invitationCodeBenefitL1Extend': '延長試用 {{days}} 日',
    'auth.invitationCodeBenefitL3Trial': '高級會員試用 {{days}} 日',
    'auth.invitationCodeInvalid': '邀請碼無效',
    'auth.invitationCodeErrorFormat': '邀請碼格式錯誤',
    'auth.invitationCodeErrorNotYetActive': '邀請碼尚未生效',
    'auth.invitationCodeErrorExpired': '邀請碼已過期',
    'auth.invitationCodeErrorDisabled': '邀請碼已停用',
    'auth.invitationCodeErrorMaxUses': '邀請碼已達使用上限',
    'auth.invitationCodeErrorAfterRegistrationOnly': '此邀請碼僅可於註冊後兌換',
    'auth.invitationCodeDeepLinkInvalid': '此邀請連結無效或已失效，請輸入其他有效邀請碼（如有）。',
    'auth.invitationCodeLockedHint': '此邀請碼來自邀請連結，註冊時不可修改。',
    'inviteReferrer.menuItem': '邀請碼使用情況',
    'inviteReferrer.pageTitle': '邀請碼使用情況',
    'inviteReferrer.pageSubtitle': '查看有多少人以你的邀請碼註冊或兌換，以及其中多少人已成為付款用戶。',
    'inviteReferrer.statsLabel': '邀請碼統計',
    'inviteReferrer.code': '邀請碼',
    'inviteReferrer.benefit': '邀請碼效果',
    'inviteReferrer.benefitL1Extend': '延長試用 {{days}} 日',
    'inviteReferrer.benefitL3Trial': '高級會員試用 {{days}} 日',
    'inviteReferrer.maxUses': '使用上限',
    'inviteReferrer.maxUsesLimited': '{{used}} / {{max}}',
    'inviteReferrer.maxUsesUnlimited': '{{used}} / 無上限',
    'inviteReferrer.validity': '使用期限',
    'inviteReferrer.validityRange': '{{start}} — {{end}}',
    'inviteReferrer.validityImmediate': '立即生效',
    'inviteReferrer.validityPermanent': '永久有效',
    'inviteReferrer.totalUses': '使用人數',
    'inviteReferrer.paidUsers': '付款用戶',
    'inviteReferrer.shareLink': '分享連結',
    'inviteReferrer.copyLink': '複製連結',
    'inviteReferrer.copied': '已複製',
    'inviteReferrer.notBound': '你尚未被綁定為邀請碼推薦人。',
    'auth.registerCta': '註冊',
    'auth.registerWithGoogle': '使用 Google 註冊',
    'auth.registering': '註冊中…',
    'auth.registerRoleConfirmTitle': '確認註冊身份',
    'auth.registerRoleConfirmDescription':
      '你目前選擇的身份是「{{role}}」。請確認身份正確；註冊完成後身份不可更改。',
    'auth.registerPicsScrollRequired': '請先將個人資料收集聲明（PICS）捲動至底部。',
    'auth.registerPicsAgreeRequired': '請勾選同意個人資料收集聲明。',
    'auth.forgotTitle': '忘記密碼',
    'auth.forgotSubtitle': '輸入電郵地址，我們會發送重設連結（如該電郵已註冊）。',
    'auth.forgotSubmit': '發送重設連結',
    'auth.forgotSending': '發送中…',
    'auth.forgotSuccess': '若此電郵已註冊，您將很快收到重設密碼的電郵。',
    'auth.resetTitle': '重設密碼',
    'auth.resetSubtitle': '請輸入新密碼。',
    'auth.newPasswordLabel': '新密碼',
    'auth.confirmPasswordLabel': '確認新密碼',
    'auth.resetSubmit': '重設密碼',
    'auth.resetting': '重設中…',
    'auth.passwordMismatch': '兩次輸入的密碼不一致',
    'auth.showPassword': '顯示',
    'auth.hidePassword': '隱藏',
    'auth.confirmPasswordRegisterLabel': '確認密碼',
    'auth.resetSuccess': '密碼已重設，請使用新密碼登入。',
    'auth.goToLogin': '前往登入',
    'auth.goToHome': '前往首頁',
    'auth.verifyTitle': '驗證電郵',
    'auth.verifyPending': '正在驗證…',
    'auth.verifySuccess': '電郵驗證成功。',
    'auth.verifyFail': '驗證失敗，連結可能已過期。',
    'auth.missingToken': '連結無效或缺少驗證碼。',
    'auth.accountRemovalConfirmTitle': '確認刪除帳戶',
    'auth.accountRemovalConfirmPrompt': '按下方按鈕後，帳戶會立即刪除，且無法復原。',
    'auth.accountRemovalConfirmButton': '確認刪除帳戶',
    'auth.accountRemovalConfirmPending': '正在刪除帳戶…',
    'auth.accountRemovalConfirmSuccess': '帳戶已刪除。',
    'auth.accountRemovalConfirmFail': '確認失敗，連結可能已過期。',
    'auth.googleCallbackTitle': 'Google 登入中',
    'auth.googleCallbackLoading': '正在完成 Google 驗證，請稍候…',
    'auth.googleDecisionNotRegistered': '系統沒有此 Google 帳戶註冊記錄，是否立即註冊？',
    'auth.googleDecisionRegisteredGoogle': '系統已有此 Google 帳戶註冊記錄，是否立即登入？',
    'auth.googleDecisionRegisteredEmail': '系統已有同一 email 的電郵註冊記錄，是否改用電郵登入？',
    'auth.googleDecisionConfirmRegister': '是，立即註冊',
    'auth.googleDecisionConfirmLogin': '是，立即登入',
    'auth.googleDecisionConfirmEmailLogin': '是，改用電郵登入',
    'auth.googleDecisionCancelHome': '否，返回主頁',
    'auth.registerWizardStepBadge': '第 {{current}} 步，共 {{total}} 步',
    'auth.registerWizardProgressAria': '註冊進度',
    'auth.registerMethodTitle': '建立帳戶',
    'auth.registerMethodSubtitle': '選擇以電郵或 Google 註冊 MaidEasy。',
    'auth.registerDividerOrEmail': '或以電郵註冊',
    'auth.registerEmailHelper': '我們會將驗證連結寄到此電郵。',
    'auth.registerContinueWithEmail': '以電郵繼續',
    'auth.registerStepRoleTitle': '選擇身份',
    'auth.registerStepRoleSubtitle': '請選擇僱主或家務助理。註冊完成後身份無法更改。',
    'auth.registerRoleImportantNotice':
      '重要：身份一經確認並完成註冊後無法更改，請務必選擇正確身份。',
    'auth.registerRoleEmployerTitle': '僱主',
    'auth.registerRoleEmployerDesc': '管理任務、開支，並與家務助理連線。',
    'auth.registerRoleHelperTitle': '家務助理',
    'auth.registerRoleHelperDesc': '查看每日任務、提交收據並追蹤工作。',
    'auth.registerStepNicknameTitle': '輸入詳細資料',
    'auth.registerStepNicknameSubtitle': '此名稱會顯示予配對另一方；使用地區及暱稱之後可在設定中修改。',
    'auth.registerStepPasswordTitle': '設定密碼',
    'auth.registerStepPasswordSubtitle': '請建立足夠強的密碼以保護帳戶。',
    'auth.registerPasswordPolicyTitle': '密碼須符合：',
    'auth.registerPasswordPolicyLength': '8–16 個字元',
    'auth.registerPasswordPolicyUpper': '至少一個大寫字母（A–Z）',
    'auth.registerPasswordPolicyLower': '至少一個小寫字母（a–z）',
    'auth.registerPasswordPolicyDigit': '至少一個數字（0–9）',
    'auth.registerStepPairingTitle': '配對碼（選填）',
    'auth.registerStepPairingSubtitle': '若僱主已提供 6 位配對碼，可在此輸入；留空則可稍後於配對頁面輸入。',
    'auth.registerStepPicsScrollFirst': '請先將下方聲明捲動至底部，才可勾選同意。',
    'auth.registerContinue': '繼續',
    'auth.registerStepReviewTitle': '確認資料',
    'auth.registerStepReviewSubtitle': '請確認以下資料正確；如有錯誤可點「修改」返回該步驟。',
    'auth.registerReviewMethod': '註冊方式',
    'auth.registerReviewMethodEmail': '電郵',
    'auth.registerReviewMethodGoogle': 'Google',
    'auth.registerReviewRole': '身份',
    'auth.registerReviewEmail': '電郵地址',
    'auth.registerReviewNickname': '暱稱',
    'auth.registerReviewUsageRegion': '使用地區',
    'auth.registerReviewPairing': '配對碼',
    'auth.registerReviewPairingEmpty': '（未填寫）',
    'auth.registerReviewInvitationCode': '邀請碼',
    'auth.registerReviewInvitationCodeEmpty': '未使用',
    'auth.registerReviewPicsAgreed': '你已同意個人資料收集聲明（PICS）及相關資料處理條款。',
    'auth.registerReviewEdit': '修改',
    'auth.registerCompleteRegistration': '完成註冊',
    'auth.registerCompleteTitle': '註冊完成',
    'auth.registerCompleteEmailVerify':
      '我們已寄出驗證電郵，請檢查收件匣（及垃圾郵件）並點擊連結完成驗證。',
    'auth.registerCompleteGoogle': '你的 Google 帳戶已完成驗證，可繼續使用 MaidEasy。',
    'auth.registerCompleteContinue': '繼續',
    'auth.emailTaken': '此電郵已被註冊',
    'auth.emailChecking': '正在檢查電郵…',
    'auth.emailInvalid': '請輸入有效的電郵地址',
    'home.navLogin': '登入',
    'home.navLoginEmail': '電郵登入',
    'home.navGoogleLogin': '使用 Google 登入',
    'home.navRegister': '註冊',
    'home.registerModalTitle': '選擇註冊方式',
    'home.registerModalSubtitle': '請先選擇使用 Google 註冊或電郵註冊。',
    'home.registerWithGoogle': '使用 Google 註冊',
    'home.registerWithEmail': '電郵註冊',
    'home.navLogout': '登出',
    'home.navPairing': '配對狀態',
    'home.navEmployerApp': '任務管理（僱主）',
    'home.navHelperApp': '今日待辦（家務助理）',
    'home.phaseLine': '',
    'home.linkPrivacy': '私隱權政策',
    'home.linkTerms': '服務條款',
    'home.linkPics': 'PICS',
    'home.linkFaq': '常見問題',
    'home.linkFeatures': '功能介紹',
    'home.linkFeaturesAria': '功能介紹（另開新分頁）',
    'faq.title': '常見問題',
    'faq.subtitle': '按身份查看常見問題與操作說明。',
    'faq.tabHelper': '家務助理',
    'faq.tabEmployer': '僱主',
    'faq.loadError': '無法載入常見問題',
    'faq.empty': '目前沒有常見問題內容。',
    'nav.home': '首頁',
    'systemMessage.menu': '系統訊息',
    'systemMessage.list.title': '系統訊息',
    'systemMessage.list.empty': '目前沒有系統訊息。',
    'systemMessage.list.colSubject': '主旨',
    'systemMessage.list.colDate': '日期',
    'systemMessage.list.openItem': '開啟：{{subject}}',
    'systemMessage.ticker.title': '系統訊息',
    'systemMessage.ticker.count': '{{current}} / {{total}}',
    'systemMessage.ticker.openDetails': '查看系統訊息詳情',
    'systemMessage.ticker.prev': '上一則系統訊息',
    'systemMessage.ticker.next': '下一則系統訊息',
    'systemMessage.action.close': '關閉',
    'systemMessage.detailEmpty': '（沒有詳細內容）',
    'systemMessage.error.loadList': '無法載入系統訊息',
    'systemMessage.image.enlargeAria': '放大圖片',
    'systemMessage.image.openLinkAria': '開啟連結',
    'systemMessage.menuItemUnreadAria': '{{label}}，{{count}} 則未讀',
    'nav.tasks': '任務',
    'nav.expensesSettlements': '開支/結算',
    'nav.expensesSettlementsHelper': '開支/結算',
    'nav.family': '家庭',
    'nav.stock': '存貨',
    'nav.mainAria': '主應用底部導覽',
    'pwa.offlineBanner': '離線 — 本程式暫時無法使用直至您的網絡恢復',
    'pwa.syncing': '同步中…',
    'pwa.syncComplete': '同步完成',
    'pwa.syncFailed': '同步失敗 — 請重試',
    'pwa.pendingSync': '尚有待同步項目',
    'pwa.installCta': '安裝應用程式',
    'pwa.installDismiss': '關閉',
    'playBanner.subtitle': 'MaidEasy — 於 Google Play',
    'playBanner.install': '安裝',
    'playBanner.installAria': '於 Google Play 安裝 MaidEasy',
    'playBanner.dismissAria': '關閉安裝橫幅',
    'playBanner.regionAria': 'Google Play 安裝提示',
    'task.complete.savedOffline': '已儲存於本機，連線後會自動同步',
    'receipt.analyze.savedOffline': '已儲存於本機，連線後會自動同步',
    'legal.privacy.title': '私隱權政策',
    'legal.privacy.body':
      '本頁為法律文件佔位內容。我們會說明如何收集、使用與保護您的個人資料；正式上線前將由法務審閱並更新。',
    'legal.terms.title': '服務條款',
    'legal.terms.body':
      '本頁為法律文件佔位內容。使用本服務即表示您同意相關條款與細則；正式條款將於上線前審閱並公布。',
    'legal.pics.title': '個人資料收集聲明（PICS）',
    'legal.pics.body':
      '本頁為多語言 PICS 佔位內容，與註冊流程中的聲明一致方向；正式文案將配合法務與合規審閱後更新。',
    'legal.backHome': '返回首頁',
    'settings.title': '個人設定',
    'settings.subtitle': '語言、時區、外觀與通知',
    'settings.nicknameLabel': '稱呼（暱稱）',
    'settings.nicknamePlaceholder': '請輸入稱呼',
    'settings.languageLabel': '顯示語言',
    'settings.timezoneLabel': '時區',
    'settings.usageRegionLabel': '使用地區',
    'settings.themeLabel': '主題',
    'settings.themeLight': '淺色',
    'settings.themeDark': '深色',
    'settings.themeSystem': '跟隨系統',
    'settings.fontSizeLabel': '字體大小',
    'settings.fontXSmall': '極小',
    'settings.fontSmall': '小',
    'settings.fontMedium': '中',
    'settings.fontLarge': '大',
    'settings.fontXLarge': '特大',
    'settings.aiUsageTitle': '今日 AI 用量',
    'settings.aiUsageHint': 'AI 收據及家裏存貨分析（每日於 UTC 0:00 重置）',
    'settings.receiptUsageTitle': '今日收據掃描用量',
    'settings.receiptUsageHint': '每日可提交收據張數（每日於 UTC 0:00 重置）',
    'settings.aiUsageLoading': '載入中…',
    'settings.dailyUsageError': '無法載入每日用量',
    'usage.pageTitle': '用量計算',
    'usage.pageSubtitle': '查看今日配額與任務上限',
    'usage.backToSettings': '返回設定',
    'usage.taskSubmitTitle': '今日任務提交用量',
    'usage.taskSubmitHint': '每日可提交任務次數（每日於 UTC 0:00 重置）',
    'usage.taskReviewTitle': '今日任務審核用量',
    'usage.taskReviewHint': '每日可審核任務次數（每日於 UTC 0:00 重置）',
    'usage.totalTasksTitle': '總任務建立數',
    'usage.totalTasksHint': '配對內累計任務槽位（已封存任務不計入）',
    'usage.pinMessagesTitle': '總置頂訊息數',
    'usage.pinMessagesHint': '配對內累計置頂訊息數量',
    'usage.resetAtLabel': '下次重置',
    'usage.unlimited': '無限',
    'usage.viewUsageDetails': '查看用量詳情',
    'usage.membershipLevel': '會員等級',
    'usage.levelTrial': '試用會員',
    'usage.levelFree': '免費會員',
    'usage.levelPaid': '高級會員',
    'usage.helperMembershipHelpTriggerAria': '查看會員等級說明',
    'usage.helperMembershipHelpAria': '會員等級說明',
    'usage.helperMembershipHelpTitle': '會員等級說明',
    'usage.helperMembershipHelpFollowsEmployer':
      '家務助理無法自行升級會員等級。會員等級跟隨僱主帳戶。',
    'usage.helperMembershipHelpContactEmployer':
      '如用量不夠，請聯絡僱主升級會員等級。',
    'usage.helperSectionTitle': '以下是家務助理的用量',
    'usage.compareLevels': '比較會員等級',
    'usage.upgradeBannerBefore': '按',
    'usage.upgradeBannerClickHere': '此',
    'usage.upgradeBannerAfter': '立即升級至高級會員，以解鎖更多用量及功能',
    'usage.upgradeBannerClickAria': '前往會員訂閱頁面',
    'usage.playStoreUpgradeNotice':
      '如需升級或管理訂閱，請使用瀏覽器或「加到主畫面」的應用程式開啟本服務（Google Play 版不提供訂閱付款）。',
    'membership.restoreModal.title': '恢復已封存的任務',
    'membership.restoreModal.description': '您已升級會員。請選擇要恢復的家裏存貨、接送或食藥提醒任務；未選取的任務會維持封存。',
    'membership.restoreModal.confirm': '恢復所選任務',
    'membership.restoreModal.skip': '暫不恢復',
    'membership.restoreModal.success': '已恢復所選任務',
    'membership.restoreModal.error': '無法恢復任務，請稍後再試',
    'membership.pricing.compareTitle': '比較會員等級',
    'membership.pricing.loading': '正在載入會員方案…',
    'membership.pricing.loadError': '暫時無法載入收費方案，請稍後再試。',
    'membership.pricing.priceUnavailable': '—',
    'membership.pricing.monthlyFeeLabel': '月費:',
    'membership.pricing.yearlyFeeLabel': '年費:',
    'membership.pricing.cheaperBadge': '年費計劃更便宜',
    'membership.pricing.discountUntil': '折扣直至 {date}',
    'membership.pricing.welcomeRemark': '首月 {first}，之後 {thereafter}',
    'membership.pricing.trialStartsOnRegister': '註冊後即開始試用',
    'membership.pricing.yes': '是',
    'membership.pricing.no': '否',
    'subscription.openPage': '會員訂閱',
    'subscription.title': '會員訂閱',
    'subscription.membershipLevel': '會員等級',
    'subscription.previousSubscriptionTitle': '上一個高級會員訂閱',
    'subscription.previousSubscriptionEnded': '上一個高級會員訂閱已結束',
    'subscription.previousSubscriptionPaymentFailed': '上一個高級會員訂閱已結束（曾扣款失敗）',
    'subscription.trialEndsAt': '試用會員到期',
    'subscription.invitationTrialEndsAt': '邀請碼高級會員試用到期',
    'subscription.subscriptionEndsAt': '訂閱到期',
    'subscription.nextChargeAt': '下次扣款',
    'subscription.payToday': '立即成為高級會員，以解鎖更多功能',
    'subscription.startTrial': '設定訂閱（試用期內先不扣款）',
    'subscription.payTodayAmount': '{{period}}只需{{amount}}',
    'subscription.noChargeToday': '今日無需付款',
    'subscription.firstChargeAmount': '首次扣款 {{amount}}',
    'subscription.coversPeriod': '首個付費期：{{start}} 至 {{end}}',
    'subscription.nextCharge': '下次扣款：{{date}}',
    'subscription.firstChargeOn': '首次扣款將於 {{date}} 進行',
    'subscription.promoPriceFirstMonth': '首月只需 {{dueAmount}}，其後{{period}} {{listPrice}}',
    'subscription.promoPriceLimitedTime':
      '活動期間{{period}}只需 {{dueAmount}}（直至 {{endsAt}}），其後{{period}} {{listPrice}}',
    'subscription.promoAppliedFirstMonth': '首月優惠：減免 {{amount}}',
    'subscription.promoAppliedLimitedTime': '期間限定優惠：減免 {{amount}}（直至 {{endsAt}}）',
    'subscription.invitationTrialNote': '邀請碼試用至 {{date}}，試用期內無需另付費。',
    'subscription.invitationTrialChargeLaterNote':
      '邀請碼試用至 {{date}}，系統會於試用結束當日開始首次扣款。',
    'subscription.subscribeButton': '付款訂閱',
    'subscription.resubscribeButton': '重新訂閱高級會員',
    'subscription.startTrialButton': '設定訂閱',
    'subscription.stripeCheckoutDiscountHiddenNote':
      '下一頁付款畫面只會顯示今日無需付款及原價，不會顯示折扣。折扣會於首次扣款時自動套用。',
    'subscription.promoMayExpireIfDeferred':
      '限時優惠將於首次扣款前結束，屆時可能改扣原價。',
    'subscription.termsTitle': '訂閱條款及細則',
    'subscription.termsLinkLabel': '《訂閱條款及細則》',
    'subscription.termsConsentPay':
      '按下「{{buttonLabel}}」並付款，即表示您同意{{termsLink}}。',
    'subscription.termsConsentSetup': '按下「{{buttonLabel}}」即表示您同意{{termsLink}}。',
    'subscription.termsLoadError': '無法載入訂閱條款，請稍後再試。',
    'subscription.alreadySubscribed': '您已有有效付費訂閱。',
    'subscription.stripeNotConfigured': '訂閱功能尚未在此環境啟用，請聯絡管理員。',
    'subscription.loadError': '無法載入訂閱資料，請稍後再試。',
    'subscription.checkoutSuccess': '訂閱設定成功。',
    'subscription.checkoutCancel': '已取消付款。',
    'subscription.manageButton': '管理訂閱',
    'subscription.playStoreBillingNotice':
      'Google Play 版不提供訂閱、管理訂閱或轉換計劃。',
    'subscription.playStoreOfficialWebsiteLine':
      '請使用瀏覽器前往 MaidEasy 官方網站\n{{url}}',
    'subscription.invoicesTitle': '最近發票',
    'subscription.viewInvoiceButton': '查看發票',
    'subscription.statusActive': '訂閱狀態：有效',
    'subscription.statusPastDue': '訂閱狀態：扣款失敗',
    'subscription.statusCanceled': '訂閱狀態：已取消',
    'subscription.willAutoRenewOn': '您的訂閱將於 {{date}} 自動續費',
    'subscription.willAutoRenewOnWithAmount': '您的訂閱將於 {{date}} 自動續費 ({{amount}})',
    'subscription.willAutoRenewOnPromo': '您的訂閱將於 {{date}} 自動續費 (優惠價{{amount}})',
    'subscription.nextRenewLimitedTimePromo':
      '限時優惠：{{period}}只需 {{dueAmount}}（直至 {{endsAt}}，原價 {{listPrice}}）',
    'subscription.periodPerMonth': '每月',
    'subscription.periodPerYear': '每年',
    'subscription.willEndOnCancelled': '您的訂閱將於 {{date}} 結束（已取消續訂）',
    'subscription.intervalMonth': '月費',
    'subscription.intervalYear': '年費',
    'subscription.selectBillingInterval': '選擇收費週期',
    'subscription.planUnavailable': '此收費週期暫未開放。',
    'subscription.currentBillingInterval': '目前計劃：{{interval}}',
    'subscription.pendingIntervalChange': '已預約於 {{date}} 改為{{interval}}（本期完結後生效）',
    'subscription.lockedPromoReserved': '已為你保留優惠減免 {{amount}}（若到期當日有更高優惠會自動套用）',
    'subscription.cancelIntervalChange': '取消預約轉換',
    'subscription.switchToYear': '改為年費（下期生效）',
    'subscription.switchToMonth': '改為月費（下期生效）',
    'subscription.intervalChangeNote': '轉換會在目前計劃完結後生效。',
    'subscription.intervalChangeScheduled': '已預約轉換收費週期。',
    'subscription.intervalChangeCancelled': '已取消預約轉換。',
    'subscription.intervalChangeConfirmTitleYear': '確認改為年費',
    'subscription.intervalChangeConfirmTitleMonth': '確認改為月費',
    'subscription.intervalChangeConfirmIntro':
      '您目前為{{current}}，將預約改為{{target}}。轉換會在現有計劃完結後生效。',
    'subscription.intervalChangeConfirmEffectiveAt': '轉換生效／現有計劃完結時間',
    'subscription.intervalChangeConfirmFirstChargeAt': '首筆新計劃扣款時間',
    'subscription.intervalChangeConfirmCoversPeriod': '首個新計劃涵蓋期間',
    'subscription.intervalChangeConfirmCoversPeriodValue': '{{start}} 至 {{end}}',
    'subscription.intervalChangeConfirmAmount': '預計首筆扣款金額',
    'subscription.intervalChangeConfirmPromoLimited':
      '目前適用限時優惠：減免 {{amount}}（直至 {{endsAt}}；若扣款當日有更高優惠會自動套用）',
    'subscription.intervalChangeConfirmPromoGeneric': '目前適用優惠：減免 {{amount}}',
    'subscription.intervalChangeConfirmNotes': '注意事項',
    'subscription.intervalChangeConfirmNoRefund': '現有計劃會用至完結為止。',
    'subscription.intervalChangeConfirmPromoLockNote':
      '確認後會先保留今日適用的限時優惠；若扣款當日有更高優惠則改用更高者，否則使用已保留優惠。',
    'subscription.intervalChangeConfirmButtonYear': '確認改為年費',
    'subscription.intervalChangeConfirmButtonMonth': '確認改為月費',
    'subscription.cancelIntervalChangeConfirmTitle': '確認取消預約轉換',
    'subscription.cancelIntervalChangeConfirmIntro':
      '將取消已預約於 {{date}} 改為{{interval}}的轉換，並繼續維持目前計劃。',
    'subscription.cancelIntervalChangeConfirmPromoNote':
      '已保留的優惠減免 {{amount}} 亦會一併取消。',
    'subscription.cancelIntervalChangeConfirmButton': '確認取消預約',
    'membership.restoreTask.action': '恢復任務',
    'membership.restoreTask.success': '任務已恢復',
    'admin.upgradeToL3': '升級至 L3（測試）',
    'admin.upgradeToL3Confirm': '確定將此僱主升級至 L3？將恢復自動翻譯設定，並於首次進入儀表板時提示恢復已封存任務。',
    'admin.upgradeToL3Success': '已升級至 L3。',
    'admin.upgradeToL3Error': '升級至 L3 失敗。',
    'admin.grantL3TrialTitle': '發放 L3 試用',
    'admin.grantL3TrialHint': '輸入結束日期時間。系統會將僱主設為 L3，並以邀請碼 L3 試用到期時間處理。',
    'admin.grantL3TrialField': '試用到期（HKT，YYYY-MM-DD hh:mm）',
    'admin.grantL3TrialButton': '發放 L3 試用',
    'admin.grantL3TrialConfirm': '確定要為此僱主發放 L3 試用？',
    'admin.grantL3TrialSuccess': '已發放 L3 試用。',
    'admin.grantL3TrialError': '發放 L3 試用失敗。',
    'admin.grantL3TrialDateRequired': '請先輸入試用到期日期時間。',
    'limitModal.title.default': '已達使用上限',
    'limitModal.title.batchInsufficient': '今日用量不足',
    'limitModal.title.TOTAL_TASK_LIMIT_REACHED': '任務建立數已滿',
    'limitModal.title.COMMENT_LIMIT_REACHED': '留言數已達上限',
    'limitModal.title.DAILY_TASK_SUBMIT_LIMIT_REACHED': '今日任務提交已達上限',
    'limitModal.title.DAILY_TASK_REVIEW_LIMIT_REACHED': '今日任務審核已達上限',
    'limitModal.title.DAILY_RECEIPT_LIMIT_REACHED': '今日收據提交已達上限',
    'limitModal.title.DAILY_AI_ANALYZE_LIMIT_REACHED': '今日 AI 分析已達上限',
    'limitModal.title.TASK_KIND_NOT_ALLOWED': '此任務類型不可用',
    'limitModal.title.AUTO_TRANSLATION_NOT_ALLOWED': '自動翻譯不可用',
    'limitModal.title.INVENTORY_LOCATION_LIMIT_REACHED': '盤點位置數已達上限',
    'limitModal.title.INVENTORY_CATEGORY_LIMIT_REACHED': '盤點類別數已達上限',
    'limitModal.title.PIN_MESSAGE_LIMIT_REACHED': '置頂訊息數已滿',
    'limitModal.body.TOTAL_TASK_LIMIT_REACHED': '已建立 {{total}}/{{max}} 個任務（封存任務不計入）。',
    'limitModal.body.COMMENT_LIMIT_REACHED': '此項目留言已達 {{total}}/{{max}} 上限。',
    'limitModal.body.DAILY_TASK_SUBMIT_LIMIT_REACHED': '今日已提交 {{used}}/{{limit}} 次；下次重置：{{retryAt}}。',
    'limitModal.body.DAILY_TASK_REVIEW_LIMIT_REACHED': '今日已審核 {{used}}/{{limit}} 次；下次重置：{{retryAt}}。',
    'limitModal.body.DAILY_RECEIPT_LIMIT_REACHED': '今日已提交 {{used}}/{{limit}} 張收據；下次重置：{{retryAt}}。',
    'limitModal.body.DAILY_AI_ANALYZE_LIMIT_REACHED': '今日已使用 {{used}}/{{limit}} 次 AI 分析；下次重置：{{retryAt}}。',
    'limitModal.body.batchInsufficient.DAILY_RECEIPT_LIMIT_REACHED':
      '今日剩餘收據提交次數為 {{remaining}} 次（已用 {{used}}/{{limit}}），不足以提交 {{requested}} 張收據。下次重置：{{retryAt}}。',
    'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_RECEIPT':
      '今日剩餘 AI 分析次數為 {{remaining}} 次（已用 {{used}}/{{limit}}），不足以分析 {{requested}} 張收據。下次重置：{{retryAt}}。',
    'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_PHOTO':
      '今日剩餘 AI 分析次數為 {{remaining}} 次（已用 {{used}}/{{limit}}），不足以分析 {{requested}} 張相片。下次重置：{{retryAt}}。',
    'limitModal.body.TASK_KIND_NOT_ALLOWED': '目前會員等級不支援此任務類型。',
    'limitModal.body.AUTO_TRANSLATION_NOT_ALLOWED': '目前會員等級不支援自動翻譯。',
    'limitModal.body.INVENTORY_LOCATION_LIMIT_REACHED': '盤點位置數已達目前會員等級上限。',
    'limitModal.body.INVENTORY_CATEGORY_LIMIT_REACHED': '盤點類別數已達目前會員等級上限。',
    'limitModal.body.PIN_MESSAGE_LIMIT_REACHED': '已建立 {{total}}/{{max}} 則置頂訊息。',
    'limitModal.title.PIN_MESSAGE_ACTIVE_LIMIT_REACHED': '啟用中的置頂訊息已滿',
    'limitModal.body.PIN_MESSAGE_ACTIVE_LIMIT_REACHED':
      '總則數已超過免費會員上限，同時啟用中的置頂訊息最多 {{max}} 則（目前 {{total}} 則）。請先停用其他置頂訊息，或升級會員。',
    'limitModal.body.default': '您的帳戶已觸及目前會員等級的使用限制。',
    'limitModal.helperHint': '如需更高配額，請聯絡僱主升級會員等級。',
    'limitModal.employerDailyHint': '可前往用量頁查看詳情；升級會員可獲更高每日配額。',
    'limitModal.employerTotalTasksHint': '您可以升級會員以建立更多任務。',
    'limitModal.employerTaskKindHint': '請升級會員以使用此任務類型。',
    'limitModal.employerPinMessagesHint': '您可以升級會員以新增更多置頂訊息。',
    'limitModal.employerL3Hint': '已達目前方案上限，可前往用量頁查看詳情。',
    'limitModal.close': '關閉',
    'limitModal.viewUsage': '查看用量詳情',
    'limitModal.goSubscription': '前往訂閱／升級',
    'limitModal.playStoreBillingHint':
      'Google Play 版無法在此付款升級。請改用瀏覽器或「加到主畫面」的應用程式管理訂閱。',
    'settings.notifyEmailLabel': '活動通知亦以電郵發送',
    'settings.autoTranslationLabel': '自動翻譯對方輸入的文字',
    'settings.autoTranslationHint': '關閉後，留言、任務說明等將只顯示原文，不會呼叫翻譯服務。',
    'settings.notifyWebPushLabel': '此裝置是否接收通知（Web Push）',
    'settings.webPushToggleError': '更新通知設定失敗',
    'settings.notifyWebPushPermissionDeniedHint':
      '你的瀏覽器通知權限目前為已封鎖。請到瀏覽器網站設定允許通知，然後重新整理此頁面。',
    'settings.webPushPromptTitle': '啟用推播通知',
    'settings.webPushPromptBody': '如要即時收到任務與審核通知，請允許此 App 發送通知。',
    'settings.webPushPromptEnable': '允許通知',
    'settings.androidChromePushHintTitle': '系統推播需要 Chrome',
    'settings.androidChromePushHintBody':
      '此裝置的瀏覽器不支援或不適合 Web Push。請安裝或更新最新版 Chrome，即可接收任務與審核的即時通知。站內鈴鐺通知仍可正常使用。',
    'settings.androidChromePushHintCta': '前往安裝／更新 Chrome',
    'settings.androidChromePushHintDismiss': '稍後再說',
    'settings.save': '儲存設定',
    'settings.saving': '儲存中…',
    'settings.saved': '已儲存',
    'settings.invitationCodeTitle': '兌換邀請碼',
    'settings.invitationCodeSubscriptionTitle': '有邀請碼？',
    'settings.invitationCodeHint': '輸入 8 位邀請碼以延長試用或升級高級會員試用。',
    'settings.invitationCodeInputLabel': '邀請碼',
    'settings.invitationCodeInputPlaceholder': '輸入 8 位邀請碼',
    'settings.invitationCodeRedeemButton': '兌換',
    'settings.invitationCodeRedeemSuccess': '邀請碼兌換成功',
    'settings.invitationCodeRedeemFailed': '邀請碼兌換失敗，請稍後再試',
    'settings.invitationCodeInvalid': '邀請碼無效',
    'settings.invitationCodeErrorRegistrationOnly': '此邀請碼僅可於註冊時兌換',
    'settings.invitationCodeErrorNotL1Trial': '此邀請碼僅適用於試用會員',
    'settings.invitationCodeErrorL1TrialEnded': '您的試用已結束',
    'settings.invitationCodeErrorSubscriptionActive': '您已有有效付費訂閱，無法兌換此碼',
    'settings.invitationCodeErrorEmailAlreadyUsed': '您已使用過此邀請碼',
    'settings.invitationCodePreviewL1Extend': '延長試用 {{days}} 日，新到期日 {{endsAt}}',
    'settings.invitationCodePreviewL3Trial': '升級高級會員試用 {{days}} 日，到期日 {{endsAt}}',
    'settings.invitationCodePreviewL3SubscriptionExtend': '延長訂閱 {{days}} 日，新到期日 {{endsAt}}',
    'settings.loadError': '無法載入設定',
    'settings.saveError': '儲存失敗',
    'settings.logout': '登出',
    'settings.logoutConfirmTitle': '確定要登出？',
    'settings.logoutConfirmDescription': '登出後需重新登入才能使用帳戶功能。',
    'settings.changePassword': '變更密碼',
    'settings.changePasswordHint': '已登入可直接變更密碼。',
    'settings.googlePasswordManagedHint': '此帳戶使用 Google 登入，不支援變更密碼或忘記密碼。',
    'settings.currentPasswordLabel': '目前密碼',
    'settings.newPasswordLabel': '新密碼',
    'settings.confirmNewPasswordLabel': '確認新密碼',
    'settings.changePasswordSubmit': '更新密碼',
    'settings.changePasswordSaving': '更新中…',
    'settings.changePasswordSuccess': '密碼已更新，請重新登入。',
    'settings.changePasswordError': '變更密碼失敗',
    'settings.changePasswordCurrentPasswordIncorrect': '目前密碼錯誤，請重新輸入。',
    'settings.expandChangePasswordSectionAria': '展開變更密碼',
    'settings.collapseChangePasswordSectionAria': '收合變更密碼',
    'settings.expandAccountRemovalSectionAria': '展開刪除帳戶',
    'settings.collapseAccountRemovalSectionAria': '收合刪除帳戶',
    'settings.resendVerification': '重新發送驗證電郵',
    'settings.resendVerificationSending': '發送中…',
    'settings.resendVerificationSuccess': '已發送驗證電郵，請檢查收件匣。',
    'settings.resendVerificationError': '發送失敗',
    'settings.resendVerificationCooldown': '請於 15 分鐘後再按「重新發送驗證電郵」。',
    'settings.emailUnverifiedBanner': '您的電郵尚未驗證，將無法使用此App的功能。',
    'settings.accountRemovalPasswordLabel': '輸入登入密碼以確認',
    'settings.accountRemovalGoogleHint': '提交後會寄出確認電郵。請於 30 分鐘內開啟連結，並在頁面按確認。確認後帳戶會立即刪除。',
    'settings.backToPairingStatus': '返回配對狀態',
    'settings.mainMenuAria': '主選單',
    'settings.menuHome': '首頁',
    'settings.menuTasksEmployer': '任務管理',
    'settings.menuTasksHelper': '工作清單',
    'settings.menuSettlements': '開支與結算',
    'settings.menuMoneyGiven': '已給的錢',
    'settings.menuSettlementRecords': '結算紀錄',
    'settings.menuPinMessages': '置頂訊息設定',
    'settings.menuBudgets': '已給的錢管理',
    'settings.menuReceipts': '掃瞄收據',
    'settings.menuReceiptBrowse': '收據列表',
    'settings.menuFamily': '家庭成員的偏好',
    'settings.menuPairingStatus': '配對狀態',
    'settings.menuConfirmSettlement': '確認結算',
    'settings.menuSettings': '個人設定',
    'settings.menuOverview': '總覽',
    'settings.menuAccountSettings': '帳戶設定',
    'settings.menuSubscriptionAndInvite': '會員訂閱 及 兌換邀請碼',
    'settings.menuMoneyGivenSettlement': '已給的錢與結算',
    'settings.menuTodayTask': '今日任務',
    'settings.menuImportPreviousPairing': '從上一配對匯入資料',
    'pairing.import.title': '從上一配對匯入資料',
    'pairing.import.description': '可將最近一次已結束配對的任務、家庭成員及置頂訊息複製到目前配對。預設全不選，請勾選要匯入的項目。',
    'pairing.import.later': '稍後',
    'pairing.import.import': '匯入',
    'pairing.import.sectionTasks': '任務',
    'pairing.import.sectionFamily': '家庭成員',
    'pairing.import.sectionPins': '置頂訊息',
    'pairing.import.alreadyImported': '已匯入',
    'pairing.import.consentLabel': '我確認匯入所選家庭成員資料，並沿用其既有同意設定。',
    'pairing.import.empty': '沒有可匯入的項目（可能已全部匯入，或上一配對沒有相關資料）。',
    'pairing.import.success': '已成功匯入所選項目',
    'pairing.import.error': '無法匯入，請稍後再試',
    'pairing.import.loadError': '無法載入可匯入項目，請稍後再試',
    'pairing.import.taskQuotaHint': '任務槽位：{{used}}/{{max}}（已封存任務不計入）',
    'pairing.import.pinQuotaHint': '置頂訊息：{{used}}/{{max}}',
    'pairing.import.taskKindStandard': '一般任務',
    'previousHelper.detailBanner': '此任務為前任家務助理負責；現任家務助理看不到此記錄。',
    'previousHelper.receiptBanner': '此收據為前任家務助理負責；現任家務助理看不到此記錄。',
    'previousHelper.settlementBanner': '此結算為前任家務助理負責；現任家務助理看不到此記錄。',
    'previousHelper.unpairedDetailBanner': '此記錄屬於已結束的配對，新的家務助理配對後也看不到。',
    'previousHelper.unpairedReceiptBanner': '此收據屬於已結束的配對，新的家務助理配對後也看不到。',
    'previousHelper.unpairedSettlementBanner': '此結算屬於已結束的配對，新的家務助理配對後也看不到。',
    'pairing.unpairedBanner': '尚未與家務助理配對。你可以先建立任務、置頂訊息、家庭成員和已給的錢；配對後家務助理才看得到。',
    'pairing.unpairedBannerAction': '產生配對碼',
    'pairing.unpairedBannerAria': '尚未配對提示',
    'settings.accountRemoval': '刪除帳戶',
    'settings.accountRemovalHint': '輸入登入密碼並提交後，帳戶會立即刪除，且無法復原。',
    'settings.accountRemovalSubmit': '確認並刪除帳戶',
    'settings.accountRemovalSuccess': '已寄出確認電郵。請於 30 分鐘內開啟連結，並在頁面按確認以刪除帳戶。',
    'settings.accountRemovalDeleted': '帳戶已刪除。',
    'settings.accountRemovalError': '提交失敗，請稍後再試。',
    'settings.accountRemovalPasswordIncorrect': '密碼錯誤，請重新輸入。',
    'settings.closePanel': '關閉',
    'toast.offlineQueued': '已加入離線佇列，恢復連線後會自動同步。',
    'toast.task.createSuccess': '成功新增任務！',
    'toast.task.updateSuccess': '成功儲存任務！',
    'toast.task.completeSuccess': '成功提交完成任務！',
    'toast.task.resubmitSuccess': '成功重新提交任務！',
    'toast.task.reviewedSuccess': '成功審核任務！',
    'toast.task.needUpdateSuccess': '成功標記任務需更新！',
    'toast.receiptAnalyze.singleSavedOnline': '收據已儲存。下一步：提交結算給僱主',
    'toast.receiptAnalyze.singleSavedStillUnsaved':
      '收據已儲存。尚有 {{count}} 張收據未儲存，請先全部儲存。',
    'toast.receiptAnalyze.singleSavedOffline': '已儲存收據到離線佇列。',
    'toast.receiptAnalyze.batchAllOnlineSuccess': '批次完成：{{successCount}} 筆已成功儲存。',
    'toast.receiptAnalyze.batchAllOfflineQueued': '批次完成：{{queuedCount}} 筆已加入離線佇列。',
    'toast.receiptAnalyze.batchMixedOnlineOfflineSuccess':
      '批次完成：{{onlineCount}} 筆已儲存，{{queuedCount}} 筆已加入離線佇列。',
    'toast.receiptAnalyze.batchPartialResult':
      '批次完成：{{onlineCount}} 筆已儲存、{{queuedCount}} 筆已加入離線佇列、{{failedCount}} 筆失敗。',
    'toast.receipt.editSuccess': '成功儲存收據！',
    'toast.receipt.deleteSuccess': '成功刪除收據！',
    'toast.budget.createSuccess': '成功新增已給的錢！',
    'toast.budget.updateSuccess': '成功儲存已給的錢！',
    'toast.budget.deleteSuccess': '已移除已給的錢！',
    'toast.pinMessage.createSuccess': '成功新增置頂訊息！',
    'toast.pinMessage.updateSuccess': '成功儲存置頂訊息！',
    'toast.pinMessage.deleteSuccess': '成功移除置頂訊息！',
    'toast.familyMember.createSuccess': '成功新增家庭成員！',
    'toast.familyMember.updateSuccess': '成功修改家庭成員！',
    'toast.familyMember.deleteSuccess': '成功移除家庭成員！',
    'toast.settings.updateSuccess': '成功儲存個人設定！',
    'toast.settings.changePasswordSuccess': '成功變更密碼！',
    'toast.pairing.joinSuccess': '成功完成配對！',
    'toast.pairing.unpairSuccess': '成功解除配對！',
    'toast.pairing.generateCodeSuccess': '成功產生配對碼！',
    'toast.consent.acceptSuccess': '成功提交同意聲明！',
    'toast.settlement.confirmSuccess': '成功提交結算！',
    'toast.settlement.unlockSuccess': '成功解鎖結算！',
    'toast.settlement.withdrawSuccess': '成功撤回結算！',
    'toast.settlement.reviewedSuccess': '成功審核結算！',
    'toast.settlement.rejectedSuccess': '成功拒絕結算！',
    'toast.settlement.withdrawConfirmTitle': '確認撤回結算？',
    'toast.settlement.withdrawConfirmBody': '撤回後此筆結算會改為已撤回狀態，確定要繼續嗎？',
    'pairing.generate.title': '產生配對碼',
    'pairing.generate.subtitle': '產生配對碼，供家務助理與您配對',
    'pairing.generate.errorGeneric': '產生配對碼失敗',
    'pairing.generate.codeLabel': '您的配對碼',
    'pairing.generate.validUntil': '有效至 {{datetime}}（{{duration}}）',
    'pairing.generate.durationMinutes': '{{count}} 分鐘',
    'pairing.generate.durationHours': '{{count}} 小時',
    'pairing.generate.durationDay': '{{count}} 天',
    'pairing.generate.durationDays': '{{count}} 天',
    'pairing.generate.copyAria': '複製配對碼到剪貼簿',
    'pairing.generate.copied': '已複製！',
    'pairing.generate.copyCode': '複製配對碼',
    'pairing.generate.regenerate': '重新產生',
    'pairing.generate.regenerating': '產生中…',
    'pairing.generate.regenerateAria': '重新產生配對碼',
    'pairing.generate.continue': '繼續',
    'pairing.generate.continueAria': '前往下一步',
    'pairing.generate.generateCta': '產生配對碼',
    'pairing.generate.generateAria': '產生配對碼',
    'pairing.generate.footer':
      '請將此註冊連結或 QR code 交給家務助理。家務助理在註冊時會自動與您配對。',
    'pairing.generate.qrAria': '家務助理註冊連結 QR code',
    'pairing.generate.shareLabel': '分享',
    'pairing.generate.shareAria': '分享 QR code 與註冊連結',
    'pairing.generate.shareInviteTitle': 'MaidEasy 邀請',
    'pairing.generate.shareInviteText':
      '僱主 {{nickname}} 邀請你使用 MaidEasy。按連結或掃描 QR code 即可註冊。',
    'pairing.generate.shareEmployerFallback': '僱主',
    'pairing.generate.shareError': '無法分享，請稍後再試。',
    'pairing.join.title': '輸入配對碼',
    'pairing.join.subtitle': '輸入僱主提供的 6 位字元配對碼以完成配對',
    'pairing.join.errorIncomplete': '請輸入完整的 {{length}} 位字元配對碼',
    'pairing.join.errorGeneric': '配對失敗',
    'pairing.join.pairing': '配對中…',
    'pairing.join.submit': '提交配對碼',
    'pairing.join.submitAria': '提交配對碼',
    'pairing.join.inputGroupAria': '配對碼輸入',
    'pairing.join.charAria': '配對碼第 {{n}} 位',
    'pairing.join.footer': '請向僱主索取配對碼。配對碼有效期為 {{duration}}。',
    'pairing.invite.landingTitle': '即將與僱主 {{nickname}} 配對',
    'pairing.invite.landingSubtitle': '請選擇登入或註冊以完成配對',
    'pairing.invite.emailLogin': '電郵登入',
    'pairing.invite.googleLogin': 'Google 登入',
    'pairing.invite.register': '註冊',
    'pairing.invite.invalidTitle': '配對連結無效',
    'pairing.invite.errorInvalid': '此配對連結已失效或無效，請向僱主索取新的連結。',
    'pairing.invite.errorFormat': '配對碼格式不正確。',
    'pairing.invite.previewError': '無法驗證配對碼，請稍後再試。',
    'pairing.invite.loginBanner': '登入後將自動與僱主 {{nickname}} 配對',
    'pairing.invite.registerBanner': '註冊為家務助理並與僱主 {{nickname}} 配對',
    'pairing.invite.confirmTitle': '與僱主 {{nickname}} 配對',
    'pairing.invite.confirmSubtitle': '確認後將立即完成配對',
    'pairing.invite.confirmCta': '確認配對',
    'pairing.invite.confirmDialogTitle': '確認與 {{nickname}} 配對？',
    'pairing.invite.confirmDialogDescription': '配對完成後即可開始使用 MaidEasy。',
    'pairing.invite.employerSignedIn': '此連結供家務助理使用。您目前已以僱主身份登入。',
    'pairing.invite.helperAlreadyPaired': '您已與僱主配對，無需使用此連結。',
    'pairing.invite.goPairingStatus': '前往配對狀態',
    'pairing.invite.expiredTitle': '配對連結已失效',
    'pairing.invite.expiredBody': '配對碼可能已過期或已被使用。',
    'pairing.invite.contactEmployer': '配對碼無效，請與僱主聯絡',
    'pairing.generate.copyInviteLink': '複製含有配對碼的註冊連結',
    'pairing.generate.copyInviteLinkAria': '複製家務助理註冊連結到剪貼簿',
    'pairing.status.regenerateCode': '重新產生配對碼',
    'pairing.status.goGenerateNew': '產生新的配對碼',
    'auth.googleLoginRequiredTitle': '請使用 Google 登入',
    'auth.googleLoginRequiredDescription': '此電郵帳戶需使用 Google 登入。是否改用 Google 登入？',
    'auth.googleLoginRequiredConfirm': '使用 Google 登入',
    'pairing.success.title': '配對成功',
    'pairing.success.subtitle': '您已成功配對',
    'pairing.success.pairedWith': '配對對象',
    'pairing.success.nextTitle': '下一步：同意書',
    'pairing.success.nextBody':
      '使用前須閱讀並同意同意書及個人資料收集聲明（PICS）。',
    'pairing.success.continue': '前往同意書',
    'pairing.success.continueAria': '前往同意書頁面',
    'pairing.success.nextTitleReady': '下一步：開始使用',
    'pairing.success.nextBodyReady':
      '您已在註冊時同意個人資料收集聲明（PICS），可進入應用程式。',
    'pairing.success.continueApp': '進入應用程式',
    'pairing.success.continueAppAria': '進入應用程式主頁',
    'role.employer': '僱主',
    'role.helper': '家務助理',
    'pairing.status.loadingAria': '載入配對狀態',
    'pairing.status.errorGeneric': '無法載入配對狀態',
    'pairing.status.activeTitle': '配對生效中',
    'pairing.status.yourPartner': '您的配對對象',
    'pairing.status.pairedSince': '配對自 {{datetime}}',
    'pairing.status.notPairedTitle': '尚未配對',
    'pairing.status.emailUnverifiedHint': '電郵尚未驗證，驗證後才可使用 App 功能。',
    'pairing.status.consentRequiredHint': '尚未同意條款，同意後才可使用 App 功能。',
    'pairing.status.notPairedEmployer': '請產生配對碼並交給家務助理',
    'pairing.status.notPairedHelper': '請輸入僱主提供的配對碼',
    'pairing.status.goGenerate': '產生配對碼',
    'pairing.status.goEnter': '輸入配對碼',
    'pairing.status.generateAria': '產生配對碼',
    'pairing.status.enterAria': '輸入配對碼',
    'pairing.status.unpairButton': '解除配對',
    'pairing.status.unpairAria': '解除目前配對',
    'pairing.status.unpairing': '解除配對中…',
    'pairing.status.unpairConfirm': '確定要解除目前配對嗎？此操作完成後需要重新配對。',
    'pairing.status.unpairError': '解除配對失敗，請稍後再試',
    'protected.loadingAria': '載入中',
    'app.autoUpdatingTitle': '程式自動更新中...',
    'app.autoUpdatingSlowHint': '更新速度似乎比預期慢，請稍等...',
    'app.autoUpdatingVersionLine': '目前: {{currentVer}} | 新版: {{newVer}}',
    // Task Management - Common
    'task.status.pending': '待審核',
    'task.status.notSubmitted': '未提交',
    'task.status.approved': '已通過',
    'task.status.rejected': '已拒絕',
    'task.status.needsUpdate': '需修改',
    'task.status.cancel': '已取消',
    'receipt.status.save': '未結算',
    'receipt.status.submitted': '已提交結算',
    'receipt.status.reviewed': '已審核',
    'task.recurrence.none': '單次',
    'task.recurrence.daily': '每日',
    'task.recurrence.weekly': '每週',
    'task.recurrence.biweekly': '隔週',
    'task.recurrence.monthly': '每月',
    'task.monthlyKind.day': '按日期（每月 X 號）',
    'task.monthlyKind.nthWeekday': '第 N 個星期',
    'task.monthlyKind.lastWeekday': '最後一個星期',
    'task.form.monthlyKindLabel': '每月規則',
    'task.form.monthDaysLabel': '每月日期',
    'task.form.monthlyOrdinalLabel': '第幾個',
    'task.form.monthlyOrdinal.1': '第 1 個',
    'task.form.monthlyOrdinal.2': '第 2 個',
    'task.form.monthlyOrdinal.3': '第 3 個',
    'task.form.monthlyOrdinal.4': '第 4 個',
    'task.form.monthlyOrdinal.5': '第 5 個',
    'task.form.monthlyDayClampHint': '若該月沒有所選日期（例如 29–31 號），會改為該月最後一日。',
    'task.form.monthlyFifthWeekdayHint': '若該月沒有第 5 個所選星期，該月不會產生任務。',
    'task.form.recurrenceMonthDaysRequired': '請至少選擇一個每月日期。',
    'task.day.sun': '日',
    'task.day.mon': '一',
    'task.day.tue': '二',
    'task.day.wed': '三',
    'task.day.thu': '四',
    'task.day.fri': '五',
    'task.day.sat': '六',
    // Task List (Employer)
    'task.list.title': '任務管理',
    'task.list.subtitle': '管理家務助理的任務清單',
    'task.list.tabAll': '全部',
    'task.list.tabToday': '今日',
    'task.list.tabRecurring': '重複任務',
    'task.list.tabTodayTask': '今日',
    'task.list.tabEditTask': '編輯任務',
    'task.list.tabOverdueTask': '逾期任務',
    'task.list.tabEffectiveTask': '生效的任務',
    'task.list.tabExpiredTask': '過期的任務',
    'task.list.addNew': '新增任務',
    'task.list.addChoiceTitle': '建立方式',
    'task.list.addChoiceSubtitle': '請選擇要建立什麼任務。',
    'task.list.addChoiceStandard': '建立一般任務',
    'task.list.addChoiceInventory': '建立家裏存貨',
    'task.list.addChoicePickupDropoff': '建立接送任務',
    'task.list.addChoiceMedication': '建立食藥提醒',
    'task.list.addChoiceLockedAria': '「{{label}}」需升級會員後方可使用',
    'task.list.kindStockTake': '家裏存貨',
    'task.list.kindPickupDropoff': '接送',
    'task.list.kindMedication': '食藥提醒',
    'task.pickup.loadError': '無法載入接送任務資料',
    'task.pickup.createHeading': '建立接送任務',
    'task.pickup.editHeading': '編輯接送任務',
    'task.pickup.stepProgress': '步驟 {{step}} / {{total}}',
    'task.pickup.legHint': '請選擇要建立接送哪個時段',
    'task.pickup.pickupLabel': '接回',
    'task.pickup.dropoffLabel': '送達',
    'task.pickup.legRequired': '至少要選擇送達或接回',
    'task.pickup.personLabel': '接送誰',
    'task.pickup.personPlaceholder': '例如：Dave',
    'task.pickup.suggestHeading': '家庭成員建議',
    'task.pickup.personRequired': '請輸入接送誰',
    'task.pickup.pickupTimeLabel': '接回時間',
    'task.pickup.dropoffTimeLabel': '送達時間',
    'task.pickup.pickupTimeRequired': '請輸入接回時間',
    'task.pickup.dropoffTimeRequired': '請輸入送達時間',
    'task.pickup.timesMustDiffer': '送達與接回時間不能相同',
    'task.pickup.locationLabel': '接送地點（可選）',
    'task.pickup.locationSuggestHeading': '建議',
    'task.pickup.locationChip.school': '學校',
    'task.pickup.locationChip.tutoring': '補習班',
    'task.pickup.locationChip.stadium': '體育館',
    'task.pickup.locationChip.soccerPitch': '足球場',
    'task.pickup.locationChip.mall': '商場',
    'task.pickup.reviewPickupTitle': '接回任務標題',
    'task.pickup.reviewDropoffTitle': '送達任務標題',
    'task.pickup.editTitleAria': '修改任務標題',
    'task.pickup.submitCreate': '建立接送任務',
    'task.pickup.customTitleModalTitle': '自訂標題',
    'task.pickup.titleChoiceTitle': '標題更新選擇',
    'task.pickup.titleChoicePickup': '接回',
    'task.pickup.titleChoiceDropoff': '送達',
    'task.pickup.titleChoiceKeep': '保留原標題',
    'task.pickup.titleChoiceUseNew': '使用新標題',
    'task.pickup.titleChoiceCustom': '自訂',
    'task.pickup.createSuccess': '接送任務已建立',
    'task.pickup.updateSuccess': '接送任務已更新',
    'task.pickup.createError': '建立接送任務失敗',
    'task.medication.loadError': '無法載入食藥提醒資料',
    'task.medication.createHeading': '建立食藥提醒',
    'task.medication.editHeading': '編輯食藥提醒',
    'task.medication.stepProgress': '步驟 {{step}} / {{total}}',
    'task.medication.personLabel': '誰要食藥',
    'task.medication.personPlaceholder': '例如：Ben',
    'task.medication.suggestHeading': '家庭成員建議',
    'task.medication.personRequired': '請輸入誰要食藥',
    'task.medication.descPlaceholder': '例如：食糖尿病藥',
    'task.medication.timeRequired': '請至少加入一個食藥時間',
    'task.medication.timesUnique': '食藥時間不可重複',
    'task.medication.timeMax': '食藥時間最多 10 個',
    'task.medication.timesSectionLabel': '食藥時間',
    'task.medication.timeLabel': '食藥時間 {{n}}',
    'task.medication.removeTimeAria': '移除此時間',
    'task.medication.addTime': '+ 新增食藥時間',
    'task.medication.reviewTitle': '任務標題',
    'task.medication.editTitleAria': '修改任務標題',
    'task.medication.submitCreate': '建立食藥提醒',
    'task.medication.customTitleModalTitle': '自訂標題',
    'task.medication.titleChoiceTitle': '標題更新選擇',
    'task.medication.titleChoiceKeep': '保留原標題',
    'task.medication.titleChoiceUseNew': '使用新標題',
    'task.medication.titleChoiceCustom': '自訂',
    'task.medication.createSuccess': '食藥提醒已建立',
    'task.medication.updateSuccess': '食藥提醒已更新',
    'task.medication.createError': '建立食藥提醒失敗',
    'task.form.notifyEmployerLabel': '完成時通知我',
    'task.form.remindHelperBeforeDueLabel': '截止前提醒家務助理',
    'task.form.remindHelperBeforeDueDisabledHint': '請先設定截止時間才可啟用此提醒。',
    'task.form.remindHelperBeforeDueModalTitle': '截止前提醒家務助理',
    'task.form.remindHelperBeforeDueModalIntro': '系統會嘗試在截止時間前10分鐘內發提醒。',
    'task.form.remindHelperBeforeDueModalLimit1':
      '家務助理需開啟接收通知 （需安裝程式或加入主畫面）或開啟電郵通知才可及時收到提醒。',
    'task.form.remindHelperBeforeDueModalLimit2': '家務助理收到提醒時需有網絡連線。',
    'task.form.remindHelperBeforeDueModalLimit3': '實際收到時間可能因網絡接收而有延誤。',
    'task.medication.remindHelperBeforeDueModalIntro': '系統會嘗試在食藥時間前10分鐘內發提醒。',
    'task.medication.step3ReminderAskTitle': '是否於食藥時間前提醒家務助理？',
    'task.medication.step3ReminderAskYes': '是，啟用提醒',
    'task.medication.step3ReminderAskNo': '否，不用提醒',
    'task.group.wizard.stepOf': '步驟 {{current}} / {{total}}',
    'task.group.wizard.reviewCardHeadingPickup': '確認接送任務',
    'task.group.wizard.reviewCardHeadingMedication': '確認食藥提醒',
    'task.group.wizard.reviewConfirmPickup': '建立接送任務',
    'task.group.wizard.reviewConfirmMedication': '建立食藥提醒',
    'task.group.wizard.reviewUpdatePickup': '儲存接送任務',
    'task.group.wizard.reviewUpdateMedication': '儲存食藥提醒',
    'task.group.wizard.summaryNone': '（無）',
    'task.group.wizard.summaryPerson': '對象',
    'task.group.wizard.summaryLocation': '地點',
    'task.group.wizard.summaryExclusion': '排除日期',
    'task.group.wizard.summaryRequirePhoto': '需要提交照片',
    'task.group.wizard.summaryYes': '是',
    'task.group.wizard.summaryNo': '否',
    'task.group.wizard.summaryMedicationTimes': '提醒時間',
    'task.list.empty': '尚未建立任何任務',
    'task.list.emptyHint': '點擊「新增任務」按鈕以建立您的第一個任務',
    'task.list.overdueEmpty': '沒有逾期任務',
    'task.list.completedCount': '已完成 {{count}} 次',
    'task.list.pendingReviewNav': '待審核任務',
    'task.list.pendingReviewNavAria': '待審核任務，目前 {{count}} 筆',
    'task.list.approvedNav': '已通過清單',
    'task.list.approvedNavAria': '已通過任務，目前 {{count}} 筆',
    'task.list.loadError': '載入任務列表失敗',
    'task.list.pageSubtitle': '今日進度與任務總覽',
    'task.list.summaryPendingLabel': '待審核任務',
    'task.list.summaryApprovedLabel': '已通過',
    'task.list.summaryTomorrowLabel': '明日任務',
    'task.list.tomorrowNavAria': '明日任務，目前 {{count}} 筆',
    'task.list.summaryCount': '{{count}} 筆',
    'task.list.summaryCountUnitOne': '筆',
    'task.list.summaryCountUnitOther': '筆',
    'task.list.editFiltersAria': '編輯任務子分頁',
    'task.list.tabEditAllTasks': '編輯所有任務',
    'task.list.menuEditAllTasks': '編輯所有任務',
    'task.list.menuHistoryTasks': '過去任務紀錄',
    'task.list.menuFutureTasks': '將來任務',
    'task.list.menuEditTask': '編輯任務',
    'task.list.menuViewTask': '查看任務',
    'task.list.menuReviewTask': '審核任務',
    'task.list.taskRowMenuAria': '{{title}} 的操作選單',
    'task.list.titleMenuAria': '任務管理選單',
    'task.list.dateStripCustom': '自訂',
    'task.list.dateStripAria': '選擇日期',
    'task.list.customDateModalTitle': '選擇日期',
    'task.list.timeSectionLateNight': '凌晨',
    'task.list.timeSectionMorning': '早上',
    'task.list.timeSectionAfternoon': '下午',
    'task.list.timeSectionEvening': '晚上',
    'task.list.timeSectionNoTime': '無指定時間',
    'task.list.overdueCardLabel': '逾期任務',
    'task.list.overdueCardView': '查看',
    'task.list.statusEffective': '生效',
    'task.list.statusNotStarted': '未開始',
    'task.list.statusExpired': '已過期',
    'employerDashboard.greetLateNight': '{{name}}，晚安',
    'employerDashboard.greetMorning': '{{name}}，早安',
    'employerDashboard.greetAfternoon': '{{name}}，午安',
    'employerDashboard.greetEvening': '{{name}}，晚安',
    'employerDashboard.loadError': '無法載入儀表板資料',
    'employerDashboard.subtitle': '今日概覽',
    'employerDashboard.todayCompletion': '今日完成率',
    'employerDashboard.completedFraction': '已完成 {{done}} / {{total}}',
    'employerDashboard.completedFractionValue': '{{done}} / {{total}}',
    'employerDashboard.completedFractionStatus': '已完成',
    'employerDashboard.pendingTitle': '待審核任務',
    'employerDashboard.pendingEmpty': '目前沒有待審核的提交',
    'employerDashboard.submittedAtLine': '提交於 {{at}}',
    'employerDashboard.viewAllPending': '查看全部待審核',
    'employerDashboard.monthExpenseTitle': '已給的錢（{{range}}）',
    'employerDashboard.budgetPlaceholder': '尚未設定使用中的已給的錢',
    'employerDashboard.budgetTotal': '已給的錢總額',
    'employerDashboard.budgetUsed': '已用',
    'employerDashboard.budgetRemaining': '尚餘',
    'employerDashboard.settlementPendingLine': '有 {{count}} 筆結算待處理',
    'employerDashboard.ariaTodayCompletionCard': '前往任務頁，查看今日完成率',
    'employerDashboard.ariaOverdueCard': '前往任務頁逾期分頁',
    'employerDashboard.gettingStarted.title': '開始使用',
    'employerDashboard.gettingStarted.subtitle':
      '已與家務助理配對。建議先完成以下設定，對方才能在 App 裡工作與回報。',
    'employerDashboard.gettingStarted.progress': '已完成 {{done}} / {{total}}',
    'employerDashboard.gettingStarted.done': '已完成',
    'employerDashboard.gettingStarted.stepCta': '去設定',
    'employerDashboard.gettingStarted.later': '稍後再說',
    'employerDashboard.gettingStarted.laterAria': '稍後再說，關閉開始使用指引',
    'employerDashboard.gettingStarted.dismissAria': '關閉開始使用指引',
    'employerDashboard.gettingStarted.dismissError': '無法關閉開始使用指引',
    'employerDashboard.gettingStarted.dismissToast':
      '已關閉開始使用指引。可點問候旁的「?」再查看。',
    'employerDashboard.gettingStarted.helpTriggerAria': '查看開始使用指引',
    'employerDashboard.gettingStarted.regionAria': '開始使用指引',
    'employerDashboard.gettingStarted.stepTaskTitle': '建立第一個任務',
    'employerDashboard.gettingStarted.stepTaskHint': '例如清潔、買菜或每日例行事項',
    'employerDashboard.gettingStarted.stepTaskAria': '前往任務頁建立第一個任務',
    'employerDashboard.gettingStarted.stepBudgetTitle': '設定已給的錢',
    'employerDashboard.gettingStarted.stepBudgetHint': '記下你給家務助理的家用期間與金額',
    'employerDashboard.gettingStarted.stepBudgetAria': '前往新增已給的錢',
    'employerDashboard.gettingStarted.stepFamilyTitle': '加入家庭成員',
    'employerDashboard.gettingStarted.stepFamilyHint': '讓家務助理認識家人資料',
    'employerDashboard.gettingStarted.stepFamilyAria': '前往家庭成員頁',
    'employerDashboard.gettingStarted.stepPinTitle': '寫一則置頂訊息',
    'employerDashboard.gettingStarted.stepPinHint': '對方首頁會看到你的提醒',
    'employerDashboard.gettingStarted.stepPinAria': '前往新增置頂訊息',
    'helperHome.greetLateNight': '{{name}}，晚安',
    'helperHome.greetMorning': '{{name}}，早安',
    'helperHome.greetAfternoon': '{{name}}，午安',
    'helperHome.greetEvening': '{{name}}，晚安',
    'pinMessage.list.title': '置頂訊息設定',
    'pinMessage.list.subtitle': '首頁輪播會依此順序顯示。上限 {{maxItems}} 個。',
    'pinMessage.list.savingOrder': '儲存排序中…',
    'pinMessage.list.empty': '目前沒有置頂訊息。',
    'pinMessage.form.addTitle': '新增置頂訊息',
    'pinMessage.form.editTitle': '編輯置頂訊息',
    'pinMessage.form.limitLine': '目前級別限制：標題 {{titleMax}} 字，內文 {{contentMax}} 字。',
    'pinMessage.form.saving': '儲存中…',
    'pinMessage.field.title': '標題',
    'pinMessage.field.titlePlaceholder': '例如: 明天超市特價,記得買豉油',
    'pinMessage.field.content': '內文',
    'pinMessage.field.contentPlaceholder': '詳細說明置頂訊息...',
    'pinMessage.field.visibility': '可見範圍',
    'pinMessage.field.icon': '圖示',
    'pinMessage.field.displayFrom': '顯示開始日',
    'pinMessage.field.displayTo': '顯示結束日',
    'pinMessage.field.status': '狀態',
    'pinMessage.status.active': '啟用',
    'pinMessage.status.inactive': '停用',
    'pinMessage.visibility.both': '僱主 + 家務助理',
    'pinMessage.visibility.employerOnly': '僱主可見',
    'pinMessage.icon.info': '資訊',
    'pinMessage.icon.remind': '提醒',
    'pinMessage.icon.specialAlert': '特別警示',
    'pinMessage.window.always': '永久顯示',
    'pinMessage.window.range': '{{from}} 至 {{to}}',
    'pinMessage.window.from': '由 {{from}} 起',
    'pinMessage.window.to': '至 {{to}}',
    'pinMessage.action.add': '新增置頂訊息',
    'pinMessage.action.edit': '編輯',
    'pinMessage.action.remove': '移除',
    'pinMessage.action.save': '儲存',
    'pinMessage.action.cancel': '取消',
    'pinMessage.action.close': '關閉',
    'pinMessage.detail.createdAt': '建立時間：{{value}}',
    'pinMessage.detail.updatedAt': '最後修改：{{value}}',
    'pinMessage.action.moveUp': '上移',
    'pinMessage.action.moveDown': '下移',
    'pinMessage.confirm.remove': '確定要移除此置頂訊息嗎？',
    'pinMessage.error.loadList': '無法載入置頂訊息清單',
    'pinMessage.error.saveOrder': '無法儲存排序',
    'pinMessage.error.remove': '無法移除置頂訊息',
    'pinMessage.error.notFound': '找不到置頂訊息',
    'pinMessage.error.loadForm': '無法載入置頂訊息表單',
    'pinMessage.error.titleContentRequired': '標題與內文不可留空',
    'pinMessage.error.titleMax': '標題不可超過 {{max}} 字',
    'pinMessage.error.contentMax': '內文不可超過 {{max}} 字',
    'pinMessage.error.dateOrder': '顯示開始日不可晚於顯示結束日',
    'pinMessage.error.save': '無法儲存置頂訊息',
    'pinMessage.ticker.openDetails': '開啟置頂訊息內容',
    'pinMessage.ticker.prev': '上一則置頂訊息',
    'pinMessage.ticker.next': '下一則置頂訊息',
    'pinMessage.ticker.title': '置頂訊息',
    'pinMessage.ticker.count': '{{current}}/{{total}}',
    'helperHome.loadError': '無法載入首頁資料',
    'helperHome.summaryLine': '今日 {{count}} 項任務 · 尚餘 {{budget}}',
    'helperHome.dataPlaceholder': '—',
    'helperHome.cardTodayTasks': '今日剩下的任務',
    'helperHome.todayCompletion': '今日完成率',
    'helperHome.submittedFraction': '已提交 {{done}} / {{total}}',
    'helperHome.submittedFractionValue': '{{done}} / {{total}}',
    'helperHome.submittedFractionStatus': '已提交',
    'helperHome.todayTaskBreakdown': '總計：{{total}} · 已完成：{{done}}',
    'helperHome.cardOverdueTasks': '逾期任務',
    'helperHome.overdueTasksHint': '尚未完成且已逾期的任務',
    'helperHome.weeklyBudgetTitle': '收到的錢 ({{range}})',
    'helperHome.detailsLabel': '詳情',
    'helperHome.remainingLabel': '剩餘',
    'helperHome.totalLabel': '總計',
    'helperHome.doneLabel': '完成',
    'helperHome.budgetSpentOverTotal': '已支出 / 收到的錢',
    'helperHome.budgetSpentLabel': '已支出',
    'helperHome.budgetReceivedLabel': '收到的錢',
    'helperHome.budgetRemainingLabel': '剩餘',
    'helperHome.budgetOverspentLabel': '超支',
    'helperHome.balanceBadge': '尚餘 {{amount}}',
    'helperHome.overspentBadge': '超支 {{amount}}',
    'helperHome.weeklyBudgetPlaceholder': '暫無收到的錢資料',
    'helperHome.budgetUsedFootnote': '已用 {{used}}',
    'helperHome.actionScanReceipt': '掃描收據',
    'helperHome.actionScanReceiptSub': 'AI 分析並提交',
    'helperHome.tomorrowBadge': '{{count}} 項',
    'helperHome.actionTomorrow': '明日任務',
    'helperHome.actionTomorrowSub': '預覽明天待辦',
    'helperHome.actionReceiptList': '收據列表',
    'helperHome.actionReceiptListSub': '查看與追蹤',
    'helperHome.settlementReadyBadge': '結算',
    'helperHome.actionSettlement': '進行結算',
    'helperHome.actionViewDetails': '查看詳情',
    'helperHome.actionSettlementSub': '開支與週期',
    'helperHome.todayPreviewTitle': '今日待辦預覽',
    'helperHome.suggestedTime': '截止時間',
    'helperHome.viewAllTasks': '查看全部任務',
    'helperHome.ariaTodayTasksCard': '前往任務頁今日分頁',
    'helperHome.ariaTodayCompletionCard': '前往任務頁，查看今日完成率',
    'helperHome.ariaOverdueTasksCard': '前往任務頁逾期分頁',
    'helperHome.ariaMoneyReceivedCard': '前往收支與結算詳情',
    'task.pendingReview.title': '待審核任務',
    'task.pendingReview.subtitle': '家務助理已提交，請審核',
    'task.pendingReview.empty': '目前沒有待審核的任務',
    'task.pendingReview.backToTasks': '返回任務管理',
    'task.pendingReview.dueDate': '到期日',
    'task.pendingReview.submittedAt': '提交時間',
    'task.pendingReview.submittedAtTime': '提交時間 : {{time}}',
    'task.pendingReview.review': '審核',
    'task.pendingReview.reviewCta': '審核：{{title}}',
    'task.pendingReview.loadError': '載入待審核列表失敗',
    'task.approvedList.title': '已通過',
    'task.approvedList.subtitle': '您已核准的任務提交',
    'task.approvedList.empty': '目前沒有已通過的紀錄',
    'task.approvedList.backToTasks': '返回任務管理',
    'task.approvedList.dueDate': '到期日',
    'task.approvedList.submittedAt': '提交時間',
    'task.approvedList.reviewedAt': '通過時間',
    'task.approvedList.view': '查看內容',
    'task.approvedList.viewCta': '查看：{{title}}',
    'task.approvedList.loadError': '載入已通過清單失敗',
    // Task Form (Employer)
    'task.form.createTitle': '新增任務',
    'task.form.editTitle': '編輯任務',
    'task.form.titleLabel': '任務標題',
    'task.form.titlePlaceholder': '例如：打掃客廳',
    'task.form.descLabel': '任務描述',
    'task.form.descLabelOptional': '任務描述（選填）',
    'task.form.descPlaceholder': '詳細說明任務內容...',
    'task.form.recurrenceLabel': '重複頻率',
    'task.form.daysLabel': '重複日期',
    'task.form.daysHint': '選擇每週/隔週的執行日',
    'task.form.recurrenceWeekdaysRequired': '每週或隔週任務須至少選擇一個重複日期',
    'task.form.occurrenceDateLabel': '發生日期',
    'task.form.startDateLabel': '開始日期',
    'task.form.endDateLabel': '完結日期',
    'task.form.endDateLabelOptional': '完結日期（選填）',
    'task.form.excludeRangesLabel': '排除日期範圍',
    'task.form.addExcludeRange': '新增排除範圍',
    'task.form.removeExcludeRange': '移除',
    'task.form.excludeStartDateLabel': '排除開始日期',
    'task.form.excludeEndDateLabel': '排除完結日期',
    'task.form.dueTimeLabel': '截止時間',
    'task.form.dueTimeLabelOptional': '截止時間（選填）',
    'task.form.dueTimePlaceholder': '例如 18:00',
    'task.form.requirePhotoLabel': '需要提交照片',
    'task.form.notifyLabel': '完成時通知我',
    'task.form.submit': '儲存任務',
    'task.form.submitting': '儲存中...',
    'task.form.createSuccess': '任務建立成功',
    'task.form.updateSuccess': '任務更新成功',
    'task.form.createError': '建立任務失敗',
    'task.form.updateError': '更新任務失敗',
    'task.form.cancel': '取消',
    // Task Review (Employer)
    'task.review.title': '審核任務',
    'task.review.stockTakeDetailTitle': '家裏存貨詳情',
    'task.review.inventoryTaskSummaryTitle': '家裏存貨任務摘要',
    'task.review.latestHomeStockTitle': '最近的家裏存貨',
    'task.review.latestHomeStockOpenRecentAria': '打開最近家裏存貨列表',
    'task.review.latestHomeStockRecentTitle': '最近提交紀錄',
    'task.review.latestHomeStockTaskDate': '任務日期',
    'task.review.latestHomeStockSubmittedAt': '提交時間',
    'task.review.latestHomeStockStatus': '狀態',
    'task.review.latestHomeStockEmptyEmployer': '暫無家務助理提交的家裏存貨記錄',
    'task.review.latestHomeStockEmptyHelper': '暫無家裏存貨記錄',
    'task.review.addHomeStockTaskFab': '新增家裏存貨任務',
    'task.review.addHomeStockTaskFabAria': '新增家裏存貨任務',
    'task.review.stocktakePhotosHeading': '家裏存貨相片',
    'task.review.stocktakeResultsHeading': '家裏存貨結果',
    'task.review.stockLevelLegendButton': '圖例',
    'task.review.stockLevelLegendButtonAria': '顯示存量圖示圖例',
    'task.review.stockLevelLegendPanelAria': '存量圖示圖例',
    'task.review.stockLevelLegend.full': '滿',
    'task.review.stockLevelLegend.half': '半滿',
    'task.review.stockLevelLegend.low': '少',
    'task.review.stockLevelLegend.empty': '空',
    'task.review.inventoryPhotosHeading': '家裏存貨相片',
    'task.review.inventoryLocationSummaryTitle': '按位置的家裏存貨審核摘要',
    'task.review.inventoryLocationSummaryLoading': '正在載入摘要...',
    'task.review.inventoryLocationSummaryCounts': '{{itemCount}} 項 · {{photoCount}} 張相片',
    'task.review.inventoryLocationSummaryWithItems': '有品項的類別',
    'task.review.inventoryLocationSummaryNoItems': '標記為無品項的類別',
    'task.review.inventoryLocationSummaryNone': '無',
    'task.review.inventoryLocationSummaryEmpty': '暫無位置摘要。',
    'task.review.helperSubmitHomeStock': '提交家裏存貨',
    'task.review.helperResubmitHomeStock': '重新提交家裏存貨',
    'task.review.inventoryLineAwaiting': '待送審明細',
    'task.review.inventoryLinePendingBody': '家務助理送審後會顯示此品目之存量與相片。',
    'task.review.inventorySharedPhotoNote': '與上方品項共用同一張相片。',
    'task.review.inventoryStatLocationsCaption': '位置',
    'task.review.inventoryStatTotalPhotosCaption': '相片',
    'task.review.inventoryStatLowStockCaption': '低庫存',
    'task.review.inventoryStatExpiringCaption': '即將到期',
    'task.review.inventoryFilterCategoriesHeading': '篩選類別',
    'task.review.inventoryLocationPhotosEmpty': '此位置尚無相片。',
    'task.review.inventoryItemsFilteredEmpty': '目前篩選下沒有可顯示的品項。',
    'task.review.inventoryNoCategoriesInLocation': '此位置沒有可篩選的類別。',
    'task.review.inventoryToggleCategoryAria': '篩選類別：{{category}}',
    'task.review.inventoryCategoryEmptyAtLocationBody': '此類別在該位置沒有點算到任何項目。',
    'task.review.inventoryExpiryExpired': '已過期',
    'task.review.inventoryExpiryToday': '今天過期',
    'task.review.inventoryExpiryTomorrow': '明天過期',
    'task.review.inventoryExpiryInDays': '{{count}}天後過期',
    'task.review.inventoryExpiryAbsolute': '{{date}} 到期',
    'task.review.inventoryLocationTabsAria': '選擇存貨位置',
    'task.review.inventoryLocationsSectionHeading': '位置',
    'task.review.inventoryLocationsFilterReminder': '可點選下方標籤切換位置，篩選該位置的相片與品項。',
    'task.review.inventoryLocationsCoachmarkDismissAria': '關閉位置篩選提示',
    'task.review.detailTitle': '任務詳情',
    'task.review.helperAwaitingEmployerBanner': '任務已提交，需待僱主審核',
    'task.review.futureDueDetailOnly': '此任務尚未到可提交日期，僅可查看詳情。',
    'task.review.futureDueOutOfRange': '此任務超出可預早提交範圍，僅可查看詳情。',
    'task.review.futureDueEmployerTomorrow': '此任務為明天的任務。',
    'task.review.futureDueEmployerDays': '此任務為{{days}}天後的任務。',
    'task.review.futureDueHelperTomorrow': '此任務為明天的任務，請留意任務日期！',
    'task.review.futureDueHelperDays': '此任務為{{days}}天後的任務，請留意任務日期！',
    'task.todo.futureSubmitBanner': '你可預早提交將來的任務',
    'task.todo.tabFuture': '將來',
    'task.todo.helperEmptyFuture': '此日期範圍內沒有任務',
    'task.todo.helperEmptyFutureHint': '可調整日期範圍，或留意僱主是否稍後安排。',
    'task.submitConfirmTitle': '確認提交此任務？',
    'task.submitConfirmPrompt': '任務日期如下，是否確認提交？',
    'task.submitConfirmAction': '確認提交',
    'task.submitConfirm.dueDateTomorrow': '{{date}} (明天)',
    'task.submitConfirm.dueDateDaysLater': '{{date}} ({{days}}天後)',
    'task.review.taskInfo': '任務資訊',
    'task.review.submittedBy': '提交者',
    'task.review.taskDate': '任務日期',
    'task.review.completedAt': '完成時間',
    'task.review.submittedAt': '提交時間',
    'task.review.photos': '照片',
    'task.review.prevPhoto': '上一張',
    'task.review.nextPhoto': '下一張',
    'task.review.photoCount': '{{current}} / {{total}}',
    'task.review.gpsLocation': 'GPS 位置',
    'task.review.gpsDisabled': 'GPS 未啟用',
    'task.review.gpsStaticMapAlt': '任務提交位置（地圖預覽）',
    'task.review.openInGoogleMaps': '在 Google 地圖開啟',
    'task.review.openInGoogleMapsAria': '在新分頁於 Google 地圖開啟此位置',
    'task.review.osmCopyright': '© OpenStreetMap 貢獻者',
    'task.review.addressLabel': '地址',
    'task.review.addressLoading': '正在查詢地址…',
    'task.review.addressUnavailable': '無法取得此座標的文字地址',
    'task.review.note': '備註',
    'task.review.noNote': '無備註',
    'task.review.reviewPrompt': '請審核↓',
    'task.review.approve': '通過',
    'task.review.reject': '拒絕',
    'task.review.needsUpdate': '需修改',
    'task.review.needsUpdateModalTitle': '標記為需修改',
    'task.review.needsUpdateReasonPrompt': '請輸入需修改原因',
    'task.review.needsUpdateReasonTooShort': '原因長度不足，至少需要 {{min}} 個字元',
    'task.review.needsUpdatePhotoHint':
      '家務助理重新提交時可刪除錯誤照片並上傳正確照片。',
    'task.review.reviewSuccess': '審核完成',
    'task.review.reviewError': '審核失敗',
    'task.review.versionConflict': '資料已被修改，請重新載入',
    'task.review.loadError': '載入任務詳情失敗',
    // Comments
    'comment.sectionTitle': '留言',
    'comment.usage': '已使用 {{used}} / {{max}} 則留言',
    'comment.charCount': '{{current}} / {{max}} 字元',
    'comment.placeholder': '輸入留言…',
    'comment.writeButton': '輸入留言',
    'comment.modalTitle': '輸入留言',
    'comment.draftPending': '草稿',
    'comment.send': '送出',
    'comment.sending': '送出中…',
    'comment.loadError': '載入留言失敗',
    'comment.sendError': '送出留言失敗',
    'comment.empty': '尚無留言',
    'comment.limitReached': '此項目的留言已達上限。',
    'comment.sendAria': '送出留言',
    'comment.listAria': '留言列表',
    'comment.emptyDraft': '請輸入留言內容',
    'comment.tooLong': '留言不可超過 {{max}} 字元',
    'comment.shortcutHint': '可使用 Ctrl+Enter 或 Cmd+Enter 送出',
    'comment.unsentSubmit.title': '尚未送出留言',
    'comment.unsentSubmit.descriptionTask':
      '您已輸入留言但尚未按「送出」。是否要一併提交任務並送出此留言？',
    'comment.unsentSubmit.descriptionStockTake':
      '您已輸入留言但尚未按「送出」。是否要一併提交家裏存貨並送出此留言？',
    'comment.unsentSubmit.descriptionResubmit':
      '您已輸入留言但尚未按「送出」。是否要一併重新提交任務並送出此留言？',
    'comment.unsentSubmit.backToEdit': '返回編輯',
    'comment.unsentSubmit.submitOnlyTask': '僅提交任務',
    'comment.unsentSubmit.submitOnlyStockTake': '僅提交家裏存貨',
    'comment.unsentSubmit.submitOnlyResubmit': '僅重新提交',
    'comment.unsentSubmit.submitWithComment': '一併提交並送出留言',
    'comment.unsentSubmit.working': '處理中…',
    // Notifications
    'notification.bellAria': '通知',
    'notification.panelTitle': '通知',
    'notification.empty': '沒有通知',
    'notification.markAllRead': '全部標為已讀',
    'notification.markAllReadAria': '將所有通知標為已讀',
    'notification.loadError': '載入通知失敗',
    'notification.expand': '展開全文▼',
    'notification.collapse': '收合▲',
    'notification.expandAria': '展開通知全文',
    'notification.collapseAria': '收合通知全文',
    // Today's Todo (Helper)
    'task.today.title': '任務列表',
    'task.today.subtitle': '按分頁查看歷史、逾期、今日與將來任務',
    'task.today.empty': '今日沒有待辦任務',
    'task.today.emptyHint': '好好休息吧！',
    'task.todo.helperEmptyOverdue': '目前沒有逾期未交任務',
    'task.todo.helperEmptyOverdueHint': '請繼續在截止時間前完成待辦。',
    'task.todo.helperEmptyTomorrow': '明日尚無安排任務',
    'task.todo.helperEmptyTomorrowHint': '如有臨時安排，請留意僱主通知。',
    'task.today.complete': '完成',
    'task.today.view': '查看',
    'task.today.pendingReview': '{{count}} 個任務待僱主審核',
    'task.today.sectionNotSubmitted': '未提交',
    'task.today.sectionNeedsUpdate': '需修改',
    'task.today.sectionCompleted': '已完成',
    'task.today.employerFeedbackLabel': '僱主留言：',
    'task.today.dueAt': '截止 {{time}}',
    'task.today.cannotSubmitTomorrow': '明日才可提交',
    'task.today.tomorrowSubmitBanner': '明日才可提交以下的任務',
    'task.todo.tabHistory': '歷史',
    'task.todo.tabOverdue': '逾期',
    'task.todo.tabToday': '今日',
    'task.todo.tabTomorrow': '明日',
    'task.todo.deadlineToday': '今日',
    'task.todo.deadlineTomorrowShort': '明日',
    'task.todo.inventoryPreviewLocationsLabel': '位置',
    'task.todo.inventoryPreviewCategoriesLabel': '類別',
    'task.todo.receiptScanBanner': '掃描收據，快速記帳',
    'task.history.startDate': '開始日期',
    'task.history.endDate': '完結日期',
    'task.history.filterAria': '依任務狀態篩選歷史記錄',
    'task.history.statusAll': '全部',
    'task.history.empty': '此日期範圍內沒有符合篩選的任務紀錄',
    'task.history.emptyHint': '請調整日期範圍或切換狀態篩選',
    'task.history.relativeToday': '今天',
    'task.history.relativeYesterday': '昨天',
    'task.history.relativeTomorrow': '明天',
    'task.history.dateHeadingWithRelative': '{{date}} ({{relative}})',
    'task.history.groupTotalCount': '{{count}} 筆',
    'task.history.completionTime': '完成時間 : {{time}}',
    'task.employerFuture.title': '將來任務',
    'task.employerFuture.empty': '此日期範圍內沒有任務',
    'task.employerFuture.loadError': '載入將來任務失敗',
    'task.employerFuture.openDetailCta': '查看任務：{{title}}',
    'task.today.loadError': '載入待辦清單失敗',
    'receipt.analyze.navLink': '收據 AI 分析',
    'receipt.analyze.title': '收據 AI 分析',
    'receipt.analyze.subtitle': '選擇收據相片，系統會自動擷取欄位（可手動修改）',
    'receipt.analyze.pickImages': '上載收據圖片（每批最多 10 張）',
    'receipt.analyze.pickImagesAria': '選擇收據圖片檔案',
    'receipt.analyze.analyzing': '分析中…',
    'receipt.analyze.analyzingProgress': '分析中 {{current}} / {{total}}',
    'receipt.analyze.analyzeError': '分析失敗',
    'receipt.analyze.rateLimit': '已達每日 AI 分析上限（{{used}} / {{limit}}）。可於 {{retryAt}} 後再試。',
    'receipt.analyze.manualHint': '分析失敗，請手動輸入或修正欄位。',
    'receipt.analyze.fileTooLarge': '檔案超過 10MB',
    'receipt.analyze.badType': '不支援的圖片格式',
    'receipt.analyze.tooManyFiles': '最多 10 張圖片',
    'receipt.analyze.field.company': '公司 / 品牌',
    'receipt.analyze.field.shop': '分店',
    'receipt.analyze.field.date': '日期',
    'receipt.analyze.field.currency': '貨幣',
    'receipt.analyze.field.total': '總金額',
    'receipt.analyze.field.remark': '備註',
    'receipt.analyze.aiRemarkLabel': '系統備註',
    'receipt.analyze.items': '明細',
    'receipt.analyze.itemsAction': '操作',
    'receipt.analyze.itemName': '項目',
    'receipt.analyze.qty': '數量',
    'receipt.analyze.amount': '金額',
    'receipt.analyze.addItem': '新增一行',
    'receipt.analyze.removeItem': '移除',
    'receipt.analyze.removeRow': '移除此筆',
    'receipt.analyze.removeConfirmTitle': '確認移除此筆？',
    'receipt.analyze.removeConfirmBody': '移除後此筆分析結果將不會保留。',
    'receipt.analyze.leaveConfirmTitle': '離開此頁？',
    'receipt.analyze.leaveConfirmBody': '你有尚未提交的收據照片與資料，離開後將不會保留。',
    'receipt.analyze.leaveConfirmLeave': '離開',
    'receipt.analyze.leaveConfirmStay': '留在此頁',
    'receipt.analyze.budgetLabel': '收到的錢期間',
    'receipt.analyze.budgetRequired': '請選擇收到的錢期間',
    'receipt.analyze.dateLabel': '收據日期',
    'receipt.analyze.dateRequired': '請選擇有效收據日期',
    'receipt.analyze.companyRequired': '請輸入公司 / 品牌',
    'receipt.analyze.submit': '儲存',
    'receipt.analyze.submitAll': '儲存所有收據',
    'receipt.analyze.submitting': '提交中…',
    'receipt.analyze.submittingAllProgress': '儲存中 {{current}} / {{total}}',
    'receipt.analyze.submitSuccess': '已提交',
    'receipt.analyze.submittedDueToPendingSettlement':
      '此收據日期已在待審核結算期內，已直接標記為「已提交結算」。',
    'receipt.analyze.submitError': '提交失敗',
    'receipt.analyze.needImageUrl': '缺少已儲存的圖片連結，無法提交（請確認雲端儲存已設定）',
    'receipt.analyze.invalidTotal': '請輸入有效總金額',
    'receipt.analyze.totalMismatchBanner': '總金額({{total}})與明細的金額({{lineSum}})不相同，請檢查',
    'receipt.analyze.totalMismatchConfirmTitle': '金額不一致',
    'receipt.analyze.totalMismatchConfirmSingle':
      '收據總金額({{total}})與明細的金額({{lineSum}})不相同，請檢查。結算是以總金額({{total}})為準，請確認。',
    'receipt.analyze.totalMismatchConfirmBatchLine':
      '{{date}} {{company}} 收據的總金額({{total}})與明細的金額({{lineSum}})不相同，請檢查。',
    'receipt.analyze.totalMismatchConfirmBatchIntro': '以下收據總金額與明細金額不符：',
    'receipt.analyze.totalMismatchConfirmBatchFooter': '結算是以總金額為準，請確認。',
    'receipt.analyze.totalMismatchConfirmConfirm': '確認儲存',
    'receipt.analyze.backToday': '返回今日待辦',
    'receipt.detail.title': '收據詳情',
    'receipt.detail.backToList': '返回收據列表',
    'receipt.detail.loadError': '無法載入收據',
    'receipt.detail.total': '總金額',
    'receipt.detail.date': '收據日期',
    'receipt.detail.submittedAt': '提交日期',
    'receipt.detail.employerReviewSubmittedAt': '僱主審核提交時間',
    'receipt.detail.delete': '刪除收據',
    'receipt.detail.deleteHint': '僅可刪除待審核、需修改或已取消的收據。',
    'receipt.detail.deleteConfirmTitle': '刪除此收據？',
    'receipt.detail.deleteConfirmBody': '刪除後無法復原，相關留言亦會一併移除。',
    'receipt.detail.deleting': '刪除中…',
    'receipt.detail.cannotDelete': '此收據狀態不允許刪除。',
    'receipt.detail.photoHeading': '收據相片',
    'receipt.detail.photoAlt': '收據相片',
    'receipt.detail.employerSavedReceiptNotice': '此日期未結算，收據亦未正式提交；家務助理仍可修改收據內容。',
    'receipt.detail.helperUnsettledBanner': '此收據尚未包含在結算中',
    'receipt.detail.helperUnsettledBannerCta': '進行結算',
    'receipt.detail.helperUnsettledBannerCtaAria': '前往開支與結算頁進行結算',
    'receipt.detail.editReceipt': '修改收據內容',
    'receipt.detail.changeReviewStatus': '修改狀態',
    'receipt.detail.changeReviewStatusAria': '開啟收據審核狀態修改視窗',
    'receipt.detail.changeReviewStatusTitle': '修改收據審核狀態',
    'receipt.detail.changeReviewStatusBody': '請選擇新的審核結果。',
    'receipt.edit.title': '修改收據',
    'receipt.edit.subtitle': '僅可修改「未結算」收據；提交結算後不可再修改。',
    'receipt.edit.backToDetail': '返回收據詳情',
    'receipt.edit.save': '儲存修改',
    'receipt.edit.saving': '儲存中…',
    'receipt.edit.saveError': '儲存失敗',
    'receipt.edit.blockedStatus': '此收據狀態不允許修改。僅由你提交且狀態為「未結算」的收據可編輯。',
    'receipt.edit.blockedNotOwner': '你只能修改自己提交的收據。',
    'receipt.analyze.photoHeading': '收據相片',
    'receipt.analyze.photoHeadingOfTotal': '收據相片（{{current}}／{{total}}）',
    'receipt.analyze.photoAlt': '收據相片',
    'receipt.analyze.enlargeImageAria': '放大檢視收據相片',
    'receipt.analyze.openDetailAria': '打開收據詳情',
    'receipt.analyze.scanMore': '掃描更多收據',
    'receipt.analyze.nextStepSheetTitle': '收據已儲存',
    'receipt.analyze.nextStepSheetLead': '下一步：提交結算給僱主。',
    'receipt.analyze.nextStepSheetUploadAll': '請先上傳以下結算期內的全部收據，再進行結算。',
    'receipt.analyze.nextStepSheetLockNote':
      '提交結算後，該期間內的收據會送交僱主；該期間日期不可再上傳或修改收據。',
    'receipt.analyze.nextStepSheetEmployerReview': '須提交結算後，僱主才會審核。',
    'receipt.analyze.nextStepSheetPeriodLabel': '對應結算期',
    'receipt.analyze.nextStepSheetPeriodRange': '{{start}} – {{end}}',
    'receipt.analyze.nextStepSheetPeriodLoading': '正在載入結算期…',
    'receipt.analyze.nextStepSheetPeriodUnknown':
      '尚無對應的「收到的錢」期間；仍可稍後到「開支與結算」選期間並提交。',
    'receipt.analyze.nextStepSheetPeriodLocked': '已鎖定',
    'receipt.analyze.nextStepSheetLockedHint':
      '對應期間已有進行中或已鎖定的結算，請到開支頁查看詳情。',
    'receipt.analyze.nextStepSheetGoSettle': '去結算',
    'receipt.analyze.nextStepSheetGoSettleAria': '前往開支與結算頁提交結算',
    'receipt.analyze.nextStepSheetLater': '稍後再說',
    'receipt.analyze.nextStepSheetLaterAria': '關閉提醒，稍後再提交結算',
    'receipt.list.title': '收據列表',
    'receipt.list.subtitle': '檢視收據狀態與提交時間',
    'receipt.list.filterAria': '依收據狀態篩選收據',
    'receipt.list.filter.all': '全部',
    'receipt.list.filter.save': '未結算',
    'receipt.list.filter.submitted': '已提交結算',
    'receipt.list.filter.reviewed': '已審核',
    'receipt.list.startDate': '開始日期（選填）',
    'receipt.list.endDate': '結束日期（選填）',
    'receipt.list.requiredStartDate': '開始日期',
    'receipt.list.requiredEndDate': '結束日期',
    'receipt.list.empty': '目前沒有符合條件的收據。',
    'receipt.list.loadError': '無法載入收據列表',
    'receipt.list.loadMore': '載入更多',
    'receipt.list.openDetailAria': '開啟 {{date}} 收據：{{summary}}',
    'receipt.list.submittedAtLine': '提交日期：{{at}}',
    // Budget (employer — Screen 2.4)
    'budget.title': '已給的錢',
    'budget.subtitle': '建立僱主給予金額（主期間與追加金錢），供家務助理收據與結算使用',
    'budget.tabActive': '進行中',
    'budget.tabSettled': '已結算',
    'budget.tabsAria': '已給的錢列表分類',
    'budget.addNew': '新增已給的錢',
    'budget.addNewAria': '新增已給的錢期間',
    'budget.addOn': '追加',
    'budget.addOnAria': '為此期間追加金錢',
    'budget.form.addOnCreateTitle': '追加金錢',
    'budget.form.parentPeriod': '所屬主期間',
    'budget.empty': '暫無資料',
    'budget.emptySettled': '目前尚無已結算的已給的錢。',
    'budget.emptyHint': '請新增一筆已給的錢，家務助理方可選擇期間並提交收據。',
    'budget.filterStartDate': '開始日期',
    'budget.filterEndDate': '結束日期',
    'budget.totalAmount': '已給的錢總額',
    'budget.mainAmountLabel': '已給的錢',
    'budget.subAmountLabel': '追加金額',
    'budget.spent': '已使用',
    'budget.remaining': '剩餘',
    'budget.badgeLocked': '已有鎖定結算',
    'budget.badgeSettled': '已結算',
    'budget.timeLabel': '時間:{{value}}',
    'budget.edit': '編輯',
    'budget.editAria': '編輯已給的錢 {{start}} 至 {{end}}',
    'budget.mergedChildCount': '包含 {{count}} 筆追加金錢',
    'budget.expandChildrenAria': '展開 {{start}} 至 {{end}} 的追加金錢明細',
    'budget.collapseChildrenAria': '收合 {{start}} 至 {{end}} 的追加金錢明細',
    'budget.backList': '返回已給的錢列表',
    'budget.loadError': '無法載入已給的錢',
    'budget.saveError': '儲存失敗',
    'budget.form.createTitle': '新增已給的錢',
    'budget.form.editTitle': '編輯已給的錢期間',
    'budget.form.startDate': '開始日期',
    'budget.form.endDate': '結束日期',
    'budget.form.amount': '已給的錢金額',
    'budget.form.amountHint': '須為零或正數；可含小數。',
    'budget.form.submitCreate': '建立已給的錢',
    'budget.form.submitEdit': '儲存變更',
    'budget.form.saving': '儲存中…',
    'budget.form.cancel': '取消',
    'budget.form.remove': '移除',
    'budget.form.removeAria': '移除此已給的錢期間',
    'budget.form.removeConfirm': '確認移除',
    'budget.form.removing': '移除中…',
    'budget.removeConfirmTitle': '移除此已給的錢？',
    'budget.removeConfirmBody':
      '確定要移除此已給的錢期間嗎？此操作無法還原。',
    'budget.removeConfirmReceiptsExtra': '目前有 {{count}} 筆收據綁定此已給的錢；移除後上述綁定會一併解除。',
    'budget.removeError': '無法移除已給的錢',
    'budget.validation.rangeRequired': '請填寫開始與結束日期',
    'budget.validation.dates': '結束日期不可早於開始日期',
    'budget.validation.amount': '請輸入有效金額（零或正數）',
    'budget.lockedEditHint':
      '此已給的錢曾用於已鎖定的結算。若縮短日期，所有關聯收據的日期仍須完全落在新區間內；若有收據落在外面，或與其他結算日期重疊，儲存會失敗。必要時請僱主先處理結算／收據後再改。',
    'budget.summaryReceipts': '此已給的錢已關聯 {{count}} 筆收據',
    // Settlement
    'settlement.title': '結算與鎖定',
    'settlement.employerFinanceTitle': '已給的錢與結算',
    'settlement.employerFinanceTitleMenuAria': '已給的錢與結算選單',
    'settlement.employerFinanceMenuPendingReviewList': '待審核結算列表',
    'settlement.employerFinanceMenuReceiptList': '收據列表',
    'settlement.formTitle': '新增結算',
    'settlement.subtitle':
      '選擇期間；系統會加總該段內收據支出，並與對應的收到的錢金額比較。確認後該範圍內收據將鎖定。',
    'settlement.navLink': '開支結算',
    'settlement.budgetLabel': '收到的錢期間',
    'settlement.startDate': '開始日期',
    'settlement.endDate': '結束日期',
    'settlement.preview': '預覽金額',
    'settlement.previewing': '計算中…',
    'settlement.totalSpent': '支出總額',
    'settlement.dailyDetail': '拆分',
    'settlement.dailyDetailOpen': '開啟每日開支總結',
    'settlement.dailyDetailTitle': '每日開支總結',
    'settlement.dailyDetailDate': '日期',
    'settlement.dailyDetailReceiptCount': '收據數量',
    'settlement.dailyDetailAmount': '金額',
    'settlement.budgetAmount': '已給的錢',
    'settlement.balance': '結算結果',
    'settlement.surplus': '淨餘',
    'settlement.overspent': '超支',
    'settlement.confirm': '確認結算並鎖定',
    'settlement.confirming': '處理中…',
    'settlement.success': '結算已鎖定',
    'settlement.error': '操作失敗',
    'settlement.listTitle': '結算紀錄',
    'settlement.employerFinanceTabsAria': '僱主已給的錢與結算分頁',
    'settlement.employerTabBudget': '已給的錢',
    'settlement.employerTabSettlement': '結算',
    'settlement.employerSubTabReceipts': '收據列表',
    'settlement.filterStartDate': '開始日期',
    'settlement.filterEndDate': '結束日期',
    'settlement.employerFilterToDateOptional': '結束日期（選填）',
    'settlement.empty': '尚無結算紀錄',
    'settlement.locked': '已審核',
    'settlement.unlocked': '已解鎖',
    'settlement.status.submitted': '待審核',
    'settlement.status.unsettled': '未結算',
    'settlement.status.settled': '已結算',
    'settlement.unsettledEmployerSavedReceiptNotice':
      '家務助理未提交此日期的結算，收據亦未正式提交；家務助理仍可修改收據內容。',
    'settlement.givenVsExpensesTitle': '已給的錢 vs 支出',
    'settlement.givenVsExpensesSubtitle': '最近 4 次已給的錢期間概覽',
    'settlement.givenVsExpensesGiven': '已給的錢',
    'settlement.givenVsExpensesSpent': '支出',
    'settlement.givenVsExpensesPendingSpent': '待審核支出',
    'settlement.helperReceivedVsExpensesTitle': '收到的錢 vs 支出',
    'settlement.helperReceivedVsExpensesSubtitle': '最近 4 次收到的錢期間概覽',
    'settlement.reviewNow': '立即審核',
    'settlement.pendingReviewPageTitle': '待審核結算',
    'settlement.historyEventAtLabel': '狀態時間',
    'settlement.historyViewDetailAria': '查看結算紀錄詳情',
    'settlement.historyDetailButton': '詳情',
    'settlement.pastSettlementHistoryTitle': '過去的結算紀錄',
    'settlement.historyStatus.withdrawn': '家務助理已撤回',
    'settlement.historyStatus.rejected': '僱主已拒絕',
    'settlement.historyStatus.unlocked': '僱主已解鎖',
    'settlement.status.reviewed': '已審核',
    'settlement.status.rejected': '已拒絕',
    'settlement.status.withdrawn': '已撤回',
    'settlement.recordStatusFilterAria': '依結算狀態篩選',
    'settlement.recordStatusFilter.submitted': '待審核',
    'settlement.recordStatusFilter.reviewed': '已審核',
    'settlement.recordStatusFilter.rejected': '已拒絕',
    'settlement.recordStatusFilter.withdrawn': '已撤回',
    'settlement.recordStatusFilter.unlocked': '已解鎖',
    'settlement.lockHint':
      '此段日期的開支已完成結算並鎖定；須請僱主在 App 內解除該筆結算的鎖定後，您才能修改或刪除該期間的收據。',
    'settlement.detailTitle': '結算詳情',
    'settlement.detailTitleMenuAria': '結算詳情選單',
    'settlement.menuEditMoneyGiven': '修改已給的錢',
    'settlement.menuAppendMoneyGiven': '追加已給的錢',
    'settlement.submittedAtLabel': '提交時間',
    'settlement.unlock': '解鎖此結算',
    'settlement.withdraw': '撤回此結算提交',
    'settlement.reviewedAction': '標記為已審核',
    'settlement.rejectReasonPrompt': '請輸入拒絕理由',
    'settlement.rejectModalTitle': '拒絕此結算',
    'settlement.unlockConfirmTitle': '確認解鎖？',
    'settlement.unlockConfirmBody': '解鎖後家務助理可修改此日期範圍內的收據。此操作會記錄於稽核日誌。',
    'settlement.unlocking': '解鎖中…',
    'settlement.unlockSuccess': '已解鎖',
    'settlement.auditTitle': '解鎖紀錄',
    'settlement.auditEmpty': '尚無解鎖紀錄',
    'settlement.receiptsTitle': '範圍內收據',
    'settlement.viewReceipt': '查看收據',
    'settlement.noBudgets':
      '無需預先建立已給的錢也可上傳收據與結算；若所選期間內尚無設定，收到的錢金額將視為 0。',
    'settlement.employerNoBudgetsBanner':
      '尚未建立任何已給的錢期間。仍可結算（金額以對應期間計）；建議到「已給的錢管理」新增資料以便對照。',
    'settlement.selectRange': '請選擇開始與結束日期',
    'settlement.confirmLockModalTitle': '提交此結算給僱主？',
    'settlement.confirmEmptyModalTitle': '此期間沒有收據',
    'settlement.confirmLockPrompt':
      '確認提交 {{start}} - {{end}} 結算嗎？此範圍內收據不可編輯及上傳。',
    'settlement.confirmEmptyReceiptsPrompt': '此日期範圍內沒有任何收據。仍要提交嗎？',
    'settlement.previewFirst': '預覽尚未就緒，請待計算完成或檢查日期後再鎖定。',
    'settlement.budgetAmountHint': '包含主期間與所有追加金錢的金額加總。',
    'settlement.helperHeroTitle': '開支與結算',
    'settlement.helperHeroSubtitle': '管理你的開支，並提交結算予僱主。',
    'settlement.helpPopoverTriggerAria': '查看結算步驟與注意事項',
    'settlement.helpPopoverAria': '結算步驟與注意事項',
    'settlement.helpPopoverTitle': '如何提交結算',
    'settlement.helpPopoverStep1': '掃描並儲存該結算期內的全部收據。',
    'settlement.helpPopoverStep2': '在本頁選擇「收到的錢」期間並預覽開支。',
    'settlement.helpPopoverStep3': '按「進行結算」提交給僱主審核。',
    'settlement.helpPopoverNotesHeading': '注意',
    'settlement.helpPopoverNote1': '儲存收據後，須提交結算，僱主才會審核。',
    'settlement.helpPopoverNote2':
      '結算提交後，該期間日期不可再上傳或修改收據，直至僱主拒絕、解鎖或您撤回。',
    'settlement.helperQuickActions': '快速操作',
    'settlement.helperActionScanTitle': '掃描收據',
    'settlement.helperActionScanMeta': 'AI 分析並提交',
    'settlement.helperActionReceiptListTitle': '收據列表',
    'settlement.helperActionReceiptListMeta': '查看已提交與待批核收據',
    'settlement.helperActionLockTitle': '進行結算',
    'settlement.helperActionLockMeta': '鎖定所選期間並提交予僱主',
    'settlement.helperActionHistoryTitle': '結算紀錄',
    'settlement.helperActionHistoryMeta': '查看過往結算記錄',
    'settlement.helperDateRangeAria': '所選結算期間，展開以變更日期',
    'settlement.helperStatPeriodBudget': '收到的錢',
    'settlement.moneyReceivedDetailOpen': '開啟收到的錢明細',
    'settlement.moneyReceivedDetailTitle': '收到的錢明細',
    'settlement.moneyReceivedDetailReceivedDate': '時間',
    'settlement.moneyReceivedDetailPeriod': '期間',
    'settlement.moneyReceivedDetailAmount': '金額',
    'settlement.moneyReceivedDetailAddOn': '追加',
    'settlement.moneyGivenDetailOpen': '開啟已給的錢明細',
    'settlement.moneyGivenDetailTitle': '已給的錢明細',
    'settlement.helperStatTotalSpent': '總支出',
    'settlement.helperStatBalance': '淨餘',
    'settlement.dailyBreakdownVsBudgetTitle': '每日支出',
    'settlement.dailyBudgetAmount': '每日收到的錢拆分',
    'settlement.dailyVsBudgetColBudget': '收到的錢',
    'settlement.dailyVsBudgetColExpense': '支出',
    'settlement.dailyBudgetVariance': '差額',
    'settlement.expensePreview': '開支預覽',
    'settlement.breakdownModalTitle': '每日開支明細',
    'settlement.openBreakdownAria': '開啟 Breakdown，查看每日收據筆數與金額',
    'settlement.helperConfirmDateRangeLabel': '結算日期',
    'settlement.helperConfirmDateRangeSeparator': '至',
    'settlement.helperConfirmLockNotice':
      '結算後將會鎖定此期間的開支記錄，需僱主解除才可再次修改。',
    'settlement.helperConfirmPrimary': '提交',
    'settlement.helperSettleButton': '進行結算',
    'settlement.confirmAction': '確認',
    'settlement.helperReviewedByEmployerNotice': '僱主已審核結算。',
    'settlement.helperSubmittedNotice': '此日期範圍已有已提交結算，等待僱主審核。',
    'settlement.helperSubmittedRangeNotice': '此日期範圍已有已提交結算（{{range}}），等待僱主審核。',
    'settlement.helperSubmittedRangesNotice': '此日期範圍已有已提交結算（{{ranges}}），等待僱主審核。',
    'settlement.helperSubmittedAndReviewedRangesNotice':
      '此日期範圍已有已提交/已審核結算（{{ranges}}）。其中已提交項目仍在等待僱主審核。',
    'settlement.confirmOverlapSubmittedRangesError':
      '你選擇的日期與以下已提交結算重疊（{{ranges}}）。請先撤回或等待僱主審核後再提交重疊日期。',
    'settlement.confirmOverlapLockedRangesError':
      '你選擇的日期與以下已結算鎖定期間重疊（{{ranges}}）。請僱主先解鎖後再提交重疊日期。',
    'settlement.helperConfirmPageTitle': '進行結算',
    'settlement.helperConfirmSubtitle': '核對所選期間的預覽金額後，鎖定並提交予僱主。',
    'settlement.helperHistoryPageTitle': '結算紀錄',
    'settlement.helperHistoryTabBySettlement': '按結算',
    'settlement.helperHistoryTabByMoneyReceived': '按收到的錢',
    'settlement.helperHistoryViewTabsAria': '結算紀錄檢視方式',
    'settlement.helperResubmitBannerPrefix': '此日期仍未提交最新結算，按',
    'settlement.helperResubmitBannerLink': '此',
    'settlement.helperResubmitBannerSuffix': '提交',
    'settlement.helperResubmitBannerLinkAria': '前往同意並結算',
    'settlement.helperBackToExpenses': '返回開支與結算',
    'settlement.totalSpentReceiptHint': '含已儲存、已提交及已審核收據。',
    'settlement.period': '{{start}} 至 {{end}}',
    'settlement.backList': '返回結算列表',
    'apiError.settlementLockedReceipt':
      '此收據日期（{{receiptDate}}）已落在「已結算並鎖定」的期間（{{start}} 至 {{end}}）。請僱主先在 App 內解除該筆結算的鎖定，之後您才能新增、修改或刪除該段期間的收據。',
    'apiError.settlementRangeOverlapsLocked':
      '您選擇的日期（{{reqStart}} 至 {{reqEnd}}）與已結算並鎖定的期間（{{start}} 至 {{end}}）重疊。請僱主先解除該筆結算的鎖定，或改選不重疊的日期後再試。',
    'apiError.settlementNoReceiptsAck':
      '此日期範圍內沒有任何收據。若仍要鎖定空期間，請在畫面上確認（勾選對應選項）後再操作。',
    'apiError.budgetOverlapsSettlement':
      '此已給的錢期間與既有結算所涵蓋的日期重疊。請改選完全落在「任何結算期間以外」的開始與結束日期。',
    'apiError.budgetShrinkReceiptsOutside':
      '無法縮短到這組日期，因為仍有收據的日期落在新範圍之外。請拉寬已給的錢日期，或先處理那些收據後再儲存。',
    'apiError.budgetDeleteLocked': '此已給的錢已有鎖定結算，無法移除。請先處理結算後再試。',
    'apiError.budgetMainOverlap': '此已給的錢期間與其他期間重疊（{{ranges}}），請改用不重疊日期。',
    'apiError.subBudgetOutOfMainRange': '追加金錢期間必須完全落在所屬主期間內。',
    'apiError.mainBudgetLockedBySubmittedSettlement':
      '此已給的錢期間已有已提交結算，現時只可查看，不能修改或刪除。',
    'apiError.receiptDeleteStatusNotAllowed':
      '此收據目前狀態無法刪除。僅限「等待僱主審核」「僱主要求修改」或「已取消」的收據可以刪除。',
    'apiError.receiptUpdateStatusNotAllowed':
      '此收據目前無法編輯。僅限「等待僱主審核」或「僱主要求修改」時可以更改內容。',
    'apiError.settlementAlreadyUnlocked': '此筆結算已經解除鎖定。請重新整理頁面以取得最新狀態。',
    // Auto-translation (user-entered content)
    'translation.autoTranslatedTo': '已自動翻譯至{{language}}。翻譯可能出錯',
    'translation.showOriginal': '顯示原文',
    'translation.showTranslated': '顯示譯文',
    'translation.hintButtonAria': '翻譯說明',
    'translation.hintPanelAria': '自動翻譯提示',
    'translation.lang.zhHant': '繁體中文',
    'translation.lang.zhHans': '简体中文',
    'translation.lang.en': '英文',
    'translation.lang.id': '印尼文',
    'translation.lang.fil': '菲律賓文',
    // Family members
    'family.navLink': '家庭成員',
    'family.title': '家庭成員的偏好',
    'family.subtitle': '僱主可新增或編輯；雙方可查看。',
    'family.listConsentLabel': '本人確認已取得家人同意分享其資料。',
    'family.listConsentHint': '請勾選以上方格以查看或管理家庭成員資料。',
    'family.addMember': '新增成員',
    'family.addMemberAria': '新增家庭成員',
    'family.editMember': '編輯',
    'family.editMemberAria': '編輯家庭成員',
    'family.deleteMember': '移除',
    'family.deleteConfirm': '確定要移除此家庭成員嗎？此操作無法還原。',
    'family.empty': '尚無家庭成員資料',
    'family.emptyHint': '點擊「新增成員」加入。',
    'family.emptyHintHelper': '家庭成員須由僱主新增；如有需要，請聯絡僱主。',
    'family.field.name': '姓名',
    'family.field.nickname': '別稱',
    'family.formField.nickname': '別稱（選填）',
    'family.formField.birthday': '生日（選填）',
    'family.formField.remark': '常規活動（選填）',
    'family.formField.foodPreference': '食物偏好（選填）',
    'family.formField.foodAllergy': '食物敏感／過敏（選填）',
    'family.formField.medicalCondition': '健康／醫療狀況（選填）',
    'family.formField.hashtags': '標籤（選填）',
    'family.placeholder.name': '例如：陳大文',
    'family.placeholder.nickname': '例如 : 哥哥',
    'family.placeholder.foodPreference': '例如：喜歡清淡、愛吃蘋果',
    'family.placeholder.foodAllergy': '例如：花生',
    'family.placeholder.medicalCondition': '例如：血壓偏高，需定時服藥',
    'family.placeholder.hashtags': '例如：飲食宜清淡、低鹽',
    'family.field.birthday': '生日',
    'family.field.age': '年齡',
    'family.field.foodPreference': '食物偏好',
    'family.field.foodAllergy': '食物敏感／過敏',
    'family.field.medicalCondition': '健康／醫療狀況',
    'family.field.remark': '常規活動',
    'family.field.remarkPlaceholder': '例如：足球課程 - 星期六朝早10點',
    'family.field.hashtags': '標籤',
    'family.field.hashtagsHint': '可輸入多個，以逗號、分號或換行分隔。',
    'family.birthday.optionalHint': '可留空；可不填年份，僅選月與日。',
    'family.birthday.exampleHint': '例如：選 2011 年、03 月、15 日；或僅選月與日（年份留空）。',
    'family.birthday.yearOptional': '年份（選填）',
    'family.birthday.month': '月',
    'family.birthday.day': '日',
    'family.display.birthdaySkipped': '未提供生日',
    'family.display.ageWithYear': '{{age}} 歲（{{year}} 年出生）',
    'family.display.birthdayMonthDay': '{{month}} 月 {{day}} 日',
    'family.validation.birthdayNeedMonthDay': '若要填寫生日，請同時選擇月與日。',
    'family.validation.birthdayInvalid': '生日日期無效，請檢查年月日。',
    'family.formConsentLabel': '本人確認已取得該家人同意分享其資料。',
    'family.formCreateTitle': '新增家庭成員',
    'family.formEditTitle': '編輯家庭成員',
    'family.formSubmit': '儲存',
    'family.formSubmitting': '儲存中…',
    'family.formCancel': '取消',
    'family.loadError': '無法載入家庭成員資料',
    'family.saveError': '儲存失敗',
    'family.deleteError': '移除失敗',
    'family.validation.consentRequired': '請勾選確認已取得家人同意。',
    // Inventory / 盤點（家務助理）
    'inventory.title': '家裏存貨任務',
    'inventory.subtitle': '先批次拍照（一張可含多品項），AI 比對清單並帶入存量；再補拍或手動確認後送審',
    'inventory.stocktakePanelTitle': '盤點拍照',
    'inventory.stocktakePanelHint': '一張相片可含多個品項。',
    'inventory.stocktakeMaxFilesHint': '單次最多 10 張',
    'inventory.uploadedStocktakePhotosHeading': '已上傳的家裏存貨相片',
    'inventory.uploadedStocktakePhotoAlt': '家裏存貨相片 {{n}}',
    'inventory.enlargePhotoAria': '放大檢視家裏存貨相片',
    'inventory.removePhotoAria': '移除此家裏存貨相片',
    'inventory.removeStocktakePhotoTitle': '移除此相片？',
    'inventory.removeStocktakePhotoDescription':
      '已送審前可移除。若品項曾套用此圖的辨識結果，將一併清除，請再拍照或手動填寫。',
    'inventory.removeStocktakePhotoConfirm': '移除',
    'inventory.notRecognizedByAi': '以下品項已在相片中仍未被 AI 辨識，請補拍或以批次相片手動填寫存量。',
    'inventory.matchedByAiHint': '已由 AI 帶入（可修改）',
    'inventory.stockLevel': '存量狀態',
    'inventory.stockLevelRequired': '請為每個品項選擇存量狀態。',
    'inventory.level.full': '滿',
    'inventory.level.half': '半滿',
    'inventory.level.low': '低',
    'inventory.level.empty': '空',
    'inventory.level.unknown': '—',
    'inventory.photoLabel': '品項照片',
    'inventory.analyze': 'AI 分析',
    'inventory.analyzing': '分析中…',
    'inventory.stocktakeUploading': '上傳中…',
    'inventory.stocktakeUploadingProgress': '上傳中 {{current}} / {{total}}',
    'inventory.analyzeNeedPhoto': '請先選擇照片後再分析',
    'inventory.analyzeError': '分析失敗，請稍後再試',
    'inventory.stocktakeDailyAiLimitPhotoSaved':
      '今日您的 AI 圖像分析額度已用完（與收據掃描共用）。本次未執行 AI。若雲端上傳成功，縮圖會留在列表中；請手動填寫存量，UTC 隔日額度重置後可再試。',
    'inventory.needAtLeastOneBatchPhoto': '請在上傳一張相片。',
    'inventory.needPhotoBeforeStockLevels': '請先為此位置（或此步驟）上傳至少一張家裏存貨相片，之後會顯示存量、到期資料與人手新增品項區。',
    'inventory.unmatchedUseSharedPhotoHint':
      '若本批已有一張成功上傳的相片，送審時未辨識品目會與其他品目共用該連結，無需逐行再補圖。',
    'inventory.emptyTemplate': '此盤點清單沒有品項，無法提交',
    'inventory.helperOnly': '僅家務助理可進行盤點',
    'inventory.chainBlockedBanner': '上一個家裏存貨任務尚未完成審核，暫時無法開始這次家裏存貨任務。',
    'inventory.wizard.locationStepBadge': '位置 {{current}}／{{total}}：{{name}}',
    'inventory.wizard.reviewPhaseHint': '總覽：請確認完成時間後提交',
    'inventory.wizard.reviewTitle': '各位置摘要',
    'inventory.wizard.reviewLocationSummaryPhotos': '相片 {{photos}} 張',
    'inventory.wizard.reviewLocationSummaryEmptyCategories': '無品項類別 {{noCat}} 個',
    'inventory.wizard.noItemsHeading': '此位置沒有以下類別的品項',
    'inventory.wizard.noItemsHint': '若該類別在此位置確實沒有任何品項，請勾選確認（無需填存量）。',
    'inventory.wizard.noItemsHintTickFirst':
      '請先依「盤點類別」處理：某類別在此位置完全沒有品項的，請先勾選下列對應方格；有待盤點品的才填存量／到期。',
    'inventory.wizard.noItemsConfirmModalTitle': '確認無品項的類別',
    'inventory.wizard.noItemsConfirmModalDescription':
      '下列類別在目前位置沒有任何點算品項。請逐類別勾選「此位置無該類品項」後按確認，才會繼續。',
    'inventory.wizard.noItemCategoryAria': '此位置無「{{category}}」類品項',
    'inventory.wizard.noItemCategoryLabel': '此位置無「{{category}}」類品項',
    'inventory.wizard.rowSkippedNoItemsCategory': '此品項所屬類別已標示為此位置無品項。',
    'inventory.wizard.manualItemNoItemsCategoryBanner':
      '此類別已標示為「此位置無該類品項」。您仍可在此填寫存量與到期；若要一併送出，請先取消該標示。',
    'inventory.wizard.continueNextLocation': '繼續下一個位置',
    'inventory.wizard.goToReview': '前往總覽',
    'inventory.wizard.photoRequiredPerLocation': '每個位置請至少上傳一張盤點相片。',
    'inventory.wizard.noItemsConflict': '已勾選「此類別無品項」時，請勿再為該類別填寫存量。',
    'inventory.wizard.categoryMarkNoItems': '任務類別「{{category}}」在清單中沒有對應品項，請勾選「此類別無品項」。',
    'inventory.wizard.helperSingleStep1Badge': '步驟 1／2：拍攝／上傳家裏存貨相片',
    'inventory.wizard.helperSingleStep2Badge': '步驟 2／2：填寫存量與到期資料',
    'inventory.wizard.continueToStockEntry': '下一步：填寫存貨資料',
    'inventory.wizard.locationHubTitle': '位置總覽',
    'inventory.wizard.locationHubHint':
      '可先完成任一位置再上傳其他位置相片；請點選下方卡片切換。每個位置須先有相片並依類別完成存量／無品項勾選，全部齊備後方可於總覽提交。',
    'inventory.wizard.locationHubReviewHint':
      '請確認各位置已齊備；如需修改資料，請按「繼續編輯此位置」返回該位置步驟。',
    'inventory.wizard.locationHubProgress': '已齊備可提交進度：{{done}}／{{total}} 個位置',
    'inventory.wizard.locationHubSelectAria': '切換到位置 {{name}}',
    'inventory.wizard.locationHubCurrentBadge': '目前編輯中',
    'inventory.wizard.locationHubStatusPhotoOk': '相片已上傳',
    'inventory.wizard.locationHubStatusPhotoMissing': '尚需相片',
    'inventory.wizard.locationHubStatusReady': '此位置資料已齊',
    'inventory.wizard.locationHubStatusIncomplete': '此位置尚有待填項目',
    'inventory.wizard.editLocationFromReview': '繼續編輯此位置',
    'inventory.wizard.editLocationFromReviewAria': '返回編輯位置 {{name}}',
    'inventory.wizard.notAllLocationsReady':
      '仍有位置資料未齊備，請先到「位置總覽」切換並補齊相片／類別／存量（完成後上方進度會顯示已全部齊備）。',
    'inventory.wizard.selectLocationTitle': '選擇位置',
    'inventory.wizard.selectLocationSubtitle': '請選擇要開始盤點的位置。',
    'inventory.wizard.hubInfoBanner': '提交前須完成所有指定位置的盤點。',
    'inventory.wizard.hubLocationsCompleted': '已完成 {{done}}／{{total}} 個位置',
    'inventory.wizard.tapToStart': '點一下開始',
    'inventory.wizard.locationCompletedLine': '已完成',
    'inventory.wizard.locationHubStatusInProgress': '尚有資料待填',
    'inventory.wizard.locationHubStatusNeedsStock': '請為品項選擇存量',
    'inventory.wizard.locationHubStatusNeedsExpiry': '請為品項選擇到期方式',
    'inventory.wizard.locationHubStatusNeedsCategories': '請在檢視項目頁按「繼續」確認無品項的類別',
    'inventory.wizard.hubNextStep': '下一步',
    'inventory.wizard.hubNextDisabledHint': '請先完成所有位置的盤點資料。',
    'inventory.wizard.stepBadge': '步驟 {{current}}／{{total}}',
    'inventory.wizard.stepBadgeWithSubstep': '步驟 {{major}}{{sub}}／{{total}}',
    'inventory.wizard.stepBadgeWithSubstepAria': '目前為步驟 {{major}}{{sub}}，共 {{total}} 步',
    'inventory.wizard.selectLocationIncompleteSection': '未完成',
    'inventory.wizard.selectLocationCompleteSection': '已完成',
    'inventory.wizard.selectLocationModalCloseAria': '關閉選擇位置',
    'inventory.wizard.selectLocationFinalReviewButton': '最後審核',
    'inventory.wizard.leaveHomeStockWorkButton': '離開家裏存貨工作',
    'inventory.wizard.viewThisTaskButton': '查看本次任務',
    'inventory.wizard.leaveHomeStockWorkConfirmTitle': '離開家裏存貨工作？',
    'inventory.wizard.leaveHomeStockWorkConfirmDescription':
      '離開後，此行未提交的資料將不會儲存。確定要返回任務清單嗎？',
    'inventory.wizard.progressAria': '進度約 {{percent}}%',
    'inventory.wizard.photosStepTitle': '拍攝／上傳相片',
    'inventory.wizard.photosStepSubtitle': '請為「{{location}}」拍攝或上傳家裏存貨相片。',
    'inventory.wizard.reviewItemsTitle': '檢視項目',
    'inventory.wizard.reviewItemsSubtitle': '請核對品項並更新存量與到期狀態。',
    'inventory.wizard.continueToReviewItems': '前往檢視項目',
    'inventory.wizard.continueWithAiAnalyze': 'AI分析及檢視項目⮕',
    'inventory.wizard.continueWithoutAi': '檢視項目(不使用AI)⮕',
    'inventory.wizard.aiSelectModalTitle': '選擇要分析的相片',
    'inventory.wizard.aiSelectModalDescription': '已分析與未分析的相片分開列出；預設只勾選未做 AI 的相片。',
    'inventory.wizard.aiSelectModalNotAnalyzedHeading': '未進行 AI 分析',
    'inventory.wizard.aiSelectModalAnalyzedHeading': '已進行 AI 分析',
    'inventory.wizard.aiSelectModalSectionEmpty': '（無）',
    'inventory.wizard.aiSelectModalConfirm': '開始分析',
    'inventory.wizard.aiSelectPhotoCheckboxAria': '分析相片 {{n}}',
    'inventory.wizard.reanalyzeConfirmTitle': '重新分析相片？',
    'inventory.wizard.reanalyzeConfirmDescription':
      '將清除並以最新 AI 結果取代與所選 {{photos}} 張相片相關的 {{items}} 個品項資料（其他相片上的品項不受影響）。',
    'inventory.wizard.reanalyzeConfirmProceed': '確認並分析',
    'inventory.stocktakeDailyAiLimitBatchCancelled': '今日 AI 配額不足，本次所選相片已全部取消分析。',
    'inventory.removeStocktakePhotoWithItemsDescription':
      '此相片已連結 {{count}} 個品項。要一併刪除相片及相關品項嗎？',
    'inventory.removeStocktakePhotoDeleteWithItems': '刪除相片及相關品項',
    'inventory.removeStocktakePhotoKeepAll': '保留相片及品項',
    'inventory.manualAdd.photoRequired': '請選擇此品項所屬的相片。',
    'inventory.manualAdd.photoPlaceholder': '選擇相片',
    'inventory.itemPhotoLabel': '所屬相片',
    'inventory.itemPhotoPickerAria': '選擇品項所屬相片',
    'task.review.inventoryItemPhotoAria': '品項相片',
    'inventory.wizard.saveAndNextLocation': '儲存並下一個位置',
    'inventory.wizard.continueToFinalReview': '前往最後審核',
    'inventory.wizard.finalReviewTitle': '最後審核',
    'inventory.wizard.finalReviewSubtitle': '送出前請核對摘要與完成時間；確認後將通知僱主審核。',
    'inventory.wizard.statLowStock': '低庫存品項',
    'inventory.wizard.statExpiringSoon': '即將到期',
    'inventory.wizard.statTotalCategories': '類別總數',
    'inventory.wizard.statTotalPhotos': '相片總數',
    'inventory.statDetailModal.lowStockTitle': '低庫存品項',
    'inventory.statDetailModal.expiringTitle': '即將到期品項 ({{days}}天內)',
    'inventory.statDetailModal.categoryLabel': '類別',
    'inventory.statDetailModal.locationLabel': '位置',
    'inventory.statDetailModal.expiryHeading': '到期狀態',
    'inventory.statDetailModal.empty': '目前沒有此類品項。',
    'inventory.statDetailModal.closeButton': '關閉',
    'inventory.statDetailModal.closeAria': '關閉詳情視窗',
    'inventory.statDetailModal.dismissBackdropAria': '關閉詳情（點擊背景）',
    'inventory.statDetailModal.openLowStockAria': '查看低庫存詳情，共 {{count}} 項',
    'inventory.statDetailModal.openExpiringAria': '查看即將到期詳情，共 {{count}} 項',
    'inventory.wizard.locationDetailsSection': '位置摘要',
    'inventory.wizard.submissionInfoSection': '提交資訊',
    'inventory.wizard.finalReviewDateLabel': '日期',
    'inventory.wizard.finalReviewCompletionTimeLabel': '完成時間',
    'inventory.wizard.readyToSubmitTitle': '確定要提交？',
    'inventory.wizard.readyToSubmitDisclaimer':
      '提交後會通知僱主審核，提交後將無法再修改本次存量。',
    'inventory.wizard.sendReport': '提交',
    'inventory.wizard.addItemManuallyTitle': '人手新增品項',
    'inventory.wizard.renameItemPrompt': '請輸入品項名稱',
    'inventory.wizard.renameItemAria': '編輯品項名稱',
    'inventory.wizard.removeManualItemTitle': '移除此品項？',
    'inventory.wizard.removeManualItemDescription': '將移除「{{name}}」及其在本位置的存量與到期資料。此動作無法還原。',
    'inventory.wizard.reviewLocationBadgeAria': '目前盤點位置：{{location}}',
    'inventory.wizard.editCategoryAria': '選擇或變更類別',
    'inventory.wizard.editAllLocations': '編輯全部位置',
    'inventory.wizard.locationItemsBadge': '品項',
    'inventory.wizard.reviewLocationStatItemsCaption': '品項',
    'inventory.wizard.reviewLocationStatEmptyCatCaption': '空類.',
    'inventory.wizard.reviewLocationStatEmptyCatCaptionFull': '無品項類別',
    'inventory.wizard.reviewLocationStatPhotosCaption': '相片',
    'inventory.wizard.previewLocationResults': 'Preview',
    'inventory.wizard.previewLocationResultsAria': '預覽位置 {{name}} 的家裏存貨結果',
    'inventory.wizard.previewLocationResultsCloseAria': '關閉家裏存貨結果預覽',
    'inventory.wizard.previewLocationResultsDismissBackdropAria': '關閉家裏存貨結果預覽（點擊背景）',
    'inventory.manualAdd.title': '人手新增品項',
    'inventory.manualAdd.sectionAria': '人手新增類別與品項',
    'inventory.manualAdd.expandToggleAria': '展開人手新增表單',
    'inventory.manualAdd.collapseToggleAria': '收合人手新增表單',
    'inventory.manualAdd.hint':
      '若主檔或相片辨識中找不到該品項，可在此輸入類別與名稱。若僱主人設定了類別，請由清單選擇相符的一項。',
    'inventory.manualAdd.saveToMasterHint':
      '此家庭已允許助理編輯「家中存貨」主檔：成功建立時會一併寫入主檔（網絡失敗時仍可先僅在本次送審內提交）。',
    'inventory.manualAdd.onlySubmissionHint':
      '若僱主尚未開放助理編輯主檔：此處新增的品項只會在本次送審資料中提交，預設不會新增到主檔列表。',
    'inventory.manualAdd.categoryLabel': '類別',
    'inventory.manualAdd.categoryPlaceholder': '請選擇類別',
    'inventory.manualAdd.categoryOtherLabel': '類別（請輸入）',
    'inventory.manualAdd.categoryOtherPlaceholder': '輸入類別名稱（最多 120 字）',
    'inventory.manualAdd.itemNameLabel': '品項名稱',
    'inventory.manualAdd.itemNamePlaceholder': '例如：洗衣液',
    'inventory.manualAdd.addButton': '加入品項',
    'inventory.manualAdd.adding': '加入中…',
    'inventory.manualAdd.validationBoth': '請填寫類別與品項名稱。',
    'inventory.manualAdd.validationCategory': '請從僱主為此任務設定的類別中選擇。',
    'inventory.manualAdd.stockRequired': '請選擇存量狀態（滿／半滿／低／空）。',
    'inventory.manualAdd.expiryChoiceRequired': '請選擇到期日為「無到期日」或「有有效期限」，並於需要時填寫日期。',
    'inventory.manualAdd.limitReached': '人手新增上限為 {{max}} 個品項。',
    'inventory.manualAdd.addFailed': '無法加入品項，請稍後再試。',
    'inventory.manualAdd.remove': '移除',
    'inventory.manualAdd.removeAria': '移除人手新增品項 {{name}}',
    'inventory.manualAdd.rowCategoryBadge': '類別：{{category}}',
    'inventory.manualAdd.rowCategoryEditLabel': '類別（可更改）',
    'inventory.expiry.sectionAria': '到期日',
    'inventory.expiry.sectionTitle': '到期日',
    'inventory.expiry.noExpiryLabel': '無到期日',
    'inventory.expiry.noExpiryAria': '「{{item}}」無到期日',
    'inventory.expiry.hasDatesLabel': '有有效期限（可新增多個日期）',
    'inventory.expiry.hasDatesAria': '「{{item}}」填寫有效期限',
    'inventory.expiry.datePickerAria': '「{{item}}」有效期限 {{n}}',
    'inventory.expiry.removeDate': '移除',
    'inventory.expiry.removeDateAria': '移除「{{item}}」有效期限 {{n}}',
    'inventory.expiry.addDate': '新增另一個到期日',
    'inventory.expiry.maxDatesHint': '每個品項最多 10 個到期日。',
    'inventory.expiry.required': '請為「{{item}}」填寫至少一個有效期限，或改選「無到期日」。',
    'inventory.expiry.chooseModeHint': '請選擇「無到期日」或「有有效期限」。',
    'inventory.expiry.choiceRequired': '請為「{{item}}」選擇到期日處理方式（無到期日或有有效期限）。',
    'inventory.reviewLinesHeading': '盤點品項',
    // Employer — 建立盤點任務
    'task.inventory.newTaskTitle': '建立家裏存貨',
    'task.inventory.editTaskTitle': '編輯家裏存貨',
    'task.inventory.loadError': '無法載入家裏存貨資料',
    'task.inventory.employerOnly': '僅僱主可使用此頁面',
    'task.inventory.chainHint':
      '家務助理須依日期順序完成盤點；較早日期的實例未通過審核前，無法開始較後日期的盤點。重複頻率於建立／編輯盤點任務時設定（非清單排程）。',
    'task.inventory.listScheduleMovedHint': '清單僅作為品項集合；重複排程請在建立盤點任務時設定。',
    'task.inventory.editListHint': '變更清單後，家務助理盤點頁與之後的送審會以新清單的品項為準。',
    'task.inventory.editListItemsPreview': '清單內品項',
    'task.inventory.editListEmpty': '此清單尚無品項',
    'task.inventory.editChecklistItemsHeading': '編輯清單內品項',
    'task.inventory.editChecklistItemsSub':
      '從家中庫存勾選要納入此清單的品項；顯示順序與庫存列表相同。請按「儲存清單品項」或一併儲存任務以寫入。',
    'task.inventory.editTaskOptionsHeading': '盤點任務內容',
    'task.inventory.editTaskSubmitHint':
      '僅改清單時可用上方綠色「儲存清單品項」。下方按鈕會儲存標題、描述、日期、通知，並將此任務綁定到目前所選清單。',
    'task.inventory.newTaskChecklistHint':
      '勾選要納入此清單之庫存品項；建立任務前若曾修改勾選，系統會先儲存清單再建立。',
    'task.inventory.sections.lists': '盤點清單',
    'task.inventory.createListCta': '新增清單',
    'task.inventory.noListsHint': '尚無盤點清單，請先新增清單並加入品項。',
    'task.inventory.createListTitle': '新增盤點清單',
    'task.inventory.listNameLabel': '清單名稱',
    'task.inventory.scheduleLabel': '排程',
    'task.inventory.schedule.none': '不設定週期（手動建立任務）',
    'task.inventory.schedule.weekly': '每週',
    'task.inventory.schedule.monthlyFirstWday': '每月第一個週內日',
    'task.inventory.weekdayLabel': '週內日',
    'task.inventory.createListError': '建立清單失敗',
    'task.inventory.createListSuccess': '已建立清單',
    'task.inventory.listNameRequired': '請輸入清單名稱',
    'task.inventory.assignItemsTitle': '此清單尚無品項',
    'task.inventory.assignItemsHint': '請從庫存品項勾選，或先新增一筆庫存品項後加入清單。',
    'task.inventory.quickAddItem': '品項名稱',
    'task.inventory.addItemCta': '加入庫存',
    'task.inventory.createItemError': '新增品項失敗',
    'task.inventory.createItemSuccess': '已新增庫存品項',
    'task.inventory.itemNameRequired': '請輸入品項名稱',
    'task.inventory.itemNameLabel': '品項名稱',
    'task.inventory.renameItemCta': '重新命名',
    'task.inventory.removeFromListCta': '從所有盤點中移除',
    'task.inventory.editListItemsModalCta': '編輯清單品項',
    'task.inventory.removeListCta': '刪除清單',
    'task.inventory.removeListConfirmTitle': '刪除此盤點清單？',
    'task.inventory.removeListConfirmBody':
      '刪除後，所有連結此清單且仍為生效中的盤點任務會改為已過期，並需要重新選擇盤點清單。',
    'task.inventory.removeListAffectedTasksCount': '目前受影響任務數：{{count}}',
    'task.inventory.removeListAffectedTasksLabel': '受影響的盤點任務：',
    'task.inventory.removeListConfirmCta': '確認刪除清單',
    'task.inventory.removeListSuccess': '已刪除盤點清單',
    'task.inventory.removeListError': '刪除盤點清單失敗',
    'task.inventory.updateListSuccess': '已更新清單名稱',
    'task.inventory.updateListError': '更新清單名稱失敗',
    'task.inventory.renameListModalTitle': '修改盤點清單名稱',
    'task.inventory.renameListModalOpenCta': '修改盤點清單名稱',
    'task.inventory.addedButNotSelected': '已加入庫存，但已達清單上限，未自動選取。',
    'task.inventory.createListUseHint': '建立清單後，請先儲存清單品項，系統會使用此清單。',
    'task.inventory.removeFromListConfirmTitle': '從所有盤點中移除此品項？',
    'task.inventory.removeFromListConfirmBody': '確認刪除？確認後將無法還原。',
    'task.inventory.removeFromListConfirmCta': '從所有盤點中移除',
    'task.inventory.renameItemModalTitle': '重新命名品項',
    'task.inventory.renameItemSave': '儲存',
    'task.inventory.updateItemError': '更新品項失敗',
    'task.inventory.maxItemsNote': '已選 {{count}} 筆品項',
    'task.inventory.saveListItemsCta': '儲存清單品項',
    'task.inventory.saveListItemsError': '儲存品項失敗',
    'task.inventory.saveListItemsSuccess': '已儲存清單品項',
    'task.inventory.pickAtLeastOneItem': '請至少選擇一筆品項',
    'task.inventory.formHeading': '建立家裏存貨工作',
    'task.inventory.wizard.stepOf': '步驟 {{current}} / {{total}}',
    'task.inventory.wizard.step1Title': '標題與描述',
    'task.inventory.wizard.step2Title': '家裏存貨位置',
    'task.inventory.wizard.step3Title': '家裏存貨類別',
    'task.inventory.wizard.step4Title': '重複與日期',
    'task.inventory.wizard.step5Title': '通知設定',
    'task.inventory.wizard.step6Title': '清單與品項',
    'task.inventory.wizard.step7Title': '確認並建立',
    'task.inventory.wizard.next': '下一步',
    'task.inventory.wizard.back': '上一步',
    'task.inventory.wizard.reviewConfirm': '確認建立',
    'task.inventory.wizard.reviewSave': '確認儲存',
    'task.inventory.wizard.categoriesHint': '新增至少一個類別（可參考下方建議）。',
    'task.inventory.wizard.locationsHint': '新增至少一個位置（可參考下方建議）。',
    'task.inventory.wizard.suggestHeading': '建議',
    'task.inventory.suggest.location.kitchen': '廚房',
    'task.inventory.suggest.location.fridge': '雪櫃',
    'task.inventory.suggest.location.storeRoom': '雜物房',
    'task.inventory.suggest.location.livingRoom': '客廳',
    'task.inventory.suggest.location.bathroomA': '洗手間 A',
    'task.inventory.suggest.location.bathroomB': '洗手間 B',
    'task.inventory.suggest.category.food': '食物',
    'task.inventory.suggest.category.drinks': '飲品',
    'task.inventory.suggest.category.babyFood': '兒童食物',
    'task.inventory.suggest.category.babyDrinks': '兒童飲品',
    'task.inventory.suggest.category.elderlyFood': '長者食物',
    'task.inventory.suggest.category.petFood': '寵物糧食',
    'task.inventory.suggest.category.dryGoods': '乾貨',
    'task.inventory.suggest.category.chilledGoods': '濕貨',
    'task.inventory.wizard.addCategoryPlaceholder': '輸入類別後加入',
    'task.inventory.wizard.addLocationPlaceholder': '輸入位置後加入',
    'task.inventory.wizard.addChip': '加入',
    'task.inventory.wizard.removeChipAria': '移除 {{label}}',
    'task.inventory.wizard.expiryLeadsLabel': '存貨到期前提早通知（天數）',
    'task.inventory.wizard.expiryLeadsHint': '可複選；若不需要到期提醒可略過。',
    'task.inventory.wizard.titleRequired': '請填寫任務標題',
    'task.inventory.wizard.categoriesRequired': '請至少新增一個家裏存貨類別',
    'task.inventory.wizard.locationsRequired': '請至少新增一個家裏存貨位置',
    'task.inventory.wizard.summaryTitle': '標題',
    'task.inventory.wizard.summaryDesc': '描述',
    'task.inventory.wizard.summaryCategories': '類別',
    'task.inventory.wizard.summaryLocations': '位置',
    'task.inventory.wizard.summaryList': '清單',
    'task.inventory.wizard.summaryRecurrence': '重複與日期',
    'task.inventory.wizard.summaryDueTime': '截止時間',
    'task.inventory.wizard.summaryNotify': '完成時通知僱主',
    'task.inventory.wizard.summaryNotifyOn': '是',
    'task.inventory.wizard.summaryNotifyOff': '否',
    'task.inventory.wizard.summaryExpiryLeads': '到期提醒（天）',
    'task.inventory.wizard.summaryNone': '（無）',
    'task.inventory.wizard.reviewCardHeading': '家裏存貨任務',
    'task.inventory.selectListLabel': '盤點清單',
    'task.inventory.selectListPlaceholder': '請選擇',
    'task.inventory.selectListRequired': '請選擇盤點清單',
    'task.inventory.listHasNoItems': '此清單尚無品項，請先完成上方「品項」設定',
    'task.inventory.titleOptional': '留空則使用預設標題',
    'task.inventory.titlePlaceholder': '例：本週廚房家裏存貨',
    'task.inventory.submitCreateTask': '建立家裏存貨任務',
    'task.inventory.createTaskError': '建立家裏存貨任務失敗',
    'task.inventory.createTaskSuccess': '已建立家裏存貨任務',
    'task.inventory.dueDateInvalid': '請選擇有效到期日',
    // Task Completion (Helper)
    'task.complete.title': '完成任務',
    'task.complete.subtitle': '提交任務完成證明',
    'task.resubmit.photoHint': '可刪除錯誤照片後重新拍攝或選擇圖片。',
    'task.complete.photoSection': '照片 (1-3張)',
    'task.complete.submittedPhotosOnly': '已提交的相片',
    'task.complete.photoHint': '點擊拍照或從相簿選取',
    'task.complete.compressingPhotos': '正在壓縮相片…',
    'task.complete.takePhoto': '拍照',
    'task.complete.choosePhoto': '相簿',
    'task.complete.photoAlt': '任務相片 {{n}}',
    'task.complete.enlargePhotoAria': '放大檢視任務相片',
    'task.complete.removePhoto': '移除照片',
    'task.complete.gpsSection': 'GPS 定位',
    'task.complete.gpsOn': '已啟用',
    'task.complete.gpsOff': '已關閉',
    'task.complete.gpsHint': '開啟後將記錄您的位置',
    'task.complete.noteSection': '備註',
    'task.complete.notePlaceholder': '輸入備註 (選填)...',
    'task.complete.completedAtLabel': '完成時間',
    'task.complete.completedAtRequired': '請輸入完成日期與時間',
    'task.complete.submit': '提交',
    'task.complete.submitting': '提交中...',
    'task.complete.submitSuccess': '任務提交成功',
    'task.complete.submitError': '提交失敗',
    'task.complete.photoRequired': '請至少上傳 1 張照片',
    'task.complete.photoUploadError':
      '無法上傳相片至雲端（請檢查網絡、稍後重試，並確認瀏覽器允許上傳；GCS 貯存桶 CORS 需允許此來源）',
    'task.complete.offlineSaved': '已離線儲存，連線後將自動上傳',
    // Task Resubmit (Helper)
    'task.resubmit.title': '重新提交',
    'task.resubmit.subtitle': '根據僱主的要求修改並重新提交',
    'task.resubmit.previousNote': '上次備註',
    'task.resubmit.submit': '重新提交',
    'task.resubmit.submitSuccess': '重新提交成功',
    // Common Actions
    'task.action.back': '返回',
    'task.action.refresh': '重新整理',
    'task.action.delete': '刪除',
    'task.action.archive': '封存',
    'task.action.archiveConfirm': '確定要封存此任務嗎？',
    'task.action.archiveSuccess': '任務已封存',
    'task.action.archiveError': '封存失敗',
    // Admin Portal
    'admin.brand': 'MaidEasy 管理後台',
    'admin.sidebarAria': '管理後台導覽',
    'admin.openMenu': '開啟選單',
    'admin.closeMenu': '關閉選單',
    'admin.languageSwitcherAria': '介面語言',
    'admin.languageChinese': '繁體中文',
    'admin.languageEnglish': '英文',
    'admin.loginTitle': '管理員登入',
    'admin.loginSubtitle': '此區與一般用戶帳號完全分開',
    'admin.email': '電郵',
    'admin.password': '密碼',
    'admin.totp': '雙因素驗證碼（6 位）',
    'admin.totpHint': '帳戶啟用電郵 2FA 時，登入後會要求驗證碼',
    'admin.otpModalTitle': '電郵驗證碼',
    'admin.otpModalHint': '我們已向你的管理員電郵發送 6 位驗證碼（約 5 分鐘有效）。',
    'admin.otpCode': '驗證碼',
    'admin.otpSentHint': '驗證碼已發送。請查收電郵。',
    'admin.otpResentHint': '已重新發送驗證碼。',
    'admin.otpRequired': '請輸入驗證碼。',
    'admin.verifyOtp': '驗證並登入',
    'admin.resendOtp': '重發驗證碼',
    'admin.signIn': '登入',
    'admin.signingIn': '登入中…',
    'admin.logout': '登出',
    'admin.idleWarningTitle': '即將因閒置而登出',
    'admin.idleWarningDescription':
      '您已約 28 分鐘沒有操作。請選擇繼續使用以延長工作階段，否則約 2 分鐘後將自動登出。',
    'admin.idleExtendSession': '繼續使用',
    'admin.idleLogoutNow': '立即登出',
    'admin.nav.dashboard': '儀表板',
    'admin.nav.users': '用戶',
    'admin.nav.settings': '系統設定',
    'admin.nav.invitationCodes': '邀請碼',
    'admin.nav.systemMessages': '系統訊息',
    'admin.nav.audit': '稽核紀錄',
    'admin.nav.admins': '管理員帳號',
    'admin.nav.faqs': 'FAQ 管理',
    'admin.nav.legalDocuments': '法律文件',
    'admin.nav.backHome': '返回主站',
    'admin.systemMessage.title': '系統訊息',
    'admin.systemMessage.subtitle': '僅 super-admin 可建立廣播系統訊息（儲存時自動翻譯五語）。',
    'admin.systemMessage.createTitle': '新增系統訊息',
    'admin.systemMessage.editTitle': '編輯系統訊息',
    'admin.systemMessage.subjectZhHant': '主旨（繁中）',
    'admin.systemMessage.subjectEn': '主旨（英文）',
    'admin.systemMessage.detailZhHant': '詳情 HTML（繁中）',
    'admin.systemMessage.detailEn': '詳情 HTML（英文）',
    'admin.systemMessage.displayFrom': '顯示開始（HKT，YYYY-MM-DD hh:mm，可留空）',
    'admin.systemMessage.displayTo': '顯示結束（HKT，YYYY-MM-DD hh:mm，可留空）',
    'admin.systemMessage.tickerBgColor': 'Ticker 底色',
    'admin.systemMessage.active': '啟用',
    'admin.systemMessage.showOnHome': '首頁 ticker 顯示',
    'admin.systemMessage.showOnHomeHint':
      '取消勾選後仍可在系統訊息列表看到；僅不上首頁輪播。',
    'admin.systemMessage.showOnEmployerDashboard': '顯示於僱主 Dashboard',
    'admin.systemMessage.showOnHelperHome': '顯示於家務助理 Home',
    'admin.systemMessage.roles': '角色（空白＝全部）',
    'admin.systemMessage.levels': '會員等級（空白＝全部；家務助理跟僱主）',
    'admin.systemMessage.blankMeansAll': '空白＝不限',
    'admin.systemMessage.matchMode': '角色與等級組合',
    'admin.systemMessage.channels': '通知通道（新建時套用；編輯不重發）',
    'admin.systemMessage.editNoResend': '編輯不會重新發送通知；延遲發送會使用最新內容。',
    'admin.systemMessage.saveCreate': '儲存並發送',
    'admin.systemMessage.saveEdit': '儲存變更',
    'admin.systemMessage.cancelEdit': '取消編輯',
    'admin.systemMessage.listTitle': '訊息列表',
    'admin.systemMessage.listEmpty': '尚未建立系統訊息。',
    'admin.systemMessage.colSubject': '主旨',
    'admin.systemMessage.colActive': '啟用',
    'admin.systemMessage.colNotify': '通知狀態',
    'admin.systemMessage.colFrom': '顯示開始',
    'admin.systemMessage.edit': '編輯',
    'admin.systemMessage.loadError': '無法載入系統訊息',
    'admin.systemMessage.saveError': '儲存失敗',
    'admin.systemMessage.estimateError': '無法預估收件人數',
    'admin.systemMessage.subjectRequired': '請至少填寫繁中或英文主旨',
    'admin.systemMessage.confirmTitle': '確認發送系統訊息？',
    'admin.systemMessage.confirmTotal': '符合條件用戶：{{count}}',
    'admin.systemMessage.confirmInApp': 'In-app 將發送：{{count}}',
    'admin.systemMessage.confirmPush': 'Web push 可送達：{{count}}',
    'admin.systemMessage.confirmEmail': 'Email 可送達：{{count}}',
    'admin.systemMessage.confirmWhen': '通知時間（HKT）：{{when}}',
    'admin.systemMessage.confirmWhenNone': '不發送通知',
    'admin.systemMessage.confirmSend': '確認儲存並發送',
    'admin.systemMessage.linkPrompt': '輸入 https 連結',
    'admin.systemMessage.editorBold': '粗體',
    'admin.systemMessage.editorItalic': '斜體',
    'admin.systemMessage.editorUl': '項目清單',
    'admin.systemMessage.editorOl': '編號清單',
    'admin.systemMessage.editorLink': '連結',
    'admin.systemMessage.editorImage': '插入圖片',
    'admin.systemMessage.editorImageLink': '文字點擊後顯示圖片',
    'admin.systemMessage.editorModeVisual': 'Visual',
    'admin.systemMessage.editorModeHtml': 'HTML',
    'admin.systemMessage.imageUrlPrompt': '輸入圖片 https 網址',
    'admin.systemMessage.imageUrlInvalid': '圖片網址必須是 https://',
    'admin.systemMessage.imageMaxWidthPrompt': 'max-width（可留空，例如 100%、20rem、320px）',
    'admin.systemMessage.imageMaxHeightPrompt': 'max-height（可留空，例如 50vh、12rem）',
    'admin.systemMessage.imageLinkTextPrompt': '顯示文字（點擊後放大圖片）',
    'admin.systemMessage.imageClickUrlPrompt': '點擊連結（可留空＝放大圖片；https 或站內路徑如 /settings）',
    'admin.systemMessage.imageClickUrlInvalid': '點擊連結必須是 https:// 或站內路徑（以 / 開頭）',
    'admin.settingsTab.maintenance': '系統維護',
    'admin.maintenance.subtitle': '排程與維護旗標（僅 super-admin）。時間為香港時區。',
    'maintenance.title': '系統維護中',
    'maintenance.defaultMessage': '系統正進行維護，暫時無法使用。請稍後再試。',
    'admin.dashboardSubtitle': '總覽與今日活動指標',
    'admin.stat.totalUsers': '用戶總數',
    'admin.stat.employers': '僱主數',
    'admin.stat.helpers': '家務助理數',
    'admin.stat.pairings': '配對總數',
    'admin.stat.activePairings': '有效配對',
    'admin.stat.todayActive': '今日活躍用戶（登入）',
    'admin.stat.todayNew': '今日新註冊',
    'admin.stat.todayTasks': '今日任務實例（到期日）',
    'admin.stat.todayReceipts': '今日收據提交',
    'admin.usersSubtitle': '搜尋帳戶並開啟帳戶詳情',
    'admin.usersEmailFilter': '依電郵篩選',
    'admin.usersRoleFilter': '角色',
    'admin.usersPairedFilter': '配對狀態',
    'admin.usersLevelFilter': '會員等級',
    'admin.usersRemovalRequestedFilter': '帳戶已刪除',
    'admin.filter.roleAll': '全部角色',
    'admin.filter.roleEmployer': '僱主',
    'admin.filter.roleHelper': '家務助理',
    'admin.filter.pairedAll': '全部',
    'admin.filter.pairedYes': '已配對',
    'admin.filter.pairedNo': '未配對',
    'admin.filter.levelAll': '所有等級',
    'admin.filter.level1': 'L1（試用）',
    'admin.filter.level2': 'L2（免費）',
    'admin.filter.level3': 'L3（高級）',
    'admin.filter.removalRequestedAll': '帳戶已刪除：全部',
    'admin.filter.removalRequestedYes': '帳戶已刪除：是',
    'admin.filter.removalRequestedNo': '帳戶已刪除：否',
    'admin.summary.accountRemovalRequested': '帳戶已刪除',
    'admin.summary.accountRemovalRequestedAt': '刪除日期時間',
    'admin.usersColNickname': '暱稱',
    'admin.usersColRole': '角色',
    'admin.usersColLevel': '等級',
    'admin.usersColPaired': '已配對',
    'admin.usersColActions': '操作',
    'admin.usersViewDetail': '帳戶詳情',
    'admin.usersNewPasswordPlaceholder': '新密碼',
    'admin.usersConfirmNewPasswordPlaceholder': '確認新密碼',
    'admin.createConfirmPasswordPlaceholder': '確認密碼',
    'admin.faqsSubtitle': '管理前台 FAQ（繁中/簡中/英文），可設定身份、排序與啟用狀態。',
    'admin.faqCreateTitle': '新增 FAQ',
    'admin.faqEditTitle': '編輯 FAQ',
    'admin.faqFormIncomplete': '請完整填寫三種語言的問題與答案。',
    'admin.faqSortOrder': '排序（數字愈小愈前）',
    'admin.faqIsActive': '啟用此 FAQ',
    'admin.faqQuestionZhHant': '問題（繁中）',
    'admin.faqAnswerZhHant': '答案（繁中）',
    'admin.faqQuestionZhHans': '問題（簡中）',
    'admin.faqAnswerZhHans': '答案（簡中）',
    'admin.faqQuestionEn': 'Question (English)',
    'admin.faqAnswerEn': 'Answer (English)',
    'admin.faqShowInactive': '顯示停用 FAQ',
    'admin.legalDocumentsSubtitle':
      '管理 Privacy Policy / Terms of Service / PICS / Subscription Terms 的英／繁／簡內容與版本。',
    'admin.legalCreateTitle': '新增法律文件版本',
    'admin.legalEditTitle': '編輯法律文件',
    'admin.legalFormIncomplete': '請填寫版本、標題與英文內容。',
    'admin.legalVersion': '版本（例如 v1、v2）',
    'admin.legalTitle': '標題',
    'admin.legalContent': '內容（可使用 Markdown 標題）',
    'admin.legalEffectiveFrom': '生效時間（HKT，YYYY-MM-DD hh:mm）',
    'admin.legalIsActive': '啟用此版本（同類文件只會有一份 active）',
    'admin.legalShowInactive': '顯示非 active 版本',
    'admin.legalType.privacy': 'Privacy Policy',
    'admin.legalType.terms': 'Terms of Service',
    'admin.legalType.pics': 'PICS',
    'admin.legalType.subscription_terms': 'Subscription Terms',
    'admin.legalSubscriptionTermsPlaceholderHint':
      'Subscription Terms may use placeholders: {{subscribeButton}}, {{resubscribeButton}}, {{startTrialButton}} (replaced by app button labels at display time). Shown only in the subscription page modal.',
    'admin.legalLocale.en': 'English',
    'admin.legalLocale.zh-Hant': '繁體中文',
    'admin.legalLocale.zh-Hans': '簡體中文',
    'admin.active': '啟用',
    'admin.inactive': '停用',
    'admin.edit': '編輯',
    'admin.remove': '刪除',
    'admin.create': '新增',
    'admin.update': '更新',
    'admin.cancel': '取消',
    'admin.usersResetPassword': '重設密碼',
    'admin.resetPasswordDialogTitle': '確認重設密碼？',
    'admin.resetPasswordDialogBody': '將為 {{email}} 設定新密碼。此操作無法還原。',
    'admin.resetPasswordSuccess': '已成功重設密碼。',
    'admin.resetPasswordLocalOnlyHint': '僅適用於電郵註冊（local）帳戶。',
    'admin.usersPageInfo': '第 {{page}} 頁 · 共 {{total}} 筆',
    'admin.prevPage': '上一頁',
    'admin.nextPage': '下一頁',
    'admin.yes': '是',
    'admin.no': '否',
    'admin.usersBack': '返回用戶列表',
    'admin.userDetailTitle': '用戶帳戶',
    'admin.summary.level': '會員等級',
    'admin.summary.authProvider': '登入方式',
    'admin.summary.trialEndsAt': '試用會員到期',
    'admin.summary.invitationLevelEndsAt': 'L3 試用到期',
    'admin.summary.subscriptionEndsAt': '付費訂閱到期',
    'admin.downgradeToL2': '降級至 L2（免費）',
    'admin.downgradeToL2Confirm': '確定要將此僱主由 L3 即時降級至 L2？將封存高階任務並套用 L2 限制。',
    'admin.downgradeToL2Success': '已降級至 L2。',
    'admin.downgradeToL2Error': '降級失敗。',
    'admin.userSummarySection': '資料概覽（唯讀）',
    'admin.summary.tasks': '任務總數',
    'admin.summary.tasksStandard': '任務 — 一般',
    'admin.summary.tasksInventory': '任務 — 家居庫存',
    'admin.summary.tasksPickup': '任務 — 接送',
    'admin.summary.tasksMedication': '任務 — 食藥提醒',
    'admin.summary.invitationCode': '邀請碼',
    'admin.summary.taskInstances': '任務實例數',
    'admin.summary.receipts': '收據數',
    'admin.summary.budgets': '已給的錢數',
    'admin.summary.settlements': '結算數',
    'admin.summary.family': '家庭成員數',
    'admin.loginInfoSection': '登入資料',
    'admin.summary.registeredAt': '註冊日期時間',
    'admin.summary.registeredIp': '註冊 IP',
    'admin.summary.lastLoginAt': '最後登入日期時間',
    'admin.summary.lastLoginIp': '最後登入 IP',
    'admin.summary.lastLoginDevice': '最後登入裝置',
    'admin.summary.usageRegion': '使用地區',
    'admin.paymentSection': '付款資料',
    'admin.summary.stripeCustomer': 'Stripe Customer',
    'admin.summary.stripeCustomerLink': '開啟 Stripe Customer',
    'admin.paymentEmpty': '沒有 Stripe 發票紀錄。',
    'admin.paymentColPaidAt': '付款日期時間',
    'admin.paymentColPeriod': '訂閱日期時間範圍',
    'admin.paymentColAmount': '金額',
    'admin.paymentColStatus': '狀態',
    'admin.removeAccountTitle': '刪除帳戶',
    'admin.removeAccountHint':
      '會先解除配對、封存帳戶、終止 Stripe 訂閱（如有），並更新邀請碼使用紀錄以釋放電郵。業務資料會保留，但對方不可見歷史。',
    'admin.removeAccountPassword': '請輸入管理員密碼確認',
    'admin.removeAccountReason': '原因（1–500 字）',
    'admin.removeAccountPasswordRequired': '必須輸入管理員密碼。',
    'admin.removeAccountReasonInvalid': '原因須為 1–500 字。',
    'admin.removeAccountButton': '刪除帳戶',
    'admin.removeAccountDialogTitle': '確認刪除此用戶帳戶？',
    'admin.removeAccountDialogBody':
      '將封存 {{email}}、解除配對並禁止登入。業務資料會保留，但對方不可見歷史。',
    'admin.removeAccountError': '刪除帳戶失敗。',
    'admin.pairingSection': '配對',
    'admin.pairingEmpty': '無配對紀錄',
    'admin.pairingStatus': '狀態',
    'admin.pairingRole': '角色',
    'admin.settingsSubtitle': '會員限制、註冊、訂閱與宣傳站設定',
    'admin.settingsTab.membership': '會員等級限制',
    'admin.settingsTab.register': '註冊與其他',
    'admin.settingsTab.subscription': '訂閱',
    'admin.settingsTab.marketing': '宣傳頁',
    'admin.set.maxDailyTasks': '每日任務上限',
    'admin.set.maxDailyReceipts': '每日收據上限',
    'admin.set.maxDailyAI': '每日 AI 分析上限',
    'admin.set.maxComments': '每目標留言上限',
    'admin.set.maxCommentLen': '留言字數上限',
    'admin.set.photoRetention': '相片／收據保留天數',
    'admin.set.consentVersion': '目前同意書版本',
    'admin.set.auditYears': '稽核紀錄保留年數',
    'admin.set.invitationCodeRequired': '僱主註冊時必須輸入邀請碼',
    'admin.set.invitationCodeRequiredHint': '關閉時僱主仍可選填邀請碼；開啟時留空將無法繼續註冊。',
    'admin.set.playStoreShowOfficialWebsiteUrl': '顯示 Play 版官方網站付款提示',
    'admin.set.playStoreShowOfficialWebsiteUrlHint':
      '開啟後，Google Play App 的訂閱頁、用量頁與用量上限視窗會提示改用瀏覽器開啟 MaidEasy 官方網站（含網址）。預設關閉。不會在 Play App 內開放付款。',
    'admin.invitationCodesTitle': '邀請碼管理',
    'admin.invitationCodesSubtitle': '建立與管理僱主註冊邀請碼，並檢視使用紀錄。',
    'admin.invitationCodesTabCodes': '邀請碼列表',
    'admin.invitationCodesTabUsages': '使用紀錄',
    'admin.invitationCodesFilterCode': '搜尋邀請碼',
    'admin.invitationCodesFilterStatus': '狀態篩選',
    'admin.invitationCodesStatusAll': '全部',
    'admin.invitationCodesStatusActive': '啟用',
    'admin.invitationCodesStatusDisabled': '停用',
    'admin.invitationCodesSearch': '搜尋',
    'admin.invitationCodesColCode': '邀請碼',
    'admin.invitationCodesColLevel': '等級',
    'admin.invitationCodesColUses': '使用次數',
    'admin.invitationCodesColDates': '有效期',
    'admin.invitationCodesColStatus': '狀態',
    'admin.invitationCodesColEmail': '電郵',
    'admin.invitationCodesColNickname': '暱稱',
    'admin.invitationCodesColUsedAt': '註冊時間',
    'admin.invitationCodesEdit': '編輯',
    'admin.invitationCodesEmpty': '暫無邀請碼',
    'admin.invitationCodesUsagesEmpty': '暫無使用紀錄',
    'admin.invitationCodesCreateTitle': '新增邀請碼',
    'admin.invitationCodesEditTitle': '編輯邀請碼',
    'admin.invitationCodesAutoGenerate': '自動產生邀請碼',
    'admin.invitationCodesTimezone': '時區',
    'admin.invitationCodesImmediate': '立即生效',
    'admin.invitationCodesPermanent': '永久有效',
    'admin.invitationCodesUnlimited': '無上限',
    'admin.invitationCodesStartDate': '開始日期（依時區）',
    'admin.invitationCodesEndDate': '結束日期（依時區）',
    'admin.invitationCodesMaxUses': '使用次數上限',
    'admin.invitationCodesUsageStart': '使用紀錄開始日期（HKT）',
    'admin.invitationCodesUsageEnd': '使用紀錄結束日期（HKT）',
    'admin.invitationCodesSave': '儲存',
    'admin.invitationCodesCancel': '取消',
    'admin.invitationCodesFilterRemark': '搜尋備註',
    'admin.invitationCodesColCodeType': '碼類型',
    'admin.invitationCodesColDuration': '日數',
    'admin.invitationCodesColRemark': '內部備註',
    'admin.invitationCodesColUsageContext': '兌換情境',
    'admin.invitationCodesTypeRegistrationGrant': '註冊授予',
    'admin.invitationCodesTypeL1TrialExtend': 'L1 試用延長',
    'admin.invitationCodesTypeL3TrialPeriod': 'L3 試用期',
    'admin.invitationCodesRemarkPlaceholder': '內部備註（選填，最多 500 字）',
    'admin.invitationCodesColReferrer': '推薦人',
    'admin.invitationCodesReferrerHint': '可選：搜尋並綁定一名現有用戶為推薦人（一碼一人、一人一碼）。',
    'admin.invitationCodesReferrerSearchPlaceholder': '以電郵搜尋用戶',
    'admin.invitationCodesReferrerClear': '解除綁定',
    'admin.invitationCodesColLevelGranted': '授予等級',
    'admin.invitationCodesColCurrentLevel': '現時等級',
    'admin.invitationCodesColFirstPayment': '首次付款（HKT）',
    'admin.invitationCodesDetail': '詳細',
    'admin.saveSettings': '儲存設定',
    'admin.saving': '儲存中…',
    'admin.auditSubtitle': '依條件篩選系統與用戶稽核紀錄',
    'admin.auditUserId': '用戶電郵',
    'admin.auditAction': '動作',
    'admin.auditTarget': '目標類型',
    'admin.auditStart': '開始日期（HKT）',
    'admin.auditEnd': '結束日期（HKT）',
    'admin.auditColTime': '時間',
    'admin.auditColAction': '動作',
    'admin.auditColUser': '用戶',
    'admin.adminsSubtitle': '僅 super_admin',
    'admin.adminsCreate': '新增管理員',
    'admin.adminsCreateSubmit': '建立',
    'admin.adminsCreateSuccess': '管理員已建立。',
    'admin.adminsList': '管理員列表',
    'admin.adminsForbidden': '需要超級管理員權限',
    'admin.adminsCol2fa': '電郵 2FA',
    'admin.adminsColStatus': '狀態',
    'admin.adminsColActions': '操作',
    'admin.adminsOpenDetail': '詳細',
    'admin.adminsDetailTitle': '管理員詳細',
    'admin.adminsCreatedAt': '建立時間',
    'admin.adminsCreatedBy': '建立者',
    'admin.adminsEnableEmail2fa': '啟用電郵雙因素驗證',
    'admin.adminsEnable2faHint': '啟用後於下次登入才需輸入電郵驗證碼。',
    'admin.adminsCannotDeactivateSelf': '不可將自己設為停用。',
    'admin.adminsUpdateSuccess': '管理員已更新。',
    'admin.adminsResetPasswordTitle': '重設密碼',
    'admin.adminsResetPasswordHint':
      '請輸入目前登入管理員（操作者）的密碼以確認。重設後對方須重新登入。',
    'admin.adminsOperatorPassword': '操作者密碼',
    'admin.adminsResetPasswordSuccess': '管理員密碼已重設；對方須重新登入。',
    'admin.adminsActivityTitle': '最近活動（3 個月）',
    'admin.adminsActivityHint': '含登入成功／失敗及其他相關稽核紀錄。',
    'admin.adminsActivityEmpty': '無活動紀錄',
    'admin.summary.emailVerified': '電郵已驗證',
    'admin.pairingPartner': '對方',
    'admin.pairingPartnerDetail': '對方帳戶詳細',
    'admin.sectionExpand': '展開',
    'admin.sectionCollapse': '收合',
  },
  [LANGUAGE.ZH_HANS]: {
    ...USAGE_REGION_UI_MESSAGES_ZH_HANS,
    'common.loading': '加载中…',
    'common.datePlaceholder': '选择日期',
    'common.openCalendarAria': '打开日历选择日期',
    'common.cancel': '取消',
    'common.close': '关闭',
    'common.closeViewer': '关闭预览',
    'common.imageViewer.zoomHint': '双指捏合可放大／缩小；双击还原；亦可使用下方 + −',
    'common.imageViewer.zoomInAria': '放大照片',
    'common.imageViewer.zoomOutAria': '缩小照片',
    'common.imageViewer.resetZoomAria': '还原缩放',
    'common.imageViewer.scalePercent': '{{percent}}%',
    'common.confirm': '确定',
    'common.next': '下一步',
    'common.back': '上一步',
    'common.save': '保存',
    'common.appTagline': '雇主与家务助理的家务管理平台',
    'common.languageSwitcherAria': '界面语言',
    'notFound.title': '找不到页面',
    'notFound.body': '您要前往的页面不存在或已被移除。',
    'language.zhHant': '繁体中文',
    'language.zhHans': '简体中文',
    'language.en': '英文',
    'language.id': '印尼文',
    'language.fil': '菲律宾文',
    'legal.contentEnglishNotice': '此页内容以英文显示。',
    'login.title': '登录',
    'login.subtitle': '使用电子邮箱与密码登录账户',
    'auth.errorGeneric': '发生错误，请重试',
    'auth.emailLabel': '电子邮箱',
    'auth.passwordLabel': '密码',
    'auth.rememberMe': '记住我',
    'auth.forgotPasswordLink': '忘记密码？',
    'auth.signIn': '登录',
    'auth.signInWithGoogle': '使用 Google 登录',
    'auth.signingIn': '登录中…',
    'auth.linkRegister': '还没有账户？注册',
    'auth.linkLogin': '已有账户？登录',
    'auth.registerTitle': '创建账户',
    'auth.registerSubtitle': '选择身份并填写资料',
    'auth.roleLabel': '身份',
    'auth.roleRequired': '请先选择身份。',
    'auth.passwordRulesHint': '8–16 个字符，需含大写、小写字母与数字',
    'auth.nicknameLabel': '称呼（昵称）',
    'auth.pairingCodeOptional': '配对码（选填）',
    'auth.pairingCodePlaceholder': '6 位字符',
    'auth.pairingCodeHint':
      '若有雇主提供的配对码可在此输入；亦可注册后再到「输入配对码」页面完成配对。',
    'auth.invitationCodeLabel': '邀请码',
    'auth.invitationCodeOptional': '邀请码（选填）',
    'auth.invitationCodePlaceholder': '8 位字符',
    'auth.invitationCodeHint': '如有邀请码请输入',
    'auth.invitationCodeRequired': '请输入邀请码',
    'auth.invitationCodeValid': '邀请码可用',
    'auth.invitationCodeBenefitL1Extend': '延长试用 {{days}} 日',
    'auth.invitationCodeBenefitL3Trial': '高级会员试用 {{days}} 日',
    'auth.invitationCodeInvalid': '邀请码无效',
    'auth.invitationCodeErrorFormat': '邀请码格式错误',
    'auth.invitationCodeErrorNotYetActive': '邀请码尚未生效',
    'auth.invitationCodeErrorExpired': '邀请码已过期',
    'auth.invitationCodeErrorDisabled': '邀请码已停用',
    'auth.invitationCodeErrorMaxUses': '邀请码已达使用上限',
    'auth.invitationCodeErrorAfterRegistrationOnly': '此邀请码仅可于注册后兑换',
    'auth.invitationCodeDeepLinkInvalid': '此邀请链接无效或已失效，请输入其他有效邀请码（如有）。',
    'auth.invitationCodeLockedHint': '此邀请码来自邀请链接，注册时不可修改。',
    'inviteReferrer.menuItem': '邀请码使用情况',
    'inviteReferrer.pageTitle': '邀请码使用情况',
    'inviteReferrer.pageSubtitle': '查看有多少人以你的邀请码注册或兑换，以及其中多少人已成为付款用户。',
    'inviteReferrer.statsLabel': '邀请码统计',
    'inviteReferrer.code': '邀请码',
    'inviteReferrer.benefit': '邀请码效果',
    'inviteReferrer.benefitL1Extend': '延长试用 {{days}} 日',
    'inviteReferrer.benefitL3Trial': '高级会员试用 {{days}} 日',
    'inviteReferrer.maxUses': '使用上限',
    'inviteReferrer.maxUsesLimited': '{{used}} / {{max}}',
    'inviteReferrer.maxUsesUnlimited': '{{used}} / 无上限',
    'inviteReferrer.validity': '使用期限',
    'inviteReferrer.validityRange': '{{start}} — {{end}}',
    'inviteReferrer.validityImmediate': '立即生效',
    'inviteReferrer.validityPermanent': '永久有效',
    'inviteReferrer.totalUses': '使用人数',
    'inviteReferrer.paidUsers': '付款用户',
    'inviteReferrer.shareLink': '分享链接',
    'inviteReferrer.copyLink': '复制链接',
    'inviteReferrer.copied': '已复制',
    'inviteReferrer.notBound': '你尚未被绑定为邀请码推荐人。',
    'auth.registerCta': '注册',
    'auth.registerWithGoogle': '使用 Google 注册',
    'auth.registering': '注册中…',
    'auth.registerRoleConfirmTitle': '确认注册身份',
    'auth.registerRoleConfirmDescription':
      '你目前选择的身份是「{{role}}」。请确认身份正确；注册完成后身份不可更改。',
    'auth.registerPicsScrollRequired': '请先将个人资料收集声明（PICS）滚动至底部。',
    'auth.registerPicsAgreeRequired': '请勾选同意个人资料收集声明。',
    'auth.forgotTitle': '忘记密码',
    'auth.forgotSubtitle': '输入电子邮箱，我们将发送重置链接（若该邮箱已注册）。',
    'auth.forgotSubmit': '发送重置链接',
    'auth.forgotSending': '发送中…',
    'auth.forgotSuccess': '若此邮箱已注册，您将很快收到重置密码的邮件。',
    'auth.resetTitle': '重置密码',
    'auth.resetSubtitle': '请输入新密码。',
    'auth.newPasswordLabel': '新密码',
    'auth.confirmPasswordLabel': '确认新密码',
    'auth.resetSubmit': '重置密码',
    'auth.resetting': '重置中…',
    'auth.passwordMismatch': '两次输入的密码不一致',
    'auth.showPassword': '显示',
    'auth.hidePassword': '隐藏',
    'auth.confirmPasswordRegisterLabel': '确认密码',
    'auth.resetSuccess': '密码已重置，请使用新密码登录。',
    'auth.goToLogin': '前往登录',
    'auth.goToHome': '前往主页',
    'auth.verifyTitle': '验证邮箱',
    'auth.verifyPending': '正在验证…',
    'auth.verifySuccess': '邮箱验证成功。',
    'auth.verifyFail': '验证失败，链接可能已过期。',
    'auth.missingToken': '链接无效或缺少验证码。',
    'auth.accountRemovalConfirmTitle': '确认删除账户',
    'auth.accountRemovalConfirmPrompt': '按下方按钮后，账户会立即删除，且无法恢复。',
    'auth.accountRemovalConfirmButton': '确认删除账户',
    'auth.accountRemovalConfirmPending': '正在删除账户…',
    'auth.accountRemovalConfirmSuccess': '账户已删除。',
    'auth.accountRemovalConfirmFail': '确认失败，链接可能已过期。',
    'auth.googleCallbackTitle': 'Google 登录中',
    'auth.googleCallbackLoading': '正在完成 Google 验证，请稍候…',
    'auth.googleDecisionNotRegistered': '系统没有此 Google 账户注册记录，是否立即注册？',
    'auth.googleDecisionRegisteredGoogle': '系统已有此 Google 账户注册记录，是否立即登录？',
    'auth.googleDecisionRegisteredEmail': '系统已有同一邮箱的邮箱注册记录，是否改用邮箱登录？',
    'auth.googleDecisionConfirmRegister': '是，立即注册',
    'auth.googleDecisionConfirmLogin': '是，立即登录',
    'auth.googleDecisionConfirmEmailLogin': '是，改用邮箱登录',
    'auth.googleDecisionCancelHome': '否，返回主页',
    'auth.registerWizardStepBadge': '第 {{current}} 步，共 {{total}} 步',
    'auth.registerWizardProgressAria': '注册进度',
    'auth.registerMethodTitle': '创建账户',
    'auth.registerMethodSubtitle': '选择使用邮箱或 Google 注册 MaidEasy。',
    'auth.registerDividerOrEmail': '或使用邮箱注册',
    'auth.registerEmailHelper': '我们会将验证链接发送到此邮箱。',
    'auth.registerContinueWithEmail': '使用邮箱继续',
    'auth.registerStepRoleTitle': '选择身份',
    'auth.registerStepRoleSubtitle': '请选择雇主或家务助理。注册完成后身份无法更改。',
    'auth.registerRoleImportantNotice':
      '重要：身份一经确认并完成注册后无法更改，请务必选择正确身份。',
    'auth.registerRoleEmployerTitle': '雇主',
    'auth.registerRoleEmployerDesc': '管理任务、开支，并与家务助理连接。',
    'auth.registerRoleHelperTitle': '家务助理',
    'auth.registerRoleHelperDesc': '查看每日任务、提交收据并跟踪工作。',
    'auth.registerStepNicknameTitle': '输入详细资料',
    'auth.registerStepNicknameSubtitle': '此名称会显示给配对另一方；使用地区及昵称之后可在设置中修改。',
    'auth.registerStepPasswordTitle': '设置密码',
    'auth.registerStepPasswordSubtitle': '请创建足够强的密码以保护账户。',
    'auth.registerPasswordPolicyTitle': '密码须符合：',
    'auth.registerPasswordPolicyLength': '8–16 个字符',
    'auth.registerPasswordPolicyUpper': '至少一个大写字母（A–Z）',
    'auth.registerPasswordPolicyLower': '至少一个小写字母（a–z）',
    'auth.registerPasswordPolicyDigit': '至少一个数字（0–9）',
    'auth.registerStepPairingTitle': '配对码（选填）',
    'auth.registerStepPairingSubtitle': '若雇主已提供 6 位配对码，可在此输入；留空可稍后在配对页面输入。',
    'auth.registerStepPicsScrollFirst': '请先将下方声明滚动至底部，才可勾选同意。',
    'auth.registerContinue': '继续',
    'auth.registerStepReviewTitle': '确认资料',
    'auth.registerStepReviewSubtitle': '请确认以下资料正确；如有错误可点「修改」返回该步骤。',
    'auth.registerReviewMethod': '注册方式',
    'auth.registerReviewMethodEmail': '邮箱',
    'auth.registerReviewMethodGoogle': 'Google',
    'auth.registerReviewRole': '身份',
    'auth.registerReviewEmail': '邮箱地址',
    'auth.registerReviewNickname': '昵称',
    'auth.registerReviewUsageRegion': '使用地区',
    'auth.registerReviewPairing': '配对码',
    'auth.registerReviewPairingEmpty': '（未填写）',
    'auth.registerReviewInvitationCode': '邀请码',
    'auth.registerReviewInvitationCodeEmpty': '未使用',
    'auth.registerReviewPicsAgreed': '你已同意个人资料收集声明（PICS）及相关数据处理条款。',
    'auth.registerReviewEdit': '修改',
    'auth.registerCompleteRegistration': '完成注册',
    'auth.registerCompleteTitle': '注册完成',
    'auth.registerCompleteEmailVerify':
      '我们已发送验证邮件，请检查收件箱（及垃圾邮件）并点击链接完成验证。',
    'auth.registerCompleteGoogle': '你的 Google 账户已完成验证，可继续使用 MaidEasy。',
    'auth.registerCompleteContinue': '继续',
    'auth.emailTaken': '此邮箱已被注册',
    'auth.emailChecking': '正在检查邮箱…',
    'auth.emailInvalid': '请输入有效的邮箱地址',
    'home.navLogin': '登录',
    'home.navLoginEmail': '邮箱登录',
    'home.navGoogleLogin': '使用 Google 登录',
    'home.navRegister': '注册',
    'home.registerModalTitle': '选择注册方式',
    'home.registerModalSubtitle': '请先选择使用 Google 注册或邮箱注册。',
    'home.registerWithGoogle': '使用 Google 注册',
    'home.registerWithEmail': '邮箱注册',
    'home.navLogout': '登出',
    'home.navPairing': '配对状态',
    'home.navEmployerApp': '任务管理（僱主）',
    'home.navHelperApp': '今日待办（家务助理）',
    'home.phaseLine': '',
    'home.linkPrivacy': '隐私政策',
    'home.linkTerms': '服务条款',
    'home.linkPics': 'PICS',
    'home.linkFaq': '常见问题',
    'home.linkFeatures': '功能介绍',
    'home.linkFeaturesAria': '功能介绍（另开新标签页）',
    'faq.title': '常见问题',
    'faq.subtitle': '按身份查看常见问题与操作说明。',
    'faq.tabHelper': '家务助理',
    'faq.tabEmployer': '僱主',
    'faq.loadError': '无法加载常见问题',
    'faq.empty': '目前暂无常见问题内容。',
    'nav.home': '首页',
    'systemMessage.menu': '系统消息',
    'systemMessage.list.title': '系统消息',
    'systemMessage.list.empty': '目前没有系统消息。',
    'systemMessage.list.colSubject': '主题',
    'systemMessage.list.colDate': '日期',
    'systemMessage.list.openItem': '打开：{{subject}}',
    'systemMessage.ticker.title': '系统消息',
    'systemMessage.ticker.count': '{{current}} / {{total}}',
    'systemMessage.ticker.openDetails': '查看系统消息详情',
    'systemMessage.ticker.prev': '上一则系统消息',
    'systemMessage.ticker.next': '下一则系统消息',
    'systemMessage.action.close': '关闭',
    'systemMessage.detailEmpty': '（没有详细内容）',
    'systemMessage.error.loadList': '无法加载系统消息',
    'systemMessage.image.enlargeAria': '放大图片',
    'systemMessage.image.openLinkAria': '打开链接',
    'systemMessage.menuItemUnreadAria': '{{label}}，{{count}} 则未读',
    'nav.tasks': '任务',
    'nav.expensesSettlements': '开支/结算',
    'nav.expensesSettlementsHelper': '开支/结算',
    'nav.family': '家庭',
    'nav.stock': '存货',
    'nav.mainAria': '主应用底部导航',
    'pwa.offlineBanner': '离线 — 本程式暂时无法使用直至您的网络恢复',
    'pwa.syncing': '同步中…',
    'pwa.syncComplete': '同步完成',
    'pwa.syncFailed': '同步失败 — 请重试',
    'pwa.pendingSync': '尚有待同步项目',
    'pwa.installCta': '安装应用',
    'pwa.installDismiss': '关闭',
    'playBanner.subtitle': 'MaidEasy — 在 Google Play',
    'playBanner.install': '安装',
    'playBanner.installAria': '在 Google Play 安装 MaidEasy',
    'playBanner.dismissAria': '关闭安装横幅',
    'playBanner.regionAria': 'Google Play 安装提示',
    'task.complete.savedOffline': '已保存在本机，联网后将自动同步',
    'receipt.analyze.savedOffline': '已保存在本机，联网后将自动同步',
    'legal.privacy.title': '隐私政策',
    'legal.privacy.body':
      '本页为法律文件占位内容。我们将说明如何收集、使用与保护您的个人信息；正式上线前将由法务审阅并更新。',
    'legal.terms.title': '服务条款',
    'legal.terms.body':
      '本页为法律文件占位内容。使用本服务即表示您同意相关条款与细则；正式条款将于上线前审阅并公布。',
    'legal.pics.title': '个人资料收集声明（PICS）',
    'legal.pics.body':
      '本页为多语言 PICS 占位内容，与注册流程中的声明方向一致；正式文案将配合法务与合规审阅后更新。',
    'legal.backHome': '返回首页',
    'settings.title': '个人设置',
    'settings.subtitle': '语言、时区、外观与通知',
    'settings.nicknameLabel': '称呼（昵称）',
    'settings.nicknamePlaceholder': '请输入称呼',
    'settings.languageLabel': '显示语言',
    'settings.timezoneLabel': '时区',
    'settings.usageRegionLabel': '使用地区',
    'settings.themeLabel': '主题',
    'settings.themeLight': '浅色',
    'settings.themeDark': '深色',
    'settings.themeSystem': '跟随系统',
    'settings.fontSizeLabel': '字体大小',
    'settings.fontXSmall': '极小',
    'settings.fontSmall': '小',
    'settings.fontMedium': '中',
    'settings.fontLarge': '大',
    'settings.fontXLarge': '特大',
    'settings.aiUsageTitle': '今日 AI 用量',
    'settings.aiUsageHint': 'AI 收据及家里存货分析（每日于 UTC 0:00 重置）',
    'settings.receiptUsageTitle': '今日收据扫描用量',
    'settings.receiptUsageHint': '每日可提交收据张数（每日于 UTC 0:00 重置）',
    'settings.aiUsageLoading': '加载中…',
    'settings.dailyUsageError': '无法加载每日用量',
    'usage.pageTitle': '用量计算',
    'usage.pageSubtitle': '查看今日配额与任务上限',
    'usage.backToSettings': '返回设置',
    'usage.taskSubmitTitle': '今日任务提交用量',
    'usage.taskSubmitHint': '每日可提交任务次数（每日于 UTC 0:00 重置）',
    'usage.taskReviewTitle': '今日任务审核用量',
    'usage.taskReviewHint': '每日可审核任务次数（每日于 UTC 0:00 重置）',
    'usage.totalTasksTitle': '总任务建立数',
    'usage.totalTasksHint': '配对内累计任务槽位（已封存任务不计入）',
    'usage.pinMessagesTitle': '总置顶讯息数',
    'usage.pinMessagesHint': '配对内累计置顶讯息数量',
    'usage.resetAtLabel': '下次重置',
    'usage.unlimited': '无限',
    'usage.viewUsageDetails': '查看用量详情',
    'usage.membershipLevel': '会员等级',
    'usage.levelTrial': '试用会员',
    'usage.levelFree': '免费会员',
    'usage.levelPaid': '高级会员',
    'usage.helperMembershipHelpTriggerAria': '查看会员等级说明',
    'usage.helperMembershipHelpAria': '会员等级说明',
    'usage.helperMembershipHelpTitle': '会员等级说明',
    'usage.helperMembershipHelpFollowsEmployer':
      '家务助理无法自行升级会员等级。会员等级跟随雇主账户。',
    'usage.helperMembershipHelpContactEmployer':
      '如用量不够，请联系雇主升级会员等级。',
    'usage.helperSectionTitle': '以下是家务助理的用量',
    'usage.compareLevels': '比较会员等级',
    'usage.upgradeBannerBefore': '按',
    'usage.upgradeBannerClickHere': '此',
    'usage.upgradeBannerAfter': '立即升级至高级会员，以解锁更多用量及功能',
    'usage.upgradeBannerClickAria': '前往会员订阅页面',
    'usage.playStoreUpgradeNotice':
      '如需升级或管理订阅，请使用浏览器或“添加到主屏幕”的应用程序打开本服务（Google Play 版不提供订阅付款）。',
    'membership.restoreModal.title': '恢复已封存的任务',
    'membership.restoreModal.description': '您已升级会员。请选择要恢复的家务存货、接送或食药提醒任务；未选取的任务会维持封存。',
    'membership.restoreModal.confirm': '恢复所选任务',
    'membership.restoreModal.skip': '暂不恢复',
    'membership.restoreModal.success': '已恢复所选任务',
    'membership.restoreModal.error': '无法恢复任务，请稍后再试',
    'membership.pricing.compareTitle': '比较会员等级',
    'membership.pricing.loading': '正在加载会员方案…',
    'membership.pricing.loadError': '暂时无法加载收费方案，请稍后再试。',
    'membership.pricing.priceUnavailable': '—',
    'membership.pricing.monthlyFeeLabel': '月费:',
    'membership.pricing.yearlyFeeLabel': '年费:',
    'membership.pricing.cheaperBadge': '年费计划更便宜',
    'membership.pricing.discountUntil': '折扣直至 {date}',
    'membership.pricing.welcomeRemark': '首月 {first}，之后 {thereafter}',
    'membership.pricing.trialStartsOnRegister': '注册后即开始试用',
    'membership.pricing.yes': '是',
    'membership.pricing.no': '否',
    'subscription.openPage': '会员订阅',
    'subscription.title': '会员订阅',
    'subscription.membershipLevel': '会员等级',
    'subscription.previousSubscriptionTitle': '上一个高级会员订阅',
    'subscription.previousSubscriptionEnded': '上一个高级会员订阅已结束',
    'subscription.previousSubscriptionPaymentFailed': '上一个高级会员订阅已结束（曾扣款失败）',
    'subscription.trialEndsAt': '试用会员到期',
    'subscription.invitationTrialEndsAt': '邀请码高级会员试用到期',
    'subscription.subscriptionEndsAt': '订阅到期',
    'subscription.nextChargeAt': '下次扣款',
    'subscription.payToday': '立即成为高级会员，以解锁更多功能',
    'subscription.startTrial': '设定订阅（试用期内先不扣款）',
    'subscription.payTodayAmount': '{{period}}只需{{amount}}',
    'subscription.noChargeToday': '今日无需付款',
    'subscription.firstChargeAmount': '首次扣款 {{amount}}',
    'subscription.coversPeriod': '首个付费期：{{start}} 至 {{end}}',
    'subscription.nextCharge': '下次扣款：{{date}}',
    'subscription.firstChargeOn': '首次扣款将于 {{date}} 进行',
    'subscription.promoPriceFirstMonth': '首月只需 {{dueAmount}}，其后{{period}} {{listPrice}}',
    'subscription.promoPriceLimitedTime':
      '活动期间{{period}}只需 {{dueAmount}}（直至 {{endsAt}}），其后{{period}} {{listPrice}}',
    'subscription.promoAppliedFirstMonth': '首月优惠：减免 {{amount}}',
    'subscription.promoAppliedLimitedTime': '期间限定优惠：减免 {{amount}}（直至 {{endsAt}}）',
    'subscription.invitationTrialNote': '邀请码试用至 {{date}}，试用期内无需另付费。',
    'subscription.invitationTrialChargeLaterNote':
      '邀请码试用至 {{date}}，系统会于试用结束当日开始首次扣款。',
    'subscription.subscribeButton': '付款订阅',
    'subscription.resubscribeButton': '重新订阅高级会员',
    'subscription.startTrialButton': '设定订阅',
    'subscription.stripeCheckoutDiscountHiddenNote':
      '下一页付款画面只会显示今日无需付款及原价，不会显示折扣。折扣会于首次扣款时自动套用。',
    'subscription.promoMayExpireIfDeferred':
      '限时优惠将于首次扣款前结束，届时可能改扣原价。',
    'subscription.termsTitle': '订阅条款及细则',
    'subscription.termsLinkLabel': '《订阅条款及细则》',
    'subscription.termsConsentPay':
      '按下「{{buttonLabel}}」并付款，即表示您同意{{termsLink}}。',
    'subscription.termsConsentSetup': '按下「{{buttonLabel}}」即表示您同意{{termsLink}}。',
    'subscription.termsLoadError': '无法加载订阅条款，请稍后再试。',
    'subscription.alreadySubscribed': '您已有有效付费订阅。',
    'subscription.stripeNotConfigured': '订阅功能尚未在此环境启用，请联系管理员。',
    'subscription.loadError': '无法加载订阅资料，请稍后再试。',
    'subscription.checkoutSuccess': '订阅设定成功。',
    'subscription.checkoutCancel': '已取消付款。',
    'subscription.manageButton': '管理订阅',
    'subscription.playStoreBillingNotice':
      'Google Play 版不提供订阅、管理订阅或转换计划。',
    'subscription.playStoreOfficialWebsiteLine':
      '请使用浏览器前往 MaidEasy 官方网站\n{{url}}',
    'subscription.invoicesTitle': '最近发票',
    'subscription.viewInvoiceButton': '查看发票',
    'subscription.statusActive': '订阅状态：有效',
    'subscription.statusPastDue': '订阅状态：扣款失败',
    'subscription.statusCanceled': '订阅状态：已取消',
    'subscription.willAutoRenewOn': '您的订阅将于 {{date}} 自动续费',
    'subscription.willAutoRenewOnWithAmount': '您的订阅将于 {{date}} 自动续费 ({{amount}})',
    'subscription.willAutoRenewOnPromo': '您的订阅将于 {{date}} 自动续费 (优惠价{{amount}})',
    'subscription.nextRenewLimitedTimePromo':
      '限时优惠：{{period}}只需 {{dueAmount}}（直至 {{endsAt}}，原价 {{listPrice}}）',
    'subscription.periodPerMonth': '每月',
    'subscription.periodPerYear': '每年',
    'subscription.willEndOnCancelled': '您的订阅将于 {{date}} 结束（已取消续订）',
    'subscription.intervalMonth': '月费',
    'subscription.intervalYear': '年费',
    'subscription.selectBillingInterval': '选择收费周期',
    'subscription.planUnavailable': '此收费周期暂未开放。',
    'subscription.currentBillingInterval': '目前计划：{{interval}}',
    'subscription.pendingIntervalChange': '已预约于 {{date}} 改为{{interval}}（本期结束后生效）',
    'subscription.lockedPromoReserved': '已为你保留优惠减免 {{amount}}（若到期当日有更高优惠会自动套用）',
    'subscription.cancelIntervalChange': '取消预约转换',
    'subscription.switchToYear': '改为年费（下期生效）',
    'subscription.switchToMonth': '改为月费（下期生效）',
    'subscription.intervalChangeNote': '转换会在目前计划结束后生效。',
    'subscription.intervalChangeScheduled': '已预约转换收费周期。',
    'subscription.intervalChangeCancelled': '已取消预约转换。',
    'subscription.intervalChangeConfirmTitleYear': '确认改为年费',
    'subscription.intervalChangeConfirmTitleMonth': '确认改为月费',
    'subscription.intervalChangeConfirmIntro':
      '您目前为{{current}}，将预约改为{{target}}。转换会在现有计划结束后生效。',
    'subscription.intervalChangeConfirmEffectiveAt': '转换生效／现有计划结束时间',
    'subscription.intervalChangeConfirmFirstChargeAt': '首笔新计划扣款时间',
    'subscription.intervalChangeConfirmCoversPeriod': '首个新计划涵盖期间',
    'subscription.intervalChangeConfirmCoversPeriodValue': '{{start}} 至 {{end}}',
    'subscription.intervalChangeConfirmAmount': '预计首笔扣款金额',
    'subscription.intervalChangeConfirmPromoLimited':
      '目前适用限时优惠：减免 {{amount}}（直至 {{endsAt}}；若扣款当日有更高优惠会自动套用）',
    'subscription.intervalChangeConfirmPromoGeneric': '目前适用优惠：减免 {{amount}}',
    'subscription.intervalChangeConfirmNotes': '注意事项',
    'subscription.intervalChangeConfirmNoRefund': '现有计划会用至结束为止。',
    'subscription.intervalChangeConfirmPromoLockNote':
      '确认后会先保留今日适用的限时优惠；若扣款当日有更高优惠则改用更高者，否则使用已保留优惠。',
    'subscription.intervalChangeConfirmButtonYear': '确认改为年费',
    'subscription.intervalChangeConfirmButtonMonth': '确认改为月费',
    'subscription.cancelIntervalChangeConfirmTitle': '确认取消预约转换',
    'subscription.cancelIntervalChangeConfirmIntro':
      '将取消已预约于 {{date}} 改为{{interval}}的转换，并继续维持目前计划。',
    'subscription.cancelIntervalChangeConfirmPromoNote':
      '已保留的优惠减免 {{amount}} 亦会一并取消。',
    'subscription.cancelIntervalChangeConfirmButton': '确认取消预约',
    'membership.restoreTask.action': '恢复任务',
    'membership.restoreTask.success': '任务已恢复',
    'admin.upgradeToL3': '升级至 L3（测试）',
    'admin.upgradeToL3Confirm': '确定将此雇主升级至 L3？将恢复自动翻译设定，并于首次进入仪表板时提示恢复已封存任务。',
    'admin.upgradeToL3Success': '已升级至 L3。',
    'admin.upgradeToL3Error': '升级至 L3 失败。',
    'admin.grantL3TrialTitle': '发放 L3 试用',
    'admin.grantL3TrialHint': '输入结束日期时间。系统会将雇主设为 L3，并以邀请码 L3 试用到期时间处理。',
    'admin.grantL3TrialField': '试用到期（HKT，YYYY-MM-DD hh:mm）',
    'admin.grantL3TrialButton': '发放 L3 试用',
    'admin.grantL3TrialConfirm': '确定要为此雇主发放 L3 试用？',
    'admin.grantL3TrialSuccess': '已发放 L3 试用。',
    'admin.grantL3TrialError': '发放 L3 试用失败。',
    'admin.grantL3TrialDateRequired': '请先输入试用到期日期时间。',
    'limitModal.title.default': '已达使用上限',
    'limitModal.title.batchInsufficient': '今日用量不足',
    'limitModal.title.TOTAL_TASK_LIMIT_REACHED': '任务建立数已满',
    'limitModal.title.COMMENT_LIMIT_REACHED': '留言数已达上限',
    'limitModal.title.DAILY_TASK_SUBMIT_LIMIT_REACHED': '今日任务提交已达上限',
    'limitModal.title.DAILY_TASK_REVIEW_LIMIT_REACHED': '今日任务审核已达上限',
    'limitModal.title.DAILY_RECEIPT_LIMIT_REACHED': '今日收据提交已达上限',
    'limitModal.title.DAILY_AI_ANALYZE_LIMIT_REACHED': '今日 AI 分析已达上限',
    'limitModal.title.TASK_KIND_NOT_ALLOWED': '此任务类型不可用',
    'limitModal.title.AUTO_TRANSLATION_NOT_ALLOWED': '自动翻译不可用',
    'limitModal.title.INVENTORY_LOCATION_LIMIT_REACHED': '盘点位置数已达上限',
    'limitModal.title.INVENTORY_CATEGORY_LIMIT_REACHED': '盘点类别数已达上限',
    'limitModal.title.PIN_MESSAGE_LIMIT_REACHED': '置顶讯息数已满',
    'limitModal.body.TOTAL_TASK_LIMIT_REACHED': '已建立 {{total}}/{{max}} 个任务（封存任务不计入）。',
    'limitModal.body.COMMENT_LIMIT_REACHED': '此项目留言已达 {{total}}/{{max}} 上限。',
    'limitModal.body.DAILY_TASK_SUBMIT_LIMIT_REACHED': '今日已提交 {{used}}/{{limit}} 次；下次重置：{{retryAt}}。',
    'limitModal.body.DAILY_TASK_REVIEW_LIMIT_REACHED': '今日已审核 {{used}}/{{limit}} 次；下次重置：{{retryAt}}。',
    'limitModal.body.DAILY_RECEIPT_LIMIT_REACHED': '今日已提交 {{used}}/{{limit}} 张收据；下次重置：{{retryAt}}。',
    'limitModal.body.DAILY_AI_ANALYZE_LIMIT_REACHED': '今日已使用 {{used}}/{{limit}} 次 AI 分析；下次重置：{{retryAt}}。',
    'limitModal.body.batchInsufficient.DAILY_RECEIPT_LIMIT_REACHED':
      '今日剩余收据提交次数为 {{remaining}} 次（已用 {{used}}/{{limit}}），不足以提交 {{requested}} 张收据。下次重置：{{retryAt}}。',
    'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_RECEIPT':
      '今日剩余 AI 分析次数为 {{remaining}} 次（已用 {{used}}/{{limit}}），不足以分析 {{requested}} 张收据。下次重置：{{retryAt}}。',
    'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_PHOTO':
      '今日剩余 AI 分析次数为 {{remaining}} 次（已用 {{used}}/{{limit}}），不足以分析 {{requested}} 张相片。下次重置：{{retryAt}}。',
    'limitModal.body.TASK_KIND_NOT_ALLOWED': '目前会员等级不支持此任务类型。',
    'limitModal.body.AUTO_TRANSLATION_NOT_ALLOWED': '目前会员等级不支持自动翻译。',
    'limitModal.body.INVENTORY_LOCATION_LIMIT_REACHED': '盘点位置数已达目前会员等级上限。',
    'limitModal.body.INVENTORY_CATEGORY_LIMIT_REACHED': '盘点类别数已达目前会员等级上限。',
    'limitModal.body.PIN_MESSAGE_LIMIT_REACHED': '已建立 {{total}}/{{max}} 则置顶讯息。',
    'limitModal.title.PIN_MESSAGE_ACTIVE_LIMIT_REACHED': '启用中的置顶讯息已满',
    'limitModal.body.PIN_MESSAGE_ACTIVE_LIMIT_REACHED':
      '总则数已超过免费会员上限，同时启用中的置顶讯息最多 {{max}} 则（目前 {{total}} 则）。请先停用其他置顶讯息，或升级会员。',
    'limitModal.body.default': '您的账户已触及目前会员等级的使用限制。',
    'limitModal.helperHint': '如需更高配额，请联系雇主升级会员等级。',
    'limitModal.employerDailyHint': '可前往用量页查看详情；升级会员可获得更高每日配额。',
    'limitModal.employerTotalTasksHint': '您可以升级会员以建立更多任务。',
    'limitModal.employerTaskKindHint': '请升级会员以使用此任务类型。',
    'limitModal.employerPinMessagesHint': '您可以升级会员以新增更多置顶讯息。',
    'limitModal.employerL3Hint': '已达目前方案上限，可前往用量页查看详情。',
    'limitModal.close': '关闭',
    'limitModal.viewUsage': '查看用量详情',
    'limitModal.goSubscription': '前往订阅／升级',
    'limitModal.playStoreBillingHint':
      'Google Play 版无法在此付款升级。请改用浏览器或“添加到主屏幕”的应用程序管理订阅。',
    'settings.notifyEmailLabel': '活动通知亦以电子邮件发送',
    'settings.autoTranslationLabel': '自动翻译对方输入的文字',
    'settings.autoTranslationHint': '关闭后，留言、任务说明等将只显示原文，不会调用翻译服务。',
    'settings.notifyWebPushLabel': '此设备是否接收通知（Web Push）',
    'settings.webPushToggleError': '更新通知设置失败',
    'settings.notifyWebPushPermissionDeniedHint':
      '你的浏览器通知权限目前为已阻止。请到浏览器网站设置允许通知，然后刷新此页面。',
    'settings.webPushPromptTitle': '启用推送通知',
    'settings.webPushPromptBody': '如要即时收到任务与审核通知，请允许此应用发送通知。',
    'settings.webPushPromptEnable': '允许通知',
    'settings.androidChromePushHintTitle': '系统推送需要 Chrome',
    'settings.androidChromePushHintBody':
      '此设备的浏览器不支持或不适合 Web Push。请安装或更新最新版 Chrome，即可接收任务与审核的即时通知。站内铃铛通知仍可正常使用。',
    'settings.androidChromePushHintCta': '前往安装／更新 Chrome',
    'settings.androidChromePushHintDismiss': '稍后再说',
    'settings.save': '保存设置',
    'settings.saving': '保存中…',
    'settings.saved': '已保存',
    'settings.invitationCodeTitle': '兑换邀请码',
    'settings.invitationCodeSubscriptionTitle': '有邀请码？',
    'settings.invitationCodeHint': '输入 8 位邀请码以延长试用或升级高级会员试用。',
    'settings.invitationCodeInputLabel': '邀请码',
    'settings.invitationCodeInputPlaceholder': '输入 8 位邀请码',
    'settings.invitationCodeRedeemButton': '兑换',
    'settings.invitationCodeRedeemSuccess': '邀请码兑换成功',
    'settings.invitationCodeRedeemFailed': '邀请码兑换失败，请稍后再试',
    'settings.invitationCodeInvalid': '邀请码无效',
    'settings.invitationCodeErrorRegistrationOnly': '此邀请码仅可于注册时兑换',
    'settings.invitationCodeErrorNotL1Trial': '此邀请码仅适用于试用会员',
    'settings.invitationCodeErrorL1TrialEnded': '您的试用已结束',
    'settings.invitationCodeErrorSubscriptionActive': '您已有有效付费订阅，无法兑换此码',
    'settings.invitationCodeErrorEmailAlreadyUsed': '您已使用过此邀请码',
    'settings.invitationCodePreviewL1Extend': '延长试用 {{days}} 日，新到期日 {{endsAt}}',
    'settings.invitationCodePreviewL3Trial': '升级高级会员试用 {{days}} 日，到期日 {{endsAt}}',
    'settings.invitationCodePreviewL3SubscriptionExtend': '延长订阅 {{days}} 日，新到期日 {{endsAt}}',
    'settings.loadError': '无法加载设置',
    'settings.saveError': '保存失败',
    'settings.logout': '登出',
    'settings.logoutConfirmTitle': '确定要登出？',
    'settings.logoutConfirmDescription': '登出后需重新登录才能使用账户功能。',
    'settings.changePassword': '变更密码',
    'settings.changePasswordHint': '已登录可直接变更密码。',
    'settings.googlePasswordManagedHint': '此账户使用 Google 登录，不支持变更密码或忘记密码。',
    'settings.currentPasswordLabel': '当前密码',
    'settings.newPasswordLabel': '新密码',
    'settings.confirmNewPasswordLabel': '确认新密码',
    'settings.changePasswordSubmit': '更新密码',
    'settings.changePasswordSaving': '更新中…',
    'settings.changePasswordSuccess': '密码已更新，请重新登录。',
    'settings.changePasswordError': '变更密码失败',
    'settings.changePasswordCurrentPasswordIncorrect': '当前密码错误，请重新输入。',
    'settings.expandChangePasswordSectionAria': '展开变更密码',
    'settings.collapseChangePasswordSectionAria': '收起变更密码',
    'settings.expandAccountRemovalSectionAria': '展开删除账户',
    'settings.collapseAccountRemovalSectionAria': '收起删除账户',
    'settings.resendVerification': '重新发送验证邮件',
    'settings.resendVerificationSending': '发送中…',
    'settings.resendVerificationSuccess': '已发送验证邮件，请查收收件箱。',
    'settings.resendVerificationError': '发送失败',
    'settings.resendVerificationCooldown': '请 15 分钟后再点击「重新发送验证邮件」。',
    'settings.emailUnverifiedBanner': '您的邮箱尚未验证，将无法使用此App的功能。',
    'settings.accountRemovalPasswordLabel': '输入登录密码以确认',
    'settings.accountRemovalGoogleHint': '提交后会寄出确认邮件。请于 30 分钟内打开链接，并在页面按确认。确认后账户会立即删除。',
    'settings.backToPairingStatus': '返回配对状态',
    'settings.mainMenuAria': '主菜单',
    'settings.menuHome': '首页',
    'settings.menuTasksEmployer': '任务管理',
    'settings.menuTasksHelper': '工作清单',
    'settings.menuSettlements': '开支与结算',
    'settings.menuMoneyGiven': '已给的钱',
    'settings.menuSettlementRecords': '结算记录',
    'settings.menuPinMessages': '置顶讯息设置',
    'settings.menuBudgets': '已给的钱管理',
    'settings.menuReceipts': '扫描收据',
    'settings.menuReceiptBrowse': '收据列表',
    'settings.menuFamily': '家庭成员的偏好',
    'settings.menuPairingStatus': '配对状态',
    'settings.menuConfirmSettlement': '确认结算',
    'settings.menuSettings': '个人设置',
    'settings.menuOverview': '总览',
    'settings.menuAccountSettings': '账户设置',
    'settings.menuSubscriptionAndInvite': '会员订阅 及 兑换邀请码',
    'settings.menuMoneyGivenSettlement': '已给的钱与结算',
    'settings.menuTodayTask': '今日任务',
    'settings.menuImportPreviousPairing': '从上一配对导入资料',
    'pairing.import.title': '从上一配对导入资料',
    'pairing.import.description': '可将最近一次已结束配对的任务、家庭成员及置顶讯息复制到目前配对。默认全不选，请勾选要导入的项目。',
    'pairing.import.later': '稍后',
    'pairing.import.import': '导入',
    'pairing.import.sectionTasks': '任务',
    'pairing.import.sectionFamily': '家庭成员',
    'pairing.import.sectionPins': '置顶讯息',
    'pairing.import.alreadyImported': '已导入',
    'pairing.import.consentLabel': '我确认导入所选家庭成员资料，并沿用其既有同意设定。',
    'pairing.import.empty': '没有可导入的项目（可能已全部导入，或上一配对没有相关资料）。',
    'pairing.import.success': '已成功导入所选项目',
    'pairing.import.error': '无法导入，请稍后再试',
    'pairing.import.loadError': '无法载入可导入项目，请稍后再试',
    'pairing.import.taskQuotaHint': '任务槽位：{{used}}/{{max}}（已封存任务不计入）',
    'pairing.import.pinQuotaHint': '置顶讯息：{{used}}/{{max}}',
    'pairing.import.taskKindStandard': '一般任务',
    'previousHelper.detailBanner': '此任务为前任家务助理负责；现任家务助理看不到此记录。',
    'previousHelper.receiptBanner': '此收据为前任家务助理负责；现任家务助理看不到此记录。',
    'previousHelper.settlementBanner': '此结算为前任家务助理负责；现任家务助理看不到此记录。',
    'previousHelper.unpairedDetailBanner': '此记录属于已结束的配对，新的家务助理配对后也看不到。',
    'previousHelper.unpairedReceiptBanner': '此收据属于已结束的配对，新的家务助理配对后也看不到。',
    'previousHelper.unpairedSettlementBanner': '此结算属于已结束的配对，新的家务助理配对后也看不到。',
    'pairing.unpairedBanner': '尚未与家务助理配对。你可以先建立任务、置顶讯息、家庭成员和已给的钱；配对后家务助理才看得到。',
    'pairing.unpairedBannerAction': '产生配对码',
    'pairing.unpairedBannerAria': '尚未配对提示',
    'settings.accountRemoval': '删除账户',
    'settings.accountRemovalHint': '输入登录密码并提交后，账户会立即删除，且无法恢复。',
    'settings.accountRemovalSubmit': '确认并删除账户',
    'settings.accountRemovalSuccess': '已寄出确认邮件。请于 30 分钟内打开链接，并在页面按确认以删除账户。',
    'settings.accountRemovalDeleted': '账户已删除。',
    'settings.accountRemovalError': '提交失败，请稍后再试。',
    'settings.accountRemovalPasswordIncorrect': '密码错误，请重新输入。',
    'settings.closePanel': '关闭',
    'toast.offlineQueued': '已加入离线队列，恢复联网后会自动同步。',
    'toast.task.createSuccess': '成功新增任务！',
    'toast.task.updateSuccess': '成功保存任务！',
    'toast.task.completeSuccess': '成功提交完成任务！',
    'toast.task.resubmitSuccess': '成功重新提交任务！',
    'toast.task.reviewedSuccess': '成功审核任务！',
    'toast.task.needUpdateSuccess': '成功标记任务需更新！',
    'toast.receiptAnalyze.singleSavedOnline': '收据已保存。下一步：提交结算给雇主',
    'toast.receiptAnalyze.singleSavedStillUnsaved':
      '收据已保存。尚有 {{count}} 张收据未保存，请先全部保存。',
    'toast.receiptAnalyze.singleSavedOffline': '已保存收据到离线队列。',
    'toast.receiptAnalyze.batchAllOnlineSuccess': '批量完成：{{successCount}} 笔已成功保存。',
    'toast.receiptAnalyze.batchAllOfflineQueued': '批量完成：{{queuedCount}} 笔已加入离线队列。',
    'toast.receiptAnalyze.batchMixedOnlineOfflineSuccess':
      '批量完成：{{onlineCount}} 笔已保存，{{queuedCount}} 笔已加入离线队列。',
    'toast.receiptAnalyze.batchPartialResult':
      '批量完成：{{onlineCount}} 笔已保存、{{queuedCount}} 笔已加入离线队列、{{failedCount}} 笔失败。',
    'toast.receipt.editSuccess': '成功保存收据！',
    'toast.receipt.deleteSuccess': '成功删除收据！',
    'toast.budget.createSuccess': '成功新增已给的钱！',
    'toast.budget.updateSuccess': '成功保存已给的钱！',
    'toast.budget.deleteSuccess': '已移除已给的钱！',
    'toast.pinMessage.createSuccess': '成功新增置顶讯息！',
    'toast.pinMessage.updateSuccess': '成功保存置顶讯息！',
    'toast.pinMessage.deleteSuccess': '成功移除置顶讯息！',
    'toast.familyMember.createSuccess': '成功新增家庭成员！',
    'toast.familyMember.updateSuccess': '成功修改家庭成员！',
    'toast.familyMember.deleteSuccess': '成功移除家庭成员！',
    'toast.settings.updateSuccess': '成功保存个人设置！',
    'toast.settings.changePasswordSuccess': '成功变更密码！',
    'toast.pairing.joinSuccess': '成功完成配对！',
    'toast.pairing.unpairSuccess': '成功解除配对！',
    'toast.pairing.generateCodeSuccess': '成功生成配对码！',
    'toast.consent.acceptSuccess': '成功提交同意声明！',
    'toast.settlement.confirmSuccess': '成功提交结算！',
    'toast.settlement.unlockSuccess': '成功解锁结算！',
    'toast.settlement.withdrawSuccess': '成功撤回结算！',
    'toast.settlement.reviewedSuccess': '成功审核结算！',
    'toast.settlement.rejectedSuccess': '成功拒绝结算！',
    'toast.settlement.withdrawConfirmTitle': '确认撤回结算？',
    'toast.settlement.withdrawConfirmBody': '撤回后此笔结算会改为已撤回状态，确定要继续吗？',
    'pairing.generate.title': '生成配对码',
    'pairing.generate.subtitle': '生成配对码，供家务助理与您配对',
    'pairing.generate.errorGeneric': '生成配对码失败',
    'pairing.generate.codeLabel': '您的配对码',
    'pairing.generate.validUntil': '有效期至 {{datetime}}（{{duration}}）',
    'pairing.generate.durationMinutes': '{{count}} 分钟',
    'pairing.generate.durationHours': '{{count}} 小时',
    'pairing.generate.durationDay': '{{count}} 天',
    'pairing.generate.durationDays': '{{count}} 天',
    'pairing.generate.copyAria': '复制配对码到剪贴板',
    'pairing.generate.copied': '已复制！',
    'pairing.generate.copyCode': '复制配对码',
    'pairing.generate.regenerate': '重新生成',
    'pairing.generate.regenerating': '生成中…',
    'pairing.generate.regenerateAria': '重新生成配对码',
    'pairing.generate.continue': '继续',
    'pairing.generate.continueAria': '前往下一步',
    'pairing.generate.generateCta': '生成配对码',
    'pairing.generate.generateAria': '生成配对码',
    'pairing.generate.footer':
      '请将此注册链接或 QR code 交给家务助理。家务助理在注册时会自动与您配对。',
    'pairing.generate.qrAria': '家务助理注册链接 QR code',
    'pairing.generate.shareLabel': '分享',
    'pairing.generate.shareAria': '分享 QR code 与注册链接',
    'pairing.generate.shareInviteTitle': 'MaidEasy 邀请',
    'pairing.generate.shareInviteText':
      '雇主 {{nickname}} 邀请你使用 MaidEasy。点击链接或扫描 QR code 即可注册。',
    'pairing.generate.shareEmployerFallback': '雇主',
    'pairing.generate.shareError': '无法分享，请稍后再试。',
    'pairing.join.title': '输入配对码',
    'pairing.join.subtitle': '输入雇主提供的 6 位字符配对码以完成配对',
    'pairing.join.errorIncomplete': '请输入完整的 {{length}} 位字符配对码',
    'pairing.join.errorGeneric': '配对失败',
    'pairing.join.pairing': '配对中…',
    'pairing.join.submit': '提交配对码',
    'pairing.join.submitAria': '提交配对码',
    'pairing.join.inputGroupAria': '配对码输入',
    'pairing.join.charAria': '配对码第 {{n}} 位',
    'pairing.join.footer': '请向雇主索取配对码。配对码有效期为 {{duration}}。',
    'pairing.invite.landingTitle': '即将与雇主 {{nickname}} 配对',
    'pairing.invite.landingSubtitle': '请选择登录或注册以完成配对',
    'pairing.invite.emailLogin': '电邮登录',
    'pairing.invite.googleLogin': 'Google 登录',
    'pairing.invite.register': '注册',
    'pairing.invite.invalidTitle': '配对链接无效',
    'pairing.invite.errorInvalid': '此配对链接已失效或无效，请向雇主索取新的链接。',
    'pairing.invite.errorFormat': '配对码格式不正确。',
    'pairing.invite.previewError': '无法验证配对码，请稍后再试。',
    'pairing.invite.loginBanner': '登录后将自动与雇主 {{nickname}} 配对',
    'pairing.invite.registerBanner': '注册为家务助理并与雇主 {{nickname}} 配对',
    'pairing.invite.confirmTitle': '与雇主 {{nickname}} 配对',
    'pairing.invite.confirmSubtitle': '确认后将立即完成配对',
    'pairing.invite.confirmCta': '确认配对',
    'pairing.invite.confirmDialogTitle': '确认与 {{nickname}} 配对？',
    'pairing.invite.confirmDialogDescription': '配对完成后即可开始使用 MaidEasy。',
    'pairing.invite.employerSignedIn': '此链接供家务助理使用。您目前已以雇主身份登录。',
    'pairing.invite.helperAlreadyPaired': '您已与雇主配对，无需使用此链接。',
    'pairing.invite.goPairingStatus': '前往配对状态',
    'pairing.invite.expiredTitle': '配对链接已失效',
    'pairing.invite.expiredBody': '配对码可能已过期或已被使用。',
    'pairing.invite.contactEmployer': '配对码无效，请与雇主联络',
    'pairing.generate.copyInviteLink': '复制包含配对码的注册链接',
    'pairing.generate.copyInviteLinkAria': '复制家务助理注册链接到剪贴板',
    'pairing.status.regenerateCode': '重新生成配对码',
    'pairing.status.goGenerateNew': '生成新的配对码',
    'auth.googleLoginRequiredTitle': '请使用 Google 登录',
    'auth.googleLoginRequiredDescription': '此电邮账户需使用 Google 登录。是否改用 Google 登录？',
    'auth.googleLoginRequiredConfirm': '使用 Google 登录',
    'pairing.success.title': '配对成功',
    'pairing.success.subtitle': '您已成功配对',
    'pairing.success.pairedWith': '配对对象',
    'pairing.success.nextTitle': '下一步：同意书',
    'pairing.success.nextBody': '使用前须阅读并同意同意书及个人资料收集声明（PICS）。',
    'pairing.success.continue': '前往同意书',
    'pairing.success.continueAria': '前往同意书页面',
    'pairing.success.nextTitleReady': '下一步：开始使用',
    'pairing.success.nextBodyReady':
      '您已在注册时同意个人资料收集声明（PICS），可进入应用程序。',
    'pairing.success.continueApp': '进入应用程序',
    'pairing.success.continueAppAria': '进入应用程序主页',
    'role.employer': '僱主',
    'role.helper': '家务助理',
    'pairing.status.loadingAria': '加载配对状态',
    'pairing.status.errorGeneric': '无法加载配对状态',
    'pairing.status.activeTitle': '配对已生效',
    'pairing.status.yourPartner': '您的配对对象',
    'pairing.status.pairedSince': '配对自 {{datetime}}',
    'pairing.status.notPairedTitle': '尚未配对',
    'pairing.status.emailUnverifiedHint': '邮箱尚未验证，验证后才可使用 App 功能。',
    'pairing.status.consentRequiredHint': '尚未同意条款，同意后才可使用 App 功能。',
    'pairing.status.notPairedEmployer': '请生成配对码并交给家务助理',
    'pairing.status.notPairedHelper': '请输入雇主提供的配对码',
    'pairing.status.goGenerate': '生成配对码',
    'pairing.status.goEnter': '输入配对码',
    'pairing.status.generateAria': '生成配对码',
    'pairing.status.enterAria': '输入配对码',
    'pairing.status.unpairButton': '解除配对',
    'pairing.status.unpairAria': '解除当前配对',
    'pairing.status.unpairing': '解除配对中…',
    'pairing.status.unpairConfirm': '确定要解除当前配对吗？完成后需要重新配对。',
    'pairing.status.unpairError': '解除配对失败，请稍后再试',
    'protected.loadingAria': '加载中',
    'app.autoUpdatingTitle': '程序自动更新中...',
    'app.autoUpdatingSlowHint': '更新速度似乎比预期慢，请稍等...',
    'app.autoUpdatingVersionLine': '当前: {{currentVer}} | 新版: {{newVer}}',
    // Task Management - Common
    'task.status.pending': '待审核',
    'task.status.notSubmitted': '未提交',
    'task.status.approved': '已通过',
    'task.status.rejected': '已拒绝',
    'task.status.needsUpdate': '需修改',
    'task.status.cancel': '已取消',
    'receipt.status.save': '未结算',
    'receipt.status.submitted': '已提交结算',
    'receipt.status.reviewed': '已审核',
    'task.recurrence.none': '单次',
    'task.recurrence.daily': '每日',
    'task.recurrence.weekly': '每周',
    'task.recurrence.biweekly': '隔周',
    'task.recurrence.monthly': '每月',
    'task.monthlyKind.day': '按日期（每月 X 号）',
    'task.monthlyKind.nthWeekday': '第 N 个星期',
    'task.monthlyKind.lastWeekday': '最后一个星期',
    'task.form.monthlyKindLabel': '每月规则',
    'task.form.monthDaysLabel': '每月日期',
    'task.form.monthlyOrdinalLabel': '第几个',
    'task.form.monthlyOrdinal.1': '第 1 个',
    'task.form.monthlyOrdinal.2': '第 2 个',
    'task.form.monthlyOrdinal.3': '第 3 个',
    'task.form.monthlyOrdinal.4': '第 4 个',
    'task.form.monthlyOrdinal.5': '第 5 个',
    'task.form.monthlyDayClampHint': '若该月没有所选日期（例如 29–31 号），会改为该月最后一日。',
    'task.form.monthlyFifthWeekdayHint': '若该月没有第 5 个所选星期，该月不会产生任务。',
    'task.form.recurrenceMonthDaysRequired': '请至少选择一个每月日期。',
    'task.day.sun': '日',
    'task.day.mon': '一',
    'task.day.tue': '二',
    'task.day.wed': '三',
    'task.day.thu': '四',
    'task.day.fri': '五',
    'task.day.sat': '六',
    // Task List (Employer)
    'task.list.title': '任务管理',
    'task.list.subtitle': '管理家务助理的任务清单',
    'task.list.tabAll': '全部',
    'task.list.tabToday': '今日',
    'task.list.tabRecurring': '重复任务',
    'task.list.tabTodayTask': '今日',
    'task.list.tabEditTask': '编辑任务',
    'task.list.tabOverdueTask': '逾期任务',
    'task.list.tabEffectiveTask': '生效的任务',
    'task.list.tabExpiredTask': '过期的任务',
    'task.list.addNew': '新增任务',
    'task.list.addChoiceTitle': '建立方式',
    'task.list.addChoiceSubtitle': '请选择要建立什么任务。',
    'task.list.addChoiceStandard': '建立一般任务',
    'task.list.addChoiceInventory': '建立盘点',
    'task.list.addChoicePickupDropoff': '建立接送任务',
    'task.list.addChoiceMedication': '建立食药提醒',
    'task.list.addChoiceLockedAria': '「{{label}}」需升级会员后方可使用',
    'task.list.kindStockTake': '家里存货',
    'task.list.kindPickupDropoff': '接送',
    'task.list.kindMedication': '食药提醒',
    'task.pickup.loadError': '无法加载接送任务资料',
    'task.pickup.createHeading': '建立接送任务',
    'task.pickup.editHeading': '编辑接送任务',
    'task.pickup.stepProgress': '步骤 {{step}} / {{total}}',
    'task.pickup.legHint': '请选择要建立接送哪个时段',
    'task.pickup.pickupLabel': '接回',
    'task.pickup.dropoffLabel': '送达',
    'task.pickup.legRequired': '至少要选择送达或接回',
    'task.pickup.personLabel': '接送谁',
    'task.pickup.personPlaceholder': '例如：Dave',
    'task.pickup.suggestHeading': '家庭成员建议',
    'task.pickup.personRequired': '请输入接送谁',
    'task.pickup.pickupTimeLabel': '接回时间',
    'task.pickup.dropoffTimeLabel': '送达时间',
    'task.pickup.pickupTimeRequired': '请输入接回时间',
    'task.pickup.dropoffTimeRequired': '请输入送达时间',
    'task.pickup.timesMustDiffer': '送达与接回时间不能相同',
    'task.pickup.locationLabel': '接送地点（可选）',
    'task.pickup.locationSuggestHeading': '建议',
    'task.pickup.locationChip.school': '学校',
    'task.pickup.locationChip.tutoring': '补习班',
    'task.pickup.locationChip.stadium': '体育馆',
    'task.pickup.locationChip.soccerPitch': '足球场',
    'task.pickup.locationChip.mall': '商场',
    'task.pickup.reviewPickupTitle': '接回任务标题',
    'task.pickup.reviewDropoffTitle': '送达任务标题',
    'task.pickup.editTitleAria': '修改任务标题',
    'task.pickup.submitCreate': '建立接送任务',
    'task.pickup.customTitleModalTitle': '自订标题',
    'task.pickup.titleChoiceTitle': '标题更新选择',
    'task.pickup.titleChoicePickup': '接回',
    'task.pickup.titleChoiceDropoff': '送达',
    'task.pickup.titleChoiceKeep': '保留原标题',
    'task.pickup.titleChoiceUseNew': '使用新标题',
    'task.pickup.titleChoiceCustom': '自订',
    'task.pickup.createSuccess': '接送任务已建立',
    'task.pickup.updateSuccess': '接送任务已更新',
    'task.pickup.createError': '建立接送任务失败',
    'task.medication.loadError': '无法加载食药提醒资料',
    'task.medication.createHeading': '建立食药提醒',
    'task.medication.editHeading': '编辑食药提醒',
    'task.medication.stepProgress': '步骤 {{step}} / {{total}}',
    'task.medication.personLabel': '谁要食药',
    'task.medication.personPlaceholder': '例如：Ben',
    'task.medication.suggestHeading': '家庭成员建议',
    'task.medication.personRequired': '请输入谁要食药',
    'task.medication.descPlaceholder': '例如：食糖尿病药',
    'task.medication.timeRequired': '请至少加入一个食药时间',
    'task.medication.timesUnique': '食药时间不可重复',
    'task.medication.timeMax': '食药时间最多 10 个',
    'task.medication.timesSectionLabel': '食药时间',
    'task.medication.timeLabel': '食药时间 {{n}}',
    'task.medication.removeTimeAria': '移除此时间',
    'task.medication.addTime': '+ 新增食药时间',
    'task.medication.reviewTitle': '任务标题',
    'task.medication.editTitleAria': '修改任务标题',
    'task.medication.submitCreate': '建立食药提醒',
    'task.medication.customTitleModalTitle': '自订标题',
    'task.medication.titleChoiceTitle': '标题更新选择',
    'task.medication.titleChoiceKeep': '保留原标题',
    'task.medication.titleChoiceUseNew': '使用新标题',
    'task.medication.titleChoiceCustom': '自订',
    'task.medication.createSuccess': '食药提醒已建立',
    'task.medication.updateSuccess': '食药提醒已更新',
    'task.medication.createError': '建立食药提醒失败',
    'task.form.notifyEmployerLabel': '完成时通知我',
    'task.form.remindHelperBeforeDueLabel': '截止前提醒家务助理',
    'task.form.remindHelperBeforeDueDisabledHint': '请先设定截止时间才可启用此提醒。',
    'task.form.remindHelperBeforeDueModalTitle': '截止前提醒家务助理',
    'task.form.remindHelperBeforeDueModalIntro': '系统会尝试在截止时间前10分钟内发提醒。',
    'task.form.remindHelperBeforeDueModalLimit1':
      '家务助理需开启接收通知 （需安装程式或加入主画面）或开启电邮通知才可及时收到提醒。',
    'task.form.remindHelperBeforeDueModalLimit2': '家务助理收到提醒时需有网络连接。',
    'task.form.remindHelperBeforeDueModalLimit3': '实际收到时间可能因网络接收而有延误。',
    'task.medication.remindHelperBeforeDueModalIntro': '系统会尝试在食药时间前10分钟内发提醒。',
    'task.medication.step3ReminderAskTitle': '是否于食药时间前提醒家务助理？',
    'task.medication.step3ReminderAskYes': '是，启用提醒',
    'task.medication.step3ReminderAskNo': '否，不用提醒',
    'task.group.wizard.stepOf': '步骤 {{current}} / {{total}}',
    'task.group.wizard.reviewCardHeadingPickup': '确认接送任务',
    'task.group.wizard.reviewCardHeadingMedication': '确认食药提醒',
    'task.group.wizard.reviewConfirmPickup': '建立接送任务',
    'task.group.wizard.reviewConfirmMedication': '建立食药提醒',
    'task.group.wizard.reviewUpdatePickup': '保存接送任务',
    'task.group.wizard.reviewUpdateMedication': '保存食药提醒',
    'task.group.wizard.summaryNone': '（无）',
    'task.group.wizard.summaryPerson': '对象',
    'task.group.wizard.summaryLocation': '地点',
    'task.group.wizard.summaryExclusion': '排除日期',
    'task.group.wizard.summaryRequirePhoto': '需要提交照片',
    'task.group.wizard.summaryYes': '是',
    'task.group.wizard.summaryNo': '否',
    'task.group.wizard.summaryMedicationTimes': '提醒时间',
    'task.list.empty': '尚未建立任何任务',
    'task.list.emptyHint': '点击「新增任务」按钮以创建您的第一个任务',
    'task.list.overdueEmpty': '没有逾期任务',
    'task.list.completedCount': '已完成 {{count}} 次',
    'task.list.pendingReviewNav': '待审核任务',
    'task.list.pendingReviewNavAria': '待审核任务，当前 {{count}} 条',
    'task.list.approvedNav': '已通过清单',
    'task.list.approvedNavAria': '已通过任务，当前 {{count}} 条',
    'task.list.loadError': '加载任务列表失败',
    'task.list.pageSubtitle': '今日进度与任务总览',
    'task.list.summaryPendingLabel': '待审核任务',
    'task.list.summaryApprovedLabel': '已通过',
    'task.list.summaryTomorrowLabel': '明日任务',
    'task.list.tomorrowNavAria': '明日任务，目前 {{count}} 条',
    'task.list.summaryCount': '{{count}} 条',
    'task.list.summaryCountUnitOne': '条',
    'task.list.summaryCountUnitOther': '条',
    'task.list.editFiltersAria': '编辑任务子分页',
    'task.list.tabEditAllTasks': '编辑所有任务',
    'task.list.menuEditAllTasks': '编辑所有任务',
    'task.list.menuHistoryTasks': '过去任务纪录',
    'task.list.menuFutureTasks': '将来任务',
    'task.list.menuEditTask': '编辑任务',
    'task.list.menuViewTask': '查看任务',
    'task.list.menuReviewTask': '审核任务',
    'task.list.taskRowMenuAria': '{{title}} 的操作菜单',
    'task.list.titleMenuAria': '任务管理菜单',
    'task.list.dateStripCustom': '自定义',
    'task.list.dateStripAria': '选择日期',
    'task.list.customDateModalTitle': '选择日期',
    'task.list.timeSectionLateNight': '凌晨',
    'task.list.timeSectionMorning': '早上',
    'task.list.timeSectionAfternoon': '下午',
    'task.list.timeSectionEvening': '晚上',
    'task.list.timeSectionNoTime': '无指定时间',
    'task.list.overdueCardLabel': '逾期任务',
    'task.list.overdueCardView': '查看',
    'task.list.statusEffective': '生效',
    'task.list.statusNotStarted': '未开始',
    'task.list.statusExpired': '已过期',
    'employerDashboard.greetLateNight': '{{name}}，晚安',
    'employerDashboard.greetMorning': '{{name}}，早安',
    'employerDashboard.greetAfternoon': '{{name}}，午安',
    'employerDashboard.greetEvening': '{{name}}，晚安',
    'employerDashboard.loadError': '无法加载仪表板数据',
    'employerDashboard.subtitle': '今日概览',
    'employerDashboard.todayCompletion': '今日完成率',
    'employerDashboard.completedFraction': '已完成 {{done}} / {{total}}',
    'employerDashboard.completedFractionValue': '{{done}} / {{total}}',
    'employerDashboard.completedFractionStatus': '已完成',
    'employerDashboard.pendingTitle': '待审核任务',
    'employerDashboard.pendingEmpty': '目前没有待审核的提交',
    'employerDashboard.submittedAtLine': '提交于 {{at}}',
    'employerDashboard.viewAllPending': '查看全部待审核',
    'employerDashboard.monthExpenseTitle': '已给的钱（{{range}}）',
    'employerDashboard.budgetPlaceholder': '尚未设置使用中的已给的钱',
    'employerDashboard.budgetTotal': '已给的钱总额',
    'employerDashboard.budgetUsed': '已用',
    'employerDashboard.budgetRemaining': '尚余',
    'employerDashboard.settlementPendingLine': '有 {{count}} 笔结算待处理',
    'employerDashboard.ariaTodayCompletionCard': '前往任务页，查看今日完成率',
    'employerDashboard.ariaOverdueCard': '前往任务页逾期分页',
    'employerDashboard.gettingStarted.title': '开始使用',
    'employerDashboard.gettingStarted.subtitle':
      '已与家务助理配对。建议先完成以下设定，对方才能在 App 里工作与回报。',
    'employerDashboard.gettingStarted.progress': '已完成 {{done}} / {{total}}',
    'employerDashboard.gettingStarted.done': '已完成',
    'employerDashboard.gettingStarted.stepCta': '去设定',
    'employerDashboard.gettingStarted.later': '稍后再说',
    'employerDashboard.gettingStarted.laterAria': '稍后再说，关闭开始使用指引',
    'employerDashboard.gettingStarted.dismissAria': '关闭开始使用指引',
    'employerDashboard.gettingStarted.dismissError': '无法关闭开始使用指引',
    'employerDashboard.gettingStarted.dismissToast':
      '已关闭开始使用指引。可点问候旁的「?」再查看。',
    'employerDashboard.gettingStarted.helpTriggerAria': '查看开始使用指引',
    'employerDashboard.gettingStarted.regionAria': '开始使用指引',
    'employerDashboard.gettingStarted.stepTaskTitle': '建立第一个任务',
    'employerDashboard.gettingStarted.stepTaskHint': '例如清洁、买菜或每日例行事项',
    'employerDashboard.gettingStarted.stepTaskAria': '前往任务页建立第一个任务',
    'employerDashboard.gettingStarted.stepBudgetTitle': '设定已给的钱',
    'employerDashboard.gettingStarted.stepBudgetHint': '记下你给家务助理的家用期间与金额',
    'employerDashboard.gettingStarted.stepBudgetAria': '前往新增已给的钱',
    'employerDashboard.gettingStarted.stepFamilyTitle': '加入家庭成员',
    'employerDashboard.gettingStarted.stepFamilyHint': '让家务助理认识家人资料',
    'employerDashboard.gettingStarted.stepFamilyAria': '前往家庭成员页',
    'employerDashboard.gettingStarted.stepPinTitle': '写一则置顶讯息',
    'employerDashboard.gettingStarted.stepPinHint': '对方首页会看到你的提醒',
    'employerDashboard.gettingStarted.stepPinAria': '前往新增置顶讯息',
    'helperHome.greetLateNight': '{{name}}，晚安',
    'helperHome.greetMorning': '{{name}}，早安',
    'helperHome.greetAfternoon': '{{name}}，午安',
    'helperHome.greetEvening': '{{name}}，晚安',
    'pinMessage.list.title': '置顶讯息设置',
    'pinMessage.list.subtitle': '首页轮播会按此顺序显示。上限 {{maxItems}} 个。',
    'pinMessage.list.savingOrder': '保存排序中…',
    'pinMessage.list.empty': '目前没有置顶讯息。',
    'pinMessage.form.addTitle': '新增置顶讯息',
    'pinMessage.form.editTitle': '编辑置顶讯息',
    'pinMessage.form.limitLine': '当前级别限制：标题 {{titleMax}} 字，内文 {{contentMax}} 字。',
    'pinMessage.form.saving': '保存中…',
    'pinMessage.field.title': '标题',
    'pinMessage.field.titlePlaceholder': '例如: 明天超市特价,记得买豉油',
    'pinMessage.field.content': '内文',
    'pinMessage.field.contentPlaceholder': '详细说明置顶讯息...',
    'pinMessage.field.visibility': '可见范围',
    'pinMessage.field.icon': '图示',
    'pinMessage.field.displayFrom': '显示开始日',
    'pinMessage.field.displayTo': '显示结束日',
    'pinMessage.field.status': '状态',
    'pinMessage.status.active': '启用',
    'pinMessage.status.inactive': '停用',
    'pinMessage.visibility.both': '雇主 + 家务助理',
    'pinMessage.visibility.employerOnly': '仅雇主可见',
    'pinMessage.icon.info': '资讯',
    'pinMessage.icon.remind': '提醒',
    'pinMessage.icon.specialAlert': '特别警示',
    'pinMessage.window.always': '永久显示',
    'pinMessage.window.range': '{{from}} 至 {{to}}',
    'pinMessage.window.from': '由 {{from}} 起',
    'pinMessage.window.to': '至 {{to}}',
    'pinMessage.action.add': '新增置顶讯息',
    'pinMessage.action.edit': '编辑',
    'pinMessage.action.remove': '移除',
    'pinMessage.action.save': '保存',
    'pinMessage.action.cancel': '取消',
    'pinMessage.action.close': '关闭',
    'pinMessage.detail.createdAt': '建立时间：{{value}}',
    'pinMessage.detail.updatedAt': '最后修改：{{value}}',
    'pinMessage.action.moveUp': '上移',
    'pinMessage.action.moveDown': '下移',
    'pinMessage.confirm.remove': '确定要移除此置顶讯息吗？',
    'pinMessage.error.loadList': '无法加载置顶讯息列表',
    'pinMessage.error.saveOrder': '无法保存排序',
    'pinMessage.error.remove': '无法移除置顶讯息',
    'pinMessage.error.notFound': '找不到置顶讯息',
    'pinMessage.error.loadForm': '无法加载置顶讯息表单',
    'pinMessage.error.titleContentRequired': '标题与内文不能为空',
    'pinMessage.error.titleMax': '标题不可超过 {{max}} 字',
    'pinMessage.error.contentMax': '内文不可超过 {{max}} 字',
    'pinMessage.error.dateOrder': '显示开始日不可晚于显示结束日',
    'pinMessage.error.save': '无法保存置顶讯息',
    'pinMessage.ticker.openDetails': '打开置顶讯息内容',
    'pinMessage.ticker.prev': '上一则置顶讯息',
    'pinMessage.ticker.next': '下一则置顶讯息',
    'pinMessage.ticker.title': '置顶讯息',
    'pinMessage.ticker.count': '{{current}}/{{total}}',
    'helperHome.loadError': '无法加载首页数据',
    'helperHome.summaryLine': '今日 {{count}} 项任务 · 尚余 {{budget}}',
    'helperHome.dataPlaceholder': '—',
    'helperHome.cardTodayTasks': '今日剩下的任务',
    'helperHome.todayCompletion': '今日完成率',
    'helperHome.submittedFraction': '已提交 {{done}} / {{total}}',
    'helperHome.submittedFractionValue': '{{done}} / {{total}}',
    'helperHome.submittedFractionStatus': '已提交',
    'helperHome.todayTaskBreakdown': '总计：{{total}} · 已完成：{{done}}',
    'helperHome.cardOverdueTasks': '逾期任务',
    'helperHome.overdueTasksHint': '尚未完成且已逾期的任务',
    'helperHome.weeklyBudgetTitle': '收到的钱 ({{range}})',
    'helperHome.detailsLabel': '详情',
    'helperHome.remainingLabel': '剩余',
    'helperHome.totalLabel': '总计',
    'helperHome.doneLabel': '完成',
    'helperHome.budgetSpentOverTotal': '已支出 / 收到的钱',
    'helperHome.budgetSpentLabel': '已支出',
    'helperHome.budgetReceivedLabel': '收到的钱',
    'helperHome.budgetRemainingLabel': '剩余',
    'helperHome.budgetOverspentLabel': '超支',
    'helperHome.balanceBadge': '尚余 {{amount}}',
    'helperHome.overspentBadge': '超支 {{amount}}',
    'helperHome.weeklyBudgetPlaceholder': '暂无收到的钱数据',
    'helperHome.budgetUsedFootnote': '已用 {{used}}',
    'helperHome.actionScanReceipt': '扫描收据',
    'helperHome.actionScanReceiptSub': 'AI 分析并提交',
    'helperHome.tomorrowBadge': '{{count}} 项',
    'helperHome.actionTomorrow': '明日任务',
    'helperHome.actionTomorrowSub': '预览明天待办',
    'helperHome.actionReceiptList': '收据列表',
    'helperHome.actionReceiptListSub': '查看与跟踪',
    'helperHome.settlementReadyBadge': '结算',
    'helperHome.actionSettlement': '进行结算',
    'helperHome.actionViewDetails': '查看详情',
    'helperHome.actionSettlementSub': '开支与周期',
    'helperHome.todayPreviewTitle': '今日待办预览',
    'helperHome.suggestedTime': '截止时间',
    'helperHome.viewAllTasks': '查看全部任务',
    'helperHome.ariaTodayTasksCard': '前往任务页今日分栏',
    'helperHome.ariaTodayCompletionCard': '前往任务页，查看今日完成率',
    'helperHome.ariaOverdueTasksCard': '前往任务页逾期分栏',
    'helperHome.ariaMoneyReceivedCard': '前往收支与结算详情',
    'task.pendingReview.title': '待审核任务',
    'task.pendingReview.subtitle': '家务助理已提交，请审核',
    'task.pendingReview.empty': '目前没有待审核的任务',
    'task.pendingReview.backToTasks': '返回任务管理',
    'task.pendingReview.dueDate': '到期日',
    'task.pendingReview.submittedAt': '提交时间',
    'task.pendingReview.submittedAtTime': '提交时间 : {{time}}',
    'task.pendingReview.review': '审核',
    'task.pendingReview.reviewCta': '审核：{{title}}',
    'task.pendingReview.loadError': '加载待审核列表失败',
    'task.approvedList.title': '已通过',
    'task.approvedList.subtitle': '您已核准的任务提交',
    'task.approvedList.empty': '目前没有已通过的记录',
    'task.approvedList.backToTasks': '返回任务管理',
    'task.approvedList.dueDate': '到期日',
    'task.approvedList.submittedAt': '提交时间',
    'task.approvedList.reviewedAt': '通过时间',
    'task.approvedList.view': '查看内容',
    'task.approvedList.viewCta': '查看：{{title}}',
    'task.approvedList.loadError': '加载已通过清单失败',
    // Task Form (Employer)
    'task.form.createTitle': '新增任务',
    'task.form.editTitle': '编辑任务',
    'task.form.titleLabel': '任务标题',
    'task.form.titlePlaceholder': '例如：打扫客厅',
    'task.form.descLabel': '任务描述',
    'task.form.descLabelOptional': '任务描述（选填）',
    'task.form.descPlaceholder': '详细说明任务内容...',
    'task.form.recurrenceLabel': '重复频率',
    'task.form.daysLabel': '重复日期',
    'task.form.daysHint': '选择每周/隔周的执行日',
    'task.form.recurrenceWeekdaysRequired': '每周或隔周任务需至少选择一个重复日期',
    'task.form.occurrenceDateLabel': '发生日期',
    'task.form.startDateLabel': '开始日期',
    'task.form.endDateLabel': '结束日期',
    'task.form.endDateLabelOptional': '结束日期（选填）',
    'task.form.excludeRangesLabel': '排除日期范围',
    'task.form.addExcludeRange': '新增排除范围',
    'task.form.removeExcludeRange': '移除',
    'task.form.excludeStartDateLabel': '排除开始日期',
    'task.form.excludeEndDateLabel': '排除结束日期',
    'task.form.dueTimeLabel': '截止时间',
    'task.form.dueTimeLabelOptional': '截止时间（选填）',
    'task.form.dueTimePlaceholder': '例如 18:00',
    'task.form.requirePhotoLabel': '需要提交照片',
    'task.form.notifyLabel': '完成时通知我',
    'task.form.submit': '保存任务',
    'task.form.submitting': '保存中...',
    'task.form.createSuccess': '任务创建成功',
    'task.form.updateSuccess': '任务更新成功',
    'task.form.createError': '创建任务失败',
    'task.form.updateError': '更新任务失败',
    'task.form.cancel': '取消',
    // Task Review (Employer)
    'task.review.title': '审核任务',
    'task.review.stockTakeDetailTitle': '家里存货详情',
    'task.review.inventoryTaskSummaryTitle': '家里存货任务摘要',
    'task.review.latestHomeStockTitle': '最近的家里存货',
    'task.review.latestHomeStockOpenRecentAria': '打开最近家里存货列表',
    'task.review.latestHomeStockRecentTitle': '最近提交记录',
    'task.review.latestHomeStockTaskDate': '任务日期',
    'task.review.latestHomeStockSubmittedAt': '提交时间',
    'task.review.latestHomeStockStatus': '状态',
    'task.review.latestHomeStockEmptyEmployer': '暂无家务助理提交的家里存货记录',
    'task.review.latestHomeStockEmptyHelper': '暂无家里存货记录',
    'task.review.addHomeStockTaskFab': '新增家里存货任务',
    'task.review.addHomeStockTaskFabAria': '新增家里存货任务',
    'task.review.stocktakePhotosHeading': '家里存货图片',
    'task.review.stocktakeResultsHeading': '家里存货结果',
    'task.review.stockLevelLegendButton': '图例',
    'task.review.stockLevelLegendButtonAria': '显示存量图示图例',
    'task.review.stockLevelLegendPanelAria': '存量图示图例',
    'task.review.stockLevelLegend.full': '满',
    'task.review.stockLevelLegend.half': '半满',
    'task.review.stockLevelLegend.low': '少',
    'task.review.stockLevelLegend.empty': '空',
    'task.review.inventoryPhotosHeading': '家里存货相片',
    'task.review.inventoryLocationSummaryTitle': '按位置的家里存货审核摘要',
    'task.review.inventoryLocationSummaryLoading': '正在加载摘要...',
    'task.review.inventoryLocationSummaryCounts': '{{itemCount}} 项 · {{photoCount}} 张图片',
    'task.review.inventoryLocationSummaryWithItems': '有条目的类别',
    'task.review.inventoryLocationSummaryNoItems': '标记为无条目的类别',
    'task.review.inventoryLocationSummaryNone': '无',
    'task.review.inventoryLocationSummaryEmpty': '暂无位置摘要。',
    'task.review.helperSubmitHomeStock': '提交家里存货',
    'task.review.helperResubmitHomeStock': '重新提交家里存货',
    'task.review.inventoryLineAwaiting': '待送审明细',
    'task.review.inventoryLinePendingBody': '家务助理送审后会显示此条目之存量与相片。',
    'task.review.inventorySharedPhotoNote': '与上方条目共用同一张图片。',
    'task.review.inventoryStatLocationsCaption': '位置',
    'task.review.inventoryStatTotalPhotosCaption': '相片',
    'task.review.inventoryStatLowStockCaption': '低库存',
    'task.review.inventoryStatExpiringCaption': '即将到期',
    'task.review.inventoryFilterCategoriesHeading': '筛选类别',
    'task.review.inventoryLocationPhotosEmpty': '此位置暂无照片。',
    'task.review.inventoryItemsFilteredEmpty': '当前筛选下没有可显示的条目。',
    'task.review.inventoryNoCategoriesInLocation': '此位置没有可筛选的类别。',
    'task.review.inventoryToggleCategoryAria': '筛选类别：{{category}}',
    'task.review.inventoryCategoryEmptyAtLocationBody': '此类别在该位置没有点算到任何项目。',
    'task.review.inventoryExpiryExpired': '已过期',
    'task.review.inventoryExpiryToday': '今天过期',
    'task.review.inventoryExpiryTomorrow': '明天过期',
    'task.review.inventoryExpiryInDays': '{{count}}天后过期',
    'task.review.inventoryExpiryAbsolute': '{{date}} 到期',
    'task.review.inventoryLocationTabsAria': '选择存货位置',
    'task.review.inventoryLocationsSectionHeading': '位置',
    'task.review.inventoryLocationsFilterReminder': '可点击下方标签切换位置，筛选该位置的图片与条目。',
    'task.review.inventoryLocationsCoachmarkDismissAria': '关闭位置筛选提示',
    'task.review.detailTitle': '任务详情',
    'task.review.helperAwaitingEmployerBanner': '任务已提交，需等待雇主审核',
    'task.review.futureDueDetailOnly': '此任务尚未到可提交日期，仅可查看详情。',
    'task.review.futureDueOutOfRange': '此任务超出可预早提交范围，仅可查看详情。',
    'task.review.futureDueEmployerTomorrow': '此任务为明天的任务。',
    'task.review.futureDueEmployerDays': '此任务为{{days}}天后的任务。',
    'task.review.futureDueHelperTomorrow': '此任务为明天的任务，请留意任务日期！',
    'task.review.futureDueHelperDays': '此任务为{{days}}天后的任务，请留意任务日期！',
    'task.todo.futureSubmitBanner': '你可预早提交将来的任务',
    'task.todo.tabFuture': '将来',
    'task.todo.helperEmptyFuture': '此日期范围内没有任务',
    'task.todo.helperEmptyFutureHint': '可调整日期范围，或留意雇主是否稍后安排。',
    'task.submitConfirmTitle': '确认提交此任务？',
    'task.submitConfirmPrompt': '任务日期如下，是否确认提交？',
    'task.submitConfirmAction': '确认提交',
    'task.submitConfirm.dueDateTomorrow': '{{date}} (明天)',
    'task.submitConfirm.dueDateDaysLater': '{{date}} ({{days}}天后)',
    'task.review.taskInfo': '任务信息',
    'task.review.submittedBy': '提交者',
    'task.review.taskDate': '任务日期',
    'task.review.completedAt': '完成时间',
    'task.review.submittedAt': '提交时间',
    'task.review.photos': '照片',
    'task.review.prevPhoto': '上一张',
    'task.review.nextPhoto': '下一张',
    'task.review.photoCount': '{{current}} / {{total}}',
    'task.review.gpsLocation': 'GPS 位置',
    'task.review.gpsDisabled': 'GPS 未启用',
    'task.review.gpsStaticMapAlt': '任务提交位置（地图预览）',
    'task.review.openInGoogleMaps': '在 Google 地图打开',
    'task.review.openInGoogleMapsAria': '在新标签页于 Google 地图打开此位置',
    'task.review.osmCopyright': '© OpenStreetMap 贡献者',
    'task.review.addressLabel': '地址',
    'task.review.addressLoading': '正在查询地址…',
    'task.review.addressUnavailable': '无法获取此坐标的文字地址',
    'task.review.note': '备注',
    'task.review.noNote': '无备注',
    'task.review.reviewPrompt': '请审核↓',
    'task.review.approve': '通过',
    'task.review.reject': '拒绝',
    'task.review.needsUpdate': '需修改',
    'task.review.needsUpdateModalTitle': '标记为需修改',
    'task.review.needsUpdateReasonPrompt': '请输入需修改原因',
    'task.review.needsUpdateReasonTooShort': '原因长度不足，至少需要 {{min}} 个字符',
    'task.review.needsUpdatePhotoHint':
      '家务助理重新提交时可删除错误照片并上传正确照片。',
    'task.review.reviewSuccess': '审核完成',
    'task.review.reviewError': '审核失败',
    'task.review.versionConflict': '数据已被修改，请重新加载',
    'task.review.loadError': '加载任务详情失败',
    // Comments
    'comment.sectionTitle': '留言',
    'comment.usage': '已使用 {{used}} / {{max}} 条留言',
    'comment.charCount': '{{current}} / {{max}} 字符',
    'comment.placeholder': '输入留言…',
    'comment.writeButton': '输入留言',
    'comment.modalTitle': '输入留言',
    'comment.draftPending': '草稿',
    'comment.send': '发送',
    'comment.sending': '发送中…',
    'comment.loadError': '加载留言失败',
    'comment.sendError': '发送留言失败',
    'comment.empty': '尚无留言',
    'comment.limitReached': '此项目的留言已达上限。',
    'comment.sendAria': '发送留言',
    'comment.listAria': '留言列表',
    'comment.emptyDraft': '请输入留言内容',
    'comment.tooLong': '留言不可超过 {{max}} 字符',
    'comment.shortcutHint': '可使用 Ctrl+Enter 或 Cmd+Enter 发送',
    'comment.unsentSubmit.title': '尚未发出留言',
    'comment.unsentSubmit.descriptionTask':
      '您已输入留言但尚未按「发送」。是否要一并提交任务并发出此留言？',
    'comment.unsentSubmit.descriptionStockTake':
      '您已输入留言但尚未按「发送」。是否要一并提交家里存货并发出此留言？',
    'comment.unsentSubmit.descriptionResubmit':
      '您已输入留言但尚未按「发送」。是否要一并重新提交任务并发出此留言？',
    'comment.unsentSubmit.backToEdit': '返回编辑',
    'comment.unsentSubmit.submitOnlyTask': '仅提交任务',
    'comment.unsentSubmit.submitOnlyStockTake': '仅提交家里存货',
    'comment.unsentSubmit.submitOnlyResubmit': '仅重新提交',
    'comment.unsentSubmit.submitWithComment': '一并提交并发出留言',
    'comment.unsentSubmit.working': '处理中…',
    // Notifications
    'notification.bellAria': '通知',
    'notification.panelTitle': '通知',
    'notification.empty': '没有通知',
    'notification.markAllRead': '全部标为已读',
    'notification.markAllReadAria': '将所有通知标为已读',
    'notification.loadError': '加载通知失败',
    'notification.expand': '展开全文▼',
    'notification.collapse': '收合▲',
    'notification.expandAria': '展开通知全文',
    'notification.collapseAria': '收合通知全文',
    // Today's Todo (Helper)
    'task.today.title': '任务列表',
    'task.today.subtitle': '按分栏查看历史、逾期、今日与将来任务',
    'task.today.empty': '今日没有待办任务',
    'task.today.emptyHint': '好好休息吧！',
    'task.todo.helperEmptyOverdue': '目前没有逾期未交任务',
    'task.todo.helperEmptyOverdueHint': '请继续在截止时间前完成待办。',
    'task.todo.helperEmptyTomorrow': '明日尚无安排任务',
    'task.todo.helperEmptyTomorrowHint': '如有临时安排，请留意雇主通知。',
    'task.today.complete': '完成',
    'task.today.view': '查看',
    'task.today.pendingReview': '{{count}} 个任务待雇主审核',
    'task.today.sectionNotSubmitted': '未提交',
    'task.today.sectionNeedsUpdate': '需修改',
    'task.today.sectionCompleted': '已完成',
    'task.today.employerFeedbackLabel': '雇主留言：',
    'task.today.dueAt': '截止 {{time}}',
    'task.today.cannotSubmitTomorrow': '明日才可提交',
    'task.today.tomorrowSubmitBanner': '明日才可提交以下的任务',
    'task.todo.tabHistory': '历史',
    'task.todo.tabOverdue': '逾期',
    'task.todo.tabToday': '今日',
    'task.todo.tabTomorrow': '明日',
    'task.todo.deadlineToday': '今日',
    'task.todo.deadlineTomorrowShort': '明日',
    'task.todo.inventoryPreviewLocationsLabel': '位置',
    'task.todo.inventoryPreviewCategoriesLabel': '类别',
    'task.todo.receiptScanBanner': '扫描收据，快速记账',
    'task.history.startDate': '开始日期',
    'task.history.endDate': '结束日期',
    'task.history.filterAria': '按任务状态筛选历史记录',
    'task.history.statusAll': '全部',
    'task.history.empty': '此日期范围内没有符合筛选的任务记录',
    'task.history.emptyHint': '请调整日期范围或切换状态筛选',
    'task.history.relativeToday': '今天',
    'task.history.relativeYesterday': '昨天',
    'task.history.relativeTomorrow': '明天',
    'task.history.dateHeadingWithRelative': '{{date}} ({{relative}})',
    'task.history.groupTotalCount': '{{count}} 笔',
    'task.history.completionTime': '完成时间 : {{time}}',
    'task.employerFuture.title': '将来任务',
    'task.employerFuture.empty': '此日期范围内没有任务',
    'task.employerFuture.loadError': '加载将来任务失败',
    'task.employerFuture.openDetailCta': '查看任务：{{title}}',
    'task.today.loadError': '加载待办清单失败',
    'receipt.analyze.navLink': '收据 AI 分析',
    'receipt.analyze.title': '收据 AI 分析',
    'receipt.analyze.subtitle': '选择收据照片，系统将自动提取字段（可手动修改）',
    'receipt.analyze.pickImages': '上传收据图片（每批最多 10 张）',
    'receipt.analyze.pickImagesAria': '选择收据图片文件',
    'receipt.analyze.analyzing': '分析中…',
    'receipt.analyze.analyzingProgress': '分析中 {{current}} / {{total}}',
    'receipt.analyze.analyzeError': '分析失败',
    'receipt.analyze.rateLimit': '已达每日 AI 分析上限（{{used}} / {{limit}}）。可于 {{retryAt}} 后再试。',
    'receipt.analyze.manualHint': '分析失败，请手动输入或修正字段。',
    'receipt.analyze.fileTooLarge': '文件超过 10MB',
    'receipt.analyze.badType': '不支持的图片格式',
    'receipt.analyze.tooManyFiles': '最多 10 张图片',
    'receipt.analyze.field.company': '公司 / 品牌',
    'receipt.analyze.field.shop': '分店',
    'receipt.analyze.field.date': '日期',
    'receipt.analyze.field.currency': '货币',
    'receipt.analyze.field.total': '总金额',
    'receipt.analyze.field.remark': '备注',
    'receipt.analyze.aiRemarkLabel': '系统备注',
    'receipt.analyze.items': '明细',
    'receipt.analyze.itemsAction': '操作',
    'receipt.analyze.itemName': '项目',
    'receipt.analyze.qty': '数量',
    'receipt.analyze.amount': '金额',
    'receipt.analyze.addItem': '新增一行',
    'receipt.analyze.removeItem': '移除',
    'receipt.analyze.removeRow': '移除此笔',
    'receipt.analyze.removeConfirmTitle': '确认移除此笔？',
    'receipt.analyze.removeConfirmBody': '移除后此笔分析结果将不会保留。',
    'receipt.analyze.leaveConfirmTitle': '离开此页？',
    'receipt.analyze.leaveConfirmBody': '你有尚未提交的收据照片与资料，离开后将不会保留。',
    'receipt.analyze.leaveConfirmLeave': '离开',
    'receipt.analyze.leaveConfirmStay': '留在此页',
    'receipt.analyze.budgetLabel': '收到的钱周期',
    'receipt.analyze.budgetRequired': '请选择收到的钱周期',
    'receipt.analyze.dateLabel': '收据日期',
    'receipt.analyze.dateRequired': '请选择有效收据日期',
    'receipt.analyze.companyRequired': '请输入公司 / 品牌',
    'receipt.analyze.submit': '保存',
    'receipt.analyze.submitAll': '保存所有收据',
    'receipt.analyze.submitting': '提交中…',
    'receipt.analyze.submittingAllProgress': '保存中 {{current}} / {{total}}',
    'receipt.analyze.submitSuccess': '已提交',
    'receipt.analyze.submittedDueToPendingSettlement':
      '此收据日期已在待审核结算期内，已直接标记为「已提交结算」。',
    'receipt.analyze.submitError': '提交失败',
    'receipt.analyze.needImageUrl': '缺少已保存的图片链接，无法提交（请确认云端存储已配置）',
    'receipt.analyze.invalidTotal': '请输入有效总金额',
    'receipt.analyze.totalMismatchBanner': '总金额({{total}})与明细的金额({{lineSum}})不相同，请检查',
    'receipt.analyze.totalMismatchConfirmTitle': '金额不一致',
    'receipt.analyze.totalMismatchConfirmSingle':
      '收据总金额({{total}})与明细的金额({{lineSum}})不相同，请检查。结算是以总金额({{total}})为准，请确认。',
    'receipt.analyze.totalMismatchConfirmBatchLine':
      '{{date}} {{company}} 收据的总金额({{total}})与明细的金额({{lineSum}})不相同，请检查。',
    'receipt.analyze.totalMismatchConfirmBatchIntro': '以下收据总金额与明细金额不符：',
    'receipt.analyze.totalMismatchConfirmBatchFooter': '结算是以总金额为准，请确认。',
    'receipt.analyze.totalMismatchConfirmConfirm': '确认保存',
    'receipt.analyze.backToday': '返回今日待办',
    'receipt.detail.title': '收据详情',
    'receipt.detail.backToList': '返回收据列表',
    'receipt.detail.loadError': '无法加载收据',
    'receipt.detail.total': '总金额',
    'receipt.detail.date': '收据日期',
    'receipt.detail.submittedAt': '提交日期',
    'receipt.detail.employerReviewSubmittedAt': '雇主审核提交时间',
    'receipt.detail.delete': '删除收据',
    'receipt.detail.deleteHint': '仅可删除待审核、需修改或已取消的收据。',
    'receipt.detail.deleteConfirmTitle': '删除此收据？',
    'receipt.detail.deleteConfirmBody': '删除后无法恢复，相关留言亦会一并移除。',
    'receipt.detail.deleting': '删除中…',
    'receipt.detail.cannotDelete': '此收据状态不允许删除。',
    'receipt.detail.photoHeading': '收据照片',
    'receipt.detail.photoAlt': '收据照片',
    'receipt.detail.employerSavedReceiptNotice': '此日期未结算，收据亦未正式提交；家务助理仍可修改收据内容。',
    'receipt.detail.helperUnsettledBanner': '此收据尚未包含在结算中',
    'receipt.detail.helperUnsettledBannerCta': '进行结算',
    'receipt.detail.helperUnsettledBannerCtaAria': '前往开支与结算页进行结算',
    'receipt.detail.editReceipt': '修改收据内容',
    'receipt.detail.changeReviewStatus': '修改状态',
    'receipt.detail.changeReviewStatusAria': '打开收据审核状态修改窗口',
    'receipt.detail.changeReviewStatusTitle': '修改收据审核状态',
    'receipt.detail.changeReviewStatusBody': '请选择新的审核结果。',
    'receipt.edit.title': '修改收据',
    'receipt.edit.subtitle': '仅可修改「未结算」收据；提交结算后不可再修改。',
    'receipt.edit.backToDetail': '返回收据详情',
    'receipt.edit.save': '保存修改',
    'receipt.edit.saving': '保存中…',
    'receipt.edit.saveError': '保存失败',
    'receipt.edit.blockedStatus': '此收据状态不允许修改。仅由你提交且状态为「未结算」的收据可编辑。',
    'receipt.edit.blockedNotOwner': '你只能修改自己提交的收据。',
    'receipt.analyze.photoHeading': '收据照片',
    'receipt.analyze.photoHeadingOfTotal': '收据照片（{{current}}／{{total}}）',
    'receipt.analyze.photoAlt': '收据照片',
    'receipt.analyze.enlargeImageAria': '放大查看收据照片',
    'receipt.analyze.openDetailAria': '打开收据详情',
    'receipt.analyze.scanMore': '扫描更多收据',
    'receipt.analyze.nextStepSheetTitle': '收据已保存',
    'receipt.analyze.nextStepSheetLead': '下一步：提交结算给雇主。',
    'receipt.analyze.nextStepSheetUploadAll': '请先上传以下结算期内的全部收据，再进行结算。',
    'receipt.analyze.nextStepSheetLockNote':
      '提交结算后，该期间内的收据会送交雇主；该期间日期不可再上传或修改收据。',
    'receipt.analyze.nextStepSheetEmployerReview': '须提交结算后，雇主才会审核。',
    'receipt.analyze.nextStepSheetPeriodLabel': '对应结算期',
    'receipt.analyze.nextStepSheetPeriodRange': '{{start}} – {{end}}',
    'receipt.analyze.nextStepSheetPeriodLoading': '正在加载结算期…',
    'receipt.analyze.nextStepSheetPeriodUnknown':
      '尚无对应的「收到的钱」期间；仍可稍后到「开支与结算」选期间并提交。',
    'receipt.analyze.nextStepSheetPeriodLocked': '已锁定',
    'receipt.analyze.nextStepSheetLockedHint':
      '对应期间已有进行中或已锁定的结算，请到开支页查看详情。',
    'receipt.analyze.nextStepSheetGoSettle': '去结算',
    'receipt.analyze.nextStepSheetGoSettleAria': '前往开支与结算页提交结算',
    'receipt.analyze.nextStepSheetLater': '稍后再说',
    'receipt.analyze.nextStepSheetLaterAria': '关闭提醒，稍后再提交结算',
    'receipt.list.title': '收据列表',
    'receipt.list.subtitle': '查看收据状态与提交时间',
    'receipt.list.filterAria': '按收据状态筛选收据',
    'receipt.list.filter.all': '全部',
    'receipt.list.filter.save': '未结算',
    'receipt.list.filter.submitted': '已提交结算',
    'receipt.list.filter.reviewed': '已审核',
    'receipt.list.startDate': '开始日期（可选）',
    'receipt.list.endDate': '结束日期（可选）',
    'receipt.list.requiredStartDate': '开始日期',
    'receipt.list.requiredEndDate': '结束日期',
    'receipt.list.empty': '目前没有符合条件的收据。',
    'receipt.list.loadError': '无法加载收据列表',
    'receipt.list.loadMore': '加载更多',
    'receipt.list.openDetailAria': '打开 {{date}} 收据：{{summary}}',
    'receipt.list.submittedAtLine': '提交日期：{{at}}',
    // Budget (employer — Screen 2.4)
    'budget.title': '已给的钱',
    'budget.subtitle': '建立雇主给出金额（主周期与追加金钱），供家务助理收据与结算使用',
    'budget.tabActive': '进行中',
    'budget.tabSettled': '已结算',
    'budget.tabsAria': '已给的钱列表分类',
    'budget.addNew': '新增已给的钱',
    'budget.addNewAria': '新增已给的钱期间',
    'budget.addOn': '追加',
    'budget.addOnAria': '为此周期追加金钱',
    'budget.form.addOnCreateTitle': '追加金钱',
    'budget.form.parentPeriod': '所属主周期',
    'budget.empty': '暂无资料',
    'budget.emptySettled': '目前尚无已结算的已给的钱。',
    'budget.emptyHint': '请新增一笔已给的钱，家务助理方可选择周期并提交收据。',
    'budget.filterStartDate': '开始日期',
    'budget.filterEndDate': '结束日期',
    'budget.totalAmount': '已给的钱总额',
    'budget.mainAmountLabel': '已给的钱',
    'budget.subAmountLabel': '追加金额',
    'budget.spent': '已使用',
    'budget.remaining': '剩余',
    'budget.badgeLocked': '已有锁定结算',
    'budget.badgeSettled': '已结算',
    'budget.timeLabel': '时间:{{value}}',
    'budget.edit': '编辑',
    'budget.editAria': '编辑已给的钱 {{start}} 至 {{end}}',
    'budget.mergedChildCount': '包含 {{count}} 笔追加金钱',
    'budget.expandChildrenAria': '展开 {{start}} 至 {{end}} 的追加金钱明细',
    'budget.collapseChildrenAria': '收起 {{start}} 至 {{end}} 的追加金钱明细',
    'budget.backList': '返回已给的钱列表',
    'budget.loadError': '无法加载已给的钱',
    'budget.saveError': '保存失败',
    'budget.form.createTitle': '新增已给的钱',
    'budget.form.editTitle': '编辑已给的钱周期',
    'budget.form.startDate': '开始日期',
    'budget.form.endDate': '结束日期',
    'budget.form.amount': '已给的钱金额',
    'budget.form.amountHint': '须为零或正数；可含小数。',
    'budget.form.submitCreate': '建立已给的钱',
    'budget.form.submitEdit': '保存变更',
    'budget.form.saving': '保存中…',
    'budget.form.cancel': '取消',
    'budget.form.remove': '移除',
    'budget.form.removeAria': '移除此已给的钱周期',
    'budget.form.removeConfirm': '确认移除',
    'budget.form.removing': '移除中…',
    'budget.removeConfirmTitle': '移除此已给的钱？',
    'budget.removeConfirmBody':
      '确定要移除此已给的钱周期吗？此操作无法还原。',
    'budget.removeConfirmReceiptsExtra': '当前有 {{count}} 笔收据绑定此已给的钱；移除后上述绑定会一并解除。',
    'budget.removeError': '无法移除已给的钱',
    'budget.validation.rangeRequired': '请填写开始与结束日期',
    'budget.validation.dates': '结束日期不可早于开始日期',
    'budget.validation.amount': '请输入有效金额（零或正数）',
    'budget.lockedEditHint':
      '此已给的钱曾用于已锁定的结算。若缩短日期，所有关联收据的日期仍须完全落在新区间内；若有收据落在外面，或与其他结算日期重叠，保存会失败。必要时请雇主先处理结算／收据后再改。',
    'budget.summaryReceipts': '此已给的钱已关联 {{count}} 笔收据',
    // Settlement
    'settlement.title': '结算与锁定',
    'settlement.employerFinanceTitle': '已给的钱与结算',
    'settlement.employerFinanceTitleMenuAria': '已给的钱与结算菜单',
    'settlement.employerFinanceMenuPendingReviewList': '待审核结算列表',
    'settlement.employerFinanceMenuReceiptList': '收据列表',
    'settlement.formTitle': '新增结算',
    'settlement.subtitle':
      '选择周期；系统会加总该段内收据支出，并与对应的收到的钱金额比较。确认后该范围内收据将锁定。',
    'settlement.navLink': '开支结算',
    'settlement.budgetLabel': '收到的钱周期',
    'settlement.startDate': '开始日期',
    'settlement.endDate': '结束日期',
    'settlement.preview': '预览金额',
    'settlement.previewing': '计算中…',
    'settlement.totalSpent': '支出总额',
    'settlement.dailyDetail': '拆分',
    'settlement.dailyDetailOpen': '打开每日开支汇总',
    'settlement.dailyDetailTitle': '每日开支汇总',
    'settlement.dailyDetailDate': '日期',
    'settlement.dailyDetailReceiptCount': '收据数量',
    'settlement.dailyDetailAmount': '金额',
    'settlement.budgetAmount': '已给的钱',
    'settlement.balance': '结算结果',
    'settlement.surplus': '净余',
    'settlement.overspent': '超支',
    'settlement.confirm': '确认结算并锁定',
    'settlement.confirming': '处理中…',
    'settlement.success': '结算已锁定',
    'settlement.error': '操作失败',
    'settlement.listTitle': '结算记录',
    'settlement.employerFinanceTabsAria': '雇主已给的钱与结算分页',
    'settlement.employerTabBudget': '已给的钱',
    'settlement.employerTabSettlement': '结算',
    'settlement.employerSubTabReceipts': '收据列表',
    'settlement.filterStartDate': '开始日期',
    'settlement.filterEndDate': '结束日期',
    'settlement.employerFilterToDateOptional': '结束日期（可选）',
    'settlement.empty': '尚无结算记录',
    'settlement.locked': '已审核',
    'settlement.unlocked': '已解锁',
    'settlement.status.submitted': '待审核',
    'settlement.status.unsettled': '未结算',
    'settlement.status.settled': '已结算',
    'settlement.unsettledEmployerSavedReceiptNotice':
      '家务助理未提交此日期的结算，收据亦未正式提交；家务助理仍可修改收据内容。',
    'settlement.givenVsExpensesTitle': '已给的钱 vs 支出',
    'settlement.givenVsExpensesSubtitle': '最近 4 次已给的钱期间概览',
    'settlement.givenVsExpensesGiven': '已给的钱',
    'settlement.givenVsExpensesSpent': '支出',
    'settlement.givenVsExpensesPendingSpent': '待审核支出',
    'settlement.helperReceivedVsExpensesTitle': '收到的钱 vs 支出',
    'settlement.helperReceivedVsExpensesSubtitle': '最近 4 次收到的钱期间概览',
    'settlement.reviewNow': '立即审核',
    'settlement.pendingReviewPageTitle': '待审核结算',
    'settlement.historyEventAtLabel': '状态时间',
    'settlement.historyViewDetailAria': '查看结算记录详情',
    'settlement.historyDetailButton': '详情',
    'settlement.pastSettlementHistoryTitle': '过去的结算记录',
    'settlement.historyStatus.withdrawn': '家务助理已撤回',
    'settlement.historyStatus.rejected': '雇主已拒绝',
    'settlement.historyStatus.unlocked': '雇主已解锁',
    'settlement.status.reviewed': '已审核',
    'settlement.status.rejected': '已拒绝',
    'settlement.status.withdrawn': '已撤回',
    'settlement.recordStatusFilterAria': '按结算状态筛选',
    'settlement.recordStatusFilter.submitted': '待审核',
    'settlement.recordStatusFilter.reviewed': '已审核',
    'settlement.recordStatusFilter.rejected': '已拒绝',
    'settlement.recordStatusFilter.withdrawn': '已撤回',
    'settlement.recordStatusFilter.unlocked': '已解锁',
    'settlement.lockHint':
      '此段日期的开支已完成结算并锁定；须请雇主在 App 内解除该笔结算的锁定后，您才能修改或删除该期间的收据。',
    'settlement.detailTitle': '结算详情',
    'settlement.detailTitleMenuAria': '结算详情菜单',
    'settlement.menuEditMoneyGiven': '修改已给的钱',
    'settlement.menuAppendMoneyGiven': '追加已给的钱',
    'settlement.submittedAtLabel': '提交时间',
    'settlement.unlock': '解锁此结算',
    'settlement.withdraw': '撤回此结算提交',
    'settlement.reviewedAction': '标记为已审核',
    'settlement.rejectReasonPrompt': '请输入拒绝理由',
    'settlement.rejectModalTitle': '拒绝此结算',
    'settlement.unlockConfirmTitle': '确认解锁？',
    'settlement.unlockConfirmBody': '解锁后家务助理可修改此日期范围内的收据。此操作会记录于审计日志。',
    'settlement.unlocking': '解锁中…',
    'settlement.unlockSuccess': '已解锁',
    'settlement.auditTitle': '解锁记录',
    'settlement.auditEmpty': '尚无解锁记录',
    'settlement.receiptsTitle': '范围内收据',
    'settlement.viewReceipt': '查看收据',
    'settlement.noBudgets':
      '无需预先建立已给的钱也可上传收据与结算；若所选周期内尚无设置，收到的钱金额将视为 0。',
    'settlement.employerNoBudgetsBanner':
      '尚未建立任何已给的钱周期。仍可结算（金额以对应周期计）；建议到「已给的钱管理」新增资料以便对照。',
    'settlement.selectRange': '请选择开始与结束日期',
    'settlement.confirmLockModalTitle': '提交此结算给雇主？',
    'settlement.confirmEmptyModalTitle': '此期间没有收据',
    'settlement.confirmLockPrompt':
      '确认提交 {{start}} - {{end}} 结算吗？此范围内收据不可编辑及上传。',
    'settlement.confirmEmptyReceiptsPrompt': '此日期范围内没有任何收据。仍要提交吗？',
    'settlement.previewFirst': '预览尚未就绪，请待计算完成或检查日期后再锁定。',
    'settlement.budgetAmountHint': '包含主周期与所有追加金钱的金额加总。',
    'settlement.helperHeroTitle': '开支与结算',
    'settlement.helperHeroSubtitle': '管理你的开支，并提交结算予雇主。',
    'settlement.helpPopoverTriggerAria': '查看结算步骤与注意事项',
    'settlement.helpPopoverAria': '结算步骤与注意事项',
    'settlement.helpPopoverTitle': '如何提交结算',
    'settlement.helpPopoverStep1': '扫描并保存该结算期内的全部收据。',
    'settlement.helpPopoverStep2': '在本页选择「收到的钱」期间并预览开支。',
    'settlement.helpPopoverStep3': '按「进行结算」提交给雇主审核。',
    'settlement.helpPopoverNotesHeading': '注意',
    'settlement.helpPopoverNote1': '保存收据后，须提交结算，雇主才会审核。',
    'settlement.helpPopoverNote2':
      '结算提交后，该期间日期不可再上传或修改收据，直至雇主拒绝、解锁或您撤回。',
    'settlement.helperQuickActions': '快速操作',
    'settlement.helperActionScanTitle': '扫描收据',
    'settlement.helperActionScanMeta': 'AI 分析并提交',
    'settlement.helperActionReceiptListTitle': '收据列表',
    'settlement.helperActionReceiptListMeta': '查看已提交与待批核收据',
    'settlement.helperActionLockTitle': '进行结算',
    'settlement.helperActionLockMeta': '锁定所选期间并提交予雇主',
    'settlement.helperActionHistoryTitle': '结算记录',
    'settlement.helperActionHistoryMeta': '查看过往结算记录',
    'settlement.helperDateRangeAria': '所选结算期间，展开以变更日期',
    'settlement.helperStatPeriodBudget': '收到的钱',
    'settlement.moneyReceivedDetailOpen': '打开收到的钱明细',
    'settlement.moneyReceivedDetailTitle': '收到的钱明细',
    'settlement.moneyReceivedDetailReceivedDate': '时间',
    'settlement.moneyReceivedDetailPeriod': '期间',
    'settlement.moneyReceivedDetailAmount': 'Amount',
    'settlement.moneyReceivedDetailAddOn': 'Add-on',
    'settlement.helperStatTotalSpent': '总支出',
    'settlement.helperStatBalance': '净余',
    'settlement.dailyBreakdownVsBudgetTitle': '每日支出',
    'settlement.dailyBudgetAmount': '每日收到的钱拆分',
    'settlement.dailyVsBudgetColBudget': '收到的钱',
    'settlement.dailyVsBudgetColExpense': '支出',
    'settlement.dailyBudgetVariance': '差额',
    'settlement.expensePreview': '开支预览',
    'settlement.breakdownModalTitle': '每日开支明细',
    'settlement.openBreakdownAria': '打开 Breakdown，查看每日收据笔数与金额',
    'settlement.helperConfirmDateRangeLabel': '结算日期',
    'settlement.helperConfirmDateRangeSeparator': '至',
    'settlement.helperConfirmLockNotice': '结算后将会锁定此期间的开支记录，需雇主解除才可再次修改。',
    'settlement.helperConfirmPrimary': '提交',
    'settlement.helperSettleButton': '进行结算',
    'settlement.confirmAction': '确认',
    'settlement.helperReviewedByEmployerNotice': '雇主已审核结算。',
    'settlement.helperSubmittedNotice': '此日期范围已有已提交结算，等待雇主审核。',
    'settlement.helperSubmittedRangeNotice': '此日期范围已有已提交结算（{{range}}），等待雇主审核。',
    'settlement.helperSubmittedRangesNotice': '此日期范围已有已提交结算（{{ranges}}），等待雇主审核。',
    'settlement.helperSubmittedAndReviewedRangesNotice':
      '此日期范围已有已提交/已审核结算（{{ranges}}）。其中已提交项目仍在等待雇主审核。',
    'settlement.confirmOverlapSubmittedRangesError':
      '你选择的日期与以下已提交结算重叠（{{ranges}}）。请先撤回或等待雇主审核后再提交重叠日期。',
    'settlement.confirmOverlapLockedRangesError':
      '你选择的日期与以下已结算锁定期间重叠（{{ranges}}）。请雇主先解锁后再提交重叠日期。',
    'settlement.helperConfirmPageTitle': '进行结算',
    'settlement.helperConfirmSubtitle': '核对所选期间的预览金额后，锁定并提交予雇主。',
    'settlement.helperHistoryPageTitle': '结算记录',
    'settlement.helperHistoryTabBySettlement': '按结算',
    'settlement.helperHistoryTabByMoneyReceived': '按收到的钱',
    'settlement.helperHistoryViewTabsAria': '结算记录查看方式',
    'settlement.helperResubmitBannerPrefix': '此日期仍未提交最新结算，按',
    'settlement.helperResubmitBannerLink': '此',
    'settlement.helperResubmitBannerSuffix': '提交',
    'settlement.helperResubmitBannerLinkAria': '前往同意并结算',
    'settlement.helperBackToExpenses': '返回开支与结算',
    'settlement.totalSpentReceiptHint': '含已保存、已提交及已审核收据。',
    'settlement.period': '{{start}} 至 {{end}}',
    'settlement.backList': '返回结算列表',
    'apiError.settlementLockedReceipt':
      '此收据日期（{{receiptDate}}）已落在「已结算并锁定」的期间（{{start}} 至 {{end}}）。请雇主先在 App 内解除该笔结算的锁定，之后您才能新增、修改或删除该段期间的收据。',
    'apiError.settlementRangeOverlapsLocked':
      '您选择的日期（{{reqStart}} 至 {{reqEnd}}）与已结算并锁定的期间（{{start}} 至 {{end}}）重叠。请雇主先解除该笔结算的锁定，或改选不重叠的日期后再试。',
    'apiError.settlementNoReceiptsAck':
      '此日期范围内没有任何收据。若仍要锁定空期间，请在画面上确认（勾选对应选项）后再操作。',
    'apiError.budgetOverlapsSettlement':
      '此已给的钱周期与既有结算所涵盖的日期重叠。请改选完全落在「任何结算期间以外」的开始与结束日期。',
    'apiError.budgetShrinkReceiptsOutside':
      '无法缩短到这组日期，因为仍有收据的日期落在新范围之外。请拉宽已给的钱日期，或先处理那些收据后再保存。',
    'apiError.budgetDeleteLocked': '此已给的钱已有锁定结算，无法移除。请先处理结算后再试。',
    'apiError.budgetMainOverlap': '此已给的钱周期与其他周期重叠（{{ranges}}），请改用不重叠日期。',
    'apiError.subBudgetOutOfMainRange': '追加金钱周期必须完全落在所属主周期内。',
    'apiError.mainBudgetLockedBySubmittedSettlement':
      '此已给的钱周期已有已提交结算，目前只可查看，不能修改或删除。',
    'apiError.receiptDeleteStatusNotAllowed':
      '此收据目前状态无法删除。仅限「等待雇主审核」「雇主要求修改」或「已取消」的收据可以删除。',
    'apiError.receiptUpdateStatusNotAllowed':
      '此收据目前无法编辑。仅限「等待雇主审核」或「雇主要求修改」时可以更改内容。',
    'apiError.settlementAlreadyUnlocked': '此笔结算已经解除锁定。请刷新页面以获取最新状态。',
    // Auto-translation (user-entered content)
    'translation.autoTranslatedTo': '已自动翻译至{{language}}。翻译可能出错',
    'translation.showOriginal': '显示原文',
    'translation.showTranslated': '显示译文',
    'translation.hintButtonAria': '翻译说明',
    'translation.hintPanelAria': '自动翻译提示',
    'translation.lang.zhHant': '繁体中文',
    'translation.lang.zhHans': '简体中文',
    'translation.lang.en': '英文',
    'translation.lang.id': '印尼文',
    'translation.lang.fil': '菲律宾文',
    // Family members
    'family.navLink': '家庭成员',
    'family.title': '家庭成员的偏好',
    'family.subtitle': '雇主可新增或编辑；双方可查看。',
    'family.listConsentLabel': '本人确认已取得家人同意分享其资料。',
    'family.listConsentHint': '请勾选以上方格以查看或管理家庭成员资料。',
    'family.addMember': '新增成员',
    'family.addMemberAria': '新增家庭成员',
    'family.editMember': '编辑',
    'family.editMemberAria': '编辑家庭成员',
    'family.deleteMember': '移除',
    'family.deleteConfirm': '确定要移除此家庭成员吗？此操作无法还原。',
    'family.empty': '尚无家庭成员资料',
    'family.emptyHint': '点击「新增成员」加入。',
    'family.emptyHintHelper': '家庭成员须由雇主新增；如有需要，请联系雇主。',
    'family.field.name': '姓名',
    'family.field.nickname': '别称',
    'family.formField.nickname': '别称（选填）',
    'family.formField.birthday': '生日（选填）',
    'family.formField.remark': '常规活动（选填）',
    'family.formField.foodPreference': '食物偏好（选填）',
    'family.formField.foodAllergy': '食物敏感／过敏（选填）',
    'family.formField.medicalCondition': '健康／医疗状况（选填）',
    'family.formField.hashtags': '标签（选填）',
    'family.placeholder.name': '例如：陈大文',
    'family.placeholder.nickname': '例如 : 哥哥',
    'family.placeholder.foodPreference': '例如：喜欢清淡、爱吃苹果',
    'family.placeholder.foodAllergy': '例如：花生',
    'family.placeholder.medicalCondition': '例如：血压偏高，需定时服药',
    'family.placeholder.hashtags': '例如：饮食宜清淡、低盐',
    'family.field.birthday': '生日',
    'family.field.age': '年龄',
    'family.field.foodPreference': '食物偏好',
    'family.field.foodAllergy': '食物敏感／过敏',
    'family.field.medicalCondition': '健康／医疗状况',
    'family.field.remark': '常规活动',
    'family.field.remarkPlaceholder': '例如：足球课程 - 星期六早上10点',
    'family.field.hashtags': '标签',
    'family.field.hashtagsHint': '可输入多个，以逗号、分号或换行分隔。',
    'family.birthday.optionalHint': '可留空；可不填年份，仅选月与日。',
    'family.birthday.exampleHint': '例如：选 2011 年、03 月、15 日；或仅选月与日（年份留空）。',
    'family.birthday.yearOptional': '年份（选填）',
    'family.birthday.month': '月',
    'family.birthday.day': '日',
    'family.display.birthdaySkipped': '未提供生日',
    'family.display.ageWithYear': '{{age}} 岁（{{year}} 年出生）',
    'family.display.birthdayMonthDay': '{{month}} 月 {{day}} 日',
    'family.validation.birthdayNeedMonthDay': '若要填写生日，请同时选择月与日。',
    'family.validation.birthdayInvalid': '生日日期无效，请检查年月日。',
    'family.formConsentLabel': '本人确认已取得该家人同意分享其资料。',
    'family.formCreateTitle': '新增家庭成员',
    'family.formEditTitle': '编辑家庭成员',
    'family.formSubmit': '保存',
    'family.formSubmitting': '保存中…',
    'family.formCancel': '取消',
    'family.loadError': '无法加载家庭成员资料',
    'family.saveError': '保存失败',
    'family.deleteError': '移除失败',
    'family.validation.consentRequired': '请勾选确认已取得家人同意。',
    // Inventory / 盘点（家务助理）
    'inventory.title': '家里存货任务',
    'inventory.subtitle': '先批量拍照（一张可含多条目），AI 比对清单并带入存量；再补拍或手动确认后送审',
    'inventory.stocktakePanelTitle': '盘点拍照',
    'inventory.stocktakePanelHint': '一张相片可含多个条目。',
    'inventory.stocktakeMaxFilesHint': '单次最多 10 张',
    'inventory.uploadedStocktakePhotosHeading': '已上传的家里存货照片',
    'inventory.uploadedStocktakePhotoAlt': '家里存货照片 {{n}}',
    'inventory.enlargePhotoAria': '放大查看家里存货照片',
    'inventory.removePhotoAria': '移除此家里存货照片',
    'inventory.removeStocktakePhotoTitle': '移除此照片？',
    'inventory.removeStocktakePhotoDescription':
      '未提交前可移除。若条目曾使用此图的识别结果，将一并清除，请再拍摄或手动填写。',
    'inventory.removeStocktakePhotoConfirm': '移除',
    'inventory.notRecognizedByAi': '上传相片后，以下条目仍未被 AI 在照片中识别；请补拍或使用同一批相片手动填写存量。',
    'inventory.matchedByAiHint': '已由 AI 带入（可修改）',
    'inventory.stockLevel': '存量状态',
    'inventory.stockLevelRequired': '请为每个条目选择存量状态。',
    'inventory.level.full': '满',
    'inventory.level.half': '半满',
    'inventory.level.low': '低',
    'inventory.level.empty': '空',
    'inventory.level.unknown': '—',
    'inventory.photoLabel': '条目照片',
    'inventory.analyze': 'AI 分析',
    'inventory.analyzing': '分析中…',
    'inventory.stocktakeUploading': '上传中…',
    'inventory.stocktakeUploadingProgress': '上传中 {{current}} / {{total}}',
    'inventory.analyzeNeedPhoto': '请先选择照片后再分析',
    'inventory.analyzeError': '分析失败，请稍后再试',
    'inventory.stocktakeDailyAiLimitPhotoSaved':
      '今日您的 AI 图像分析额度已用完（与收据扫描共用）。本次未执行 AI。若云端上传成功，缩略图会留在列表中；请手动填写存量，UTC 隔日额度重置后可再试。',
    'inventory.needAtLeastOneBatchPhoto': '请至少上传一张相片。',
    'inventory.needPhotoBeforeStockLevels': '请先为此位置（或该步骤）上传至少一张存货照片，之后才会显示存量、到期以及手动新增条目。',
    'inventory.unmatchedUseSharedPhotoHint':
      '若本批已有一张成功上传的相片，送审时未识别条目会与其他条目共用该链接，无需逐行再补图。',
    'inventory.emptyTemplate': '此盘点清单没有条目，无法提交',
    'inventory.helperOnly': '仅家务助理可进行盘点',
    'inventory.chainBlockedBanner': '上一次家里存货任务尚未完成审核，暂时无法开始本次家里存货任务。',
    'inventory.wizard.locationStepBadge': '位置 {{current}}／{{total}}：{{name}}',
    'inventory.wizard.reviewPhaseHint': '总览：请确认完成时间后提交',
    'inventory.wizard.reviewTitle': '各位置摘要',
    'inventory.wizard.reviewLocationSummaryPhotos': '相片 {{photos}} 张',
    'inventory.wizard.reviewLocationSummaryEmptyCategories': '无物品类别 {{noCat}} 个',
    'inventory.wizard.noItemsHeading': '此位置没有以下类别的物品',
    'inventory.wizard.noItemsHint': '若该类别在此位置确实没有任何物品，请勾选确认（无需填存量）。',
    'inventory.wizard.noItemsHintTickFirst':
      '请先处理每个「盘点类别」：某类别在此位置完全没有物品的请先勾选下列对应格子；有需要盘点的才填写存量／到期。',
    'inventory.wizard.noItemsConfirmModalTitle': '确认无物品的类别',
    'inventory.wizard.noItemsConfirmModalDescription':
      '下列类别在当前位置没有任何盘点条目。请逐项勾选「此位置无该类物品」后再确认，才会继续。',
    'inventory.wizard.noItemCategoryAria': '此位置无「{{category}}」类物品',
    'inventory.wizard.noItemCategoryLabel': '此位置无「{{category}}」类物品',
    'inventory.wizard.rowSkippedNoItemsCategory': '该条目所属类别已标示为此位置无物品。',
    'inventory.wizard.manualItemNoItemsCategoryBanner':
      '此类别已标示为「此位置无该类物品」。您仍可在此填写库存与到期；若要一并提交，请先取消该标示。',
    'inventory.wizard.continueNextLocation': '继续下一个位置',
    'inventory.wizard.goToReview': '前往总览',
    'inventory.wizard.photoRequiredPerLocation': '每个位置请至少上传一张盘点照片。',
    'inventory.wizard.noItemsConflict': '已勾选「此类别无物品」时，请勿再填写存量。',
    'inventory.wizard.categoryMarkNoItems': '任务类别「{{category}}」在清单中没有对应条目，请勾选「此类别无物品」。',
    'inventory.wizard.helperSingleStep1Badge': '步骤 1／2：拍摄／上传家中存货照片',
    'inventory.wizard.helperSingleStep2Badge': '步骤 2／2：填写存量与到期资料',
    'inventory.wizard.continueToStockEntry': '下一步：填写存货资料',
    'inventory.wizard.locationHubTitle': '位置总览',
    'inventory.wizard.locationHubHint':
      '可先完成任一位置再上传其他位置照片；请点击下方卡片切换。每个位置须先有照片并按类别完成存量／无物品勾选，全部齐备后方可在总览提交。',
    'inventory.wizard.locationHubReviewHint':
      '请确认各位置已齐备；如需修改资料，请按「继续编辑此位置」返回该位置步骤。',
    'inventory.wizard.locationHubProgress': '已齐备可提交进度：{{done}}／{{total}} 个位置',
    'inventory.wizard.locationHubSelectAria': '切换到位置 {{name}}',
    'inventory.wizard.locationHubCurrentBadge': '当前编辑中',
    'inventory.wizard.locationHubStatusPhotoOk': '相片已上传',
    'inventory.wizard.locationHubStatusPhotoMissing': '尚需相片',
    'inventory.wizard.locationHubStatusReady': '此位置资料已齐',
    'inventory.wizard.locationHubStatusIncomplete': '此位置尚有待填项目',
    'inventory.wizard.editLocationFromReview': '继续编辑此位置',
    'inventory.wizard.editLocationFromReviewAria': '返回编辑位置 {{name}}',
    'inventory.wizard.notAllLocationsReady':
      '仍有位置资料未齐备，请先到「位置总览」切换并补齐相片／类别／存量（齐备后上方进度会显示已全部齐备）。',
    'inventory.wizard.selectLocationTitle': '选择位置',
    'inventory.wizard.selectLocationSubtitle': '请选择要开始盘点的位置。',
    'inventory.wizard.hubInfoBanner': '提交前须完成所有指定位置的盘点。',
    'inventory.wizard.hubLocationsCompleted': '已完成 {{done}}／{{total}} 个位置',
    'inventory.wizard.tapToStart': '点按开始',
    'inventory.wizard.locationCompletedLine': '已完成',
    'inventory.wizard.locationHubStatusInProgress': '尚有资料待填',
    'inventory.wizard.locationHubStatusNeedsStock': '请为条目选择存量',
    'inventory.wizard.locationHubStatusNeedsExpiry': '请为条目选择到期方式',
    'inventory.wizard.locationHubStatusNeedsCategories': '请在检视条目页按「继续」确认无条目的类别',
    'inventory.wizard.hubNextStep': '下一步',
    'inventory.wizard.hubNextDisabledHint': '请先完成所有位置的盘点资料。',
    'inventory.wizard.stepBadge': '步骤 {{current}}／{{total}}',
    'inventory.wizard.stepBadgeWithSubstep': '步骤 {{major}}{{sub}}／{{total}}',
    'inventory.wizard.stepBadgeWithSubstepAria': '当前为步骤 {{major}}{{sub}}，共 {{total}} 步',
    'inventory.wizard.selectLocationIncompleteSection': '未完成',
    'inventory.wizard.selectLocationCompleteSection': '已完成',
    'inventory.wizard.selectLocationModalCloseAria': '关闭选择位置',
    'inventory.wizard.selectLocationFinalReviewButton': '最后审核',
    'inventory.wizard.leaveHomeStockWorkButton': '离开家里存货工作',
    'inventory.wizard.viewThisTaskButton': '查看本次任务',
    'inventory.wizard.leaveHomeStockWorkConfirmTitle': '离开家里存货工作？',
    'inventory.wizard.leaveHomeStockWorkConfirmDescription':
      '离开后，此行未提交的数据不会保存。确定要返回任务列表吗？',
    'inventory.wizard.progressAria': '进度约 {{percent}}%',
    'inventory.wizard.photosStepTitle': '拍摄／上传照片',
    'inventory.wizard.photosStepSubtitle': '请为「{{location}}」拍摄或上传家中存货照片。',
    'inventory.wizard.reviewItemsTitle': '检视项目',
    'inventory.wizard.reviewItemsSubtitle': '请核对条目并更新存量与到期状态。',
    'inventory.wizard.continueToReviewItems': '前往检视项目',
    'inventory.wizard.continueWithAiAnalyze': 'AI分析及检视项目⮕',
    'inventory.wizard.continueWithoutAi': '检视项目(不使用AI)⮕',
    'inventory.wizard.aiSelectModalTitle': '选择要分析的照片',
    'inventory.wizard.aiSelectModalDescription': '已分析与未分析的照片分开列出；默认只勾选未做 AI 的照片。',
    'inventory.wizard.aiSelectModalNotAnalyzedHeading': '未进行 AI 分析',
    'inventory.wizard.aiSelectModalAnalyzedHeading': '已进行 AI 分析',
    'inventory.wizard.aiSelectModalSectionEmpty': '（无）',
    'inventory.wizard.aiSelectModalConfirm': '开始分析',
    'inventory.wizard.aiSelectPhotoCheckboxAria': '分析照片 {{n}}',
    'inventory.wizard.reanalyzeConfirmTitle': '重新分析照片？',
    'inventory.wizard.reanalyzeConfirmDescription':
      '将清除并以最新 AI 结果取代与所选 {{photos}} 张照片相关的 {{items}} 个条目数据（其他照片上的条目不受影响）。',
    'inventory.wizard.reanalyzeConfirmProceed': '确认并分析',
    'inventory.stocktakeDailyAiLimitBatchCancelled': '今日 AI 配额不足，本次所选照片已全部取消分析。',
    'inventory.removeStocktakePhotoWithItemsDescription':
      '此照片已连结 {{count}} 个条目。要一并删除照片及相关条目吗？',
    'inventory.removeStocktakePhotoDeleteWithItems': '删除照片及相关条目',
    'inventory.removeStocktakePhotoKeepAll': '保留照片及条目',
    'inventory.manualAdd.photoRequired': '请选择此条目所属的照片。',
    'inventory.manualAdd.photoPlaceholder': '选择照片',
    'inventory.itemPhotoLabel': '所属照片',
    'inventory.itemPhotoPickerAria': '选择条目所属照片',
    'task.review.inventoryItemPhotoAria': '条目照片',
    'inventory.wizard.saveAndNextLocation': '保存并下一个位置',
    'inventory.wizard.continueToFinalReview': '前往最后审核',
    'inventory.wizard.finalReviewTitle': '最后审核',
    'inventory.wizard.finalReviewSubtitle': '提交前请核对摘要与完成时间；提交后将通知雇主审核。',
    'inventory.wizard.statLowStock': '低库存条目',
    'inventory.wizard.statExpiringSoon': '即将到期',
    'inventory.wizard.statTotalCategories': '类别总数',
    'inventory.wizard.statTotalPhotos': '相片总数',
    'inventory.statDetailModal.lowStockTitle': '低库存条目',
    'inventory.statDetailModal.expiringTitle': '即将到期条目 ({{days}}天内)',
    'inventory.statDetailModal.categoryLabel': '类别',
    'inventory.statDetailModal.locationLabel': '位置',
    'inventory.statDetailModal.expiryHeading': '到期状态',
    'inventory.statDetailModal.empty': '目前没有此类条目。',
    'inventory.statDetailModal.closeButton': '关闭',
    'inventory.statDetailModal.closeAria': '关闭详情窗口',
    'inventory.statDetailModal.dismissBackdropAria': '关闭详情（点击背景）',
    'inventory.statDetailModal.openLowStockAria': '查看低库存详情，共 {{count}} 项',
    'inventory.statDetailModal.openExpiringAria': '查看即将到期详情，共 {{count}} 项',
    'inventory.wizard.locationDetailsSection': '位置摘要',
    'inventory.wizard.submissionInfoSection': '提交信息',
    'inventory.wizard.finalReviewDateLabel': '日期',
    'inventory.wizard.finalReviewCompletionTimeLabel': '完成时间',
    'inventory.wizard.readyToSubmitTitle': '确定要提交？',
    'inventory.wizard.readyToSubmitDisclaimer': '提交后会通知雇主审核，提交后将无法再修改本次存量。',
    'inventory.wizard.sendReport': '提交',
    'inventory.wizard.addItemManuallyTitle': '手动新增条目',
    'inventory.wizard.renameItemPrompt': '请输入条目名称',
    'inventory.wizard.renameItemAria': '编辑条目名称',
    'inventory.wizard.removeManualItemTitle': '移除此条目？',
    'inventory.wizard.removeManualItemDescription': '将移除「{{name}}」及其在本位置的存量与到期数据。此操作无法撤销。',
    'inventory.wizard.reviewLocationBadgeAria': '当前盘点位置：{{location}}',
    'inventory.wizard.editCategoryAria': '选择或更改类别',
    'inventory.wizard.editAllLocations': '编辑全部位置',
    'inventory.wizard.locationItemsBadge': '条目',
    'inventory.wizard.reviewLocationStatItemsCaption': '条目',
    'inventory.wizard.reviewLocationStatEmptyCatCaption': '空类.',
    'inventory.wizard.reviewLocationStatEmptyCatCaptionFull': '无物品类别',
    'inventory.wizard.reviewLocationStatPhotosCaption': '相片',
    'inventory.wizard.previewLocationResults': 'Preview',
    'inventory.wizard.previewLocationResultsAria': '预览位置 {{name}} 的家里存货结果',
    'inventory.wizard.previewLocationResultsCloseAria': '关闭家里存货结果预览',
    'inventory.wizard.previewLocationResultsDismissBackdropAria': '关闭家里存货结果预览（点击背景）',
    'inventory.manualAdd.title': '手动新增条目',
    'inventory.manualAdd.sectionAria': '手动输入类别与条目',
    'inventory.manualAdd.expandToggleAria': '展开手动新增表单',
    'inventory.manualAdd.collapseToggleAria': '收起手动新增表单',
    'inventory.manualAdd.hint':
      '若在主档或照片识别里找不到该条目，可在此填写类别与名称；若雇主设定了类别，请从列表选择相符的一项。',
    'inventory.manualAdd.saveToMasterHint':
      '该家庭已允许助手编辑「家中存货」主档：成功时会写入主档（网络失败时仍可仅在本轮提交中包含）。',
    'inventory.manualAdd.onlySubmissionHint':
      '若雇主尚未开放助手编辑主档：此处新增的条目只会出现在本轮提交数据中，默认不会写入主档列表。',
    'inventory.manualAdd.categoryLabel': '类别',
    'inventory.manualAdd.categoryPlaceholder': '请选择类别',
    'inventory.manualAdd.categoryOtherLabel': '类别（请填写）',
    'inventory.manualAdd.categoryOtherPlaceholder': '请输入类别名称（最多 120 字）',
    'inventory.manualAdd.itemNameLabel': '条目名称',
    'inventory.manualAdd.itemNamePlaceholder': '例如：洗衣液',
    'inventory.manualAdd.addButton': '加入条目',
    'inventory.manualAdd.adding': '加入中…',
    'inventory.manualAdd.validationBoth': '请填写类别与条目名称。',
    'inventory.manualAdd.validationCategory': '请从此任务雇主设定的类别中选择。',
    'inventory.manualAdd.stockRequired': '请选择存量状态（满／半满／低／空）。',
    'inventory.manualAdd.expiryChoiceRequired': '请选择到期日为「无到期日」或「有有效期限」，并在需要时填写日期。',
    'inventory.manualAdd.limitReached': '手动新增上限为 {{max}} 条条目。',
    'inventory.manualAdd.addFailed': '无法加入条目，请稍后重试。',
    'inventory.manualAdd.remove': '移除',
    'inventory.manualAdd.removeAria': '移除手动新增条目 {{name}}',
    'inventory.manualAdd.rowCategoryBadge': '类别：{{category}}',
    'inventory.manualAdd.rowCategoryEditLabel': '类别（可更改）',
    'inventory.expiry.sectionAria': '到期日',
    'inventory.expiry.sectionTitle': '到期日',
    'inventory.expiry.noExpiryLabel': '无到期日',
    'inventory.expiry.noExpiryAria': '「{{item}}」无到期日',
    'inventory.expiry.hasDatesLabel': '有有效期限（可新增多个日期）',
    'inventory.expiry.hasDatesAria': '「{{item}}」填写有效期限',
    'inventory.expiry.datePickerAria': '「{{item}}」有效期限 {{n}}',
    'inventory.expiry.removeDate': '移除',
    'inventory.expiry.removeDateAria': '移除「{{item}}」有效期限 {{n}}',
    'inventory.expiry.addDate': '新增另一个到期日',
    'inventory.expiry.maxDatesHint': '每个条目最多 10 个到期日。',
    'inventory.expiry.required': '请为「{{item}}」填写至少一个有效期限，或改选「无到期日」。',
    'inventory.expiry.chooseModeHint': '请选择「无到期日」或「有有效期限」。',
    'inventory.expiry.choiceRequired': '请为「{{item}}」选择到期处理方式（无到期日或有有效期限）。',
    'inventory.reviewLinesHeading': '盘点条目',
    // Employer — 建立盘点任务
    'task.inventory.newTaskTitle': '建立家里存货',
    'task.inventory.editTaskTitle': '编辑盘点',
    'task.inventory.loadError': '无法载入盘点数据',
    'task.inventory.employerOnly': '仅雇主可使用此页面',
    'task.inventory.chainHint':
      '佣工须按日期顺序完成盘点；较早日期的实例未通过审核前，无法开始较晚日期的盘点。重复频率在建立／编辑盘点任务时设置（非清单排程）。',
    'task.inventory.listScheduleMovedHint': '清单仅作为品项集合；重复排程请在建立盘点任务时设置。',
    'task.inventory.editListHint': '变更清单后，家务助理盘点页与之后的送审会以新清单的条目为准。',
    'task.inventory.editListItemsPreview': '清单内条目',
    'task.inventory.editListEmpty': '此清单尚无条目',
    'task.inventory.editChecklistItemsHeading': '编辑清单内条目',
    'task.inventory.editChecklistItemsSub':
      '从家中库存勾选要纳入此清单的条目；显示顺序与库存列表相同。请按「保存清单条目」或一并保存任务以写入。',
    'task.inventory.editTaskOptionsHeading': '盘点任务内容',
    'task.inventory.editTaskSubmitHint':
      '仅改清单时可用上方绿色「保存清单条目」。下方按钮会保存标题、描述、日期、通知，并将此任务绑定到当前所选清单。',
    'task.inventory.newTaskChecklistHint':
      '勾选要纳入此清单之库存条目；若修改了勾选，建立任务前会先保存清单再建立。',
    'task.inventory.sections.lists': '盘点清单',
    'task.inventory.createListCta': '新增清单',
    'task.inventory.noListsHint': '尚无盘点清单，请先新增清单并加入条目。',
    'task.inventory.createListTitle': '新增盘点清单',
    'task.inventory.listNameLabel': '清单名称',
    'task.inventory.scheduleLabel': '排程',
    'task.inventory.schedule.none': '不设定周期（手动建立任务）',
    'task.inventory.schedule.weekly': '每周',
    'task.inventory.schedule.monthlyFirstWday': '每月第一个周内日',
    'task.inventory.weekdayLabel': '周内日',
    'task.inventory.createListError': '建立清单失败',
    'task.inventory.createListSuccess': '已建立清单',
    'task.inventory.listNameRequired': '请输入清单名称',
    'task.inventory.assignItemsTitle': '此清单尚无条目',
    'task.inventory.assignItemsHint': '请从库存条目勾选，或先新增一条库存条目后加入清单。',
    'task.inventory.quickAddItem': '条目名称',
    'task.inventory.addItemCta': '加入库存',
    'task.inventory.createItemError': '新增条目失败',
    'task.inventory.createItemSuccess': '已新增库存条目',
    'task.inventory.itemNameRequired': '请输入条目名称',
    'task.inventory.itemNameLabel': '条目名称',
    'task.inventory.renameItemCta': '重命名',
    'task.inventory.removeFromListCta': '从所有盘点中移除',
    'task.inventory.editListItemsModalCta': '编辑清单条目',
    'task.inventory.removeListCta': '删除清单',
    'task.inventory.removeListConfirmTitle': '删除此盘点清单？',
    'task.inventory.removeListConfirmBody':
      '删除后，所有仍为生效中且连结此清单的盘点任务都会变成已过期，并需要重新选择盘点清单。',
    'task.inventory.removeListAffectedTasksCount': '目前受影响任务数：{{count}}',
    'task.inventory.removeListAffectedTasksLabel': '受影响的盘点任务：',
    'task.inventory.removeListConfirmCta': '确认删除清单',
    'task.inventory.removeListSuccess': '已删除盘点清单',
    'task.inventory.removeListError': '删除盘点清单失败',
    'task.inventory.updateListSuccess': '已更新清单名称',
    'task.inventory.updateListError': '更新清单名称失败',
    'task.inventory.renameListModalTitle': '修改盘点清单名称',
    'task.inventory.renameListModalOpenCta': '修改盘点清单名称',
    'task.inventory.addedButNotSelected': '已加入库存，但已达清单上限，未自动勾选。',
    'task.inventory.createListUseHint': '建立清单后，请先保存清单条目，系统会使用此清单。',
    'task.inventory.removeFromListConfirmTitle': '从所有盘点中移除此条目？',
    'task.inventory.removeFromListConfirmBody': '确认删除？确认后将无法还原。',
    'task.inventory.removeFromListConfirmCta': '从所有盘点中移除',
    'task.inventory.renameItemModalTitle': '重命名条目',
    'task.inventory.renameItemSave': '保存',
    'task.inventory.updateItemError': '更新条目失败',
    'task.inventory.maxItemsNote': '已选 {{count}} 条品项',
    'task.inventory.saveListItemsCta': '保存清单条目',
    'task.inventory.saveListItemsError': '保存条目失败',
    'task.inventory.saveListItemsSuccess': '已保存清单条目',
    'task.inventory.pickAtLeastOneItem': '请至少选择一条',
    'task.inventory.formHeading': '建立家里存货工作',
    'task.inventory.wizard.stepOf': '步骤 {{current}} / {{total}}',
    'task.inventory.wizard.step1Title': '标题与描述',
    'task.inventory.wizard.step2Title': '盘点位置',
    'task.inventory.wizard.step3Title': '盘点类别',
    'task.inventory.wizard.step4Title': '重复与日期',
    'task.inventory.wizard.step5Title': '通知设置',
    'task.inventory.wizard.step6Title': '清单与条目',
    'task.inventory.wizard.step7Title': '确认并建立',
    'task.inventory.wizard.next': '下一步',
    'task.inventory.wizard.back': '上一步',
    'task.inventory.wizard.reviewConfirm': '确认建立',
    'task.inventory.wizard.reviewSave': '确认保存',
    'task.inventory.wizard.categoriesHint': '至少添加一个类别（可参考下方建议）。',
    'task.inventory.wizard.locationsHint': '至少添加一个位置（可参考下方建议）。',
    'task.inventory.wizard.suggestHeading': '建议',
    'task.inventory.suggest.location.kitchen': '厨房',
    'task.inventory.suggest.location.fridge': '冰箱',
    'task.inventory.suggest.location.storeRoom': '杂物房',
    'task.inventory.suggest.location.livingRoom': '客厅',
    'task.inventory.suggest.location.bathroomA': '洗手间 A',
    'task.inventory.suggest.location.bathroomB': '洗手间 B',
    'task.inventory.suggest.category.food': '食物',
    'task.inventory.suggest.category.drinks': '饮品',
    'task.inventory.suggest.category.babyFood': '儿童食物',
    'task.inventory.suggest.category.babyDrinks': '儿童饮品',
    'task.inventory.suggest.category.elderlyFood': '长者食物',
    'task.inventory.suggest.category.petFood': '宠物粮食',
    'task.inventory.suggest.category.dryGoods': '干货',
    'task.inventory.suggest.category.chilledGoods': '湿货',
    'task.inventory.wizard.addCategoryPlaceholder': '输入类别后加入',
    'task.inventory.wizard.addLocationPlaceholder': '输入位置后加入',
    'task.inventory.wizard.addChip': '加入',
    'task.inventory.wizard.removeChipAria': '移除 {{label}}',
    'task.inventory.wizard.expiryLeadsLabel': '存货到期前提前通知（天数）',
    'task.inventory.wizard.expiryLeadsHint': '可多选；若不需要到期提醒可跳过。',
    'task.inventory.wizard.titleRequired': '请填写任务标题',
    'task.inventory.wizard.categoriesRequired': '请至少添加一个盘点类别',
    'task.inventory.wizard.locationsRequired': '请至少添加一个盘点位置',
    'task.inventory.wizard.summaryTitle': '标题',
    'task.inventory.wizard.summaryDesc': '描述',
    'task.inventory.wizard.summaryCategories': '类别',
    'task.inventory.wizard.summaryLocations': '位置',
    'task.inventory.wizard.summaryList': '清单',
    'task.inventory.wizard.summaryRecurrence': '重复与日期',
    'task.inventory.wizard.summaryDueTime': '截止时间',
    'task.inventory.wizard.summaryNotify': '完成时通知雇主',
    'task.inventory.wizard.summaryNotifyOn': '是',
    'task.inventory.wizard.summaryNotifyOff': '否',
    'task.inventory.wizard.summaryExpiryLeads': '到期提醒（天）',
    'task.inventory.wizard.summaryNone': '（无）',
    'task.inventory.wizard.reviewCardHeading': '家里存货任务',
    'task.inventory.selectListLabel': '盘点清单',
    'task.inventory.selectListPlaceholder': '请选择',
    'task.inventory.selectListRequired': '请选择盘点清单',
    'task.inventory.listHasNoItems': '此清单尚无条目，请先完成上方设置',
    'task.inventory.titleOptional': '留空则使用默认标题',
    'task.inventory.titlePlaceholder': '例：本周厨房家里存货',
    'task.inventory.submitCreateTask': '建立盘点任务',
    'task.inventory.createTaskError': '建立盘点任务失败',
    'task.inventory.createTaskSuccess': '已建立盘点任务',
    'task.inventory.dueDateInvalid': '请选择有效到期日',
    // Task Completion (Helper)
    'task.complete.title': '完成任务',
    'task.complete.subtitle': '提交任务完成证明',
    'task.resubmit.photoHint': '可删除错误照片后重新拍摄或选择图片。',
    'task.complete.photoSection': '照片 (1-3张)',
    'task.complete.submittedPhotosOnly': '已提交的照片',
    'task.complete.photoHint': '点击拍照或从相册选取',
    'task.complete.compressingPhotos': '正在压缩照片…',
    'task.complete.takePhoto': '拍照',
    'task.complete.choosePhoto': '相册',
    'task.complete.photoAlt': '任务照片 {{n}}',
    'task.complete.enlargePhotoAria': '放大查看任务照片',
    'task.complete.removePhoto': '移除照片',
    'task.complete.gpsSection': 'GPS 定位',
    'task.complete.gpsOn': '已启用',
    'task.complete.gpsOff': '已关闭',
    'task.complete.gpsHint': '开启后将记录您的位置',
    'task.complete.noteSection': '备注',
    'task.complete.notePlaceholder': '输入备注 (选填)...',
    'task.complete.completedAtLabel': '完成时间',
    'task.complete.completedAtRequired': '请输入完成日期与时间',
    'task.complete.submit': '提交',
    'task.complete.submitting': '提交中...',
    'task.complete.submitSuccess': '任务提交成功',
    'task.complete.submitError': '提交失败',
    'task.complete.photoRequired': '请至少上传 1 张照片',
    'task.complete.photoUploadError':
      '无法上传照片至云端（请检查网络、稍后重试，并确认浏览器允许上传；GCS 存储桶 CORS 需允许此来源）',
    'task.complete.offlineSaved': '已离线存储，联网后将自动上传',
    // Task Resubmit (Helper)
    'task.resubmit.title': '重新提交',
    'task.resubmit.subtitle': '根据雇主的要求修改并重新提交',
    'task.resubmit.previousNote': '上次备注',
    'task.resubmit.submit': '重新提交',
    'task.resubmit.submitSuccess': '重新提交成功',
    // Common Actions
    'task.action.back': '返回',
    'task.action.refresh': '刷新',
    'task.action.delete': '删除',
    'task.action.archive': '归档',
    'task.action.archiveConfirm': '确定要归档此任务吗？',
    'task.action.archiveSuccess': '任务已归档',
    'task.action.archiveError': '归档失败',
    // Admin Portal
    'admin.brand': 'MaidEasy 管理后台',
    'admin.sidebarAria': '管理后台导航',
    'admin.openMenu': '打开菜单',
    'admin.closeMenu': '关闭菜单',
    'admin.languageSwitcherAria': '界面语言',
    'admin.languageChinese': '繁体中文',
    'admin.languageEnglish': '英文',
    'admin.loginTitle': '管理员登录',
    'admin.loginSubtitle': '此区域与普通用户账号完全分开',
    'admin.email': '邮箱',
    'admin.password': '密码',
    'admin.totp': '双因素验证码（6 位）',
    'admin.totpHint': '账户启用电邮 2FA 时，登录后会要求验证码',
    'admin.otpModalTitle': '电邮验证码',
    'admin.otpModalHint': '我们已向你的管理员电邮发送 6 位验证码（约 5 分钟有效）。',
    'admin.otpCode': '验证码',
    'admin.otpSentHint': '验证码已发送。请查收电邮。',
    'admin.otpResentHint': '已重新发送验证码。',
    'admin.otpRequired': '请输入验证码。',
    'admin.verifyOtp': '验证并登录',
    'admin.resendOtp': '重发验证码',
    'admin.signIn': '登录',
    'admin.signingIn': '登录中…',
    'admin.logout': '登出',
    'admin.idleWarningTitle': '即将因闲置而登出',
    'admin.idleWarningDescription':
      '您已约 28 分钟没有操作。请选择继续使用以延长工作阶段，否则约 2 分钟后将自动登出。',
    'admin.idleExtendSession': '继续使用',
    'admin.idleLogoutNow': '立即登出',
    'admin.nav.dashboard': '仪表盘',
    'admin.nav.users': '用户',
    'admin.nav.settings': '系统设置',
    'admin.nav.invitationCodes': '邀请码',
    'admin.nav.systemMessages': '系统消息',
    'admin.nav.audit': '审计记录',
    'admin.nav.admins': '管理员账号',
    'admin.nav.faqs': 'FAQ 管理',
    'admin.nav.legalDocuments': '法律文件',
    'admin.nav.backHome': '返回主站',
    'admin.systemMessage.title': '系統訊息',
    'admin.systemMessage.subtitle': '僅 super-admin 可建立廣播系統訊息（儲存時自動翻譯五語）。',
    'admin.systemMessage.createTitle': '新增系統訊息',
    'admin.systemMessage.editTitle': '編輯系統訊息',
    'admin.systemMessage.subjectZhHant': '主旨（繁中）',
    'admin.systemMessage.subjectEn': '主旨（英文）',
    'admin.systemMessage.detailZhHant': '詳情 HTML（繁中）',
    'admin.systemMessage.detailEn': '詳情 HTML（英文）',
    'admin.systemMessage.displayFrom': '顯示開始（HKT，YYYY-MM-DD hh:mm，可留空）',
    'admin.systemMessage.displayTo': '顯示結束（HKT，YYYY-MM-DD hh:mm，可留空）',
    'admin.systemMessage.tickerBgColor': 'Ticker 底色',
    'admin.systemMessage.active': '啟用',
    'admin.systemMessage.showOnHome': '首頁 ticker 顯示',
    'admin.systemMessage.showOnHomeHint':
      '取消勾選後仍可在系統訊息列表看到；僅不上首頁輪播。',
    'admin.systemMessage.showOnEmployerDashboard': '顯示於僱主 Dashboard',
    'admin.systemMessage.showOnHelperHome': '顯示於家務助理 Home',
    'admin.systemMessage.roles': '角色（空白＝全部）',
    'admin.systemMessage.levels': '會員等級（空白＝全部；家務助理跟僱主）',
    'admin.systemMessage.blankMeansAll': '空白＝不限',
    'admin.systemMessage.matchMode': '角色與等級組合',
    'admin.systemMessage.channels': '通知通道（新建時套用；編輯不重發）',
    'admin.systemMessage.editNoResend': '編輯不會重新發送通知；延遲發送會使用最新內容。',
    'admin.systemMessage.saveCreate': '儲存並發送',
    'admin.systemMessage.saveEdit': '儲存變更',
    'admin.systemMessage.cancelEdit': '取消編輯',
    'admin.systemMessage.listTitle': '訊息列表',
    'admin.systemMessage.listEmpty': '尚未建立系統訊息。',
    'admin.systemMessage.colSubject': '主旨',
    'admin.systemMessage.colActive': '啟用',
    'admin.systemMessage.colNotify': '通知狀態',
    'admin.systemMessage.colFrom': '顯示開始',
    'admin.systemMessage.edit': '編輯',
    'admin.systemMessage.loadError': '無法載入系統訊息',
    'admin.systemMessage.saveError': '儲存失敗',
    'admin.systemMessage.estimateError': '無法預估收件人數',
    'admin.systemMessage.subjectRequired': '請至少填寫繁中或英文主旨',
    'admin.systemMessage.confirmTitle': '確認發送系統訊息？',
    'admin.systemMessage.confirmTotal': '符合條件用戶：{{count}}',
    'admin.systemMessage.confirmInApp': 'In-app 將發送：{{count}}',
    'admin.systemMessage.confirmPush': 'Web push 可送達：{{count}}',
    'admin.systemMessage.confirmEmail': 'Email 可送達：{{count}}',
    'admin.systemMessage.confirmWhen': '通知時間（HKT）：{{when}}',
    'admin.systemMessage.confirmWhenNone': '不發送通知',
    'admin.systemMessage.confirmSend': '確認儲存並發送',
    'admin.systemMessage.linkPrompt': '輸入 https 連結',
    'admin.systemMessage.editorBold': '粗體',
    'admin.systemMessage.editorItalic': '斜體',
    'admin.systemMessage.editorUl': '項目清單',
    'admin.systemMessage.editorOl': '編號清單',
    'admin.systemMessage.editorLink': '連結',
    'admin.systemMessage.editorImage': '插入图片',
    'admin.systemMessage.editorImageLink': '文字点击后显示图片',
    'admin.systemMessage.editorModeVisual': 'Visual',
    'admin.systemMessage.editorModeHtml': 'HTML',
    'admin.systemMessage.imageUrlPrompt': '输入图片 https 网址',
    'admin.systemMessage.imageUrlInvalid': '图片网址必须是 https://',
    'admin.systemMessage.imageMaxWidthPrompt': 'max-width（可留空，例如 100%、20rem、320px）',
    'admin.systemMessage.imageMaxHeightPrompt': 'max-height（可留空，例如 50vh、12rem）',
    'admin.systemMessage.imageLinkTextPrompt': '显示文字（点击后放大图片）',
    'admin.systemMessage.imageClickUrlPrompt': '点击链接（可留空＝放大图片；https 或站内路径如 /settings）',
    'admin.systemMessage.imageClickUrlInvalid': '点击链接必须是 https:// 或站内路径（以 / 开头）',
    'admin.settingsTab.maintenance': '系统维护',
    'admin.maintenance.subtitle': '排程与维护旗标（仅 super-admin）。时间为香港时区。',
    'maintenance.title': '系统维护中',
    'maintenance.defaultMessage': '系统正在维护，暂时无法使用。请稍后再试。',
    'admin.dashboardSubtitle': '总览与今日活动指标',
    'admin.stat.totalUsers': '用户总数',
    'admin.stat.employers': '雇主数',
    'admin.stat.helpers': '家务助理数',
    'admin.stat.pairings': '配对总数',
    'admin.stat.activePairings': '有效配对',
    'admin.stat.todayActive': '今日活跃用户（登录）',
    'admin.stat.todayNew': '今日新注册',
    'admin.stat.todayTasks': '今日任务实例（到期日）',
    'admin.stat.todayReceipts': '今日收据提交',
    'admin.usersSubtitle': '搜索账户并打开账户详情',
    'admin.usersEmailFilter': '按邮箱筛选',
    'admin.usersRoleFilter': '角色',
    'admin.usersPairedFilter': '配对状态',
    'admin.usersLevelFilter': '会员等级',
    'admin.usersRemovalRequestedFilter': '账户已删除',
    'admin.filter.roleAll': '全部角色',
    'admin.filter.roleEmployer': '雇主',
    'admin.filter.roleHelper': '家务助理',
    'admin.filter.pairedAll': '全部',
    'admin.filter.pairedYes': '已配对',
    'admin.filter.pairedNo': '未配对',
    'admin.filter.levelAll': '所有等级',
    'admin.filter.level1': 'L1（试用）',
    'admin.filter.level2': 'L2（免费）',
    'admin.filter.level3': 'L3（高级）',
    'admin.filter.removalRequestedAll': '账户已删除：全部',
    'admin.filter.removalRequestedYes': '账户已删除：是',
    'admin.filter.removalRequestedNo': '账户已删除：否',
    'admin.summary.accountRemovalRequested': '账户已删除',
    'admin.summary.accountRemovalRequestedAt': '删除日期时间',
    'admin.usersColNickname': '昵称',
    'admin.usersColRole': '角色',
    'admin.usersColLevel': '等级',
    'admin.usersColPaired': '已配对',
    'admin.usersColActions': '操作',
    'admin.usersViewDetail': '账户详情',
    'admin.usersNewPasswordPlaceholder': '新密码',
    'admin.usersConfirmNewPasswordPlaceholder': '确认新密码',
    'admin.createConfirmPasswordPlaceholder': '确认密码',
    'admin.faqsSubtitle': '管理前台 FAQ（繁中/简中/英文），可设置身份、排序与启用状态。',
    'admin.faqCreateTitle': '新增 FAQ',
    'admin.faqEditTitle': '编辑 FAQ',
    'admin.faqFormIncomplete': '请完整填写三种语言的问题与答案。',
    'admin.faqSortOrder': '排序（数字越小越靠前）',
    'admin.faqIsActive': '启用此 FAQ',
    'admin.faqQuestionZhHant': '问题（繁中）',
    'admin.faqAnswerZhHant': '答案（繁中）',
    'admin.faqQuestionZhHans': '问题（简中）',
    'admin.faqAnswerZhHans': '答案（简中）',
    'admin.faqQuestionEn': 'Question (English)',
    'admin.faqAnswerEn': 'Answer (English)',
    'admin.faqShowInactive': '显示停用 FAQ',
    'admin.legalDocumentsSubtitle':
      '管理 Privacy Policy / Terms of Service / PICS / Subscription Terms 的英／繁／简内容与版本。',
    'admin.legalCreateTitle': '新增法律文件版本',
    'admin.legalEditTitle': '编辑法律文件',
    'admin.legalFormIncomplete': '请填写版本、标题与英文内容。',
    'admin.legalVersion': '版本（例如 v1、v2）',
    'admin.legalTitle': '标题',
    'admin.legalContent': '内容（可使用 Markdown 标题）',
    'admin.legalEffectiveFrom': '生效时间（HKT，YYYY-MM-DD hh:mm）',
    'admin.legalIsActive': '启用此版本（同类文件只会有一份 active）',
    'admin.legalShowInactive': '显示非 active 版本',
    'admin.legalType.privacy': 'Privacy Policy',
    'admin.legalType.terms': 'Terms of Service',
    'admin.legalType.pics': 'PICS',
    'admin.legalType.subscription_terms': 'Subscription Terms',
    'admin.legalSubscriptionTermsPlaceholderHint':
      'Subscription Terms may use placeholders: {{subscribeButton}}, {{resubscribeButton}}, {{startTrialButton}} (replaced by app button labels at display time). Shown only in the subscription page modal.',
    'admin.legalLocale.en': 'English',
    'admin.legalLocale.zh-Hant': '繁体中文',
    'admin.legalLocale.zh-Hans': '简体中文',
    'admin.active': '启用',
    'admin.inactive': '停用',
    'admin.edit': '编辑',
    'admin.remove': '删除',
    'admin.create': '新增',
    'admin.update': '更新',
    'admin.cancel': '取消',
    'admin.usersResetPassword': '重置密码',
    'admin.resetPasswordDialogTitle': '确认重置密码？',
    'admin.resetPasswordDialogBody': '将为 {{email}} 设置新密码。此操作无法还原。',
    'admin.resetPasswordSuccess': '已成功重置密码。',
    'admin.resetPasswordLocalOnlyHint': '仅适用于邮箱注册（local）账户。',
    'admin.usersPageInfo': '第 {{page}} 页 · 共 {{total}} 条',
    'admin.prevPage': '上一页',
    'admin.nextPage': '下一页',
    'admin.yes': '是',
    'admin.no': '否',
    'admin.usersBack': '返回用户列表',
    'admin.userDetailTitle': '用户账户',
    'admin.summary.level': '会员等级',
    'admin.summary.authProvider': '登录方式',
    'admin.summary.trialEndsAt': '试用会员到期',
    'admin.summary.invitationLevelEndsAt': 'L3 试用到期',
    'admin.summary.subscriptionEndsAt': '付费订阅到期',
    'admin.downgradeToL2': '降级至 L2（免费）',
    'admin.downgradeToL2Confirm': '确定要将此雇主由 L3 即时降级至 L2？将封存高阶任务并套用 L2 限制。',
    'admin.downgradeToL2Success': '已降级至 L2。',
    'admin.downgradeToL2Error': '降级失败。',
    'admin.userSummarySection': '资料概览（只读）',
    'admin.summary.tasks': '任务总数',
    'admin.summary.tasksStandard': '任务 — 一般',
    'admin.summary.tasksInventory': '任务 — 家居库存',
    'admin.summary.tasksPickup': '任务 — 接送',
    'admin.summary.tasksMedication': '任务 — 服药提醒',
    'admin.summary.invitationCode': '邀请码',
    'admin.summary.taskInstances': '任务实例数',
    'admin.summary.receipts': '收据数',
    'admin.summary.budgets': '已给的钱数',
    'admin.summary.settlements': '结算数',
    'admin.summary.family': '家庭成员数',
    'admin.loginInfoSection': '登录资料',
    'admin.summary.registeredAt': '注册日期时间',
    'admin.summary.registeredIp': '注册 IP',
    'admin.summary.lastLoginAt': '最后登录日期时间',
    'admin.summary.lastLoginIp': '最后登录 IP',
    'admin.summary.lastLoginDevice': '最后登录设备',
    'admin.summary.usageRegion': '使用地区',
    'admin.paymentSection': '付款资料',
    'admin.summary.stripeCustomer': 'Stripe Customer',
    'admin.summary.stripeCustomerLink': '打开 Stripe Customer',
    'admin.paymentEmpty': '没有 Stripe 发票记录。',
    'admin.paymentColPaidAt': '付款日期时间',
    'admin.paymentColPeriod': '订阅日期时间范围',
    'admin.paymentColAmount': '金额',
    'admin.paymentColStatus': '状态',
    'admin.removeAccountTitle': '删除账户',
    'admin.removeAccountHint':
      '会先解除配对、封存账户、终止 Stripe 订阅（如有），并更新邀请码使用记录以释放邮箱。业务数据会保留，但对方不可见历史。',
    'admin.removeAccountPassword': '请输入管理员密码确认',
    'admin.removeAccountReason': '原因（1–500 字）',
    'admin.removeAccountPasswordRequired': '必须输入管理员密码。',
    'admin.removeAccountReasonInvalid': '原因须为 1–500 字。',
    'admin.removeAccountButton': '删除账户',
    'admin.removeAccountDialogTitle': '确认删除此用户账户？',
    'admin.removeAccountDialogBody':
      '将封存 {{email}}、解除配对并禁止登录。业务数据会保留，但对方不可见历史。',
    'admin.removeAccountError': '删除账户失败。',
    'admin.pairingSection': '配对',
    'admin.pairingEmpty': '无配对记录',
    'admin.pairingStatus': '状态',
    'admin.pairingRole': '角色',
    'admin.settingsSubtitle': '会员限制、注册、订阅与宣传站设定',
    'admin.settingsTab.membership': '会员等级限制',
    'admin.settingsTab.register': '注册与其他',
    'admin.settingsTab.subscription': '订阅',
    'admin.settingsTab.marketing': '宣传页',
    'admin.set.maxDailyTasks': '每日任务上限',
    'admin.set.maxDailyReceipts': '每日收据上限',
    'admin.set.maxDailyAI': '每日 AI 分析上限',
    'admin.set.maxComments': '每目标评论上限',
    'admin.set.maxCommentLen': '评论字数上限',
    'admin.set.photoRetention': '照片／收据保留天数',
    'admin.set.consentVersion': '当前同意书版本',
    'admin.set.auditYears': '审计记录保留年数',
    'admin.set.invitationCodeRequired': '雇主注册时必须输入邀请码',
    'admin.set.invitationCodeRequiredHint': '关闭时雇主仍可选填邀请码；开启时留空将无法继续注册。',
    'admin.set.playStoreShowOfficialWebsiteUrl': '显示 Play 版官方网站付款提示',
    'admin.set.playStoreShowOfficialWebsiteUrlHint':
      '开启后，Google Play App 的订阅页、用量页与用量上限视窗会提示改用浏览器打开 MaidEasy 官方网站（含网址）。默认关闭。不会在 Play App 内开放付款。',
    'admin.invitationCodesTitle': '邀请码管理',
    'admin.invitationCodesSubtitle': '创建与管理雇主注册邀请码，并查看使用记录。',
    'admin.invitationCodesTabCodes': '邀请码列表',
    'admin.invitationCodesTabUsages': '使用记录',
    'admin.invitationCodesFilterCode': '搜索邀请码',
    'admin.invitationCodesFilterStatus': '状态筛选',
    'admin.invitationCodesStatusAll': '全部',
    'admin.invitationCodesStatusActive': '启用',
    'admin.invitationCodesStatusDisabled': '停用',
    'admin.invitationCodesSearch': '搜索',
    'admin.invitationCodesColCode': '邀请码',
    'admin.invitationCodesColLevel': '等级',
    'admin.invitationCodesColUses': '使用次数',
    'admin.invitationCodesColDates': '有效期',
    'admin.invitationCodesColStatus': '状态',
    'admin.invitationCodesColEmail': '邮箱',
    'admin.invitationCodesColNickname': '昵称',
    'admin.invitationCodesColUsedAt': '注册时间',
    'admin.invitationCodesEdit': '编辑',
    'admin.invitationCodesEmpty': '暂无邀请码',
    'admin.invitationCodesUsagesEmpty': '暂无使用记录',
    'admin.invitationCodesCreateTitle': '新增邀请码',
    'admin.invitationCodesEditTitle': '编辑邀请码',
    'admin.invitationCodesAutoGenerate': '自动生成邀请码',
    'admin.invitationCodesTimezone': '时区',
    'admin.invitationCodesImmediate': '立即生效',
    'admin.invitationCodesPermanent': '永久有效',
    'admin.invitationCodesUnlimited': '无上限',
    'admin.invitationCodesStartDate': '开始日期（依时区）',
    'admin.invitationCodesEndDate': '结束日期（依时区）',
    'admin.invitationCodesMaxUses': '使用次数上限',
    'admin.invitationCodesUsageStart': '使用记录开始日期（HKT）',
    'admin.invitationCodesUsageEnd': '使用记录结束日期（HKT）',
    'admin.invitationCodesSave': '保存',
    'admin.invitationCodesCancel': '取消',
    'admin.invitationCodesFilterRemark': '搜索备注',
    'admin.invitationCodesColCodeType': '码类型',
    'admin.invitationCodesColDuration': '日数',
    'admin.invitationCodesColRemark': '内部备注',
    'admin.invitationCodesColUsageContext': '兑换情境',
    'admin.invitationCodesTypeRegistrationGrant': '注册授予',
    'admin.invitationCodesTypeL1TrialExtend': 'L1 试用延长',
    'admin.invitationCodesTypeL3TrialPeriod': 'L3 试用期',
    'admin.invitationCodesRemarkPlaceholder': '内部备注（选填，最多 500 字）',
    'admin.invitationCodesColReferrer': '推荐人',
    'admin.invitationCodesReferrerHint': '可选：搜索并绑定一名现有用户为推荐人（一码一人、一人一码）。',
    'admin.invitationCodesReferrerSearchPlaceholder': '以电邮搜索用户',
    'admin.invitationCodesReferrerClear': '解除绑定',
    'admin.invitationCodesColLevelGranted': '授予等级',
    'admin.invitationCodesColCurrentLevel': '现时等级',
    'admin.invitationCodesColFirstPayment': '首次付款（HKT）',
    'admin.invitationCodesDetail': '详细',
    'admin.saveSettings': '保存设置',
    'admin.saving': '保存中…',
    'admin.auditSubtitle': '按条件筛选系统与用户审计记录',
    'admin.auditUserId': '用户电邮',
    'admin.auditAction': '动作',
    'admin.auditTarget': '目标类型',
    'admin.auditStart': '开始日期（HKT）',
    'admin.auditEnd': '结束日期（HKT）',
    'admin.auditColTime': '时间',
    'admin.auditColAction': '动作',
    'admin.auditColUser': '用户',
    'admin.adminsSubtitle': '仅 super_admin',
    'admin.adminsCreate': '新增管理员',
    'admin.adminsCreateSubmit': '创建',
    'admin.adminsCreateSuccess': '管理员已创建。',
    'admin.adminsList': '管理员列表',
    'admin.adminsForbidden': '需要超级管理员权限',
    'admin.adminsCol2fa': '电邮 2FA',
    'admin.adminsColStatus': '状态',
    'admin.adminsColActions': '操作',
    'admin.adminsOpenDetail': '详细',
    'admin.adminsDetailTitle': '管理员详细',
    'admin.adminsCreatedAt': '创建时间',
    'admin.adminsCreatedBy': '创建者',
    'admin.adminsEnableEmail2fa': '启用电邮双因素验证',
    'admin.adminsEnable2faHint': '启用后于下次登录才需输入电邮验证码。',
    'admin.adminsCannotDeactivateSelf': '不可将自己设为停用。',
    'admin.adminsUpdateSuccess': '管理员已更新。',
    'admin.adminsResetPasswordTitle': '重置密码',
    'admin.adminsResetPasswordHint':
      '请输入当前登录管理员（操作者）的密码以确认。重置后对方须重新登录。',
    'admin.adminsOperatorPassword': '操作者密码',
    'admin.adminsResetPasswordSuccess': '管理员密码已重置；对方须重新登录。',
    'admin.adminsActivityTitle': '最近活动（3 个月）',
    'admin.adminsActivityHint': '含登录成功／失败及其他相关稽核记录。',
    'admin.adminsActivityEmpty': '无活动记录',
    'admin.summary.emailVerified': '电邮已验证',
    'admin.pairingPartner': '对方',
    'admin.pairingPartnerDetail': '对方账户详细',
    'admin.sectionExpand': '展开',
    'admin.sectionCollapse': '收起',
  },
  [LANGUAGE.EN]: {
    ...USAGE_REGION_UI_MESSAGES_EN,
    'common.loading': 'Loading…',
    'common.datePlaceholder': 'Pick a date',
    'common.openCalendarAria': 'Open calendar to pick a date',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.closeViewer': 'Close preview',
    'common.imageViewer.zoomHint': 'Pinch to zoom; double-tap to reset; or use + / − below',
    'common.imageViewer.zoomInAria': 'Zoom in',
    'common.imageViewer.zoomOutAria': 'Zoom out',
    'common.imageViewer.resetZoomAria': 'Reset zoom',
    'common.imageViewer.scalePercent': '{{percent}}%',
    'common.confirm': 'Confirm',
    'common.next': 'Next',
    'common.back': 'Back',
    'common.save': 'Save',
    'common.appTagline': 'Household Management for Employers and Helpers',
    'common.languageSwitcherAria': 'Interface language',
    'notFound.title': 'Page not found',
    'notFound.body': 'The page you are looking for does not exist or has been removed.',
    'language.zhHant': 'Traditional Chinese',
    'language.zhHans': 'Simplified Chinese',
    'language.en': 'English',
    'language.id': 'Indonesian',
    'language.fil': 'Filipino',
    'legal.contentEnglishNotice': 'This page content is shown in English.',
    'login.title': 'Login',
    'login.subtitle': 'Sign in with your email and password',
    'auth.errorGeneric': 'Something went wrong. Please try again.',
    'auth.emailLabel': 'Email',
    'auth.passwordLabel': 'Password',
    'auth.rememberMe': 'Remember me',
    'auth.forgotPasswordLink': 'Forgot password?',
    'auth.signIn': 'Sign in',
    'auth.signInWithGoogle': 'Sign in with Google',
    'auth.signingIn': 'Signing in…',
    'auth.linkRegister': 'Create an account',
    'auth.linkLogin': 'Already have an account? Sign in',
    'auth.registerTitle': 'Create account',
    'auth.registerSubtitle': 'Choose your role and enter your details',
    'auth.roleLabel': 'Role',
    'auth.roleRequired': 'Please select a role first.',
    'auth.passwordRulesHint': '8–16 characters with uppercase, lowercase, and a digit',
    'auth.nicknameLabel': 'Nickname',
    'auth.pairingCodeOptional': 'Pairing code (optional)',
    'auth.pairingCodePlaceholder': '6 characters',
    'auth.pairingCodeHint':
      'If your employer gave you a code, enter it here. You can also register first and enter the code on the pairing page later.',
    'auth.invitationCodeLabel': 'Invitation code',
    'auth.invitationCodeOptional': 'Invitation code (optional)',
    'auth.invitationCodePlaceholder': '8 characters',
    'auth.invitationCodeHint': 'Enter an invitation code if you have one.',
    'auth.invitationCodeRequired': 'Please enter an invitation code',
    'auth.invitationCodeValid': 'Invitation code is valid',
    'auth.invitationCodeBenefitL1Extend': 'Extend trial by {{days}} day(s)',
    'auth.invitationCodeBenefitL3Trial': 'Paid-member trial for {{days}} day(s)',
    'auth.invitationCodeInvalid': 'Invitation code is invalid',
    'auth.invitationCodeErrorFormat': 'Invitation code format is invalid',
    'auth.invitationCodeErrorNotYetActive': 'Invitation code is not yet active',
    'auth.invitationCodeErrorExpired': 'Invitation code has expired',
    'auth.invitationCodeErrorDisabled': 'Invitation code is disabled',
    'auth.invitationCodeErrorMaxUses': 'Invitation code has reached its usage limit',
    'auth.invitationCodeErrorAfterRegistrationOnly':
      'This invitation code can only be redeemed after registration',
    'auth.invitationCodeDeepLinkInvalid':
      'This invite link is invalid or no longer available. Enter another valid invitation code if you have one.',
    'auth.invitationCodeLockedHint':
      'This invitation code came from your invite link and cannot be changed during registration.',
    'inviteReferrer.menuItem': 'Invitation code usage',
    'inviteReferrer.pageTitle': 'Invitation code usage',
    'inviteReferrer.pageSubtitle':
      'See how many people used your invitation code to register or redeem, and how many became paying users.',
    'inviteReferrer.statsLabel': 'Invitation code stats',
    'inviteReferrer.code': 'Invitation code',
    'inviteReferrer.benefit': 'Code benefit',
    'inviteReferrer.benefitL1Extend': 'Extend trial by {{days}} days',
    'inviteReferrer.benefitL3Trial': 'Premium membership trial for {{days}} days',
    'inviteReferrer.maxUses': 'Usage limit',
    'inviteReferrer.maxUsesLimited': '{{used}} / {{max}}',
    'inviteReferrer.maxUsesUnlimited': '{{used}} / Unlimited',
    'inviteReferrer.validity': 'Validity period',
    'inviteReferrer.validityRange': '{{start}} — {{end}}',
    'inviteReferrer.validityImmediate': 'Effective immediately',
    'inviteReferrer.validityPermanent': 'No end date',
    'inviteReferrer.totalUses': 'Total uses',
    'inviteReferrer.paidUsers': 'Paying users',
    'inviteReferrer.shareLink': 'Share link',
    'inviteReferrer.copyLink': 'Copy link',
    'inviteReferrer.copied': 'Copied',
    'inviteReferrer.notBound': 'You are not bound as an invitation-code referrer.',
    'auth.registerCta': 'Register',
    'auth.registerWithGoogle': 'Register with Google',
    'auth.registering': 'Registering…',
    'auth.registerRoleConfirmTitle': 'Confirm your registration role',
    'auth.registerRoleConfirmDescription':
      'Your selected role is "{{role}}". Please confirm it is correct. Your role cannot be changed after registration.',
    'auth.registerPicsScrollRequired':
      'Please scroll the Personal Information Collection Statement (PICS) to the bottom first.',
    'auth.registerPicsAgreeRequired':
      'Please check the box to agree to the Personal Information Collection Statement.',
    'auth.forgotTitle': 'Forgot password',
    'auth.forgotSubtitle':
      'Enter your email and we will send a reset link if the account exists.',
    'auth.forgotSubmit': 'Send reset link',
    'auth.forgotSending': 'Sending…',
    'auth.forgotSuccess':
      'If that email is registered, you will receive a password reset email shortly.',
    'auth.resetTitle': 'Reset password',
    'auth.resetSubtitle': 'Choose a new password.',
    'auth.newPasswordLabel': 'New password',
    'auth.confirmPasswordLabel': 'Confirm new password',
    'auth.resetSubmit': 'Reset password',
    'auth.resetting': 'Resetting…',
    'auth.passwordMismatch': 'Passwords do not match',
    'auth.showPassword': 'Show',
    'auth.hidePassword': 'Hide',
    'auth.confirmPasswordRegisterLabel': 'Confirm password',
    'auth.resetSuccess': 'Your password has been reset. Please sign in.',
    'auth.goToLogin': 'Go to login',
    'auth.goToHome': 'Go to home',
    'auth.verifyTitle': 'Verify email',
    'auth.verifyPending': 'Verifying…',
    'auth.verifySuccess': 'Email verified successfully.',
    'auth.verifyFail': 'Verification failed. The link may have expired.',
    'auth.missingToken': 'Invalid or missing verification link.',
    'auth.accountRemovalConfirmTitle': 'Confirm account deletion',
    'auth.accountRemovalConfirmPrompt': 'Your account will be deleted immediately after you press the button below. This cannot be undone.',
    'auth.accountRemovalConfirmButton': 'Confirm and delete account',
    'auth.accountRemovalConfirmPending': 'Deleting your account…',
    'auth.accountRemovalConfirmSuccess': 'Your account has been deleted.',
    'auth.accountRemovalConfirmFail': 'Confirmation failed. The link may have expired.',
    'auth.googleCallbackTitle': 'Signing in with Google',
    'auth.googleCallbackLoading': 'Finishing Google authentication. Please wait…',
    'auth.googleDecisionNotRegistered':
      'No registration record is found for this Google account. Do you want to register now?',
    'auth.googleDecisionRegisteredGoogle':
      'A Google registration record already exists. Do you want to sign in now?',
    'auth.googleDecisionRegisteredEmail':
      'An email/password account already exists for this email. Do you want to sign in with email instead?',
    'auth.googleDecisionConfirmRegister': 'Yes, register now',
    'auth.googleDecisionConfirmLogin': 'Yes, sign in now',
    'auth.googleDecisionConfirmEmailLogin': 'Yes, use email sign in',
    'auth.googleDecisionCancelHome': 'No, back to home',
    'auth.registerWizardStepBadge': 'Step {{current}} of {{total}}',
    'auth.registerWizardProgressAria': 'Registration progress',
    'auth.registerMethodTitle': 'Create account',
    'auth.registerMethodSubtitle': 'Choose how you would like to register for MaidEasy.',
    'auth.registerDividerOrEmail': 'Or register with email',
    'auth.registerEmailHelper': 'We will send a verification link to this email.',
    'auth.registerContinueWithEmail': 'Continue with email',
    'auth.registerStepRoleTitle': 'Choose your role',
    'auth.registerStepRoleSubtitle':
      'Please select whether you are an Employer or a Helper. This cannot be changed later.',
    'auth.registerRoleImportantNotice':
      'Important: Once you select your role and complete registration, it cannot be changed. Make sure you choose the correct role.',
    'auth.registerRoleEmployerTitle': 'Employer',
    'auth.registerRoleEmployerDesc':
      'I want to manage tasks, expenses, and connect with my helper.',
    'auth.registerRoleHelperTitle': 'Helper',
    'auth.registerRoleHelperDesc':
      'I want to view my daily tasks, submit receipts, and track my work.',
    'auth.registerStepNicknameTitle': 'Enter details',
    'auth.registerStepNicknameSubtitle':
      'This name is shown to your paired party; usage region and nickname can be changed later in Settings.',
    'auth.registerStepPasswordTitle': 'Set your password',
    'auth.registerStepPasswordSubtitle': 'Please create a secure password to protect your account.',
    'auth.registerPasswordPolicyTitle': 'Password must contain at least:',
    'auth.registerPasswordPolicyLength': '8–16 characters',
    'auth.registerPasswordPolicyUpper': 'One uppercase letter (A–Z)',
    'auth.registerPasswordPolicyLower': 'One lowercase letter (a–z)',
    'auth.registerPasswordPolicyDigit': 'One number (0–9)',
    'auth.registerStepPairingTitle': 'Pairing code (optional)',
    'auth.registerStepPairingSubtitle':
      'If your employer shared a 6-character pairing code, enter it here. You can also leave it blank and pair later.',
    'auth.registerStepPicsScrollFirst': 'Scroll the statement below to the bottom before you can tick consent.',
    'auth.registerContinue': 'Continue',
    'auth.registerStepReviewTitle': 'Review your details',
    'auth.registerStepReviewSubtitle':
      'Please confirm the information below is correct. Use Edit to go back to a step.',
    'auth.registerReviewMethod': 'Registration method',
    'auth.registerReviewMethodEmail': 'Email',
    'auth.registerReviewMethodGoogle': 'Google',
    'auth.registerReviewRole': 'Account role',
    'auth.registerReviewEmail': 'Email address',
    'auth.registerReviewNickname': 'Nickname',
    'auth.registerReviewUsageRegion': 'Usage region',
    'auth.registerReviewPairing': 'Pairing code',
    'auth.registerReviewPairingEmpty': '(Not provided)',
    'auth.registerReviewInvitationCode': 'Invitation code',
    'auth.registerReviewInvitationCodeEmpty': 'Not used',
    'auth.registerReviewPicsAgreed':
      'You have agreed to the Personal Information Collection Statement (PICS) and data processing terms.',
    'auth.registerReviewEdit': 'Edit',
    'auth.registerCompleteRegistration': 'Complete registration',
    'auth.registerCompleteTitle': 'Registration complete',
    'auth.registerCompleteEmailVerify':
      'We have sent a verification email. Please check your inbox (and spam) and click the link to verify.',
    'auth.registerCompleteGoogle': 'Your Google account is verified. You can continue using MaidEasy.',
    'auth.registerCompleteContinue': 'Continue',
    'auth.emailTaken': 'This email is already registered',
    'auth.emailChecking': 'Checking email…',
    'auth.emailInvalid': 'Please enter a valid email address',
    'home.navLogin': 'Sign in',
    'home.navLoginEmail': 'Email sign in',
    'home.navGoogleLogin': 'Sign in with Google',
    'home.navRegister': 'Register',
    'home.registerModalTitle': 'Choose registration method',
    'home.registerModalSubtitle': 'Please choose Google registration or email registration.',
    'home.registerWithGoogle': 'Register with Google',
    'home.registerWithEmail': 'Register with email',
    'home.navLogout': 'Log out',
    'home.navPairing': 'Pairing status',
    'home.navEmployerApp': 'Tasks (employer)',
    'home.navHelperApp': 'Today’s tasks (helper)',
    'home.phaseLine': '',
    'home.linkPrivacy': 'Privacy Policy',
    'home.linkTerms': 'Terms of Service',
    'home.linkPics': 'PICS',
    'home.linkFaq': 'FAQ',
    'home.linkFeatures': 'Features',
    'home.linkFeaturesAria': 'Features (opens in a new tab)',
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'View FAQ content by role.',
    'faq.tabHelper': 'Helper',
    'faq.tabEmployer': 'Employer',
    'faq.loadError': 'Unable to load FAQ.',
    'faq.empty': 'No FAQ content yet.',
    'nav.home': 'Home',
    'systemMessage.menu': 'System messages',
    'systemMessage.list.title': 'System messages',
    'systemMessage.list.empty': 'No system messages yet.',
    'systemMessage.list.colSubject': 'Subject',
    'systemMessage.list.colDate': 'Date',
    'systemMessage.list.openItem': 'Open: {{subject}}',
    'systemMessage.ticker.title': 'System message',
    'systemMessage.ticker.count': '{{current}} / {{total}}',
    'systemMessage.ticker.openDetails': 'View system message details',
    'systemMessage.ticker.prev': 'Previous system message',
    'systemMessage.ticker.next': 'Next system message',
    'systemMessage.action.close': 'Close',
    'systemMessage.detailEmpty': '(No details)',
    'systemMessage.error.loadList': 'Failed to load system messages',
    'systemMessage.image.enlargeAria': 'Enlarge image',
    'systemMessage.image.openLinkAria': 'Open link',
    'systemMessage.menuItemUnreadAria': '{{label}}, {{count}} unread',
    'nav.tasks': 'Tasks',
    'nav.expensesSettlements': 'Expenses',
    'nav.expensesSettlementsHelper': 'Expenses',
    'nav.family': 'Family',
    'nav.stock': 'Stock',
    'nav.mainAria': 'Main app bottom navigation',
    'pwa.offlineBanner': 'Offline — This App is temporarily unavailable until your network is restored.',
    'pwa.syncing': 'Syncing…',
    'pwa.syncComplete': 'Sync complete',
    'pwa.syncFailed': 'Sync failed — please retry',
    'pwa.pendingSync': 'Items pending sync',
    'pwa.installCta': 'Install app',
    'pwa.installDismiss': 'Dismiss',
    'playBanner.subtitle': 'MaidEasy - In Google Play',
    'playBanner.install': 'Install',
    'playBanner.installAria': 'Install MaidEasy on Google Play',
    'playBanner.dismissAria': 'Dismiss install banner',
    'playBanner.regionAria': 'Google Play install prompt',
    'task.complete.savedOffline': 'Saved on this device; will sync when online',
    'receipt.analyze.savedOffline': 'Saved on this device; will sync when online',
    'legal.privacy.title': 'Privacy Policy',
    'legal.privacy.body':
      'This page is placeholder legal content. It describes how we collect, use, and protect personal data, and will be legally reviewed before production.',
    'legal.terms.title': 'Terms of Service',
    'legal.terms.body':
      'This page is placeholder legal content. Use of the service is subject to terms to be finalized before production launch.',
    'legal.pics.title': 'Personal Information Collection Statement (PICS)',
    'legal.pics.body':
      'Multi-language PICS placeholder aligned with the registration flow; final copy after legal/compliance review.',
    'legal.backHome': 'Back to home',
    'settings.title': 'Settings',
    'settings.subtitle': 'Language, timezone, appearance, and notifications',
    'settings.nicknameLabel': 'Nickname',
    'settings.nicknamePlaceholder': 'Enter your nickname',
    'settings.languageLabel': 'Display language',
    'settings.timezoneLabel': 'Timezone',
    'settings.usageRegionLabel': 'Usage region',
    'settings.themeLabel': 'Theme',
    'settings.themeLight': 'Light',
    'settings.themeDark': 'Dark',
    'settings.themeSystem': 'System',
    'settings.fontSizeLabel': 'Font size',
    'settings.fontXSmall': 'Extra small',
    'settings.fontSmall': 'Small',
    'settings.fontMedium': 'Medium',
    'settings.fontLarge': 'Large',
    'settings.fontXLarge': 'Extra large',
    'settings.aiUsageTitle': "Today's AI usage",
    'settings.aiUsageHint': 'AI receipt and home stock analysis (resets daily at UTC 00:00)',
    'settings.receiptUsageTitle': "Today's receipt scan usage",
    'settings.receiptUsageHint': 'Receipts you can submit per day (resets daily at UTC 00:00)',
    'settings.aiUsageLoading': 'Loading…',
    'settings.dailyUsageError': 'Could not load daily usage',
    'usage.pageTitle': 'Usage summary',
    'usage.pageSubtitle': 'Daily quotas and task limits',
    'usage.backToSettings': 'Back to settings',
    'usage.taskSubmitTitle': "Today's task submit usage",
    'usage.taskSubmitHint': 'Task submissions per day (resets daily at UTC 00:00)',
    'usage.taskReviewTitle': "Today's task review usage",
    'usage.taskReviewHint': 'Task reviews per day (resets daily at UTC 00:00)',
    'usage.totalTasksTitle': 'Total tasks created',
    'usage.totalTasksHint': 'Task slots in your pairing (archived tasks are not counted)',
    'usage.pinMessagesTitle': 'Total pin messages',
    'usage.pinMessagesHint': 'Pin messages in your pairing (cumulative count)',
    'usage.resetAtLabel': 'Next reset',
    'usage.unlimited': 'Unlimited',
    'usage.viewUsageDetails': 'View usage details',
    'usage.membershipLevel': 'Membership level',
    'usage.levelTrial': 'Trial member',
    'usage.levelFree': 'Free member',
    'usage.levelPaid': 'Premium member',
    'usage.helperMembershipHelpTriggerAria': 'View membership level help',
    'usage.helperMembershipHelpAria': 'Membership level help',
    'usage.helperMembershipHelpTitle': 'Membership level',
    'usage.helperMembershipHelpFollowsEmployer':
      'Helpers cannot upgrade membership themselves. Membership level follows the employer account.',
    'usage.helperMembershipHelpContactEmployer':
      'If your usage quota is not enough, please contact your employer to upgrade.',
    'usage.helperSectionTitle': "Below is your Helper's usage",
    'usage.compareLevels': 'Compare levels',
    'usage.upgradeBannerBefore': 'Click ',
    'usage.upgradeBannerClickHere': 'here',
    'usage.upgradeBannerAfter': ' to upgrade to Premium and unlock more usage quotas and features',
    'usage.upgradeBannerClickAria': 'Go to membership subscription',
    'usage.playStoreUpgradeNotice':
      'To upgrade or manage your subscription, open this service in a browser or the Add to Home Screen app (the Google Play app does not offer subscription payment).',
    'membership.restoreModal.title': 'Restore archived tasks',
    'membership.restoreModal.description': 'Your membership was upgraded. Select home stock, pickup/drop-off, or medication reminder tasks to restore. Unselected tasks stay archived.',
    'membership.restoreModal.confirm': 'Restore selected',
    'membership.restoreModal.skip': 'Not now',
    'membership.restoreModal.success': 'Selected tasks were restored',
    'membership.restoreModal.error': 'Could not restore tasks. Please try again.',
    'membership.pricing.compareTitle': 'Compare membership levels',
    'membership.pricing.loading': 'Loading membership plans…',
    'membership.pricing.loadError': 'Unable to load pricing for now. Please try again later.',
    'membership.pricing.priceUnavailable': '—',
    'membership.pricing.monthlyFeeLabel': 'Monthly:',
    'membership.pricing.yearlyFeeLabel': 'Yearly:',
    'membership.pricing.cheaperBadge': 'Yearly plan is cheaper',
    'membership.pricing.discountUntil': 'Discount until {date}',
    'membership.pricing.welcomeRemark': 'First month {first}, then {thereafter}',
    'membership.pricing.trialStartsOnRegister': 'Trial starts after registration',
    'membership.pricing.yes': 'Yes',
    'membership.pricing.no': 'No',
    'subscription.openPage': 'Membership subscription',
    'subscription.title': 'Membership subscription',
    'subscription.membershipLevel': 'Membership level',
    'subscription.previousSubscriptionTitle': 'Previous premium subscription',
    'subscription.previousSubscriptionEnded': 'Your previous premium subscription has ended',
    'subscription.previousSubscriptionPaymentFailed':
      'Your previous premium subscription has ended (payment previously failed)',
    'subscription.trialEndsAt': 'Trial member period ends',
    'subscription.invitationTrialEndsAt': 'Invitation premium membership trial ends',
    'subscription.subscriptionEndsAt': 'Subscription ends',
    'subscription.nextChargeAt': 'Next charge',
    'subscription.payToday': 'Become a premium member now to unlock more features',
    'subscription.startTrial': 'Set up subscription (no charge during trial)',
    'subscription.payTodayAmount': 'Only {{amount}} {{period}}',
    'subscription.noChargeToday': 'No payment due today',
    'subscription.firstChargeAmount': 'First charge {{amount}}',
    'subscription.coversPeriod': 'First paid period: {{start}} to {{end}}',
    'subscription.nextCharge': 'Next charge: {{date}}',
    'subscription.firstChargeOn': 'First charge on {{date}}',
    'subscription.promoPriceFirstMonth': 'First month {{dueAmount}}, then {{listPrice}} {{period}}',
    'subscription.promoPriceLimitedTime':
      '{{dueAmount}} {{period}} during the offer (until {{endsAt}}), then {{listPrice}} {{period}}',
    'subscription.promoAppliedFirstMonth': 'First-month offer: {{amount}} off',
    'subscription.promoAppliedLimitedTime': 'Limited-time offer: {{amount}} off (until {{endsAt}})',
    'subscription.invitationTrialNote':
      'Invitation trial until {{date}}; no extra payment during the trial.',
    'subscription.invitationTrialChargeLaterNote':
      'Invitation trial until {{date}}. The first charge will start on the trial end date.',
    'subscription.subscribeButton': 'Pay & subscribe',
    'subscription.resubscribeButton': 'Resubscribe to premium membership',
    'subscription.startTrialButton': 'Set up subscription',
    'subscription.stripeCheckoutDiscountHiddenNote':
      'The next payment page will only show no charge today and the regular price. It cannot show the discount. The discount is applied automatically on the first charge.',
    'subscription.promoMayExpireIfDeferred':
      'The limited-time offer may end before the first charge, so the regular price may apply then.',
    'subscription.termsTitle': 'Subscription Terms and Conditions',
    'subscription.termsLinkLabel': 'Subscription Terms and Conditions',
    'subscription.termsConsentPay':
      'By selecting “{{buttonLabel}}” and paying, you agree to the {{termsLink}}.',
    'subscription.termsConsentSetup':
      'By selecting “{{buttonLabel}}”, you agree to the {{termsLink}}.',
    'subscription.termsLoadError': 'Could not load subscription terms. Please try again.',
    'subscription.alreadySubscribed': 'You already have an active paid subscription.',
    'subscription.stripeNotConfigured': 'Subscription is not enabled in this environment.',
    'subscription.loadError': 'Could not load subscription details. Please try again.',
    'subscription.checkoutSuccess': 'Subscription was set up successfully.',
    'subscription.checkoutCancel': 'Payment was canceled.',
    'subscription.manageButton': 'Manage subscription',
    'subscription.playStoreBillingNotice':
      'The Google Play app does not offer subscribe, manage subscription, or change plan.',
    'subscription.playStoreOfficialWebsiteLine':
      'Please use a browser to access MaidEasy official website\n{{url}}',
    'subscription.invoicesTitle': 'Recent invoices',
    'subscription.viewInvoiceButton': 'View invoice',
    'subscription.statusActive': 'Subscription status: active',
    'subscription.statusPastDue': 'Subscription status: payment failed',
    'subscription.statusCanceled': 'Subscription status: canceled',
    'subscription.willAutoRenewOn': 'Your subscription will auto renew on {{date}}',
    'subscription.willAutoRenewOnWithAmount':
      'Your subscription will auto renew on {{date}} ({{amount}})',
    'subscription.willAutoRenewOnPromo':
      'Your subscription will auto renew on {{date}} (promo {{amount}})',
    'subscription.nextRenewLimitedTimePromo':
      'Limited-time offer: {{dueAmount}} {{period}} (until {{endsAt}}, list price {{listPrice}})',
    'subscription.periodPerMonth': 'per month',
    'subscription.periodPerYear': 'per year',
    'subscription.willEndOnCancelled': 'Your subscription will end on {{date}} (subscription cancelled)',
    'subscription.intervalMonth': 'Monthly',
    'subscription.intervalYear': 'Yearly',
    'subscription.selectBillingInterval': 'Select billing interval',
    'subscription.planUnavailable': 'This billing interval is not available yet.',
    'subscription.currentBillingInterval': 'Current plan: {{interval}}',
    'subscription.pendingIntervalChange':
      'Scheduled to switch to {{interval}} on {{date}} (takes effect after the current period)',
    'subscription.lockedPromoReserved':
      'Discount of {{amount}} reserved for you (a better offer on the charge date will be used automatically)',
    'subscription.cancelIntervalChange': 'Cancel scheduled switch',
    'subscription.switchToYear': 'Switch to yearly (next period)',
    'subscription.switchToMonth': 'Switch to monthly (next period)',
    'subscription.intervalChangeNote':
      'The switch takes effect after your current plan ends.',
    'subscription.intervalChangeScheduled': 'Billing interval switch scheduled.',
    'subscription.intervalChangeCancelled': 'Scheduled switch cancelled.',
    'subscription.intervalChangeConfirmTitleYear': 'Confirm switch to yearly',
    'subscription.intervalChangeConfirmTitleMonth': 'Confirm switch to monthly',
    'subscription.intervalChangeConfirmIntro':
      'You are currently on {{current}}. The switch to {{target}} takes effect after your current plan ends.',
    'subscription.intervalChangeConfirmEffectiveAt': 'Switch effective / current plan ends',
    'subscription.intervalChangeConfirmFirstChargeAt': 'First charge for the new plan',
    'subscription.intervalChangeConfirmCoversPeriod': 'First new-plan coverage period',
    'subscription.intervalChangeConfirmCoversPeriodValue': '{{start}} to {{end}}',
    'subscription.intervalChangeConfirmAmount': 'Estimated first charge amount',
    'subscription.intervalChangeConfirmPromoLimited':
      'Limited-time discount now: {{amount}} off (until {{endsAt}}; a better offer on the charge date will be used automatically)',
    'subscription.intervalChangeConfirmPromoGeneric': 'Current discount: {{amount}} off',
    'subscription.intervalChangeConfirmNotes': 'Notes',
    'subscription.intervalChangeConfirmNoRefund':
      'Your current plan stays active until it ends.',
    'subscription.intervalChangeConfirmPromoLockNote':
      'On confirm, today’s limited-time offer is reserved. If a better offer applies on the charge date, that higher discount is used; otherwise the reserved offer is kept.',
    'subscription.intervalChangeConfirmButtonYear': 'Confirm switch to yearly',
    'subscription.intervalChangeConfirmButtonMonth': 'Confirm switch to monthly',
    'subscription.cancelIntervalChangeConfirmTitle': 'Confirm cancel scheduled switch',
    'subscription.cancelIntervalChangeConfirmIntro':
      'This cancels the scheduled switch to {{interval}} on {{date}}. You will stay on your current plan.',
    'subscription.cancelIntervalChangeConfirmPromoNote':
      'The reserved discount of {{amount}} will also be cleared.',
    'subscription.cancelIntervalChangeConfirmButton': 'Confirm cancel',
    'membership.restoreTask.action': 'Restore task',
    'membership.restoreTask.success': 'Task restored',
    'admin.upgradeToL3': 'Upgrade to L3 (test)',
    'admin.upgradeToL3Confirm': 'Upgrade this employer to L3? Auto-translation preferences will be restored and a restore prompt will appear on the next dashboard visit.',
    'admin.upgradeToL3Success': 'Upgraded to L3.',
    'admin.upgradeToL3Error': 'Failed to upgrade to L3.',
    'admin.grantL3TrialTitle': 'Grant L3 trial',
    'admin.grantL3TrialHint': 'Enter the end datetime. The employer will be kept at L3 and the end time will be stored as an invitation L3 trial.',
    'admin.grantL3TrialField': 'Trial ends at (HKT, YYYY-MM-DD hh:mm)',
    'admin.grantL3TrialButton': 'Grant L3 trial',
    'admin.grantL3TrialConfirm': 'Grant an L3 trial to this employer?',
    'admin.grantL3TrialSuccess': 'L3 trial granted.',
    'admin.grantL3TrialError': 'Failed to grant L3 trial.',
    'admin.grantL3TrialDateRequired': 'Enter a trial end datetime first.',
    'limitModal.title.default': 'Usage limit reached',
    'limitModal.title.batchInsufficient': 'Insufficient daily quota',
    'limitModal.title.TOTAL_TASK_LIMIT_REACHED': 'Task creation limit reached',
    'limitModal.title.COMMENT_LIMIT_REACHED': 'Comment limit reached',
    'limitModal.title.DAILY_TASK_SUBMIT_LIMIT_REACHED': 'Daily task submit limit reached',
    'limitModal.title.DAILY_TASK_REVIEW_LIMIT_REACHED': 'Daily task review limit reached',
    'limitModal.title.DAILY_RECEIPT_LIMIT_REACHED': 'Daily receipt limit reached',
    'limitModal.title.DAILY_AI_ANALYZE_LIMIT_REACHED': 'Daily AI analyze limit reached',
    'limitModal.title.TASK_KIND_NOT_ALLOWED': 'Task type not available',
    'limitModal.title.AUTO_TRANSLATION_NOT_ALLOWED': 'Auto-translation not available',
    'limitModal.title.INVENTORY_LOCATION_LIMIT_REACHED': 'Inventory location limit reached',
    'limitModal.title.INVENTORY_CATEGORY_LIMIT_REACHED': 'Inventory category limit reached',
    'limitModal.title.PIN_MESSAGE_LIMIT_REACHED': 'Pin message limit reached',
    'limitModal.body.TOTAL_TASK_LIMIT_REACHED': 'You have created {{total}}/{{max}} tasks (archived tasks are not counted).',
    'limitModal.body.COMMENT_LIMIT_REACHED': 'Comments for this item reached {{total}}/{{max}}.',
    'limitModal.body.DAILY_TASK_SUBMIT_LIMIT_REACHED': 'Submitted {{used}}/{{limit}} today. Next reset: {{retryAt}}.',
    'limitModal.body.DAILY_TASK_REVIEW_LIMIT_REACHED': 'Reviewed {{used}}/{{limit}} today. Next reset: {{retryAt}}.',
    'limitModal.body.DAILY_RECEIPT_LIMIT_REACHED': 'Submitted {{used}}/{{limit}} receipts today. Next reset: {{retryAt}}.',
    'limitModal.body.DAILY_AI_ANALYZE_LIMIT_REACHED': 'Used {{used}}/{{limit}} AI analyses today. Next reset: {{retryAt}}.',
    'limitModal.body.batchInsufficient.DAILY_RECEIPT_LIMIT_REACHED':
      'You have {{remaining}} receipt submission(s) remaining today ({{used}}/{{limit}} used), which is not enough to submit {{requested}} receipt(s). Next reset: {{retryAt}}.',
    'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_RECEIPT':
      'You have {{remaining}} AI analysis(es) remaining today ({{used}}/{{limit}} used), which is not enough to analyze {{requested}} receipt(s). Next reset: {{retryAt}}.',
    'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_PHOTO':
      'You have {{remaining}} AI analysis(es) remaining today ({{used}}/{{limit}} used), which is not enough to analyze {{requested}} photo(s). Next reset: {{retryAt}}.',
    'limitModal.body.TASK_KIND_NOT_ALLOWED': 'Your membership level does not support this task type.',
    'limitModal.body.AUTO_TRANSLATION_NOT_ALLOWED': 'Your membership level does not support auto-translation.',
    'limitModal.body.INVENTORY_LOCATION_LIMIT_REACHED': 'Inventory location count has reached your membership limit.',
    'limitModal.body.INVENTORY_CATEGORY_LIMIT_REACHED': 'Inventory category count has reached your membership limit.',
    'limitModal.body.PIN_MESSAGE_LIMIT_REACHED': 'You have created {{total}}/{{max}} pin messages.',
    'limitModal.title.PIN_MESSAGE_ACTIVE_LIMIT_REACHED': 'Active pin message limit reached',
    'limitModal.body.PIN_MESSAGE_ACTIVE_LIMIT_REACHED':
      'Total pin messages exceed the free membership cap. At most {{max}} may be active at once (currently {{total}} active). Deactivate another pin message or upgrade membership.',
    'limitModal.body.default': 'Your account has reached a limit for your current membership level.',
    'limitModal.helperHint': 'Contact your employer to upgrade membership for higher quotas.',
    'limitModal.employerDailyHint': 'See the usage page for details; upgrading increases daily quotas.',
    'limitModal.employerTotalTasksHint': 'You can upgrade to create more tasks.',
    'limitModal.employerTaskKindHint': 'Please upgrade your membership to use this task type.',
    'limitModal.employerPinMessagesHint': 'You can upgrade to add more pin messages.',
    'limitModal.employerL3Hint':
      'You have reached the limit for your current plan. You can view details on the usage page.',
    'limitModal.close': 'Close',
    'limitModal.viewUsage': 'View usage details',
    'limitModal.goSubscription': 'Go to subscription / upgrade',
    'limitModal.playStoreBillingHint':
      'The Google Play app cannot process upgrades here. Please manage your subscription in a browser or the Add to Home Screen app.',
    'settings.notifyEmailLabel': 'Also send activity notifications by email',
    'settings.autoTranslationLabel': 'Automatically translate text entered by the other party',
    'settings.autoTranslationHint':
      'When off, comments, task descriptions, etc. show only the original text and no translation API is called.',
    'settings.notifyWebPushLabel': 'Receive notifications on this device (Web Push)',
    'settings.webPushToggleError': 'Could not update notification setting',
    'settings.notifyWebPushPermissionDeniedHint':
      'Browser notification permission is blocked. Please allow notifications in browser site settings, then refresh this page.',
    'settings.webPushPromptTitle': 'Enable push notifications',
    'settings.webPushPromptBody':
      'Allow notifications to receive task and review updates in real time.',
    'settings.webPushPromptEnable': 'Allow notifications',
    'settings.androidChromePushHintTitle': 'System push needs Chrome',
    'settings.androidChromePushHintBody':
      'This browser does not support Web Push well. Install or update the latest Chrome to receive real-time task and review alerts. In-app bell notifications still work.',
    'settings.androidChromePushHintCta': 'Install / update Chrome',
    'settings.androidChromePushHintDismiss': 'Not now',
    'settings.save': 'Save settings',
    'settings.saving': 'Saving…',
    'settings.saved': 'Saved',
    'settings.invitationCodeTitle': 'Redeem invitation code',
    'settings.invitationCodeSubscriptionTitle': 'Have an invitation code?',
    'settings.invitationCodeHint': 'Enter an 8-character code to extend your trial or unlock a paid-member trial.',
    'settings.invitationCodeInputLabel': 'Invitation code',
    'settings.invitationCodeInputPlaceholder': 'Enter 8-character code',
    'settings.invitationCodeRedeemButton': 'Redeem',
    'settings.invitationCodeRedeemSuccess': 'Invitation code redeemed successfully',
    'settings.invitationCodeRedeemFailed': 'Could not redeem invitation code. Please try again.',
    'settings.invitationCodeInvalid': 'Invitation code is invalid',
    'settings.invitationCodeErrorRegistrationOnly':
      'This invitation code can only be used during registration',
    'settings.invitationCodeErrorNotL1Trial': 'This code is only valid during the trial membership',
    'settings.invitationCodeErrorL1TrialEnded': 'Your trial has already ended',
    'settings.invitationCodeErrorSubscriptionActive': 'You already have an active paid subscription',
    'settings.invitationCodeErrorEmailAlreadyUsed': 'You have already used this invitation code',
    'settings.invitationCodePreviewL1Extend': 'Extend trial by {{days}} day(s). New end: {{endsAt}}',
    'settings.invitationCodePreviewL3Trial': 'Upgrade to paid-member trial for {{days}} day(s). Ends: {{endsAt}}',
    'settings.invitationCodePreviewL3SubscriptionExtend': 'Extend subscription by {{days}} day(s). New end: {{endsAt}}',
    'settings.loadError': 'Could not load settings',
    'settings.saveError': 'Save failed',
    'settings.logout': 'Log out',
    'settings.logoutConfirmTitle': 'Log out?',
    'settings.logoutConfirmDescription': 'You will need to sign in again to use your account.',
    'settings.changePassword': 'Change password',
    'settings.changePasswordHint': 'Change password here while signed in.',
    'settings.googlePasswordManagedHint':
      'This account uses Google sign-in and does not support changing or resetting password.',
    'settings.currentPasswordLabel': 'Current password',
    'settings.newPasswordLabel': 'New password',
    'settings.confirmNewPasswordLabel': 'Confirm new password',
    'settings.changePasswordSubmit': 'Update password',
    'settings.changePasswordSaving': 'Updating…',
    'settings.changePasswordSuccess': 'Password updated. Please sign in again.',
    'settings.changePasswordError': 'Could not change password',
    'settings.changePasswordCurrentPasswordIncorrect': 'Current password is incorrect. Please try again.',
    'settings.expandChangePasswordSectionAria': 'Expand change password section',
    'settings.collapseChangePasswordSectionAria': 'Collapse change password section',
    'settings.expandAccountRemovalSectionAria': 'Expand delete account section',
    'settings.collapseAccountRemovalSectionAria': 'Collapse delete account section',
    'settings.resendVerification': 'Resend verification email',
    'settings.resendVerificationSending': 'Sending…',
    'settings.resendVerificationSuccess': 'Verification email sent. Check your inbox.',
    'settings.resendVerificationError': 'Send failed',
    'settings.resendVerificationCooldown':
      'Please wait 15 minutes before requesting another verification email.',
    'settings.emailUnverifiedBanner':
      'Your email is not verified yet. You will not be able to use the features of this app.',
    'settings.accountRemovalPasswordLabel': 'Enter your password to confirm',
    'settings.accountRemovalGoogleHint':
      'After you submit, a confirmation email is sent. Open the link within 30 minutes and press confirm on the page. Your account is deleted immediately after that confirmation.',
    'settings.backToPairingStatus': 'Back to pairing status',
    'settings.mainMenuAria': 'Main menu',
    'settings.menuHome': 'Home',
    'settings.menuTasksEmployer': 'Tasks',
    'settings.menuTasksHelper': 'Tasks list',
    'settings.menuSettlements': 'Expenses & settlement',
    'settings.menuMoneyGiven': 'Money Given',
    'settings.menuSettlementRecords': 'Settlement records',
    'settings.menuPinMessages': 'Pin message settings',
    'settings.menuBudgets': 'Money Given management',
    'settings.menuReceipts': 'Scan Receipts',
    'settings.menuReceiptBrowse': 'Receipt list',
    'settings.menuFamily': 'Family Member Preferences',
    'settings.menuPairingStatus': 'Pairing status',
    'settings.menuConfirmSettlement': 'Confirm settlement',
    'settings.menuSettings': 'Settings',
    'settings.menuOverview': 'Overview',
    'settings.menuAccountSettings': 'Account settings',
    'settings.menuSubscriptionAndInvite': 'Membership & redeem invitation code',
    'settings.menuMoneyGivenSettlement': 'Money Given & Settlement',
    'settings.menuTodayTask': "Today's task",
    'settings.menuImportPreviousPairing': 'Import from previous pairing',
    'pairing.import.title': 'Import from previous pairing',
    'pairing.import.description':
      'Copy tasks, family members, and pin messages from your most recent ended pairing into the current one. Nothing is selected by default — tick the items you want.',
    'pairing.import.later': 'Later',
    'pairing.import.import': 'Import',
    'pairing.import.sectionTasks': 'Tasks',
    'pairing.import.sectionFamily': 'Family members',
    'pairing.import.sectionPins': 'Pin messages',
    'pairing.import.alreadyImported': 'Already imported',
    'pairing.import.consentLabel':
      'I confirm importing the selected family members and that their existing consent flags apply.',
    'pairing.import.empty':
      'Nothing to import (items may already be imported, or the previous pairing had no data).',
    'pairing.import.success': 'Selected items were imported',
    'pairing.import.error': 'Could not import. Please try again.',
    'pairing.import.loadError': 'Could not load import options. Please try again.',
    'pairing.import.taskQuotaHint': 'Task slots: {{used}}/{{max}} (archived tasks not counted)',
    'pairing.import.pinQuotaHint': 'Pin messages: {{used}}/{{max}}',
    'pairing.import.taskKindStandard': 'Standard task',
    'previousHelper.detailBanner':
      'This task was handled by a previous Helper. Your current Helper cannot see this record.',
    'previousHelper.receiptBanner':
      'This receipt was submitted by a previous Helper. Your current Helper cannot see this record.',
    'previousHelper.settlementBanner':
      'This settlement was submitted by a previous Helper. Your current Helper cannot see this record.',
    'previousHelper.unpairedDetailBanner':
      'This record belongs to an ended pairing. A new Helper will not see it after pairing.',
    'previousHelper.unpairedReceiptBanner':
      'This receipt belongs to an ended pairing. A new Helper will not see it after pairing.',
    'previousHelper.unpairedSettlementBanner':
      'This settlement belongs to an ended pairing. A new Helper will not see it after pairing.',
    'pairing.unpairedBanner':
      'You are not paired with a Helper yet. You can set up tasks, pin messages, family members, and money given now. A Helper can see them after pairing.',
    'pairing.unpairedBannerAction': 'Create pairing code',
    'pairing.unpairedBannerAria': 'Not paired yet',
    'settings.accountRemoval': 'Delete account',
    'settings.accountRemovalHint':
      'After you enter your password and submit, your account is deleted immediately and cannot be restored.',
    'settings.accountRemovalSubmit': 'Confirm and delete account',
    'settings.accountRemovalSuccess':
      'A confirmation email has been sent. Open the link within 30 minutes and press confirm on the page to delete your account.',
    'settings.accountRemovalDeleted': 'Your account has been deleted.',
    'settings.accountRemovalError': 'Request failed. Please try again later.',
    'settings.accountRemovalPasswordIncorrect': 'Incorrect password. Please try again.',
    'settings.closePanel': 'Close',
    'toast.offlineQueued': 'Added to offline queue. It will sync automatically when online.',
    'toast.task.createSuccess': 'Task created successfully!',
    'toast.task.updateSuccess': 'Task saved successfully!',
    'toast.task.completeSuccess': 'Task completion submitted successfully!',
    'toast.task.resubmitSuccess': 'Task resubmitted successfully!',
    'toast.task.reviewedSuccess': 'Task reviewed successfully!',
    'toast.task.needUpdateSuccess': 'Task marked as needs update successfully!',
    'toast.receiptAnalyze.singleSavedOnline':
      'Receipt saved. Next: submit settlement to your employer',
    'toast.receiptAnalyze.singleSavedStillUnsaved':
      'Receipt saved. {{count}} receipt(s) still not saved — please save all first.',
    'toast.receiptAnalyze.singleSavedOffline': 'Receipt saved to offline queue.',
    'toast.receiptAnalyze.batchAllOnlineSuccess':
      'Batch finished: {{successCount}} receipt(s) saved successfully.',
    'toast.receiptAnalyze.batchAllOfflineQueued':
      'Batch finished: {{queuedCount}} receipt(s) added to offline queue.',
    'toast.receiptAnalyze.batchMixedOnlineOfflineSuccess':
      'Batch finished: {{onlineCount}} saved, {{queuedCount}} queued offline.',
    'toast.receiptAnalyze.batchPartialResult':
      'Batch finished: {{onlineCount}} saved, {{queuedCount}} queued offline, {{failedCount}} failed.',
    'toast.receipt.editSuccess': 'Receipt saved successfully!',
    'toast.receipt.deleteSuccess': 'Receipt deleted successfully!',
    'toast.budget.createSuccess': 'Money Given created successfully!',
    'toast.budget.updateSuccess': 'Money Given saved successfully!',
    'toast.budget.deleteSuccess': 'Money Given removed successfully!',
    'toast.pinMessage.createSuccess': 'Pin message created successfully!',
    'toast.pinMessage.updateSuccess': 'Pin message saved successfully!',
    'toast.pinMessage.deleteSuccess': 'Pin message removed successfully!',
    'toast.familyMember.createSuccess': 'Family member added successfully!',
    'toast.familyMember.updateSuccess': 'Family member updated successfully!',
    'toast.familyMember.deleteSuccess': 'Family member removed successfully!',
    'toast.settings.updateSuccess': 'Settings saved successfully!',
    'toast.settings.changePasswordSuccess': 'Password changed successfully!',
    'toast.pairing.joinSuccess': 'Pairing completed successfully!',
    'toast.pairing.unpairSuccess': 'Pairing removed successfully!',
    'toast.pairing.generateCodeSuccess': 'Pairing code generated successfully!',
    'toast.consent.acceptSuccess': 'Consent submitted successfully!',
    'toast.settlement.confirmSuccess': 'Settlement submitted successfully!',
    'toast.settlement.unlockSuccess': 'Settlement unlocked successfully!',
    'toast.settlement.withdrawSuccess': 'Settlement withdrawn successfully!',
    'toast.settlement.reviewedSuccess': 'Settlement reviewed successfully!',
    'toast.settlement.rejectedSuccess': 'Settlement rejected successfully!',
    'toast.settlement.withdrawConfirmTitle': 'Withdraw this settlement?',
    'toast.settlement.withdrawConfirmBody':
      'After withdrawal, this settlement will be marked as withdrawn. Continue?',
    'pairing.generate.title': 'Generate pairing code',
    'pairing.generate.subtitle': 'Generate a code to share with your helper for pairing',
    'pairing.generate.errorGeneric': 'Failed to generate pairing code',
    'pairing.generate.codeLabel': 'Your pairing code',
    'pairing.generate.validUntil': 'Valid until {{datetime}} ({{duration}})',
    'pairing.generate.durationMinutes': '{{count}} minutes',
    'pairing.generate.durationHours': '{{count}} hours',
    'pairing.generate.durationDay': '{{count}} day',
    'pairing.generate.durationDays': '{{count}} days',
    'pairing.generate.copyAria': 'Copy pairing code to clipboard',
    'pairing.generate.copied': 'Copied!',
    'pairing.generate.copyCode': 'Copy code',
    'pairing.generate.regenerate': 'Regenerate',
    'pairing.generate.regenerating': 'Generating…',
    'pairing.generate.regenerateAria': 'Regenerate pairing code',
    'pairing.generate.continue': 'Continue',
    'pairing.generate.continueAria': 'Continue to next step',
    'pairing.generate.generateCta': 'Generate pairing code',
    'pairing.generate.generateAria': 'Generate pairing code',
    'pairing.generate.footer':
      'Share this registration link or QR code with your helper. They will pair with you automatically when they register.',
    'pairing.generate.qrAria': 'Helper registration link QR code',
    'pairing.generate.shareLabel': 'Share',
    'pairing.generate.shareAria': 'Share QR code and registration link',
    'pairing.generate.shareInviteTitle': 'MaidEasy invitation',
    'pairing.generate.shareInviteText':
      '{{nickname}} invites you to use MaidEasy. Tap the link or scan the QR code to register.',
    'pairing.generate.shareEmployerFallback': 'Your employer',
    'pairing.generate.shareError': 'Could not share. Please try again.',
    'pairing.join.title': 'Enter pairing code',
    'pairing.join.subtitle': 'Enter the 6-character code from your employer to pair',
    'pairing.join.errorIncomplete': 'Please enter the full {{length}}-character code',
    'pairing.join.errorGeneric': 'Pairing failed',
    'pairing.join.pairing': 'Pairing…',
    'pairing.join.submit': 'Submit code',
    'pairing.join.submitAria': 'Submit pairing code',
    'pairing.join.inputGroupAria': 'Pairing code input',
    'pairing.join.charAria': 'Code character {{n}}',
    'pairing.join.footer':
      'Ask your employer for the pairing code. The code is valid for {{duration}}.',
    'pairing.invite.landingTitle': 'You are about to pair with {{nickname}}',
    'pairing.invite.landingSubtitle': 'Sign in or register to complete pairing',
    'pairing.invite.emailLogin': 'Sign in with email',
    'pairing.invite.googleLogin': 'Sign in with Google',
    'pairing.invite.register': 'Register',
    'pairing.invite.invalidTitle': 'Invalid pairing link',
    'pairing.invite.errorInvalid': 'This pairing link is invalid or has expired. Ask your employer for a new link.',
    'pairing.invite.errorFormat': 'The pairing code format is invalid.',
    'pairing.invite.previewError': 'Could not verify the pairing code. Please try again later.',
    'pairing.invite.loginBanner': 'After sign-in you will pair with {{nickname}} automatically',
    'pairing.invite.registerBanner': 'Register as Helper and pair with {{nickname}}',
    'pairing.invite.confirmTitle': 'Pair with {{nickname}}',
    'pairing.invite.confirmSubtitle': 'Pairing will complete immediately after you confirm',
    'pairing.invite.confirmCta': 'Confirm pairing',
    'pairing.invite.confirmDialogTitle': 'Pair with {{nickname}}?',
    'pairing.invite.confirmDialogDescription': 'After pairing you can start using MaidEasy.',
    'pairing.invite.employerSignedIn': 'This link is for Helpers. You are signed in as an employer.',
    'pairing.invite.helperAlreadyPaired': 'You are already paired. This link is not needed.',
    'pairing.invite.goPairingStatus': 'Go to pairing status',
    'pairing.invite.expiredTitle': 'Pairing link expired',
    'pairing.invite.expiredBody': 'The pairing code may have expired or already been used.',
    'pairing.invite.contactEmployer': 'Invalid pairing code. Please contact your employer.',
    'pairing.generate.copyInviteLink': 'Copy registration link with pairing code',
    'pairing.generate.copyInviteLinkAria': 'Copy Helper registration link to clipboard',
    'pairing.status.regenerateCode': 'Generate a new code',
    'pairing.status.goGenerateNew': 'Generate new pairing code',
    'auth.googleLoginRequiredTitle': 'Sign in with Google',
    'auth.googleLoginRequiredDescription': 'This email must sign in with Google. Switch to Google sign-in?',
    'auth.googleLoginRequiredConfirm': 'Sign in with Google',
    'pairing.success.title': 'Pairing successful!',
    'pairing.success.subtitle': 'You have been paired successfully',
    'pairing.success.pairedWith': 'Paired with',
    'pairing.success.nextTitle': 'Next: Consent agreement',
    'pairing.success.nextBody':
      'You need to review and accept the consent agreement and Personal Information Collection Statement (PICS) before you can start using the app.',
    'pairing.success.continue': 'Continue to consent',
    'pairing.success.continueAria': 'Continue to consent agreement',
    'pairing.success.nextTitleReady': 'Next: Start using the app',
    'pairing.success.nextBodyReady':
      'You already accepted the Personal Information Collection Statement (PICS) at registration. You can enter the app.',
    'pairing.success.continueApp': 'Go to app',
    'pairing.success.continueAppAria': 'Go to the app home',
    'role.employer': 'Employer',
    'role.helper': 'Helper',
    'pairing.status.loadingAria': 'Loading pairing status',
    'pairing.status.errorGeneric': 'Failed to fetch pairing status',
    'pairing.status.activeTitle': 'Active pairing',
    'pairing.status.yourPartner': 'Your partner',
    'pairing.status.pairedSince': 'Paired since {{datetime}}',
    'pairing.status.notPairedTitle': 'Not paired yet',
    'pairing.status.emailUnverifiedHint': 'Your email is not verified yet. Verify it before using the app.',
    'pairing.status.consentRequiredHint': 'You have not agreed to the terms yet. Agree before using the app.',
    'pairing.status.notPairedEmployer': 'Generate a pairing code to share with your helper',
    'pairing.status.notPairedHelper': 'Enter the pairing code from your employer',
    'pairing.status.goGenerate': 'Generate pairing code',
    'pairing.status.goEnter': 'Enter pairing code',
    'pairing.status.generateAria': 'Generate pairing code',
    'pairing.status.enterAria': 'Enter pairing code',
    'pairing.status.unpairButton': 'Unpair',
    'pairing.status.unpairAria': 'Unpair current pairing',
    'pairing.status.unpairing': 'Unpairing…',
    'pairing.status.unpairConfirm':
      'Are you sure you want to unpair the current pairing? You will need to pair again afterwards.',
    'pairing.status.unpairError': 'Failed to unpair. Please try again later.',
    'protected.loadingAria': 'Loading',
    'app.autoUpdatingTitle': 'Updating app automatically...',
    'app.autoUpdatingSlowHint': 'The update seems slower than expected. Please wait...',
    'app.autoUpdatingVersionLine': 'Current: {{currentVer}} | New: {{newVer}}',
    // Task Management - Common
    'task.status.pending': 'Pending',
    'task.status.notSubmitted': 'Not submitted',
    'task.status.approved': 'Approved',
    'task.status.rejected': 'Rejected',
    'task.status.needsUpdate': 'Needs Update',
    'task.status.cancel': 'Cancelled',
    'receipt.status.save': 'Not settled',
    'receipt.status.submitted': 'Submitted',
    'receipt.status.reviewed': 'Reviewed',
    'task.recurrence.none': 'Once',
    'task.recurrence.daily': 'Daily',
    'task.recurrence.weekly': 'Weekly',
    'task.recurrence.biweekly': 'Biweekly',
    'task.recurrence.monthly': 'Monthly',
    'task.monthlyKind.day': 'By date (day of month)',
    'task.monthlyKind.nthWeekday': 'Nth weekday',
    'task.monthlyKind.lastWeekday': 'Last weekday',
    'task.form.monthlyKindLabel': 'Monthly rule',
    'task.form.monthDaysLabel': 'Days of month',
    'task.form.monthlyOrdinalLabel': 'Which occurrence',
    'task.form.monthlyOrdinal.1': '1st',
    'task.form.monthlyOrdinal.2': '2nd',
    'task.form.monthlyOrdinal.3': '3rd',
    'task.form.monthlyOrdinal.4': '4th',
    'task.form.monthlyOrdinal.5': '5th',
    'task.form.monthlyDayClampHint': 'If a month does not have the selected day (e.g. 29–31), the task falls on the last day of that month.',
    'task.form.monthlyFifthWeekdayHint': 'If a month has no 5th occurrence of the selected weekday, no task is created that month.',
    'task.form.recurrenceMonthDaysRequired': 'Please select at least one day of the month.',
    'task.day.sun': 'Sun',
    'task.day.mon': 'Mon',
    'task.day.tue': 'Tue',
    'task.day.wed': 'Wed',
    'task.day.thu': 'Thu',
    'task.day.fri': 'Fri',
    'task.day.sat': 'Sat',
    // Task List (Employer)
    'task.list.title': 'Task Management',
    'task.list.subtitle': 'Manage tasks for your helper',
    'task.list.tabAll': 'All',
    'task.list.tabToday': 'Today',
    'task.list.tabRecurring': 'Recurring',
    'task.list.tabTodayTask': 'Today',
    'task.list.tabEditTask': 'Edit task',
    'task.list.tabOverdueTask': 'Overdue tasks',
    'task.list.tabEffectiveTask': 'Effective Tasks',
    'task.list.tabExpiredTask': 'Expired Tasks',
    'task.list.addNew': 'Add Task',
    'task.list.addChoiceTitle': 'What to create',
    'task.list.addChoiceSubtitle': 'Select the task type you want to create.',
    'task.list.addChoiceStandard': 'Create Standard task',
    'task.list.addChoiceInventory': 'Create Home Stock',
    'task.list.addChoicePickupDropoff': 'Create Pick up / Drop off Task',
    'task.list.addChoiceMedication': 'Create Medication Reminder',
    'task.list.addChoiceLockedAria': '"{{label}}" requires a membership upgrade',
    'task.list.kindStockTake': 'Home Stock',
    'task.list.kindPickupDropoff': 'Pick up / Drop off',
    'task.list.kindMedication': 'Medication Reminder',
    'task.pickup.loadError': 'Could not load pickup/drop-off task data',
    'task.pickup.createHeading': 'Create Pick up / Drop off Task',
    'task.pickup.editHeading': 'Edit Pick up / Drop off Task',
    'task.pickup.stepProgress': 'Step {{step}} / {{total}}',
    'task.pickup.legHint': 'Choose whether to create pick-up, drop-off, or both',
    'task.pickup.pickupLabel': 'Pick up',
    'task.pickup.dropoffLabel': 'Drop off',
    'task.pickup.legRequired': 'Select at least Drop off or Pick up',
    'task.pickup.personLabel': 'Who to pick up / drop off',
    'task.pickup.personPlaceholder': 'e.g. Dave',
    'task.pickup.suggestHeading': 'Family member suggestions',
    'task.pickup.personRequired': 'Please enter who to pick up / drop off',
    'task.pickup.pickupTimeLabel': 'Pick up time',
    'task.pickup.dropoffTimeLabel': 'Drop off time',
    'task.pickup.pickupTimeRequired': 'Pick up time is required',
    'task.pickup.dropoffTimeRequired': 'Drop off time is required',
    'task.pickup.timesMustDiffer': 'Drop off and Pick up times must be different',
    'task.pickup.locationLabel': 'Location (optional)',
    'task.pickup.locationSuggestHeading': 'Suggestion',
    'task.pickup.locationChip.school': 'School',
    'task.pickup.locationChip.tutoring': 'Tutoring class',
    'task.pickup.locationChip.stadium': 'Stadium',
    'task.pickup.locationChip.soccerPitch': 'Soccer pitch',
    'task.pickup.locationChip.mall': 'Mall',
    'task.pickup.reviewPickupTitle': 'Pick up title',
    'task.pickup.reviewDropoffTitle': 'Drop off title',
    'task.pickup.editTitleAria': 'Edit task title',
    'task.pickup.submitCreate': 'Create Pick up / Drop off Task',
    'task.pickup.customTitleModalTitle': 'Custom title',
    'task.pickup.titleChoiceTitle': 'How to update titles',
    'task.pickup.titleChoicePickup': 'Pick up',
    'task.pickup.titleChoiceDropoff': 'Drop off',
    'task.pickup.titleChoiceKeep': 'Keep original title',
    'task.pickup.titleChoiceUseNew': 'Use new title',
    'task.pickup.titleChoiceCustom': 'Custom',
    'task.pickup.createSuccess': 'Pick up / Drop off task created',
    'task.pickup.updateSuccess': 'Pick up / Drop off task updated',
    'task.pickup.createError': 'Could not create Pick up / Drop off task',
    'task.medication.loadError': 'Could not load medication reminder data',
    'task.medication.createHeading': 'Create Medication Reminder',
    'task.medication.editHeading': 'Edit Medication Reminder',
    'task.medication.stepProgress': 'Step {{step}} / {{total}}',
    'task.medication.personLabel': 'Who needs medication',
    'task.medication.personPlaceholder': 'e.g. Ben',
    'task.medication.suggestHeading': 'Family member suggestions',
    'task.medication.personRequired': 'Please enter who needs medication',
    'task.medication.descPlaceholder': 'e.g. Take diabetes medicine',
    'task.medication.timeRequired': 'Add at least one medication time',
    'task.medication.timesUnique': 'Medication times must be unique',
    'task.medication.timeMax': 'Maximum 10 medication times',
    'task.medication.timesSectionLabel': 'Medication time',
    'task.medication.timeLabel': 'Medication time {{n}}',
    'task.medication.removeTimeAria': 'Remove this time',
    'task.medication.addTime': '+ Add medication time',
    'task.medication.reviewTitle': 'Task title',
    'task.medication.editTitleAria': 'Edit task title',
    'task.medication.submitCreate': 'Create Medication Reminder',
    'task.medication.customTitleModalTitle': 'Custom title',
    'task.medication.titleChoiceTitle': 'How to update title',
    'task.medication.titleChoiceKeep': 'Keep original title',
    'task.medication.titleChoiceUseNew': 'Use new title',
    'task.medication.titleChoiceCustom': 'Custom',
    'task.medication.createSuccess': 'Medication reminder created',
    'task.medication.updateSuccess': 'Medication reminder updated',
    'task.medication.createError': 'Could not create medication reminder',
    'task.form.notifyEmployerLabel': 'Notify me when completed',
    'task.form.remindHelperBeforeDueLabel': 'Remind helper before due time',
    'task.form.remindHelperBeforeDueDisabledHint': 'Set a due time before enabling this reminder.',
    'task.form.remindHelperBeforeDueModalTitle': 'Remind helper before due time',
    'task.form.remindHelperBeforeDueModalIntro':
      'The system will try to send a reminder within 10 minutes before the due time.',
    'task.form.remindHelperBeforeDueModalLimit1':
      'Your helper needs to have notifications enabled (the app needs to be installed or added to the home screen) or email notifications enabled to receive timely reminders.',
    'task.form.remindHelperBeforeDueModalLimit2': 'Your helper needs a network connection when the reminder is sent.',
    'task.form.remindHelperBeforeDueModalLimit3':
      'The actual delivery time may be delayed due to network connection.',
    'task.medication.remindHelperBeforeDueModalIntro':
      'The system will try to send a reminder within 10 minutes before medication time.',
    'task.medication.step3ReminderAskTitle': 'Remind helper before medication time?',
    'task.medication.step3ReminderAskYes': 'Yes, enable reminder',
    'task.medication.step3ReminderAskNo': 'No, skip reminder',
    'task.group.wizard.stepOf': 'Step {{current}} / {{total}}',
    'task.group.wizard.reviewCardHeadingPickup': 'Review pick up / drop off',
    'task.group.wizard.reviewCardHeadingMedication': 'Review medication reminder',
    'task.group.wizard.reviewConfirmPickup': 'Create pick up / drop off',
    'task.group.wizard.reviewConfirmMedication': 'Create medication reminder',
    'task.group.wizard.reviewUpdatePickup': 'Save pick up / drop off',
    'task.group.wizard.reviewUpdateMedication': 'Save medication reminder',
    'task.group.wizard.summaryNone': '(None)',
    'task.group.wizard.summaryPerson': 'Person',
    'task.group.wizard.summaryLocation': 'Location',
    'task.group.wizard.summaryExclusion': 'Exclusion ranges',
    'task.group.wizard.summaryRequirePhoto': 'Require photo submission',
    'task.group.wizard.summaryYes': 'Yes',
    'task.group.wizard.summaryNo': 'No',
    'task.group.wizard.summaryMedicationTimes': 'Reminder times',
    'task.list.empty': 'No tasks yet',
    'task.list.emptyHint': 'Click the Add Task button to create your first task',
    'task.list.overdueEmpty': 'No overdue tasks',
    'task.list.completedCount': 'Completed {{count}} times',
    'task.list.pendingReviewNav': 'Pending review tasks',
    'task.list.pendingReviewNavAria': 'Pending review tasks, {{count}} item(s)',
    'task.list.approvedNav': 'Approved',
    'task.list.approvedNavAria': 'Approved task submissions, {{count}} item(s)',
    'task.list.loadError': 'Failed to load tasks',
    'task.list.pageSubtitle': 'Today’s progress and task overview',
    'task.list.summaryPendingLabel': 'Pending review tasks',
    'task.list.summaryApprovedLabel': 'Approved',
    'task.list.summaryTomorrowLabel': "Tomorrow's tasks",
    'task.list.tomorrowNavAria': "Tomorrow's tasks — {{count}} total",
    'task.list.summaryCount': '{{count}} item(s)',
    'task.list.summaryCountUnitOne': 'item',
    'task.list.summaryCountUnitOther': 'items',
    'task.list.editFiltersAria': 'Edit-task sub-tabs',
    'task.list.tabEditAllTasks': 'Edit all tasks',
    'task.list.menuEditAllTasks': 'Edit all tasks',
    'task.list.menuHistoryTasks': 'Past task history',
    'task.list.menuFutureTasks': 'Upcoming tasks',
    'task.list.menuEditTask': 'Edit task',
    'task.list.menuViewTask': 'View task',
    'task.list.menuReviewTask': 'Review task',
    'task.list.taskRowMenuAria': 'Actions for {{title}}',
    'task.list.titleMenuAria': 'Task management menu',
    'task.list.dateStripCustom': 'Custom',
    'task.list.dateStripAria': 'Select date',
    'task.list.customDateModalTitle': 'Select date',
    'task.list.timeSectionLateNight': 'Late night',
    'task.list.timeSectionMorning': 'Morning',
    'task.list.timeSectionAfternoon': 'Afternoon',
    'task.list.timeSectionEvening': 'Evening',
    'task.list.timeSectionNoTime': 'No specified time',
    'task.list.overdueCardLabel': 'Overdue tasks',
    'task.list.overdueCardView': 'View',
    'task.list.statusEffective': 'Active',
    'task.list.statusNotStarted': 'Not started',
    'task.list.statusExpired': 'Expired',
    'employerDashboard.greetLateNight': 'Hello, {{name}}',
    'employerDashboard.greetMorning': 'Good morning, {{name}}',
    'employerDashboard.greetAfternoon': 'Good afternoon, {{name}}',
    'employerDashboard.greetEvening': 'Good evening, {{name}}',
    'employerDashboard.loadError': 'Could not load dashboard',
    'employerDashboard.subtitle': 'Today at a glance',
    'employerDashboard.todayCompletion': 'Today’s completion',
    'employerDashboard.completedFraction': '{{done}} / {{total}} done',
    'employerDashboard.completedFractionValue': '{{done}} / {{total}}',
    'employerDashboard.completedFractionStatus': 'Done',
    'employerDashboard.pendingTitle': 'Pending review tasks',
    'employerDashboard.pendingEmpty': 'No submissions awaiting review',
    'employerDashboard.submittedAtLine': 'Submitted {{at}}',
    'employerDashboard.viewAllPending': 'View all pending',
    'employerDashboard.monthExpenseTitle': 'Money Given ({{range}})',
    'employerDashboard.budgetPlaceholder': 'No active Money Given yet',
    'employerDashboard.budgetTotal': 'Money Given total',
    'employerDashboard.budgetUsed': 'Spent',
    'employerDashboard.budgetRemaining': 'Remaining',
    'employerDashboard.settlementPendingLine': '{{count}} settlement(s) need attention',
    'employerDashboard.ariaTodayCompletionCard': 'Go to tasks — today’s completion rate',
    'employerDashboard.ariaOverdueCard': 'Go to tasks — overdue tab',
    'employerDashboard.gettingStarted.title': 'Getting started',
    'employerDashboard.gettingStarted.subtitle':
      'You are paired with your Helper. Finish these setup steps so they can work and report in the app.',
    'employerDashboard.gettingStarted.progress': 'Completed {{done}} / {{total}}',
    'employerDashboard.gettingStarted.done': 'Done',
    'employerDashboard.gettingStarted.stepCta': 'Set up',
    'employerDashboard.gettingStarted.later': 'Maybe later',
    'employerDashboard.gettingStarted.laterAria': 'Dismiss getting started guide for later',
    'employerDashboard.gettingStarted.dismissAria': 'Dismiss getting started guide',
    'employerDashboard.gettingStarted.dismissError': 'Could not dismiss getting started guide',
    'employerDashboard.gettingStarted.dismissToast':
      'Getting started guide dismissed. Tap “?” next to the greeting to view it again.',
    'employerDashboard.gettingStarted.helpTriggerAria': 'View getting started guide',
    'employerDashboard.gettingStarted.regionAria': 'Getting started guide',
    'employerDashboard.gettingStarted.stepTaskTitle': 'Create your first task',
    'employerDashboard.gettingStarted.stepTaskHint': 'For example cleaning, shopping, or daily routines',
    'employerDashboard.gettingStarted.stepTaskAria': 'Go to tasks to create your first task',
    'employerDashboard.gettingStarted.stepBudgetTitle': 'Set Money Given',
    'employerDashboard.gettingStarted.stepBudgetHint':
      'Record the household money period and amount for your Helper',
    'employerDashboard.gettingStarted.stepBudgetAria': 'Go to add Money Given',
    'employerDashboard.gettingStarted.stepFamilyTitle': 'Add family members',
    'employerDashboard.gettingStarted.stepFamilyHint': 'Help your Helper learn about your family',
    'employerDashboard.gettingStarted.stepFamilyAria': 'Go to family members',
    'employerDashboard.gettingStarted.stepPinTitle': 'Write a pin message',
    'employerDashboard.gettingStarted.stepPinHint': 'Your Helper will see the reminder on their home',
    'employerDashboard.gettingStarted.stepPinAria': 'Go to create a pin message',
    'helperHome.greetLateNight': 'Hello, {{name}}',
    'helperHome.greetMorning': 'Good morning, {{name}}',
    'helperHome.greetAfternoon': 'Good afternoon, {{name}}',
    'helperHome.greetEvening': 'Good evening, {{name}}',
    'pinMessage.list.title': 'Pin message settings',
    'pinMessage.list.subtitle': 'Home ticker follows this order. Max {{maxItems}} items.',
    'pinMessage.list.savingOrder': 'Saving order…',
    'pinMessage.list.empty': 'No pin messages yet.',
    'pinMessage.form.addTitle': 'Add pin message',
    'pinMessage.form.editTitle': 'Edit pin message',
    'pinMessage.form.limitLine': 'Current level limit: title {{titleMax}}, content {{contentMax}}.',
    'pinMessage.form.saving': 'Saving…',
    'pinMessage.field.title': 'Title',
    'pinMessage.field.titlePlaceholder': 'Example: Supermarket sale tomorrow, remember to buy soy sauce',
    'pinMessage.field.content': 'Content',
    'pinMessage.field.contentPlaceholder': 'Detailed pinned message...',
    'pinMessage.field.visibility': 'Visibility',
    'pinMessage.field.icon': 'Icon',
    'pinMessage.field.displayFrom': 'Display from',
    'pinMessage.field.displayTo': 'Display to',
    'pinMessage.field.status': 'Status',
    'pinMessage.status.active': 'Active',
    'pinMessage.status.inactive': 'Inactive',
    'pinMessage.visibility.both': 'Employer + Helper',
    'pinMessage.visibility.employerOnly': 'Employer only',
    'pinMessage.icon.info': 'Info',
    'pinMessage.icon.remind': 'Remind',
    'pinMessage.icon.specialAlert': 'Special alert',
    'pinMessage.window.always': 'Always visible',
    'pinMessage.window.range': '{{from}} to {{to}}',
    'pinMessage.window.from': 'From {{from}}',
    'pinMessage.window.to': 'Until {{to}}',
    'pinMessage.action.add': 'Add pin message',
    'pinMessage.action.edit': 'Edit',
    'pinMessage.action.remove': 'Remove',
    'pinMessage.action.save': 'Save',
    'pinMessage.action.cancel': 'Cancel',
    'pinMessage.action.close': 'Close',
    'pinMessage.detail.createdAt': 'Created at: {{value}}',
    'pinMessage.detail.updatedAt': 'Last modified: {{value}}',
    'pinMessage.action.moveUp': 'Move up',
    'pinMessage.action.moveDown': 'Move down',
    'pinMessage.confirm.remove': 'Remove this pin message?',
    'pinMessage.error.loadList': 'Failed to load pin messages',
    'pinMessage.error.saveOrder': 'Failed to save order',
    'pinMessage.error.remove': 'Failed to remove pin message',
    'pinMessage.error.notFound': 'Pin message not found',
    'pinMessage.error.loadForm': 'Failed to load form',
    'pinMessage.error.titleContentRequired': 'Title and content are required',
    'pinMessage.error.titleMax': 'Title cannot exceed {{max}} characters',
    'pinMessage.error.contentMax': 'Content cannot exceed {{max}} characters',
    'pinMessage.error.dateOrder': 'Display from date must be before display to date',
    'pinMessage.error.save': 'Failed to save pin message',
    'pinMessage.ticker.openDetails': 'Open pin message details',
    'pinMessage.ticker.prev': 'Previous pin message',
    'pinMessage.ticker.next': 'Next pin message',
    'pinMessage.ticker.title': 'Pin Message',
    'pinMessage.ticker.count': '{{current}}/{{total}}',
    'helperHome.loadError': 'Could not load home',
    'helperHome.summaryLine': '{{count}} task(s) today · {{budget}} left',
    'helperHome.dataPlaceholder': '—',
    'helperHome.cardTodayTasks': "Today's Remaining Tasks",
    'helperHome.todayCompletion': 'Today’s completion',
    'helperHome.submittedFraction': '{{done}} / {{total}}',
    'helperHome.submittedFractionValue': '{{done}} / {{total}}',
    'helperHome.submittedFractionStatus': 'Submitted',
    'helperHome.todayTaskBreakdown': 'Total: {{total}} · Done: {{done}}',
    'helperHome.cardOverdueTasks': 'Overdue tasks',
    'helperHome.overdueTasksHint': 'Tasks not completed past their due time',
    'helperHome.weeklyBudgetTitle': 'Money Received ({{range}})',
    'helperHome.detailsLabel': 'Details',
    'helperHome.remainingLabel': 'Remain',
    'helperHome.totalLabel': 'Total',
    'helperHome.doneLabel': 'Done',
    'helperHome.budgetSpentOverTotal': 'Spent / Money Received',
    'helperHome.budgetSpentLabel': 'Spent',
    'helperHome.budgetReceivedLabel': 'Received',
    'helperHome.budgetRemainingLabel': 'Remaining',
    'helperHome.budgetOverspentLabel': 'Overspent',
    'helperHome.balanceBadge': '{{amount}} left',
    'helperHome.overspentBadge': 'Overspent {{amount}}',
    'helperHome.weeklyBudgetPlaceholder': 'No Money Received data yet',
    'helperHome.budgetUsedFootnote': 'Spent {{used}}',
    'helperHome.actionScanReceipt': 'Scan receipt',
    'helperHome.actionScanReceiptSub': 'AI extract and submit',
    'helperHome.tomorrowBadge': '{{count}}',
    'helperHome.actionTomorrow': "Tomorrow's tasks",
    'helperHome.actionTomorrowSub': "Preview tomorrow's list",
    'helperHome.actionReceiptList': 'Receipt list',
    'helperHome.actionReceiptListSub': 'View and track',
    'helperHome.settlementReadyBadge': 'Settle',
    'helperHome.actionSettlement': 'Agree & Settle',
    'helperHome.actionViewDetails': 'View details',
    'helperHome.actionSettlementSub': 'Spending and periods',
    'helperHome.todayPreviewTitle': 'Today’s preview',
    'helperHome.suggestedTime': 'Deadline',
    'helperHome.viewAllTasks': 'View all tasks',
    'helperHome.ariaTodayTasksCard': 'Go to the Tasks page Today tab',
    'helperHome.ariaTodayCompletionCard': 'Go to tasks — today’s completion rate',
    'helperHome.ariaOverdueTasksCard': 'Go to the Tasks page Overdue tab',
    'helperHome.ariaMoneyReceivedCard': 'Open expenses and settlement details',
    'task.pendingReview.title': 'Pending review tasks',
    'task.pendingReview.subtitle': 'Your helper submitted these — please review',
    'task.pendingReview.empty': 'No submissions awaiting review',
    'task.pendingReview.backToTasks': 'Back to task management',
    'task.pendingReview.dueDate': 'Due date',
    'task.pendingReview.submittedAt': 'Submitted at',
    'task.pendingReview.submittedAtTime': 'Submitted: {{time}}',
    'task.pendingReview.review': 'Review',
    'task.pendingReview.reviewCta': 'Review: {{title}}',
    'task.pendingReview.loadError': 'Failed to load pending reviews',
    'task.approvedList.title': 'Approved',
    'task.approvedList.subtitle': 'Submissions you have approved',
    'task.approvedList.empty': 'No approved submissions yet',
    'task.approvedList.backToTasks': 'Back to task management',
    'task.approvedList.dueDate': 'Due date',
    'task.approvedList.submittedAt': 'Submitted at',
    'task.approvedList.reviewedAt': 'Approved at',
    'task.approvedList.view': 'View details',
    'task.approvedList.viewCta': 'View: {{title}}',
    'task.approvedList.loadError': 'Failed to load approved list',
    // Task Form (Employer)
    'task.form.createTitle': 'Create Task',
    'task.form.editTitle': 'Edit Task',
    'task.form.titleLabel': 'Task Title',
    'task.form.titlePlaceholder': 'e.g., Clean living room',
    'task.form.descLabel': 'Description',
    'task.form.descLabelOptional': 'Description (optional)',
    'task.form.descPlaceholder': 'Describe the task in detail...',
    'task.form.recurrenceLabel': 'Recurrence',
    'task.form.daysLabel': 'Days',
    'task.form.daysHint': 'Select days for weekly/biweekly tasks',
    'task.form.recurrenceWeekdaysRequired': 'Select at least one day for weekly or biweekly tasks',
    'task.form.occurrenceDateLabel': 'Occurrence date',
    'task.form.startDateLabel': 'Start date',
    'task.form.endDateLabel': 'End date',
    'task.form.endDateLabelOptional': 'End date (optional)',
    'task.form.excludeRangesLabel': 'Excluded date ranges',
    'task.form.addExcludeRange': 'Add exclusion range',
    'task.form.removeExcludeRange': 'Remove',
    'task.form.excludeStartDateLabel': 'Exclude start date',
    'task.form.excludeEndDateLabel': 'Exclude end date',
    'task.form.dueTimeLabel': 'Due Time',
    'task.form.dueTimeLabelOptional': 'Due Time (optional)',
    'task.form.dueTimePlaceholder': 'e.g., 18:00',
    'task.form.requirePhotoLabel': 'Require photo submission',
    'task.form.notifyLabel': 'Notify me when completed',
    'task.form.submit': 'Save Task',
    'task.form.submitting': 'Saving...',
    'task.form.createSuccess': 'Task created successfully',
    'task.form.updateSuccess': 'Task updated successfully',
    'task.form.createError': 'Failed to create task',
    'task.form.updateError': 'Failed to update task',
    'task.form.cancel': 'Cancel',
    // Task Review (Employer)
    'task.review.title': 'Review Task',
    'task.review.stockTakeDetailTitle': 'Home Stock Detail',
    'task.review.inventoryTaskSummaryTitle': 'Home Stock Task Summary',
    'task.review.latestHomeStockTitle': 'Latest Home Stock',
    'task.review.latestHomeStockOpenRecentAria': 'Open recent Home Stock submissions',
    'task.review.latestHomeStockRecentTitle': 'Recent submissions',
    'task.review.latestHomeStockTaskDate': 'Task date',
    'task.review.latestHomeStockSubmittedAt': 'Submitted at',
    'task.review.latestHomeStockStatus': 'Status',
    'task.review.latestHomeStockEmptyEmployer': 'No Home Stock submitted by your helper yet.',
    'task.review.latestHomeStockEmptyHelper': 'No Home Stock record yet.',
    'task.review.addHomeStockTaskFab': 'Add Home Stock Task',
    'task.review.addHomeStockTaskFabAria': 'Add Home Stock task',
    'task.review.stocktakePhotosHeading': 'Home Stock photos',
    'task.review.stocktakeResultsHeading': 'Home Stock results',
    'task.review.stockLevelLegendButton': 'Legend',
    'task.review.stockLevelLegendButtonAria': 'Show stock level icon legend',
    'task.review.stockLevelLegendPanelAria': 'Stock level icon legend',
    'task.review.stockLevelLegend.full': 'Full',
    'task.review.stockLevelLegend.half': 'Half',
    'task.review.stockLevelLegend.low': 'Low',
    'task.review.stockLevelLegend.empty': 'Empty',
    'task.review.inventoryPhotosHeading': 'Photo of home stock',
    'task.review.inventoryLocationSummaryTitle': 'Home Stock review summary by location',
    'task.review.inventoryLocationSummaryLoading': 'Loading summary...',
    'task.review.inventoryLocationSummaryCounts': '{{itemCount}} items · {{photoCount}} photos',
    'task.review.inventoryLocationSummaryWithItems': 'Categories with items',
    'task.review.inventoryLocationSummaryNoItems': 'Categories marked no item',
    'task.review.inventoryLocationSummaryNone': 'None',
    'task.review.inventoryLocationSummaryEmpty': 'No location summary yet.',
    'task.review.helperSubmitHomeStock': 'Submit Home Stock',
    'task.review.helperResubmitHomeStock': 'Resubmit Home Stock',
    'task.review.inventoryLineAwaiting': 'Awaiting line detail',
    'task.review.inventoryLinePendingBody': 'Stock level and photo appear after the helper submits.',
    'task.review.inventorySharedPhotoNote': 'Same photo as an item above.',
    'task.review.inventoryStatLocationsCaption': 'LOCATIONS',
    'task.review.inventoryStatTotalPhotosCaption': 'PHOTOS',
    'task.review.inventoryStatLowStockCaption': 'LOW STOCK',
    'task.review.inventoryStatExpiringCaption': 'EXPIRING SOON',
    'task.review.inventoryFilterCategoriesHeading': 'Filter categories',
    'task.review.inventoryLocationPhotosEmpty': 'No photos for this location yet.',
    'task.review.inventoryItemsFilteredEmpty': 'No items match the current category filters.',
    'task.review.inventoryNoCategoriesInLocation': 'No categories to filter for this location.',
    'task.review.inventoryToggleCategoryAria': 'Filter category: {{category}}',
    'task.review.inventoryCategoryEmptyAtLocationBody': 'No items were counted for this category at this location.',
    'task.review.inventoryExpiryExpired': 'Expired',
    'task.review.inventoryExpiryToday': 'Expires today',
    'task.review.inventoryExpiryTomorrow': 'Expires tomorrow',
    'task.review.inventoryExpiryInDays': 'Expires in {{count}} days',
    'task.review.inventoryExpiryAbsolute': '{{date}} expiry',
    'task.review.inventoryLocationTabsAria': 'Select inventory location',
    'task.review.inventoryLocationsSectionHeading': 'Location',
    'task.review.inventoryLocationsFilterReminder':
      'Select a location tab below to filter photos and items for that place.',
    'task.review.inventoryLocationsCoachmarkDismissAria': 'Close location tip',
    'task.review.detailTitle': 'Task details',
    'task.review.helperAwaitingEmployerBanner':
      'This task has been submitted and is waiting for your employer to review it.',
    'task.review.futureDueDetailOnly':
      'This task cannot be submitted yet. You can only view the details here.',
    'task.review.futureDueOutOfRange':
      'This task is outside the early-submit window. You can only view the details here.',
    'task.review.futureDueEmployerTomorrow': 'This task is scheduled for tomorrow.',
    'task.review.futureDueEmployerDays': 'This task is scheduled {{days}} days from now.',
    'task.review.futureDueHelperTomorrow':
      'This task is scheduled for tomorrow. Please note the task date!',
    'task.review.futureDueHelperDays':
      'This task is scheduled {{days}} days from now. Please note the task date!',
    'task.todo.futureSubmitBanner': 'You may submit upcoming tasks early',
    'task.todo.tabFuture': 'Future',
    'task.todo.helperEmptyFuture': 'No tasks in this date range',
    'task.todo.helperEmptyFutureHint': 'Try another range or check with your employer.',
    'task.submitConfirmTitle': 'Submit this task?',
    'task.submitConfirmPrompt': 'Task date is shown below. Confirm submit?',
    'task.submitConfirmAction': 'Confirm submit',
    'task.submitConfirm.dueDateTomorrow': '{{date}} (Tomorrow)',
    'task.submitConfirm.dueDateDaysLater': '{{date}} (in {{days}} days)',
    'task.review.taskInfo': 'Task Info',
    'task.review.submittedBy': 'Submitted by',
    'task.review.taskDate': 'Task date',
    'task.review.completedAt': 'Completed at',
    'task.review.submittedAt': 'Submitted at',
    'task.review.photos': 'Photos',
    'task.review.prevPhoto': 'Previous photo',
    'task.review.nextPhoto': 'Next photo',
    'task.review.photoCount': '{{current}} / {{total}}',
    'task.review.gpsLocation': 'GPS Location',
    'task.review.gpsDisabled': 'GPS not enabled',
    'task.review.gpsStaticMapAlt': 'Submitted task location (map preview)',
    'task.review.openInGoogleMaps': 'Open in Google Maps',
    'task.review.openInGoogleMapsAria': 'Open this location in Google Maps in a new tab',
    'task.review.osmCopyright': '© OpenStreetMap contributors',
    'task.review.addressLabel': 'Address',
    'task.review.addressLoading': 'Looking up address…',
    'task.review.addressUnavailable': 'No address text available for this location',
    'task.review.note': 'Note',
    'task.review.noNote': 'No note',
    'task.review.reviewPrompt': 'Please review ↓',
    'task.review.approve': 'Approve',
    'task.review.reject': 'Reject',
    'task.review.needsUpdate': 'Needs Update',
    'task.review.needsUpdateModalTitle': 'Needs update',
    'task.review.needsUpdateReasonPrompt': 'Enter why this submission needs changes.',
    'task.review.needsUpdateReasonTooShort': 'Reason is too short. Minimum {{min}} characters.',
    'task.review.needsUpdatePhotoHint':
      'The helper can remove wrong photos and upload correct ones when resubmitting.',
    'task.review.reviewSuccess': 'Review submitted',
    'task.review.reviewError': 'Review failed',
    'task.review.versionConflict': 'Data has been modified, please reload',
    'task.review.loadError': 'Failed to load task details',
    // Comments
    'comment.sectionTitle': 'Comments',
    'comment.usage': '{{used}} / {{max}} comments used',
    'comment.charCount': '{{current}} / {{max}} characters',
    'comment.placeholder': 'Write a comment…',
    'comment.writeButton': 'Write a comment',
    'comment.modalTitle': 'Write a comment',
    'comment.draftPending': 'Draft',
    'comment.send': 'Send',
    'comment.sending': 'Sending…',
    'comment.loadError': 'Failed to load comments',
    'comment.sendError': 'Failed to send comment',
    'comment.empty': 'No comments yet',
    'comment.limitReached': 'Comment limit reached for this item.',
    'comment.sendAria': 'Send comment',
    'comment.listAria': 'Comment list',
    'comment.emptyDraft': 'Please enter a comment',
    'comment.tooLong': 'Comment must not exceed {{max}} characters',
    'comment.shortcutHint': 'Press Ctrl+Enter or Cmd+Enter to send',
    'comment.unsentSubmit.title': 'Comment not sent',
    'comment.unsentSubmit.descriptionTask':
      'You typed a comment but did not press Send. Submit the task and post this comment together?',
    'comment.unsentSubmit.descriptionStockTake':
      'You typed a comment but did not press Send. Submit Home Stock and post this comment together?',
    'comment.unsentSubmit.descriptionResubmit':
      'You typed a comment but did not press Send. Resubmit the task and post this comment together?',
    'comment.unsentSubmit.backToEdit': 'Back to edit',
    'comment.unsentSubmit.submitOnlyTask': 'Submit task only',
    'comment.unsentSubmit.submitOnlyStockTake': 'Submit Home Stock only',
    'comment.unsentSubmit.submitOnlyResubmit': 'Resubmit only',
    'comment.unsentSubmit.submitWithComment': 'Submit and post comment',
    'comment.unsentSubmit.working': 'Please wait…',
    // Notifications
    'notification.bellAria': 'Notifications',
    'notification.panelTitle': 'Notifications',
    'notification.empty': 'No notifications',
    'notification.markAllRead': 'Mark all as read',
    'notification.markAllReadAria': 'Mark all notifications as read',
    'notification.loadError': 'Failed to load notifications',
    'notification.expand': 'Show more ▼',
    'notification.collapse': 'Show less ▲',
    'notification.expandAria': 'Expand full notification text',
    'notification.collapseAria': 'Collapse notification text',
    // Today's Todo (Helper)
    'task.today.title': 'Tasks list',
    'task.today.subtitle': 'Browse tasks by history, overdue, today, and future',
    'task.today.empty': 'No tasks for today',
    'task.today.emptyHint': 'Take a rest!',
    'task.todo.helperEmptyOverdue': 'No overdue tasks right now',
    'task.todo.helperEmptyOverdueHint': 'Keep completing tasks before their deadlines.',
    'task.todo.helperEmptyTomorrow': 'Nothing scheduled for tomorrow yet',
    'task.todo.helperEmptyTomorrowHint': 'Check back later or ask your employer if something is added.',
    'task.today.complete': 'Complete',
    'task.today.view': 'View',
    'task.today.pendingReview': '{{count}} tasks pending review by your employer',
    'task.today.sectionNotSubmitted': 'Not submitted',
    'task.today.sectionNeedsUpdate': 'Needs update',
    'task.today.sectionCompleted': 'Completed',
    'task.today.employerFeedbackLabel': "Employer's message:",
    'task.today.dueAt': 'Due at {{time}}',
    'task.today.cannotSubmitTomorrow': 'Available tomorrow',
    'task.today.tomorrowSubmitBanner': 'You can submit the tasks below starting tomorrow.',
    'task.todo.tabHistory': 'History',
    'task.todo.tabOverdue': 'Overdue',
    'task.todo.tabToday': 'Today',
    'task.todo.tabTomorrow': 'Tomorrow',
    'task.todo.deadlineToday': 'Today',
    'task.todo.deadlineTomorrowShort': 'Tmr.',
    'task.todo.inventoryPreviewLocationsLabel': 'Locations',
    'task.todo.inventoryPreviewCategoriesLabel': 'Categories',
    'task.todo.receiptScanBanner': 'Scan receipts to log spending quickly',
    'task.history.startDate': 'Start date',
    'task.history.endDate': 'End date',
    'task.history.filterAria': 'Filter task history by status',
    'task.history.statusAll': 'All',
    'task.history.empty': 'No tasks match your filters in this date range',
    'task.history.emptyHint':
      'Adjust the date range or status filter',
    'task.history.relativeToday': 'Today',
    'task.history.relativeYesterday': 'Yesterday',
    'task.history.relativeTomorrow': 'Tomorrow',
    'task.history.dateHeadingWithRelative': '{{date}} ({{relative}})',
    'task.history.groupTotalCount': '{{count}} record(s)',
    'task.history.completionTime': 'Completed: {{time}}',
    'task.employerFuture.title': 'Upcoming tasks',
    'task.employerFuture.empty': 'No tasks in this date range',
    'task.employerFuture.loadError': 'Failed to load upcoming tasks',
    'task.employerFuture.openDetailCta': 'View task: {{title}}',
    'task.today.loadError': 'Failed to load tasks',
    'receipt.analyze.navLink': 'Receipt AI',
    'receipt.analyze.title': 'Receipt AI analysis',
    'receipt.analyze.subtitle': 'Pick receipt photos; fields are filled automatically (you can edit)',
    'receipt.analyze.pickImages': 'Upload receipt images (Max. 10 images per batch)',
    'receipt.analyze.pickImagesAria': 'Choose receipt image files',
    'receipt.analyze.analyzing': 'Analyzing…',
    'receipt.analyze.analyzingProgress': 'Analyzing {{current}} / {{total}}',
    'receipt.analyze.analyzeError': 'Analysis failed',
    'receipt.analyze.rateLimit': 'Daily AI analyze limit reached ({{used}} / {{limit}}). Try again after {{retryAt}}.',
    'receipt.analyze.manualHint': 'Analysis failed — please enter or correct fields manually.',
    'receipt.analyze.fileTooLarge': 'File exceeds 10MB',
    'receipt.analyze.badType': 'Unsupported image type',
    'receipt.analyze.tooManyFiles': 'Maximum 10 images',
    'receipt.analyze.field.company': 'Company / brand',
    'receipt.analyze.field.shop': 'Shop / branch',
    'receipt.analyze.field.date': 'Date',
    'receipt.analyze.field.currency': 'Currency',
    'receipt.analyze.field.total': 'Total',
    'receipt.analyze.field.remark': 'Remark',
    'receipt.analyze.aiRemarkLabel': 'System remark',
    'receipt.analyze.items': 'Line items',
    'receipt.analyze.itemsAction': 'Action',
    'receipt.analyze.itemName': 'Item',
    'receipt.analyze.qty': 'Qty',
    'receipt.analyze.amount': 'Amount',
    'receipt.analyze.addItem': 'Add line',
    'receipt.analyze.removeItem': 'Remove',
    'receipt.analyze.removeRow': 'Remove this receipt',
    'receipt.analyze.removeConfirmTitle': 'Remove this receipt?',
    'receipt.analyze.removeConfirmBody': 'This analyzed entry will be discarded.',
    'receipt.analyze.leaveConfirmTitle': 'Leave this page?',
    'receipt.analyze.leaveConfirmBody': 'You have receipt photos and data that are not submitted yet. They will be lost if you leave.',
    'receipt.analyze.leaveConfirmLeave': 'Leave',
    'receipt.analyze.leaveConfirmStay': 'Stay on this page',
    'receipt.analyze.budgetLabel': 'Money Received period',
    'receipt.analyze.budgetRequired': 'Please select a Money Received period',
    'receipt.analyze.dateLabel': 'Receipt date',
    'receipt.analyze.dateRequired': 'Please choose a valid receipt date',
    'receipt.analyze.companyRequired': 'Please enter company / brand',
    'receipt.analyze.submit': 'Save',
    'receipt.analyze.submitAll': 'Save All Receipts',
    'receipt.analyze.submitting': 'Submitting…',
    'receipt.analyze.submittingAllProgress': 'Saving {{current}} / {{total}}',
    'receipt.analyze.submitSuccess': 'Submitted',
    'receipt.analyze.submittedDueToPendingSettlement':
      'This receipt date is inside a pending settlement period, so it is marked as "Submitted" directly.',
    'receipt.analyze.submitError': 'Submit failed',
    'receipt.analyze.needImageUrl': 'Stored image URL is missing — cannot submit (check cloud storage)',
    'receipt.analyze.invalidTotal': 'Enter a valid total amount',
    'receipt.analyze.totalMismatchBanner':
      'Total ({{total}}) does not match line items ({{lineSum}}). Please check.',
    'receipt.analyze.totalMismatchConfirmTitle': 'Amount mismatch',
    'receipt.analyze.totalMismatchConfirmSingle':
      'Receipt total ({{total}}) does not match line items ({{lineSum}}). Please check. Settlement uses total ({{total}}). Confirm to save?',
    'receipt.analyze.totalMismatchConfirmBatchLine':
      '{{date}} {{company}} receipt: total ({{total}}) does not match line items ({{lineSum}}). Please check.',
    'receipt.analyze.totalMismatchConfirmBatchIntro':
      'The following receipts have a total that does not match line items:',
    'receipt.analyze.totalMismatchConfirmBatchFooter':
      'Settlement uses the receipt total. Confirm to save?',
    'receipt.analyze.totalMismatchConfirmConfirm': 'Confirm save',
    'receipt.analyze.backToday': 'Back to today',
    'receipt.detail.title': 'Receipt detail',
    'receipt.detail.backToList': 'Back to receipt list',
    'receipt.detail.loadError': 'Could not load receipt',
    'receipt.detail.total': 'Total',
    'receipt.detail.date': 'Receipt date',
    'receipt.detail.submittedAt': 'Submitted at',
    'receipt.detail.employerReviewSubmittedAt': 'Employer review submitted at',
    'receipt.detail.delete': 'Delete receipt',
    'receipt.detail.deleteHint': 'You can only delete receipts that are pending, need update, or cancelled.',
    'receipt.detail.deleteConfirmTitle': 'Delete this receipt?',
    'receipt.detail.deleteConfirmBody': 'This cannot be undone. Comments on this receipt will also be removed.',
    'receipt.detail.deleting': 'Deleting…',
    'receipt.detail.cannotDelete': 'This receipt cannot be deleted in its current status.',
    'receipt.detail.photoHeading': 'Receipt photo',
    'receipt.detail.photoAlt': 'Receipt photo',
    'receipt.detail.employerSavedReceiptNotice':
      'This date is not settled yet, and this receipt is not formally submitted; the helper can still edit it.',
    'receipt.detail.helperUnsettledBanner': 'This receipt is not included in a settlement yet',
    'receipt.detail.helperUnsettledBannerCta': 'Agree & Settle',
    'receipt.detail.helperUnsettledBannerCtaAria': 'Go to Expenses & settlement to submit settlement',
    'receipt.detail.editReceipt': 'Edit receipt',
    'receipt.detail.changeReviewStatus': 'Change status',
    'receipt.detail.changeReviewStatusAria': 'Open receipt status change dialog',
    'receipt.detail.changeReviewStatusTitle': 'Change receipt review status',
    'receipt.detail.changeReviewStatusBody': 'Please choose the new review status.',
    'receipt.edit.title': 'Edit receipt',
    'receipt.edit.subtitle': 'Only “Not settled” receipts can be edited before settlement submission.',
    'receipt.edit.backToDetail': 'Back to receipt detail',
    'receipt.edit.save': 'Save changes',
    'receipt.edit.saving': 'Saving…',
    'receipt.edit.saveError': 'Save failed',
    'receipt.edit.blockedStatus':
      'This receipt cannot be edited. Only your receipts with status “Not settled” can be edited.',
    'receipt.edit.blockedNotOwner': 'You can only edit receipts you submitted.',
    'receipt.analyze.photoHeading': 'Receipt photo',
    'receipt.analyze.photoHeadingOfTotal': 'Receipt photo ({{current}} of {{total}})',
    'receipt.analyze.photoAlt': 'Receipt photo',
    'receipt.analyze.enlargeImageAria': 'View receipt photo full size',
    'receipt.analyze.openDetailAria': 'Open receipt detail',
    'receipt.analyze.scanMore': 'Scan more receipt',
    'receipt.analyze.nextStepSheetTitle': 'Receipt saved',
    'receipt.analyze.nextStepSheetLead': 'Next step: submit settlement to your employer.',
    'receipt.analyze.nextStepSheetUploadAll':
      'Upload all receipts for below settlement period before settling.',
    'receipt.analyze.nextStepSheetLockNote':
      'After settlement submission, receipts in the period are sent to your employer. You cannot upload or edit receipts for those dates.',
    'receipt.analyze.nextStepSheetEmployerReview':
      'Your employer reviews only after you submit settlement.',
    'receipt.analyze.nextStepSheetPeriodLabel': 'Settlement period',
    'receipt.analyze.nextStepSheetPeriodRange': '{{start}} – {{end}}',
    'receipt.analyze.nextStepSheetPeriodLoading': 'Loading settlement period…',
    'receipt.analyze.nextStepSheetPeriodUnknown':
      'No matching Money Received period yet. You can still open Expenses & settlement later to choose a period and submit.',
    'receipt.analyze.nextStepSheetPeriodLocked': 'Locked',
    'receipt.analyze.nextStepSheetLockedHint':
      'The matching period already has an active or locked settlement. Check details on the expenses page.',
    'receipt.analyze.nextStepSheetGoSettle': 'Go to settlement',
    'receipt.analyze.nextStepSheetGoSettleAria': 'Go to Expenses & settlement to submit',
    'receipt.analyze.nextStepSheetLater': 'Later',
    'receipt.analyze.nextStepSheetLaterAria': 'Dismiss reminder and submit settlement later',
    'receipt.list.title': 'Receipt list',
    'receipt.list.subtitle': 'View receipt statuses and submitted times',
    'receipt.list.filterAria': 'Filter receipts by status',
    'receipt.list.filter.all': 'All',
    'receipt.list.filter.save': 'Not settled',
    'receipt.list.filter.submitted': 'Submitted for settlement',
    'receipt.list.filter.reviewed': 'Reviewed',
    'receipt.list.startDate': 'Start date (optional)',
    'receipt.list.endDate': 'End date (optional)',
    'receipt.list.requiredStartDate': 'Start date',
    'receipt.list.requiredEndDate': 'End date',
    'receipt.list.empty': 'No receipts match these filters.',
    'receipt.list.loadError': 'Could not load receipt list',
    'receipt.list.loadMore': 'Load more',
    'receipt.list.openDetailAria': 'Open receipt {{date}}: {{summary}}',
    'receipt.list.submittedAtLine': 'Submitted at: {{at}}',
    // Budget (employer — Screen 2.4)
    'budget.title': 'Money Given',
    'budget.subtitle': 'Set main and Add-on Money periods and amounts',
    'budget.tabActive': 'In progress',
    'budget.tabSettled': 'Settled',
    'budget.tabsAria': 'Money Given list categories',
    'budget.addNew': 'New Money Given',
    'budget.addNewAria': 'Create a new Money Given period',
    'budget.addOn': 'Add',
    'budget.addOnAria': 'Add-on Money for this period',
    'budget.form.addOnCreateTitle': 'Add-on Money',
    'budget.form.parentPeriod': 'Main period',
    'budget.empty': 'No data',
    'budget.emptySettled': 'No settled Money Given periods yet.',
    'budget.emptyHint': 'Create Money Given so your helper can tag receipts and run settlements.',
    'budget.filterStartDate': 'Start date',
    'budget.filterEndDate': 'End date',
    'budget.totalAmount': 'Money Given amount',
    'budget.mainAmountLabel': 'Money Given',
    'budget.subAmountLabel': 'Add-on amount',
    'budget.spent': 'Spent',
    'budget.remaining': 'Remaining',
    'budget.badgeLocked': 'Has locked settlement',
    'budget.badgeSettled': 'Settled',
    'budget.timeLabel': 'Time: {{value}}',
    'budget.edit': 'Edit',
    'budget.editAria': 'Edit Money Given {{start}} to {{end}}',
    'budget.mergedChildCount': 'Includes {{count}} Add-on Money item(s)',
    'budget.expandChildrenAria': 'Expand Add-on Money details for {{start}} to {{end}}',
    'budget.collapseChildrenAria': 'Collapse Add-on Money details for {{start}} to {{end}}',
    'budget.backList': 'Back to Money Given list',
    'budget.loadError': 'Could not load Money Given',
    'budget.saveError': 'Save failed',
    'budget.form.createTitle': 'New Money Given',
    'budget.form.editTitle': 'Edit Money Given period',
    'budget.form.startDate': 'Start date',
    'budget.form.endDate': 'End date',
    'budget.form.amount': 'Money Given amount',
    'budget.form.amountHint': 'Must be zero or positive; decimals allowed.',
    'budget.form.submitCreate': 'Create Money Given',
    'budget.form.submitEdit': 'Save changes',
    'budget.form.saving': 'Saving…',
    'budget.form.cancel': 'Cancel',
    'budget.form.remove': 'Remove',
    'budget.form.removeAria': 'Remove this Money Given period',
    'budget.form.removeConfirm': 'Remove',
    'budget.form.removing': 'Removing…',
    'budget.removeConfirmTitle': 'Remove this Money Given?',
    'budget.removeConfirmBody':
      'Remove this Money Given period? This cannot be undone.',
    'budget.removeConfirmReceiptsExtra':
      '{{count}} receipt(s) are linked to this Money Given period; removing it clears that link.',
    'budget.removeError': 'Could not remove Money Given',
    'budget.validation.rangeRequired': 'Please enter start and end dates',
    'budget.validation.dates': 'End date cannot be before start date',
    'budget.validation.amount': 'Enter a valid amount (zero or positive)',
    'budget.lockedEditHint':
      'This Money Given is tied to a locked settlement. If you shorten the date range, every linked receipt must still fall entirely inside the new range. Save will fail if any receipt date is outside the new range or overlaps another settlement period; resolve receipts or settlements first if needed.',
    'budget.summaryReceipts': 'This Money Given has {{count}} linked receipt(s)',
    // Settlement
    'settlement.title': 'Settlement & locking',
    'settlement.employerFinanceTitle': 'Money Given & Settlement',
    'settlement.employerFinanceTitleMenuAria': 'Money Given & Settlement menu',
    'settlement.employerFinanceMenuPendingReviewList': 'Settlements pending review',
    'settlement.employerFinanceMenuReceiptList': 'Receipt list',
    'settlement.formTitle': 'New settlement',
    'settlement.subtitle':
      'Pick a period. Totals include all receipts in range versus the matching Money Received amount. Locking freezes receipts in that range.',
    'settlement.navLink': 'Expense settlement',
    'settlement.budgetLabel': 'Money Received period',
    'settlement.startDate': 'Start date',
    'settlement.endDate': 'End date',
    'settlement.preview': 'Preview totals',
    'settlement.previewing': 'Calculating…',
    'settlement.totalSpent': 'Total Spent',
    'settlement.dailyDetail': 'Breakdown',
    'settlement.dailyDetailOpen': 'Open daily expense breakdown',
    'settlement.dailyDetailTitle': 'Daily expense breakdown',
    'settlement.dailyDetailDate': 'Date',
    'settlement.dailyDetailReceiptCount': 'Receipt count',
    'settlement.dailyDetailAmount': 'Amount',
    'settlement.budgetAmount': 'Money Given',
    'settlement.submittedAtLabel': 'Submitted at',
    'settlement.balance': 'Settlement Result',
    'settlement.surplus': 'Surplus',
    'settlement.overspent': 'Overspent',
    'settlement.confirm': 'Confirm settlement & lock',
    'settlement.confirming': 'Processing…',
    'settlement.success': 'Settlement locked',
    'settlement.error': 'Something went wrong',
    'settlement.listTitle': 'Settlement history',
    'settlement.employerFinanceTabsAria': 'Employer Money Given and settlement tabs',
    'settlement.employerTabBudget': 'Money Given',
    'settlement.employerTabSettlement': 'Settlement',
    'settlement.employerSubTabReceipts': 'Receipt list',
    'settlement.filterStartDate': 'Start date',
    'settlement.filterEndDate': 'End date',
    'settlement.employerFilterToDateOptional': 'End date (optional)',
    'settlement.empty': 'No settlements yet',
    'settlement.locked': 'Reviewed',
    'settlement.unlocked': 'Unlocked',
    'settlement.status.submitted': 'Pending review',
    'settlement.status.unsettled': 'Unsettled',
    'settlement.status.settled': 'Settled',
    'settlement.unsettledEmployerSavedReceiptNotice':
      'Your helper has not submitted a settlement for this date, and the receipt has not been officially submitted. Your helper can still modify the receipt content.',
    'settlement.givenVsExpensesTitle': 'Money Given vs Expenses',
    'settlement.givenVsExpensesSubtitle': 'Overview of the last 4 Money Given periods',
    'settlement.givenVsExpensesGiven': 'Given',
    'settlement.givenVsExpensesSpent': 'Spent',
    'settlement.givenVsExpensesPendingSpent': 'Pending spent',
    'settlement.helperReceivedVsExpensesTitle': 'Money Received vs Expenses',
    'settlement.helperReceivedVsExpensesSubtitle': 'Overview of the last 4 Money Received periods',
    'settlement.reviewNow': 'Review now',
    'settlement.pendingReviewPageTitle': 'Settlements pending review',
    'settlement.historyEventAtLabel': 'Status time',
    'settlement.historyViewDetailAria': 'View settlement history details',
    'settlement.historyDetailButton': 'Detail',
    'settlement.pastSettlementHistoryTitle': 'Past settlement history',
    'settlement.historyStatus.withdrawn': 'Helper withdrawn',
    'settlement.historyStatus.rejected': 'Employer rejected',
    'settlement.historyStatus.unlocked': 'Employer unlocked',
    'settlement.status.reviewed': 'Reviewed',
    'settlement.status.rejected': 'Rejected',
    'settlement.status.withdrawn': 'Withdrawn',
    'settlement.recordStatusFilterAria': 'Filter settlements by status',
    'settlement.recordStatusFilter.submitted': 'Pending',
    'settlement.recordStatusFilter.reviewed': 'Reviewed',
    'settlement.recordStatusFilter.rejected': 'Rejected',
    'settlement.recordStatusFilter.withdrawn': 'Withdrawn',
    'settlement.recordStatusFilter.unlocked': 'Unlocked',
    'settlement.lockHint':
      'Spending for these dates was already settled and locked. Ask your employer to unlock that settlement in the app before you can edit or delete receipts in this period.',
    'settlement.detailTitle': 'Settlement detail',
    'settlement.detailTitleMenuAria': 'Settlement detail menu',
    'settlement.menuEditMoneyGiven': 'Edit money given',
    'settlement.menuAppendMoneyGiven': 'Add money given',
    'settlement.unlock': 'Unlock settlement',
    'settlement.withdraw': 'Withdraw settlement submission',
    'settlement.reviewedAction': 'Mark as reviewed',
    'settlement.rejectReasonPrompt': 'Please enter reject reason',
    'settlement.rejectModalTitle': 'Reject this settlement',
    'settlement.unlockConfirmTitle': 'Unlock settlement?',
    'settlement.unlockConfirmBody':
      'After unlock, the helper can edit receipts in this date range. This action is recorded in the audit log.',
    'settlement.unlocking': 'Unlocking…',
    'settlement.unlockSuccess': 'Unlocked',
    'settlement.auditTitle': 'Unlock history',
    'settlement.auditEmpty': 'No unlock events yet',
    'settlement.receiptsTitle': 'Receipts in range',
    'settlement.viewReceipt': 'View receipt',
    'settlement.noBudgets':
      'You can upload receipts and settle without setting up Money Given first; if no matching period exists, the Money Received total is treated as 0.',
    'settlement.employerNoBudgetsBanner':
      'No Money Given periods yet. Settlements still work; consider adding Money Given for clarity.',
    'settlement.selectRange': 'Please choose start and end dates',
    'settlement.confirmLockModalTitle': 'Submit this settlement to employer?',
    'settlement.confirmEmptyModalTitle': 'No receipts in this range',
    'settlement.confirmLockPrompt':
      'Confirm to submit {{start}} - {{end}} settlement? Receipts in the range cannot be edited and uploaded.',
    'settlement.confirmEmptyReceiptsPrompt': 'There are no receipts in this range. Submit anyway?',
    'settlement.previewFirst': 'Preview is not ready yet. Wait for totals to load or adjust the dates before locking.',
    'settlement.budgetAmountHint':
      'Sum of the main period and all Add-on Money amounts.',
    'settlement.helperHeroTitle': 'Expenses & settlement',
    'settlement.helperHeroSubtitle': 'Manage your expenses and submit to your employer.',
    'settlement.helpPopoverTriggerAria': 'View settlement steps and notes',
    'settlement.helpPopoverAria': 'Settlement steps and notes',
    'settlement.helpPopoverTitle': 'How to submit settlement',
    'settlement.helpPopoverStep1': 'Scan and save all receipts for the settlement period.',
    'settlement.helpPopoverStep2': 'On this page, choose a Money Received period and preview spending.',
    'settlement.helpPopoverStep3': 'Tap Agree & Settle to submit for employer review.',
    'settlement.helpPopoverNotesHeading': 'Notes',
    'settlement.helpPopoverNote1':
      'After saving receipts, you must submit settlement before your employer reviews.',
    'settlement.helpPopoverNote2':
      'After submission, you cannot upload or edit receipts for those dates until the employer rejects, unlocks, or you withdraw.',
    'settlement.helperQuickActions': 'Quick actions',
    'settlement.helperActionScanTitle': 'Scan receipt',
    'settlement.helperActionScanMeta': 'AI extract and submit',
    'settlement.helperActionReceiptListTitle': 'Receipt list',
    'settlement.helperActionReceiptListMeta': 'View submitted and pending receipts',
    'settlement.helperActionLockTitle': 'Agree & Settle',
    'settlement.helperActionLockMeta': 'Lock the selected range and submit to employer',
    'settlement.helperActionHistoryTitle': 'Settlement history',
    'settlement.helperActionHistoryMeta': 'View past settlement records',
    'settlement.helperDateRangeAria': 'Selected settlement period; expand to change dates',
    'settlement.helperStatPeriodBudget': 'Money Received',
    'settlement.moneyReceivedDetailOpen': 'Open money received details',
    'settlement.moneyReceivedDetailTitle': 'Money Received details',
    'settlement.moneyReceivedDetailReceivedDate': 'Time',
    'settlement.moneyReceivedDetailPeriod': 'Period',
    'settlement.moneyReceivedDetailAmount': 'Amount',
    'settlement.moneyReceivedDetailAddOn': 'Add-on',
    'settlement.moneyGivenDetailOpen': 'Open Money Given details',
    'settlement.moneyGivenDetailTitle': 'Money Given details',
    'settlement.helperStatTotalSpent': 'Total spent',
    'settlement.helperStatBalance': 'Balance',
    'settlement.dailyBreakdownVsBudgetTitle': 'Daily spend',
    'settlement.dailyBudgetAmount': 'Daily Money Received breakdown',
    'settlement.dailyVsBudgetColBudget': 'Money Received',
    'settlement.dailyVsBudgetColExpense': 'Spend',
    'settlement.dailyBudgetVariance': 'Diff.',
    'settlement.expensePreview': 'Expense preview',
    'settlement.breakdownModalTitle': 'Daily expense breakdown',
    'settlement.openBreakdownAria': 'Open Breakdown: daily receipt counts and amounts',
    'settlement.helperConfirmDateRangeLabel': 'Settlement date range',
    'settlement.helperConfirmDateRangeSeparator': 'to',
    'settlement.helperConfirmLockNotice':
      'After settlement, expense records for this period will be locked. Your employer must unlock them before you can edit again.',
    'settlement.helperConfirmPrimary': 'Submit',
    'settlement.helperSettleButton': 'Agree & Settle',
    'settlement.confirmAction': 'Confirm',
    'settlement.helperReviewedByEmployerNotice': 'Your employer has reviewed this settlement.',
    'settlement.helperSubmittedNotice':
      'A submitted settlement already exists in this date range and is pending employer review.',
    'settlement.helperSubmittedRangeNotice':
      'A submitted settlement already exists in this date range ({{range}}) and is pending employer review.',
    'settlement.helperSubmittedRangesNotice':
      'Submitted settlements already exist in this date range ({{ranges}}) and are pending employer review.',
    'settlement.helperSubmittedAndReviewedRangesNotice':
      'Submitted/reviewed settlements already exist in this date range ({{ranges}}). Submitted items are pending employer review.',
    'settlement.confirmOverlapSubmittedRangesError':
      'The selected dates overlap submitted settlements ({{ranges}}). Please withdraw them or wait for employer review before submitting overlapping dates.',
    'settlement.confirmOverlapLockedRangesError':
      'The selected dates overlap locked settlement periods ({{ranges}}). Ask your employer to unlock them before submitting overlapping dates.',
    'settlement.helperConfirmPageTitle': 'Agree & Settle',
    'settlement.helperConfirmSubtitle': 'Review totals for the selected period, then lock and submit to your employer.',
    'settlement.helperHistoryPageTitle': 'Settlement history',
    'settlement.helperHistoryTabBySettlement': 'By settlement',
    'settlement.helperHistoryTabByMoneyReceived': 'By money received',
    'settlement.helperHistoryViewTabsAria': 'Settlement history view',
    'settlement.helperResubmitBannerPrefix': 'No up-to-date settlement has been submitted for this period. Tap ',
    'settlement.helperResubmitBannerLink': 'here',
    'settlement.helperResubmitBannerSuffix': ' to submit.',
    'settlement.helperResubmitBannerLinkAria': 'Go to Agree & Settle',
    'settlement.helperBackToExpenses': 'Back to expenses & settlement',
    'settlement.totalSpentReceiptHint': 'Includes saved, submitted, and reviewed receipts.',
    'settlement.period': '{{start}} – {{end}}',
    'settlement.backList': 'Back to settlements',
    'apiError.settlementLockedReceipt':
      'This receipt date ({{receiptDate}}) falls within a period that was already settled and locked ({{start}} to {{end}}). Ask your employer to unlock that settlement in the app; then you can add, edit, or delete receipts for those dates.',
    'apiError.settlementRangeOverlapsLocked':
      'The dates you picked ({{reqStart}} to {{reqEnd}}) overlap a period that was already settled and locked ({{start}} to {{end}}). Ask your employer to unlock that settlement first, or choose dates that do not overlap.',
    'apiError.settlementNoReceiptsAck':
      'There are no receipts in this date range. If you still want to lock an empty period, confirm using the on-screen option first.',
    'apiError.budgetOverlapsSettlement':
      'This Money Given period overlaps dates that already belong to an existing settlement. Choose dates that fall completely outside every settlement period.',
    'apiError.budgetShrinkReceiptsOutside':
      'These dates are too narrow: one or more linked receipts have dates outside the new range. Widen the Money Given dates or adjust those receipts first.',
    'apiError.budgetDeleteLocked':
      'This Money Given cannot be removed because it has a locked settlement. Resolve the settlement first.',
    'apiError.budgetMainOverlap':
      'This Money Given period overlaps existing period(s): {{ranges}}. Please choose a non-overlapping date range.',
    'apiError.subBudgetOutOfMainRange':
      'The Add-on Money period must be fully inside the selected main period.',
    'apiError.mainBudgetLockedBySubmittedSettlement':
      'This Money Given period has a submitted settlement and is read-only now. You cannot edit or remove it.',
    'apiError.receiptDeleteStatusNotAllowed':
      'This receipt cannot be deleted in its current status. Only receipts waiting for review, marked as needing an update, or cancelled can be deleted.',
    'apiError.receiptUpdateStatusNotAllowed':
      'This receipt cannot be edited right now. You can only edit receipts that are waiting for employer review or that the employer asked you to update.',
    'apiError.settlementAlreadyUnlocked':
      'This settlement is already unlocked. Refresh the page to see the latest status.',
    // Auto-translation (user-entered content)
    'translation.autoTranslatedTo': 'Automatically translated to {{language}}. Translation may be incorrect.',
    'translation.showOriginal': 'Show original',
    'translation.showTranslated': 'Show translation',
    'translation.hintButtonAria': 'Translation info',
    'translation.hintPanelAria': 'Auto-translation hint',
    'translation.lang.zhHant': 'Traditional Chinese',
    'translation.lang.zhHans': 'Simplified Chinese',
    'translation.lang.en': 'English',
    'translation.lang.id': 'Indonesian',
    'translation.lang.fil': 'Filipino',
    // Family members
    'family.navLink': 'Family members',
    'family.title': 'Family Member Preferences',
    'family.subtitle': 'Employers can add or edit; both parties can view.',
    'family.listConsentLabel': 'I confirm family members have consented to share their data.',
    'family.listConsentHint': 'Check the box above to view or manage family member data.',
    'family.addMember': 'Add member',
    'family.addMemberAria': 'Add family member',
    'family.editMember': 'Edit',
    'family.editMemberAria': 'Edit family member',
    'family.deleteMember': 'Remove',
    'family.deleteConfirm': 'Remove this family member? This cannot be undone.',
    'family.empty': 'No family members yet',
    'family.emptyHint': 'Tap “Add member” to add one.',
    'family.emptyHintHelper': 'Only the employer can add family members. Contact your employer if needed.',
    'family.field.name': 'Name',
    'family.field.nickname': 'Nickname',
    'family.formField.nickname': 'Nickname (optional)',
    'family.formField.birthday': 'Birthday (optional)',
    'family.formField.remark': 'Regular activities (optional)',
    'family.formField.foodPreference': 'Food preference (optional)',
    'family.formField.foodAllergy': 'Food allergy (optional)',
    'family.formField.medicalCondition': 'Medical condition (optional)',
    'family.formField.hashtags': 'Tags (optional)',
    'family.placeholder.name': 'e.g. Ben Chan',
    'family.placeholder.nickname': 'e.g. elder brother',
    'family.placeholder.foodPreference': 'e.g. mild food, likes apples',
    'family.placeholder.foodAllergy': 'e.g. peanuts',
    'family.placeholder.medicalCondition': 'e.g. high blood pressure, takes daily medicine',
    'family.placeholder.hashtags': 'e.g. light diet, low salt',
    'family.field.birthday': 'Birthday',
    'family.field.age': 'Age',
    'family.field.foodPreference': 'Food preference',
    'family.field.foodAllergy': 'Food allergy',
    'family.field.medicalCondition': 'Medical condition',
    'family.field.remark': 'Regular activities',
    'family.field.remarkPlaceholder': 'e.g. Football class - Sat 10am',
    'family.field.hashtags': 'Tags',
    'family.field.hashtagsHint': 'Enter several tags separated by commas, semicolons, or new lines.',
    'family.birthday.optionalHint': 'Optional. You may leave the year blank and only choose month and day.',
    'family.birthday.exampleHint': 'e.g. year 2011, month 03, day 15; or pick month and day only (leave year blank).',
    'family.birthday.yearOptional': 'Year (optional)',
    'family.birthday.month': 'Month',
    'family.birthday.day': 'Day',
    'family.display.birthdaySkipped': 'No birthday on file',
    'family.display.ageWithYear': '{{age}} years old (born in {{year}})',
    'family.display.birthdayMonthDay': '{{month}}/{{day}}',
    'family.validation.birthdayNeedMonthDay': 'To add a birthday, choose both month and day.',
    'family.validation.birthdayInvalid': 'That birthday is not valid. Check year, month, and day.',
    'family.formConsentLabel': 'I confirm this person has consented to share their data.',
    'family.formCreateTitle': 'Add family member',
    'family.formEditTitle': 'Edit family member',
    'family.formSubmit': 'Save',
    'family.formSubmitting': 'Saving…',
    'family.formCancel': 'Cancel',
    'family.loadError': 'Could not load family members',
    'family.saveError': 'Save failed',
    'family.deleteError': 'Remove failed',
    'family.validation.consentRequired': 'Please confirm consent before saving.',
    // Inventory (helper)
    'inventory.title': 'Home Stock task',
    'inventory.subtitle':
      'Add photos in batches (one photo can cover many items), AI matches your checklist, then add photos or confirm manually before submit',
    'inventory.stocktakePanelTitle': 'Stocktake photos',
    'inventory.stocktakePanelHint': 'One image may show multiple list items.',
    'inventory.stocktakeMaxFilesHint': 'Up to 10 images at a time',
    'inventory.uploadedStocktakePhotosHeading': 'Uploaded Home Stock photos',
    'inventory.uploadedStocktakePhotoAlt': 'Home Stock photo {{n}}',
    'inventory.enlargePhotoAria': 'View Home Stock photo full size',
    'inventory.removePhotoAria': 'Remove this Home Stock photo',
    'inventory.removeStocktakePhotoTitle': 'Remove this photo?',
    'inventory.removeStocktakePhotoDescription':
      'You can remove photos before submit. If any line used this image for AI, that will be cleared—add a new photo or set levels manually.',
    'inventory.removeStocktakePhotoConfirm': 'Remove',
    'inventory.notRecognizedByAi':
      'After at least one photo is uploaded, these lines were still not matched by AI. Add another photo or choose stock levels below (they can share the same batch photo when you submit).',
    'inventory.matchedByAiHint': 'Filled by AI (you can change it)',
    'inventory.stockLevel': 'Stock level',
    'inventory.stockLevelRequired': 'Choose a stock level for every list item.',
    'inventory.level.full': 'Full',
    'inventory.level.half': 'Half',
    'inventory.level.low': 'Low',
    'inventory.level.empty': 'Empty',
    'inventory.level.unknown': '—',
    'inventory.photoLabel': 'Item photo',
    'inventory.analyze': 'AI analyze',
    'inventory.analyzing': 'Analyzing…',
    'inventory.stocktakeUploading': 'Uploading…',
    'inventory.stocktakeUploadingProgress': 'Uploading {{current}} / {{total}}',
    'inventory.analyzeNeedPhoto': 'Choose a photo before analyzing',
    'inventory.analyzeError': 'Analysis failed. Try again.',
    'inventory.stocktakeDailyAiLimitPhotoSaved':
      'Your daily AI image quota is used up (shared with receipt scanning). No AI ran for this upload. If the cloud upload succeeded, the thumbnail stays in the list—enter stock levels manually; the quota resets at the next UTC day.',
    'inventory.needAtLeastOneBatchPhoto': 'Upload at least one Home Stock photo.',
    'inventory.needPhotoBeforeStockLevels':
      'Upload at least one Home Stock photo for this location first. Stock levels, expiry, and manual item entry appear after that.',
    'inventory.unmatchedUseSharedPhotoHint':
      'If this batch already has an uploaded image, unrecognized lines will share that same link when you submit; you do not need a separate photo for every line.',
    'inventory.emptyTemplate': 'This checklist has no items and cannot be submitted',
    'inventory.helperOnly': 'Only helpers can stocktake',
    'inventory.chainBlockedBanner': 'The previous home stock is still waiting for review, so this home stock task is locked for now.',
    'inventory.wizard.locationStepBadge': 'Location {{current}} of {{total}}: {{name}}',
    'inventory.wizard.reviewPhaseHint': 'Review: confirm completion time, then submit',
    'inventory.wizard.reviewTitle': 'Summary by location',
    'inventory.wizard.reviewLocationSummaryPhotos': '{{photos}} photos',
    'inventory.wizard.reviewLocationSummaryEmptyCategories': '{{noCat}} categories marked empty',
    'inventory.wizard.noItemsHeading': 'No items for these categories at this location',
    'inventory.wizard.noItemsHint': 'If a category truly has no items here, tick to confirm (no stock level needed).',
    'inventory.wizard.noItemsHintTickFirst':
      'Go through each employer category first: tick below when there is truly nothing here for that category; only then fill stock and expiry rows for detected or manually added items.',
    'inventory.wizard.noItemsConfirmModalTitle': 'Confirm empty categories',
    'inventory.wizard.noItemsConfirmModalDescription':
      'These categories have no counted items at this location. Tick each confirmation line, then press Confirm to continue.',
    'inventory.wizard.noItemCategoryAria': 'No items for category “{{category}}” at this location',
    'inventory.wizard.noItemCategoryLabel': 'No items for category “{{category}}” at this location',
    'inventory.wizard.rowSkippedNoItemsCategory': 'This row’s category is marked as having no items at this location.',
    'inventory.wizard.manualItemNoItemsCategoryBanner':
      'This category is marked as “no items of this type at this location.” You can still enter stock and expiry here; to include it in your submission, clear that mark first.',
    'inventory.wizard.continueNextLocation': 'Continue to next location',
    'inventory.wizard.goToReview': 'Go to review',
    'inventory.wizard.photoRequiredPerLocation': 'Upload at least one stocktake photo for every location.',
    'inventory.wizard.noItemsConflict': 'You marked this category as having no items; clear stock levels for those rows.',
    'inventory.wizard.categoryMarkNoItems': 'Category “{{category}}” has no matching list rows; mark “no items for this category”.',
    'inventory.wizard.helperSingleStep1Badge': 'Step 1/2: take or upload Home Stock photos',
    'inventory.wizard.helperSingleStep2Badge': 'Step 2/2: enter stock levels and expiry',
    'inventory.wizard.continueToStockEntry': 'Next: enter stock details',
    'inventory.wizard.locationHubTitle': 'Locations',
    'inventory.wizard.locationHubHint':
      'Work on any location in any order — tap a card to switch. Each location needs at least one photo plus completed categories/stocktake rules before you can submit from the summary.',
    'inventory.wizard.locationHubReviewHint':
      'Check each location looks correct. Tap “Continue editing this location” to go back when you need changes.',
    'inventory.wizard.locationHubProgress': 'Ready to submit: {{done}} / {{total}} locations',
    'inventory.wizard.locationHubSelectAria': 'Switch to {{name}}',
    'inventory.wizard.locationHubCurrentBadge': 'Editing',
    'inventory.wizard.locationHubStatusPhotoOk': 'Photo added',
    'inventory.wizard.locationHubStatusPhotoMissing': 'Photo missing',
    'inventory.wizard.locationHubStatusReady': 'All required fields OK',
    'inventory.wizard.locationHubStatusIncomplete': 'Still incomplete',
    'inventory.wizard.editLocationFromReview': 'Continue editing this location',
    'inventory.wizard.editLocationFromReviewAria': 'Return to editing {{name}}',
    'inventory.wizard.notAllLocationsReady':
      'Some locations are still incomplete — use “Locations” to switch and finish photos, categories, and stocktake fields (progress updates when ready).',
    'inventory.wizard.selectLocationTitle': 'Select location',
    'inventory.wizard.selectLocationSubtitle': 'Choose a location to start your inventory count.',
    'inventory.wizard.hubInfoBanner': 'Complete every assigned location before you can submit.',
    'inventory.wizard.hubLocationsCompleted': '{{done}} / {{total}} locations completed',
    'inventory.wizard.tapToStart': 'Tap to start',
    'inventory.wizard.locationCompletedLine': 'Completed',
    'inventory.wizard.locationHubStatusInProgress': 'More details needed',
    'inventory.wizard.locationHubStatusNeedsStock': 'Choose stock level for items',
    'inventory.wizard.locationHubStatusNeedsExpiry': 'Choose expiry option for items',
    'inventory.wizard.locationHubStatusNeedsCategories': 'Tap Continue on Review items to confirm empty categories',
    'inventory.wizard.hubNextStep': 'Next step',
    'inventory.wizard.hubNextDisabledHint': 'Finish all locations before continuing.',
    'inventory.wizard.stepBadge': 'Step {{current}} / {{total}}',
    'inventory.wizard.stepBadgeWithSubstep': 'Step {{major}}{{sub}} / {{total}}',
    'inventory.wizard.stepBadgeWithSubstepAria': 'Current step {{major}}{{sub}} of {{total}}',
    'inventory.wizard.selectLocationIncompleteSection': 'Incomplete',
    'inventory.wizard.selectLocationCompleteSection': 'Completed',
    'inventory.wizard.selectLocationModalCloseAria': 'Close location picker',
    'inventory.wizard.selectLocationFinalReviewButton': 'Final review',
    'inventory.wizard.leaveHomeStockWorkButton': 'Leave Home Stock work',
    'inventory.wizard.viewThisTaskButton': 'View this task',
    'inventory.wizard.leaveHomeStockWorkConfirmTitle': 'Leave Home Stock work?',
    'inventory.wizard.leaveHomeStockWorkConfirmDescription':
      'Anything you have not submitted will not be saved. Return to the Today task list?',
    'inventory.wizard.progressAria': 'Progress about {{percent}} percent',
    'inventory.wizard.photosStepTitle': 'Capture photos',
    'inventory.wizard.photosStepSubtitle': 'Take or upload Home Stock photos for {{location}}.',
    'inventory.wizard.reviewItemsTitle': 'Review items',
    'inventory.wizard.reviewItemsSubtitle': 'Verify the items and update their status.',
    'inventory.wizard.continueToReviewItems': 'Continue to review items',
    'inventory.wizard.continueWithAiAnalyze': 'AI analyze & review items ⮕',
    'inventory.wizard.continueWithoutAi': 'Review items (no AI) ⮕',
    'inventory.wizard.aiSelectModalTitle': 'Choose photos to analyze',
    'inventory.wizard.aiSelectModalDescription':
      'Analyzed and not-yet-analyzed photos are listed separately. Only not-yet-analyzed photos are selected by default.',
    'inventory.wizard.aiSelectModalNotAnalyzedHeading': 'Not yet analyzed',
    'inventory.wizard.aiSelectModalAnalyzedHeading': 'Already analyzed',
    'inventory.wizard.aiSelectModalSectionEmpty': '(none)',
    'inventory.wizard.aiSelectModalConfirm': 'Run AI analysis',
    'inventory.wizard.aiSelectPhotoCheckboxAria': 'Analyze photo {{n}}',
    'inventory.wizard.reanalyzeConfirmTitle': 'Re-analyze photos?',
    'inventory.wizard.reanalyzeConfirmDescription':
      'Data for {{items}} item(s) linked to the selected {{photos}} photo(s) will be cleared and replaced with new AI results. Items on other photos are unchanged.',
    'inventory.wizard.reanalyzeConfirmProceed': 'Confirm & analyze',
    'inventory.stocktakeDailyAiLimitBatchCancelled':
      "Today's AI quota is insufficient; analysis for all selected photos was cancelled.",
    'inventory.removeStocktakePhotoWithItemsDescription':
      'This photo is linked to {{count}} item(s). Delete the photo and those items?',
    'inventory.removeStocktakePhotoDeleteWithItems': 'Delete photo & linked items',
    'inventory.removeStocktakePhotoKeepAll': 'Keep photo & items',
    'inventory.manualAdd.photoRequired': 'Choose which photo this item belongs to.',
    'inventory.manualAdd.photoPlaceholder': 'Select photo',
    'inventory.itemPhotoLabel': 'Photo',
    'inventory.itemPhotoPickerAria': 'Choose item photo',
    'task.review.inventoryItemPhotoAria': 'Item photo',
    'inventory.wizard.saveAndNextLocation': 'Save & next location',
    'inventory.wizard.continueToFinalReview': 'Continue to final review',
    'inventory.wizard.finalReviewTitle': 'Final review',
    'inventory.wizard.finalReviewSubtitle': 'Verify your counts and summaries before notifying your employer.',
    'inventory.wizard.statLowStock': 'Low stock items',
    'inventory.wizard.statExpiringSoon': 'Expiring soon',
    'inventory.wizard.statTotalCategories': 'Total categories',
    'inventory.wizard.statTotalPhotos': 'Total photos',
    'inventory.statDetailModal.lowStockTitle': 'Low stock items',
    'inventory.statDetailModal.expiringTitle': 'Expiring items (within {{days}} days)',
    'inventory.statDetailModal.categoryLabel': 'Category',
    'inventory.statDetailModal.locationLabel': 'Location',
    'inventory.statDetailModal.expiryHeading': 'Expiry status',
    'inventory.statDetailModal.empty': 'No items to list.',
    'inventory.statDetailModal.closeButton': 'Close',
    'inventory.statDetailModal.closeAria': 'Close details dialog',
    'inventory.statDetailModal.dismissBackdropAria': 'Close details (tap backdrop)',
    'inventory.statDetailModal.openLowStockAria': 'Open low stock details, {{count}} items',
    'inventory.statDetailModal.openExpiringAria': 'Open expiring details, {{count}} items',
    'inventory.wizard.locationDetailsSection': 'Location summary',
    'inventory.wizard.submissionInfoSection': 'Submission info',
    'inventory.wizard.finalReviewDateLabel': 'Date',
    'inventory.wizard.finalReviewCompletionTimeLabel': 'Completion time',
    'inventory.wizard.readyToSubmitTitle': 'Ready to submit?',
    'inventory.wizard.readyToSubmitDisclaimer':
      'Submitting notifies your employer for review. You cannot edit these counts after submission.',
    'inventory.wizard.sendReport': 'Submit',
    'inventory.wizard.addItemManuallyTitle': 'Add item manually',
    'inventory.wizard.renameItemPrompt': 'Item name',
    'inventory.wizard.renameItemAria': 'Edit item name',
    'inventory.wizard.removeManualItemTitle': 'Remove this item?',
    'inventory.wizard.removeManualItemDescription':
      'This will remove “{{name}}” and its stock and expiry data for this location. This cannot be undone.',
    'inventory.wizard.reviewLocationBadgeAria': 'Current location: {{location}}',
    'inventory.wizard.editCategoryAria': 'Edit category',
    'inventory.wizard.editAllLocations': 'Edit all',
    'inventory.wizard.locationItemsBadge': 'Items',
    'inventory.wizard.reviewLocationStatItemsCaption': 'ITEMS',
    'inventory.wizard.reviewLocationStatEmptyCatCaption': 'EMPTY CAT.',
    'inventory.wizard.reviewLocationStatEmptyCatCaptionFull': 'Empty categories',
    'inventory.wizard.reviewLocationStatPhotosCaption': 'PHOTOS',
    'inventory.wizard.previewLocationResults': 'Preview',
    'inventory.wizard.previewLocationResultsAria': 'Preview home stock results for {{name}}',
    'inventory.wizard.previewLocationResultsCloseAria': 'Close home stock results preview',
    'inventory.wizard.previewLocationResultsDismissBackdropAria': 'Close home stock results preview (tap backdrop)',
    'inventory.manualAdd.title': 'Add an item manually',
    'inventory.manualAdd.sectionAria': 'Manual category and item entry',
    'inventory.manualAdd.expandToggleAria': 'Expand add item manually form',
    'inventory.manualAdd.collapseToggleAria': 'Collapse add item manually form',
    'inventory.manualAdd.hint':
      'If an item is missing from the inventory list or was not detected in photos, type its category and name. When the employer defined categories for this task, choose one from the list.',
    'inventory.manualAdd.saveToMasterHint':
      'Your employer allows helpers to edit the inventory master list: when creation succeeds it is saved there too (if it fails, the item can still be included in this submission only).',
    'inventory.manualAdd.onlySubmissionHint':
      'If editing the inventory master list is disabled: items you add here are submitted with this Home Stock task only and are not added to the master list by default.',
    'inventory.manualAdd.categoryLabel': 'Category',
    'inventory.manualAdd.categoryPlaceholder': 'Select a category',
    'inventory.manualAdd.categoryOtherLabel': 'Category (type)',
    'inventory.manualAdd.categoryOtherPlaceholder': 'Category name (up to 120 characters)',
    'inventory.manualAdd.itemNameLabel': 'Item name',
    'inventory.manualAdd.itemNamePlaceholder': 'e.g. Laundry detergent',
    'inventory.manualAdd.addButton': 'Add item',
    'inventory.manualAdd.adding': 'Adding…',
    'inventory.manualAdd.validationBoth': 'Enter both category and item name.',
    'inventory.manualAdd.validationCategory': 'Pick one of the categories defined for this task.',
    'inventory.manualAdd.stockRequired': 'Choose a stock level (full / half / low / empty).',
    'inventory.manualAdd.expiryChoiceRequired':
      'Choose “no expiry” or “has expiry date(s)”, and enter dates when needed.',
    'inventory.manualAdd.limitReached': 'You can add up to {{max}} manual items.',
    'inventory.manualAdd.addFailed': 'Could not add the item. Try again.',
    'inventory.manualAdd.remove': 'Remove',
    'inventory.manualAdd.removeAria': 'Remove manually added item {{name}}',
    'inventory.manualAdd.rowCategoryBadge': 'Category: {{category}}',
    'inventory.manualAdd.rowCategoryEditLabel': 'Category (editable)',
    'inventory.expiry.sectionAria': 'Expiry dates',
    'inventory.expiry.sectionTitle': 'Expiry',
    'inventory.expiry.noExpiryLabel': 'No expiry',
    'inventory.expiry.noExpiryAria': '{{item}}: no expiry',
    'inventory.expiry.hasDatesLabel': 'Has expiry date(s)',
    'inventory.expiry.hasDatesAria': '{{item}}: enter expiry date(s)',
    'inventory.expiry.datePickerAria': '{{item}} expiry date {{n}}',
    'inventory.expiry.removeDate': 'Remove',
    'inventory.expiry.removeDateAria': 'Remove {{item}} expiry date {{n}}',
    'inventory.expiry.addDate': 'Add another expiry date',
    'inventory.expiry.maxDatesHint': 'Up to 10 expiry dates per item.',
    'inventory.expiry.required': 'Enter at least one expiry date for “{{item}}”, or choose “no expiry”.',
    'inventory.expiry.chooseModeHint': 'Choose “no expiry” or “has expiry date(s)”.',
    'inventory.expiry.choiceRequired':
      'Choose how to handle expiry for “{{item}}” (no expiry or one or more dates).',
    'inventory.reviewLinesHeading': 'Stocktake items',
    // Employer — create stocktake
    'task.inventory.newTaskTitle': 'Create Home Stock',
    'task.inventory.editTaskTitle': 'Edit Home Stock',
    'task.inventory.loadError': 'Could not load inventory data',
    'task.inventory.employerOnly': 'Employer accounts only',
    'task.inventory.chainHint':
      'Helpers must complete Home Stock in due date order. You cannot work on a later instance until all earlier ones are approved. Set recurrence when you create or edit Home Stock (not on the checklist).',
    'task.inventory.listScheduleMovedHint': 'A checklist is only a set of line items. Set recurrence when you create a Home Stock task.',
    'task.inventory.editListHint':
      'After you change the checklist, the helper stocktake flow and future submissions use the new line items.',
    'task.inventory.editListItemsPreview': 'Items in this checklist',
    'task.inventory.editListEmpty': 'This checklist has no items yet',
    'task.inventory.editChecklistItemsHeading': 'Checklist line items',
    'task.inventory.editChecklistItemsSub':
      'Select inventory items to include. Order follows your inventory list. Use the green save button, or save the task below to persist.',
    'task.inventory.editTaskOptionsHeading': 'Home Stock task',
    'task.inventory.editTaskSubmitHint':
      'To save only the checklist, use the green “Save list line items” button. The main button also saves title, description, date, notification, and binds this task to the selected checklist.',
    'task.inventory.newTaskChecklistHint':
      'Select inventory items for this checklist. If you change the checkboxes, the list is saved before the task is created.',
    'task.inventory.sections.lists': 'Checklists',
    'task.inventory.createListCta': 'New checklist',
    'task.inventory.noListsHint': 'No checklists yet. Create a checklist and add line items first.',
    'task.inventory.createListTitle': 'New checklist',
    'task.inventory.listNameLabel': 'Checklist name',
    'task.inventory.scheduleLabel': 'Schedule',
    'task.inventory.schedule.none': 'No recurring schedule (create tasks manually)',
    'task.inventory.schedule.weekly': 'Weekly',
    'task.inventory.schedule.monthlyFirstWday': 'First weekday of month',
    'task.inventory.weekdayLabel': 'Weekday',
    'task.inventory.createListError': 'Could not create checklist',
    'task.inventory.createListSuccess': 'Checklist created',
    'task.inventory.listNameRequired': 'Enter a checklist name',
    'task.inventory.assignItemsTitle': 'This checklist has no items',
    'task.inventory.assignItemsHint': 'Pick items from your inventory, or add a new item first.',
    'task.inventory.quickAddItem': 'Item name',
    'task.inventory.addItemCta': 'Add to inventory',
    'task.inventory.createItemError': 'Could not add item',
    'task.inventory.createItemSuccess': 'Item added to inventory',
    'task.inventory.itemNameRequired': 'Enter an item name',
    'task.inventory.itemNameLabel': 'Item name',
    'task.inventory.renameItemCta': 'Rename',
    'task.inventory.removeFromListCta': 'Remove from all stocktake checklists',
    'task.inventory.editListItemsModalCta': 'Edit checklist items',
    'task.inventory.removeListCta': 'Delete checklist',
    'task.inventory.removeListConfirmTitle': 'Delete this Home Stock checklist?',
    'task.inventory.removeListConfirmBody':
      'After deletion, all active Home Stock tasks linked to this checklist become expired and must pick another checklist.',
    'task.inventory.removeListAffectedTasksCount': 'Affected tasks count: {{count}}',
    'task.inventory.removeListAffectedTasksLabel': 'Affected Home Stock tasks:',
    'task.inventory.removeListConfirmCta': 'Delete checklist',
    'task.inventory.removeListSuccess': 'Checklist deleted',
    'task.inventory.removeListError': 'Could not delete checklist',
    'task.inventory.updateListSuccess': 'Checklist name updated',
    'task.inventory.updateListError': 'Could not update checklist name',
    'task.inventory.renameListModalTitle': 'Rename checklist',
    'task.inventory.renameListModalOpenCta': 'Rename checklist',
    'task.inventory.addedButNotSelected': 'Added to inventory only. Checklist max reached, so it was not selected.',
    'task.inventory.createListUseHint': 'After creating this checklist, save checklist items first to use it.',
    'task.inventory.removeFromListConfirmTitle': 'Remove this item from all stocktake checklists?',
    'task.inventory.removeFromListConfirmBody': 'Confirm to remove? Cannot undo once confirmed.',
    'task.inventory.removeFromListConfirmCta': 'Remove from all stocktake checklists',
    'task.inventory.renameItemModalTitle': 'Rename item',
    'task.inventory.renameItemSave': 'Save',
    'task.inventory.updateItemError': 'Could not update item',
    'task.inventory.maxItemsNote': '{{count}} items selected',
    'task.inventory.saveListItemsCta': 'Save checklist items',
    'task.inventory.saveListItemsError': 'Could not save items',
    'task.inventory.saveListItemsSuccess': 'Checklist items saved',
    'task.inventory.pickAtLeastOneItem': 'Select at least one item',
    'task.inventory.formHeading': 'Create Home Stock work',
    'task.inventory.wizard.stepOf': 'Step {{current}} / {{total}}',
    'task.inventory.wizard.step1Title': 'Title & description',
    'task.inventory.wizard.step2Title': 'Locations',
    'task.inventory.wizard.step3Title': 'Categories',
    'task.inventory.wizard.step4Title': 'Schedule & dates',
    'task.inventory.wizard.step5Title': 'Notifications',
    'task.inventory.wizard.step6Title': 'Checklist & items',
    'task.inventory.wizard.step7Title': 'Review & create',
    'task.inventory.wizard.next': 'Next',
    'task.inventory.wizard.back': 'Back',
    'task.inventory.wizard.reviewConfirm': 'Create task',
    'task.inventory.wizard.reviewSave': 'Save changes',
    'task.inventory.wizard.categoriesHint': 'Add at least one category (suggestions below).',
    'task.inventory.wizard.locationsHint': 'Add at least one location (suggestions below).',
    'task.inventory.wizard.suggestHeading': 'Suggestions',
    'task.inventory.suggest.location.kitchen': 'Kitchen',
    'task.inventory.suggest.location.fridge': 'Fridge',
    'task.inventory.suggest.location.storeRoom': 'Store Room',
    'task.inventory.suggest.location.livingRoom': 'Living Room',
    'task.inventory.suggest.location.bathroomA': 'Bathroom A',
    'task.inventory.suggest.location.bathroomB': 'Bathroom B',
    'task.inventory.suggest.category.food': 'Food',
    'task.inventory.suggest.category.drinks': 'Drinks',
    'task.inventory.suggest.category.babyFood': 'Baby & Kids Food',
    'task.inventory.suggest.category.babyDrinks': 'Baby & Kids Drinks',
    'task.inventory.suggest.category.elderlyFood': 'Elderly Food',
    'task.inventory.suggest.category.petFood': 'Pet Food',
    'task.inventory.suggest.category.dryGoods': 'Dry Goods',
    'task.inventory.suggest.category.chilledGoods': 'Chilled Goods',
    'task.inventory.wizard.addCategoryPlaceholder': 'Type a category, then add',
    'task.inventory.wizard.addLocationPlaceholder': 'Type a location, then add',
    'task.inventory.wizard.addChip': 'Add',
    'task.inventory.wizard.removeChipAria': 'Remove {{label}}',
    'task.inventory.wizard.expiryLeadsLabel': 'Notify before Home Stock expiry (days)',
    'task.inventory.wizard.expiryLeadsHint': 'Optional multi-select; skip if you do not want expiry alerts.',
    'task.inventory.wizard.titleRequired': 'Title is required',
    'task.inventory.wizard.categoriesRequired': 'Add at least one category',
    'task.inventory.wizard.locationsRequired': 'Add at least one location',
    'task.inventory.wizard.summaryTitle': 'Title',
    'task.inventory.wizard.summaryDesc': 'Description',
    'task.inventory.wizard.summaryCategories': 'Categories',
    'task.inventory.wizard.summaryLocations': 'Locations',
    'task.inventory.wizard.summaryList': 'Checklist',
    'task.inventory.wizard.summaryRecurrence': 'Schedule',
    'task.inventory.wizard.summaryDueTime': 'Due time',
    'task.inventory.wizard.summaryNotify': 'Notify employer on completion',
    'task.inventory.wizard.summaryNotifyOn': 'Yes',
    'task.inventory.wizard.summaryNotifyOff': 'No',
    'task.inventory.wizard.summaryExpiryLeads': 'Expiry lead days',
    'task.inventory.wizard.summaryNone': '(none)',
    'task.inventory.wizard.reviewCardHeading': 'Home Stock task',
    'task.inventory.selectListLabel': 'Checklist',
    'task.inventory.selectListPlaceholder': 'Select…',
    'task.inventory.selectListRequired': 'Select a checklist',
    'task.inventory.listHasNoItems': 'This checklist has no items yet. Add items above first.',
    'task.inventory.titleOptional': 'Optional — a default title is used if empty',
    'task.inventory.titlePlaceholder': 'e.g. Kitchen Home Stock this week',
    'task.inventory.submitCreateTask': 'Create Home Stock task',
    'task.inventory.createTaskError': 'Could not create Home Stock task',
    'task.inventory.createTaskSuccess': 'Home Stock task created',
    'task.inventory.dueDateInvalid': 'Choose a valid due date',
    // Task Completion (Helper)
    'task.complete.title': 'Complete Task',
    'task.complete.subtitle': 'Submit proof of task completion',
    'task.resubmit.photoHint': 'You can remove wrong photos, then take or pick new ones.',
    'task.complete.photoSection': 'Photos (1-3)',
    'task.complete.submittedPhotosOnly': 'Submitted photos',
    'task.complete.photoHint': 'Tap to take a photo or choose from gallery',
    'task.complete.compressingPhotos': 'Compressing photos…',
    'task.complete.takePhoto': 'Camera',
    'task.complete.choosePhoto': 'Gallery',
    'task.complete.photoAlt': 'Task photo {{n}}',
    'task.complete.enlargePhotoAria': 'View task photo full size',
    'task.complete.removePhoto': 'Remove photo',
    'task.complete.gpsSection': 'GPS Location',
    'task.complete.gpsOn': 'Enabled',
    'task.complete.gpsOff': 'Disabled',
    'task.complete.gpsHint': 'Enable to record your location',
    'task.complete.noteSection': 'Note',
    'task.complete.notePlaceholder': 'Add a note (optional)...',
    'task.complete.completedAtLabel': 'Completed time',
    'task.complete.completedAtRequired': 'Please enter completed date and time',
    'task.complete.submit': 'Submit',
    'task.complete.submitting': 'Submitting...',
    'task.complete.submitSuccess': 'Task submitted successfully',
    'task.complete.submitError': 'Submission failed',
    'task.complete.photoRequired': 'Please upload at least 1 photo',
    'task.complete.photoUploadError':
      'Could not upload photos to cloud storage. Check your connection, try again, and ensure the GCS bucket CORS allows this site.',
    'task.complete.offlineSaved': 'Saved offline, will sync when online',
    // Task Resubmit (Helper)
    'task.resubmit.title': 'Resubmit Task',
    'task.resubmit.subtitle': 'Modify and resubmit based on employer feedback',
    'task.resubmit.previousNote': 'Previous note',
    'task.resubmit.submit': 'Resubmit',
    'task.resubmit.submitSuccess': 'Resubmitted successfully',
    // Common Actions
    'task.action.back': 'Back',
    'task.action.refresh': 'Refresh',
    'task.action.delete': 'Delete',
    'task.action.archive': 'Archive',
    'task.action.archiveConfirm': 'Are you sure you want to archive this task?',
    'task.action.archiveSuccess': 'Task archived',
    'task.action.archiveError': 'Failed to archive task',
    // Admin Portal
    'admin.brand': 'MaidEasy Admin',
    'admin.sidebarAria': 'Admin navigation',
    'admin.openMenu': 'Open menu',
    'admin.closeMenu': 'Close menu',
    'admin.languageSwitcherAria': 'Interface language',
    'admin.languageChinese': 'Traditional Chinese',
    'admin.languageEnglish': 'English',
    'admin.loginTitle': 'Admin sign-in',
    'admin.loginSubtitle': 'Separate from employer/helper accounts',
    'admin.email': 'Email',
    'admin.password': 'Password',
    'admin.totp': 'Two-factor code (6 digits)',
    'admin.totpHint': 'When email 2FA is enabled, a code is required after password sign-in',
    'admin.otpModalTitle': 'Email verification code',
    'admin.otpModalHint':
      'We sent a 6-digit code to your admin email (valid for about 5 minutes).',
    'admin.otpCode': 'Verification code',
    'admin.otpSentHint': 'Verification code sent. Check your email.',
    'admin.otpResentHint': 'A new verification code was sent.',
    'admin.otpRequired': 'Please enter the verification code.',
    'admin.verifyOtp': 'Verify and sign in',
    'admin.resendOtp': 'Resend code',
    'admin.signIn': 'Sign in',
    'admin.signingIn': 'Signing in…',
    'admin.logout': 'Log out',
    'admin.idleWarningTitle': 'Session about to expire',
    'admin.idleWarningDescription':
      'You have been inactive for about 28 minutes. Choose Continue to extend your session, or you will be signed out in about 2 minutes.',
    'admin.idleExtendSession': 'Continue',
    'admin.idleLogoutNow': 'Log out now',
    'admin.nav.dashboard': 'Dashboard',
    'admin.nav.users': 'Users',
    'admin.nav.settings': 'System settings',
    'admin.nav.invitationCodes': 'Invitation codes',
    'admin.nav.systemMessages': 'System messages',
    'admin.nav.audit': 'Audit logs',
    'admin.nav.admins': 'Admin accounts',
    'admin.nav.faqs': 'FAQ management',
    'admin.nav.legalDocuments': 'Legal documents',
    'admin.nav.backHome': 'Back to site',
    'admin.systemMessage.title': 'System messages',
    'admin.systemMessage.subtitle':
      'Super-admin only. Broadcast messages are auto-translated to all 5 languages on save.',
    'admin.systemMessage.createTitle': 'Create system message',
    'admin.systemMessage.editTitle': 'Edit system message',
    'admin.systemMessage.subjectZhHant': 'Subject (Traditional Chinese)',
    'admin.systemMessage.subjectEn': 'Subject (English)',
    'admin.systemMessage.detailZhHant': 'Detail HTML (Traditional Chinese)',
    'admin.systemMessage.detailEn': 'Detail HTML (English)',
    'admin.systemMessage.displayFrom': 'Display from (HKT, YYYY-MM-DD hh:mm, optional)',
    'admin.systemMessage.displayTo': 'Display to (HKT, YYYY-MM-DD hh:mm, optional)',
    'admin.systemMessage.tickerBgColor': 'Ticker background color',
    'admin.systemMessage.active': 'Active',
    'admin.systemMessage.showOnHome': 'Home ticker display',
    'admin.systemMessage.showOnHomeHint':
      'If unchecked, the message still appears in the system message list but not on the home ticker.',
    'admin.systemMessage.showOnEmployerDashboard': 'Show on employer dashboard',
    'admin.systemMessage.showOnHelperHome': 'Show on helper home',
    'admin.systemMessage.roles': 'Roles (blank = all)',
    'admin.systemMessage.levels': 'Levels (blank = all; helper follows employer)',
    'admin.systemMessage.blankMeansAll': 'Blank = no filter',
    'admin.systemMessage.matchMode': 'Role × level combine mode',
    'admin.systemMessage.channels': 'Notification channels (create only; edits do not resend)',
    'admin.systemMessage.editNoResend':
      'Edits update content only and do not resend. Pending scheduled sends use the latest content.',
    'admin.systemMessage.saveCreate': 'Save & send',
    'admin.systemMessage.saveEdit': 'Save changes',
    'admin.systemMessage.cancelEdit': 'Cancel edit',
    'admin.systemMessage.listTitle': 'Message list',
    'admin.systemMessage.listEmpty': 'No system messages yet.',
    'admin.systemMessage.colSubject': 'Subject',
    'admin.systemMessage.colActive': 'Active',
    'admin.systemMessage.colNotify': 'Notify status',
    'admin.systemMessage.colFrom': 'Display from',
    'admin.systemMessage.edit': 'Edit',
    'admin.systemMessage.loadError': 'Failed to load system messages',
    'admin.systemMessage.saveError': 'Failed to save',
    'admin.systemMessage.estimateError': 'Failed to estimate audience',
    'admin.systemMessage.subjectRequired': 'Enter at least Traditional Chinese or English subject',
    'admin.systemMessage.confirmTitle': 'Confirm send system message?',
    'admin.systemMessage.confirmTotal': 'Matching users: {{count}}',
    'admin.systemMessage.confirmInApp': 'In-app will send: {{count}}',
    'admin.systemMessage.confirmPush': 'Web push eligible: {{count}}',
    'admin.systemMessage.confirmEmail': 'Email eligible: {{count}}',
    'admin.systemMessage.confirmWhen': 'Notify at (HKT): {{when}}',
    'admin.systemMessage.confirmWhenNone': 'No notification',
    'admin.systemMessage.confirmSend': 'Confirm save & send',
    'admin.systemMessage.linkPrompt': 'Enter https URL',
    'admin.systemMessage.editorBold': 'Bold',
    'admin.systemMessage.editorItalic': 'Italic',
    'admin.systemMessage.editorUl': 'Bullet list',
    'admin.systemMessage.editorOl': 'Numbered list',
    'admin.systemMessage.editorLink': 'Link',
    'admin.systemMessage.editorImage': 'Insert image',
    'admin.systemMessage.editorImageLink': 'Text that opens an image',
    'admin.systemMessage.editorModeVisual': 'Visual',
    'admin.systemMessage.editorModeHtml': 'HTML',
    'admin.systemMessage.imageUrlPrompt': 'Enter https image URL',
    'admin.systemMessage.imageUrlInvalid': 'Image URL must be https://',
    'admin.systemMessage.imageMaxWidthPrompt': 'max-width (optional, e.g. 100%, 20rem, 320px)',
    'admin.systemMessage.imageMaxHeightPrompt': 'max-height (optional, e.g. 50vh, 12rem)',
    'admin.systemMessage.imageLinkTextPrompt': 'Link text (opens image viewer)',
    'admin.systemMessage.imageClickUrlPrompt':
      'Click URL (leave empty to enlarge the image; https or a site path such as /settings)',
    'admin.systemMessage.imageClickUrlInvalid':
      'Click URL must be https:// or a site path starting with /',
    'admin.settingsTab.maintenance': 'Maintenance',
    'admin.maintenance.subtitle': 'Schedule and flags (super-admin only). Times are Asia/Hong_Kong.',
    'maintenance.title': 'Under maintenance',
    'maintenance.defaultMessage':
      'The system is under maintenance and temporarily unavailable. Please try again later.',
    'admin.dashboardSubtitle': 'Overview and daily activity',
    'admin.stat.totalUsers': 'Total users',
    'admin.stat.employers': 'Employers',
    'admin.stat.helpers': 'Helpers',
    'admin.stat.pairings': 'Total pairings',
    'admin.stat.activePairings': 'Active pairings',
    'admin.stat.todayActive': 'Today active users (logins)',
    'admin.stat.todayNew': 'New users today',
    'admin.stat.todayTasks': 'Task instances due today',
    'admin.stat.todayReceipts': 'Receipts submitted today',
    'admin.usersSubtitle': 'Search accounts and open account detail',
    'admin.usersEmailFilter': 'Filter by email',
    'admin.usersRoleFilter': 'Role',
    'admin.usersPairedFilter': 'Pairing',
    'admin.usersLevelFilter': 'Membership level',
    'admin.usersRemovalRequestedFilter': 'Account removed',
    'admin.filter.roleAll': 'All roles',
    'admin.filter.roleEmployer': 'Employer',
    'admin.filter.roleHelper': 'Helper',
    'admin.filter.pairedAll': 'All',
    'admin.filter.pairedYes': 'Paired',
    'admin.filter.pairedNo': 'Not paired',
    'admin.filter.levelAll': 'All levels',
    'admin.filter.level1': 'L1 (Trial)',
    'admin.filter.level2': 'L2 (Free)',
    'admin.filter.level3': 'L3 (Premium)',
    'admin.filter.removalRequestedAll': 'Account removed: All',
    'admin.filter.removalRequestedYes': 'Account removed: Yes',
    'admin.filter.removalRequestedNo': 'Account removed: No',
    'admin.summary.accountRemovalRequested': 'Account removed',
    'admin.summary.accountRemovalRequestedAt': 'Removed at',
    'admin.usersColNickname': 'Nickname',
    'admin.usersColRole': 'Role',
    'admin.usersColLevel': 'Level',
    'admin.usersColPaired': 'Paired',
    'admin.usersColActions': 'Actions',
    'admin.usersViewDetail': 'Account detail',
    'admin.usersNewPasswordPlaceholder': 'New password',
    'admin.usersConfirmNewPasswordPlaceholder': 'Confirm new password',
    'admin.createConfirmPasswordPlaceholder': 'Confirm password',
    'admin.faqsSubtitle':
      'Manage public FAQ content (Traditional Chinese / Simplified Chinese / English), with role, order, and active status.',
    'admin.faqCreateTitle': 'Create FAQ',
    'admin.faqEditTitle': 'Edit FAQ',
    'admin.faqFormIncomplete': 'Please complete question and answer fields in all three languages.',
    'admin.faqSortOrder': 'Sort order (smaller number first)',
    'admin.faqIsActive': 'Active FAQ',
    'admin.faqQuestionZhHant': 'Question (Traditional Chinese)',
    'admin.faqAnswerZhHant': 'Answer (Traditional Chinese)',
    'admin.faqQuestionZhHans': 'Question (Simplified Chinese)',
    'admin.faqAnswerZhHans': 'Answer (Simplified Chinese)',
    'admin.faqQuestionEn': 'Question (English)',
    'admin.faqAnswerEn': 'Answer (English)',
    'admin.faqShowInactive': 'Show inactive FAQ',
    'admin.legalDocumentsSubtitle':
      'Manage English, Traditional Chinese, and Simplified Chinese content and versions for Privacy Policy / Terms of Service / PICS / Subscription Terms.',
    'admin.legalCreateTitle': 'Create legal document version',
    'admin.legalEditTitle': 'Edit legal document',
    'admin.legalFormIncomplete': 'Please complete version, title, and English content.',
    'admin.legalVersion': 'Version (for example v1, v2)',
    'admin.legalTitle': 'Title',
    'admin.legalContent': 'Content (Markdown headings supported)',
    'admin.legalEffectiveFrom': 'Effective from (HKT, YYYY-MM-DD hh:mm)',
    'admin.legalIsActive': 'Activate this version (only one active version per document type)',
    'admin.legalShowInactive': 'Show non-active versions',
    'admin.legalType.privacy': 'Privacy Policy',
    'admin.legalType.terms': 'Terms of Service',
    'admin.legalType.pics': 'PICS',
    'admin.legalType.subscription_terms': 'Subscription Terms',
    'admin.legalSubscriptionTermsPlaceholderHint':
      'Subscription Terms may use placeholders: {{subscribeButton}}, {{resubscribeButton}}, {{startTrialButton}} (replaced by app button labels at display time). Shown only in the subscription page modal.',
    'admin.legalLocale.en': 'English',
    'admin.legalLocale.zh-Hant': 'Traditional Chinese',
    'admin.legalLocale.zh-Hans': 'Simplified Chinese',
    'admin.active': 'Active',
    'admin.inactive': 'Inactive',
    'admin.edit': 'Edit',
    'admin.remove': 'Remove',
    'admin.create': 'Create',
    'admin.update': 'Update',
    'admin.cancel': 'Cancel',
    'admin.usersResetPassword': 'Reset password',
    'admin.resetPasswordDialogTitle': 'Reset this user’s password?',
    'admin.resetPasswordDialogBody':
      'A new password will be set for {{email}}. This cannot be undone.',
    'admin.resetPasswordSuccess': 'Password reset successfully.',
    'admin.resetPasswordLocalOnlyHint': 'Only available for email-registered (local) accounts.',
    'admin.usersPageInfo': 'Page {{page}} · {{total}} total',
    'admin.prevPage': 'Previous',
    'admin.nextPage': 'Next',
    'admin.yes': 'Yes',
    'admin.no': 'No',
    'admin.usersBack': 'Back to users',
    'admin.userDetailTitle': 'User account',
    'admin.summary.level': 'Membership level',
    'admin.summary.authProvider': 'Auth provider',
    'admin.summary.trialEndsAt': 'L1 trial ends',
    'admin.summary.invitationLevelEndsAt': 'L3 trial ends',
    'admin.summary.subscriptionEndsAt': 'Paid subscription ends',
    'admin.downgradeToL2': 'Downgrade to L2 (free)',
    'admin.downgradeToL2Confirm': 'Downgrade this employer from L3 to L2 immediately? Advanced tasks will be archived and L2 limits applied.',
    'admin.downgradeToL2Success': 'Downgraded to L2.',
    'admin.downgradeToL2Error': 'Downgrade failed.',
    'admin.userSummarySection': 'Overview (read-only)',
    'admin.summary.tasks': 'Tasks (total)',
    'admin.summary.tasksStandard': 'Tasks — general',
    'admin.summary.tasksInventory': 'Tasks — home stock',
    'admin.summary.tasksPickup': 'Tasks — pickup / drop-off',
    'admin.summary.tasksMedication': 'Tasks — medication reminder',
    'admin.summary.invitationCode': 'Invitation code',
    'admin.summary.taskInstances': 'Task instances',
    'admin.summary.receipts': 'Receipts',
    'admin.summary.budgets': 'Budgets',
    'admin.summary.settlements': 'Settlements',
    'admin.summary.family': 'Family members',
    'admin.loginInfoSection': 'Login information',
    'admin.summary.registeredAt': 'Registered at',
    'admin.summary.registeredIp': 'Registration IP',
    'admin.summary.lastLoginAt': 'Last login at',
    'admin.summary.lastLoginIp': 'Last login IP',
    'admin.summary.lastLoginDevice': 'Last login device',
    'admin.summary.usageRegion': 'Usage region',
    'admin.paymentSection': 'Payment information',
    'admin.summary.stripeCustomer': 'Stripe customer',
    'admin.summary.stripeCustomerLink': 'Open Stripe customer',
    'admin.paymentEmpty': 'No Stripe invoices found.',
    'admin.paymentColPaidAt': 'Paid at',
    'admin.paymentColPeriod': 'Subscription period',
    'admin.paymentColAmount': 'Amount',
    'admin.paymentColStatus': 'Status',
    'admin.removeAccountTitle': 'Delete account',
    'admin.removeAccountHint':
      'Ends active pairing, archives the account, cancels Stripe subscription if any, and frees the invitation-code email for reuse. Business data is kept but hidden from the partner.',
    'admin.removeAccountPassword': 'Confirm with your admin password',
    'admin.removeAccountReason': 'Reason (1–500 characters)',
    'admin.removeAccountPasswordRequired': 'Admin password is required.',
    'admin.removeAccountReasonInvalid': 'Reason must be 1–500 characters.',
    'admin.removeAccountButton': 'Delete account',
    'admin.removeAccountDialogTitle': 'Delete this user account?',
    'admin.removeAccountDialogBody':
      'This will archive {{email}}, end pairing, and block login. Business data remains but the partner will not see history.',
    'admin.removeAccountError': 'Failed to delete account.',
    'admin.pairingSection': 'Pairings',
    'admin.pairingEmpty': 'No pairings',
    'admin.pairingStatus': 'Status',
    'admin.pairingRole': 'Role',
    'admin.settingsSubtitle': 'Membership limits, registration, subscription, and marketing settings',
    'admin.settingsTab.membership': 'Membership limits',
    'admin.settingsTab.register': 'Register & other',
    'admin.settingsTab.subscription': 'Subscription',
    'admin.settingsTab.marketing': 'Marketing page',
    'admin.set.maxDailyTasks': 'Max daily tasks',
    'admin.set.maxDailyReceipts': 'Max daily receipts',
    'admin.set.maxDailyAI': 'Max daily AI analyses',
    'admin.set.maxComments': 'Max comments per target',
    'admin.set.maxCommentLen': 'Max comment length',
    'admin.set.photoRetention': 'Photo/receipt retention (days)',
    'admin.set.consentVersion': 'Current consent version',
    'admin.set.auditYears': 'Audit log retention (years)',
    'admin.set.invitationCodeRequired': 'Require invitation code for employer registration',
    'admin.set.invitationCodeRequiredHint':
      'When off, employers may still enter a code optionally. When on, leaving it blank blocks registration.',
    'admin.set.playStoreShowOfficialWebsiteUrl': 'Show Play Store official-website billing line',
    'admin.set.playStoreShowOfficialWebsiteUrlHint':
      'When on, the Google Play app subscription page, usage page, and limit modal tell users to open MaidEasy in a browser (including the official website URL). Default off. Does not enable in-app Play payments.',
    'admin.invitationCodesTitle': 'Invitation codes',
    'admin.invitationCodesSubtitle': 'Create and manage employer registration invitation codes and view usage.',
    'admin.invitationCodesTabCodes': 'Codes',
    'admin.invitationCodesTabUsages': 'Usage log',
    'admin.invitationCodesFilterCode': 'Search code',
    'admin.invitationCodesFilterStatus': 'Status filter',
    'admin.invitationCodesStatusAll': 'All',
    'admin.invitationCodesStatusActive': 'Active',
    'admin.invitationCodesStatusDisabled': 'Disabled',
    'admin.invitationCodesSearch': 'Search',
    'admin.invitationCodesColCode': 'Code',
    'admin.invitationCodesColLevel': 'Level',
    'admin.invitationCodesColUses': 'Uses',
    'admin.invitationCodesColDates': 'Valid period',
    'admin.invitationCodesColStatus': 'Status',
    'admin.invitationCodesColEmail': 'Email',
    'admin.invitationCodesColNickname': 'Nickname',
    'admin.invitationCodesColUsedAt': 'Registered at',
    'admin.invitationCodesEdit': 'Edit',
    'admin.invitationCodesEmpty': 'No invitation codes yet',
    'admin.invitationCodesUsagesEmpty': 'No usage records yet',
    'admin.invitationCodesCreateTitle': 'Create invitation code',
    'admin.invitationCodesEditTitle': 'Edit invitation code',
    'admin.invitationCodesAutoGenerate': 'Auto-generate code',
    'admin.invitationCodesTimezone': 'Timezone',
    'admin.invitationCodesImmediate': 'Effective immediately',
    'admin.invitationCodesPermanent': 'No end date',
    'admin.invitationCodesUnlimited': 'Unlimited',
    'admin.invitationCodesStartDate': 'Start date (per timezone)',
    'admin.invitationCodesEndDate': 'End date (per timezone)',
    'admin.invitationCodesMaxUses': 'Max uses',
    'admin.invitationCodesUsageStart': 'Usage from date (HKT)',
    'admin.invitationCodesUsageEnd': 'Usage to date (HKT)',
    'admin.invitationCodesSave': 'Save',
    'admin.invitationCodesCancel': 'Cancel',
    'admin.invitationCodesFilterRemark': 'Search remark',
    'admin.invitationCodesColCodeType': 'Code type',
    'admin.invitationCodesColDuration': 'Days',
    'admin.invitationCodesColRemark': 'Internal remark',
    'admin.invitationCodesColUsageContext': 'Usage context',
    'admin.invitationCodesTypeRegistrationGrant': 'Registration grant',
    'admin.invitationCodesTypeL1TrialExtend': 'L1 trial extend',
    'admin.invitationCodesTypeL3TrialPeriod': 'L3 trial period',
    'admin.invitationCodesRemarkPlaceholder': 'Internal remark (optional, max 500 chars)',
    'admin.invitationCodesColReferrer': 'Referrer',
    'admin.invitationCodesReferrerHint':
      'Optional: search and bind one existing user as referrer (one code per person, one person per code).',
    'admin.invitationCodesReferrerSearchPlaceholder': 'Search user by email',
    'admin.invitationCodesReferrerClear': 'Unbind',
    'admin.invitationCodesColLevelGranted': 'Level granted',
    'admin.invitationCodesColCurrentLevel': 'Current level',
    'admin.invitationCodesColFirstPayment': 'First payment (HKT)',
    'admin.invitationCodesDetail': 'Details',
    'admin.saveSettings': 'Save settings',
    'admin.saving': 'Saving…',
    'admin.auditSubtitle': 'Filter audit and security events',
    'admin.auditUserId': 'User Email',
    'admin.auditAction': 'Action',
    'admin.auditTarget': 'Target type',
    'admin.auditStart': 'Start date (HKT)',
    'admin.auditEnd': 'End date (HKT)',
    'admin.auditColTime': 'Time',
    'admin.auditColAction': 'Action',
    'admin.auditColUser': 'User',
    'admin.adminsSubtitle': 'Super admin only',
    'admin.adminsCreate': 'Create admin',
    'admin.adminsCreateSubmit': 'Create',
    'admin.adminsCreateSuccess': 'Admin account created.',
    'admin.adminsList': 'Admin list',
    'admin.adminsForbidden': 'Super admin access required',
    'admin.adminsCol2fa': 'Email 2FA',
    'admin.adminsColStatus': 'Status',
    'admin.adminsColActions': 'Actions',
    'admin.adminsOpenDetail': 'Detail',
    'admin.adminsDetailTitle': 'Admin detail',
    'admin.adminsCreatedAt': 'Created at',
    'admin.adminsCreatedBy': 'Created by',
    'admin.adminsEnableEmail2fa': 'Enable email two-factor authentication',
    'admin.adminsEnable2faHint': 'Takes effect on the next sign-in (email code required then).',
    'admin.adminsCannotDeactivateSelf': 'You cannot deactivate your own account.',
    'admin.adminsUpdateSuccess': 'Admin account updated.',
    'admin.adminsResetPasswordTitle': 'Reset password',
    'admin.adminsResetPasswordHint':
      'Enter the currently signed-in admin (operator) password to confirm. The target must sign in again afterwards.',
    'admin.adminsOperatorPassword': 'Operator password',
    'admin.adminsResetPasswordSuccess':
      'Admin password reset. The target must sign in again.',
    'admin.adminsActivityTitle': 'Recent activity (3 months)',
    'admin.adminsActivityHint': 'Includes login success/failure and related audit events.',
    'admin.adminsActivityEmpty': 'No activity',
    'admin.summary.emailVerified': 'Email verified',
    'admin.pairingPartner': 'Partner',
    'admin.pairingPartnerDetail': 'Partner account detail',
    'admin.sectionExpand': 'Expand',
    'admin.sectionCollapse': 'Collapse',
  },
  [LANGUAGE.ID]: {},
  [LANGUAGE.FIL]: {},
};

UI_MESSAGES[LANGUAGE.ID] = {
  ...UI_MESSAGES[LANGUAGE.EN],
  ...USAGE_REGION_UI_MESSAGES_ID,
  ...UI_MESSAGES_ID,
};

UI_MESSAGES[LANGUAGE.FIL] = {
  ...UI_MESSAGES[LANGUAGE.EN],
  ...USAGE_REGION_UI_MESSAGES_FIL,
  ...UI_MESSAGES_FIL,
};

export const formatMessage = (
  template: string | undefined,
  params?: MessageParams
): string => {
  if (!template) return '';
  if (!params) return template;
  return Object.entries(params).reduce((acc, [paramKey, paramValue]) => {
    const placeholder = `{{${paramKey}}}`;
    const parts = acc.split(placeholder);
    return parts.join(String(paramValue));
  }, template);
};
